import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut, sendPasswordResetEmail, updateProfile, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import {
  MdSearchOff, MdStar, MdStarBorder, MdWarningAmber, MdCalendarToday,
  MdTrendingUp, MdTrendingDown, MdSmartToy, MdCheckCircle, MdCheck,
  MdShowChart, MdBarChart, MdAutoAwesome, MdLightbulb,
  MdPerson, MdLock, MdChevronRight, MdPrivacyTip, MdGavel, MdLogout, MdImage,
  MdGetApp, MdPhoneIphone, MdAndroid, MdIosShare, MdClose, MdAddBox, MdMailOutline,
  MdGpsFixed, MdPublic, MdBalance, MdAccountBalanceWallet, MdStickyNote2,
  MdMenuBook, MdSearch, MdHelpOutline, MdShare, MdTune, MdLocalFireDepartment, MdInsights, MdDownload,
  MdWorkspacePremium, MdCardGiftcard, MdNotificationsActive, MdNotifications, MdNotificationsOff, MdEdit, MdRefresh, MdPsychology,
  MdMemory, MdLocalHospital, MdShoppingCart, MdDirectionsCar, MdBolt, MdCode, MdDomain, MdSchedule,
  MdFlightTakeoff, MdFactory, MdMovie, MdSportsEsports, MdCellTower, MdShoppingBag, MdTerrain, MdStyle, MdRocketLaunch, MdInventory2, MdGroups
} from "react-icons/md";

/* ── Firebase Setup ── */
// IMPORTANT: Replace this config with your actual config from Firebase console
const firebaseConfig = {
  apiKey: "AIzaSyBwpo7iyR0rc3u9aKfORC_vWBSxV8l7tcc",
  authDomain: "quantara-61f8a.firebaseapp.com",
  projectId: "quantara-61f8a",
  storageBucket: "quantara-61f8a.firebasestorage.app",
  messagingSenderId: "933870481982",
  appId: "1:933870481982:web:8db92eca736e71f7b0976f"
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const firestore = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, "europe-west2");
const analyseStockFn = httpsCallable(functions, "analyseStock");
const searchStocksFn = httpsCallable(functions, "searchStocks");
const getMarketIndicesFn = httpsCallable(functions, "getMarketIndices");
const getStockHistoryFn = httpsCallable(functions, "getStockHistory");
const getDailyPickFn = httpsCallable(functions, "getDailyPick");
const getWeeklyInsightFn = httpsCallable(functions, "getWeeklyInsight");
const getWeeklyProposalFn = httpsCallable(functions, "getWeeklyProposal");
const overrideWeeklyPickFn = httpsCallable(functions, "overrideWeeklyPick");
const shuffleWeeklyPickFn = httpsCallable(functions, "shuffleWeeklyPick");
const pushForceRefreshFn = httpsCallable(functions, "pushForceRefresh");
const markDeepDiveReadFn = httpsCallable(functions, "markDeepDiveRead");
const getExchangeRatesFn = httpsCallable(functions, "getExchangeRates");
const getValidationSnapshotsFn = httpsCallable(functions, "getValidationSnapshots");
const dismissThesisAlertFn = httpsCallable(functions, "dismissThesisAlert");
const claimTrialFn = httpsCallable(functions, "claimTrial");
const sendAlertFn = httpsCallable(functions, "sendPriceAlert");
const getStockNewsFn = httpsCallable(functions, "getStockNews");
const screenStocksFn = httpsCallable(functions, "screenStocks", { timeout: 120000 });
const challengeThesisFn = httpsCallable(functions, "challengeThesis", { timeout: 30000 });
const grantFounderGiftFn = httpsCallable(functions, "grantFounderGift");
const applyPendingGiftFn = httpsCallable(functions, "applyPendingGift");

// IMPORTANT — replace with the email you actually sign in with. This account gets
// permanent, unlimited Founder access and is the only account allowed to gift
// Pro subscriptions to other users (enforced server-side in functions/index.js too —
// update it there as well, not just here).
const FOUNDER_EMAIL = "karankothari@gmail.com";
let messagingInstance = null;
try { messagingInstance = getMessaging(firebaseApp); } catch(e) { /* not supported in all environments */ }

/*
  ZOLTRA v10 — Clean rebuild
  - Normal readable sizes (14px base, 16px headings)
  - No viewport units (vw/vh) — they break in artifact sandbox
  - Simple overflow:hidden on root div only
  - All content padded 12px sides
*/

/* ────────────────────────────────────────────────────────────
   GLOSSARY — Add new terms here. One line per entry.
   Fields: term, cat, def
   Categories: Valuation | Earnings | Profitability | Risk |
               Income | Price | Trading | Analysis | Macro | Classification
   ──────────────────────────────────────────────────────────── */
var GLOSSARY = [
  /* Valuation */
  { term: "P/E Ratio",             cat: "Valuation",      def: "Price-to-Earnings ratio. Share price divided by earnings per share. Shows how much investors pay per £/$1 of profit. Lower can mean better value, but varies widely by sector." },
  { term: "EV/EBITDA",             cat: "Valuation",      def: "Enterprise Value divided by EBITDA (earnings before interest, tax, depreciation and amortisation). Unlike P/E, this accounts for how much debt a company carries — useful for comparing companies with very different capital structures, since two stocks can look equally cheap on P/E while one is financed mostly with debt and the other mostly with equity." },
  { term: "Forward P/E",           cat: "Valuation",      def: "Price divided by next 12 months' estimated earnings. More forward-looking than trailing P/E as it reflects expected rather than historical performance." },
  { term: "PEG Ratio",             cat: "Valuation",      def: "P/E divided by earnings growth rate. Below 1.0 suggests undervalued relative to growth. Above 2.0 may indicate overvaluation. Best used for growth stocks." },
  { term: "P/B Ratio",             cat: "Valuation",      def: "Price-to-Book ratio. Share price divided by book value per share (assets minus liabilities). Below 1.0 means the stock trades below its net asset value." },
  { term: "P/S Ratio",             cat: "Valuation",      def: "Price-to-Sales ratio. Market cap divided by annual revenue. Useful for unprofitable companies. Lower is generally better; varies widely by industry." },
  { term: "EV/EBITDA",             cat: "Valuation",      def: "Enterprise Value divided by earnings before interest, tax, depreciation and amortisation. Useful for comparing companies with different capital structures or debt levels." },
  { term: "EV/Revenue",            cat: "Valuation",      def: "Enterprise Value divided by total revenue. Similar to P/S but uses EV (includes debt) rather than market cap. Useful for high-growth or pre-profit companies." },
  { term: "Market Cap",            cat: "Valuation",      def: "Total market value of all outstanding shares. Mega (>$200B), Large ($10–200B), Mid ($2–10B), Small ($300M–2B), Micro (<$300M)." },
  { term: "Enterprise Value",      cat: "Valuation",      def: "Market cap plus total debt minus cash. Represents the theoretical takeover price of a company. More comprehensive than market cap alone." },
  { term: "Intrinsic Value",       cat: "Valuation",      def: "An estimate of a stock's true worth based on fundamentals (earnings, growth, assets), independent of its market price. The goal of value investing." },
  { term: "Margin of Safety",      cat: "Valuation",      def: "Buying a stock significantly below its estimated intrinsic value. Popularised by Benjamin Graham. Reduces downside risk if your valuation estimate is wrong. Often confused with Upside (see that entry) — they describe the same gap between intrinsic value and price, but as a percentage of two different things, so they're never quite the same number." },
  { term: "Upside",                cat: "Valuation",      def: "How much a stock could gain if it reached its estimated intrinsic value, calculated as (intrinsic value − price) ÷ price. Margin of Safety uses the same gap but divides by intrinsic value instead of price, so it's always a smaller percentage than Upside when a stock looks undervalued. Example: intrinsic value $90.50 vs price $71.72 — Upside = 18.78 ÷ 71.72 = 26.2%, Margin of Safety = 18.78 ÷ 90.50 = 20.7%. Upside answers \"how much could I gain\"; Margin of Safety answers \"how much cushion do I have if my estimate is too optimistic.\"" },
  /* Earnings */
  { term: "EPS",                   cat: "Earnings",       def: "Earnings Per Share. Net profit divided by shares outstanding. Growing EPS over multiple years is a strong sign of a healthy, compounding business." },
  { term: "EPS Growth",            cat: "Earnings",       def: "Year-over-year change in earnings per share. High sustained EPS growth drives long-term share price appreciation. Watch for one-off items inflating the figure." },
  { term: "Revenue Growth",        cat: "Earnings",       def: "Year-over-year increase in total sales. Always compare against margin trends — fast growth with shrinking margins needs scrutiny." },
  { term: "Earnings Surprise",     cat: "Earnings",       def: "Difference between actual reported EPS and analyst consensus estimate. A positive surprise often lifts the share price; a miss often causes a sharp fall." },
  { term: "Guidance",              cat: "Earnings",       def: "Management's forward-looking forecast for revenue or earnings. Lowering guidance is often more damaging to a share price than missing the current quarter." },
  /* Profitability */
  { term: "Gross Margin",          cat: "Profitability",  def: "Revenue minus cost of goods sold, as a % of revenue. High gross margins (60%+) indicate pricing power and a strong competitive position." },
  { term: "Operating Margin",      cat: "Profitability",  def: "Operating profit as a % of revenue after all operating expenses. A more complete efficiency measure than gross margin as it includes overhead and R&D." },
  { term: "Net Profit Margin",     cat: "Profitability",  def: "Net income as a % of revenue. The bottom-line margin after all expenses, taxes and interest. Useful for comparing profitability across different-sized companies." },
  { term: "ROE",                   cat: "Profitability",  def: "Return on Equity. Net income divided by shareholders' equity. Measures how effectively management generates profit from shareholder capital. Above 15% is generally strong." },
  { term: "ROA",                   cat: "Profitability",  def: "Return on Assets. Net income divided by total assets. Shows how efficiently a company uses its assets. Compare within the same sector only." },
  { term: "ROIC",                  cat: "Profitability",  def: "Return on Invested Capital. Profit divided by total capital (debt + equity). Above its cost of capital = value creation. One of the best measures of business quality." },
  { term: "FCF",                   cat: "Profitability",  def: "Free Cash Flow. Operating cash flow minus capital expenditure. The real cash a business generates after reinvesting. Often more reliable than reported earnings." },
  { term: "FCF Yield",             cat: "Profitability",  def: "Free cash flow per share divided by share price. Like an earnings yield but uses cash flow. Above 5% is generally considered attractive." },
  { term: "FCF Margin",            cat: "Profitability",  def: "Free cash flow as a % of revenue. High FCF margins indicate a capital-light business that converts revenue into cash efficiently." },
  /* Risk */
  { term: "Beta",                  cat: "Risk",           def: "Measures volatility relative to the market. 1.0 = moves with market. Above 1.0 = more volatile (higher potential reward and risk). Below 1.0 = more stable." },
  { term: "Debt/Equity",           cat: "Risk",           def: "Total debt divided by shareholders' equity. High D/E amplifies returns in good times but magnifies losses in downturns. Varies hugely by industry." },
  { term: "Current Ratio",         cat: "Risk",           def: "Current assets divided by current liabilities. Measures short-term liquidity. Below 1.0 means the company may struggle to meet near-term obligations." },
  { term: "Interest Coverage",     cat: "Risk",           def: "Operating profit divided by interest expense. Shows how easily a company can service its debt. Below 2.0 is a warning sign; above 5.0 is comfortable." },
  { term: "Volatility",            cat: "Risk",           def: "The degree of price fluctuation in a stock. Measured by standard deviation of returns. High volatility means larger potential gains and losses." },
  { term: "Drawdown",              cat: "Risk",           def: "The peak-to-trough decline in a stock's price. A maximum drawdown of 50% means the stock halved at its worst point from peak — psychologically very hard to hold through." },
  /* Income */
  { term: "Dividend Yield",        cat: "Income",         def: "Annual dividend per share divided by share price, as a %. A very high yield (8%+) may signal the dividend is at risk of being cut." },
  { term: "Dividend Cover",        cat: "Income",         def: "Earnings per share divided by dividend per share. Shows how many times profits cover the dividend. Below 1.5x the dividend may be unsustainable." },
  { term: "Payout Ratio",          cat: "Income",         def: "Dividends paid as a % of earnings. A low ratio (30–50%) suggests room to grow the dividend. A very high ratio (>80%) leaves little for reinvestment." },
  { term: "Dividend Growth",       cat: "Income",         def: "The rate at which a company increases its dividend over time. Consistent dividend growers often outperform over the long run. Look for 5+ consecutive years of increases." },
  { term: "Share Buyback",         cat: "Income",         def: "When a company repurchases its own shares, reducing the share count. Increases EPS and often signals management confidence. An alternative to paying dividends." },
  /* Price */
  { term: "52w High / Low",        cat: "Price",          def: "The highest and lowest prices the stock has traded at over the past 52 weeks. Helps understand where current price sits within its recent range." },
  { term: "Analyst Target",        cat: "Price",          def: "The average price target set by sell-side analysts. Reflects consensus upside or downside from current price. Treat as a guide — analysts are often wrong." },
  { term: "Moving Average",        cat: "Price",          def: "The average closing price over a set period (e.g. 50-day or 200-day MA). Used to identify trends. Price above the 200-day MA is generally considered bullish." },
  { term: "Support / Resistance",  cat: "Price",          def: "Price levels where a stock has historically struggled to fall below (support) or rise above (resistance). Widely used in technical analysis." },
  { term: "All-Time High",         cat: "Price",          def: "The highest price a stock has ever traded at. Breaking to a new all-time high often signals strong momentum and attracts more buyers." },
  /* Trading */
  { term: "Bid/Ask Spread",        cat: "Trading",        def: "The gap between what buyers will pay (bid) and sellers will accept (ask). Narrow spread = high liquidity and low transaction cost. Wide spread = illiquid stock." },
  { term: "Liquidity",             cat: "Trading",        def: "How easily a stock can be bought or sold without moving the price significantly. Large-cap stocks like AAPL are highly liquid; small-caps may have thin markets." },
  { term: "Volume",                cat: "Trading",        def: "The number of shares traded in a given period. High volume on a price move confirms its significance. Low-volume moves are less reliable signals." },
  { term: "Market Order",          cat: "Trading",        def: "An instruction to buy or sell at the best available price immediately. Fast execution but no price guarantee — avoid on illiquid stocks." },
  { term: "Limit Order",           cat: "Trading",        def: "An instruction to buy or sell only at a specified price or better. Gives price control but may not execute if the market doesn't reach your price." },
  /* Analysis */
  { term: "Bull Case",             cat: "Analysis",       def: "The optimistic investment thesis. The reasons a stock could significantly outperform — strong growth, undervaluation, new products, or favourable competitive dynamics." },
  { term: "Bear Case",             cat: "Analysis",       def: "The pessimistic investment thesis. The risks that could cause the stock to underperform — competition, debt, slowing growth, regulation, or macro headwinds." },
  { term: "Catalyst",              cat: "Analysis",       def: "An upcoming event expected to move the share price significantly. Common catalysts include earnings reports, product launches, regulatory decisions, and M&A activity." },
  { term: "Moat",                  cat: "Analysis",       def: "A durable competitive advantage that protects a company from competition. Types: network effects (Meta), switching costs (Salesforce), cost advantages (Walmart)." },
  { term: "Margin Expansion",      cat: "Analysis",       def: "When a company's profit margins increase over time due to operating leverage or pricing power. Often drives significant share price re-rating." },
  { term: "Re-rating",             cat: "Analysis",       def: "When the market assigns a higher (or lower) valuation multiple to a stock. A positive re-rating can drive returns independently of earnings growth." },
  { term: "DCF",                   cat: "Analysis",       def: "Discounted Cash Flow. A valuation method that estimates a stock's intrinsic value by projecting future free cash flows and discounting them back to today's value. The core idea: a pound today is worth more than a pound tomorrow. If a DCF gives a fair value higher than the current price, the stock may be undervalued. Very sensitive to assumptions about growth rate and discount rate." },
  { term: "Justified P/B",         cat: "Analysis",       def: "Zoltra's alternative to DCF for banks and financial companies, whose business model doesn't produce a clean, cash-flow-based valuation the way a normal company does. Instead of projecting cash flows, it works out what a stock's price-to-book ratio should be, given its return on equity relative to its cost of equity, and compares that to its actual current P/B to estimate a 1-year target price. A bank earning more than its cost of capital justifies trading above book value; one earning less justifies trading below it." },
  { term: "Discount Rate",         cat: "Analysis",       def: "The rate used in DCF to convert future cash flows into today's value. Zoltra builds this from a country-specific risk-free rate plus equity risk premium (CAPM), adjusted for the stock's beta and company size — typically 7-18% depending on market and risk. A higher discount rate makes future cash flows worth less today, lowering the intrinsic value estimate." },
  { term: "Terminal Value",        cat: "Analysis",       def: "In a DCF, the terminal value captures all cash flows beyond the 10-year projection period. It assumes the business grows at a slow, stable rate (e.g. 2.5%) forever after. Terminal value often makes up 60–80% of a DCF's total estimated value." },
  { term: "Earnings Quality",      cat: "Analysis",       def: "A measure of whether reported profits are backed by real cash. Calculated as operating cash flow ÷ net income. Above 1.0x is healthy — the business generates more cash than it reports as profit. Below 0.7x consistently is a warning sign that profits may be inflated through accounting choices rather than real cash generation." },
  { term: "Short Interest",        cat: "Analysis",       def: "The percentage of a company's shares that have been sold short — borrowed and sold by investors betting the price will fall. Low short interest (under 3%) means few professionals are betting against it. High short interest (above 10%) signals meaningful bearish conviction and is worth investigating." },
  { term: "Days to Cover",         cat: "Analysis",       def: "Also called the short ratio. The number of days it would take all short sellers to buy back their borrowed shares at normal trading volume. A high days-to-cover (above 5) can amplify price moves — if the stock rises, shorts are forced to buy back quickly, pushing the price even higher. This is called a short squeeze." },
  { term: "Short Squeeze",         cat: "Analysis",       def: "When a stock with high short interest rises sharply, forcing short sellers to buy back shares to limit losses. This buying pressure pushes the price even higher, triggering more short covering in a feedback loop. Stocks with high short interest and improving fundamentals are most vulnerable to squeezes." },
  { term: "Cash Flow Ratio",       cat: "Profitability",  def: "Operating cash flow divided by net income. Shows whether a company's reported profits are supported by actual cash generation. A ratio consistently above 1.0x is a sign of high earnings quality. A ratio below 0.7x may indicate aggressive accounting or one-off items inflating profits." },
  { term: "Thesis",                cat: "Analysis",       def: "Your core reason for owning a stock. A clear thesis helps you decide when to buy more, hold, or sell — especially when the price is falling." },
  { term: "CAPM",                  cat: "Analysis",       def: "Capital Asset Pricing Model. A formula for the return investors should require for holding a stock: risk-free rate + (beta × equity risk premium). Zoltra uses a CAPM-style formula to set each stock's DCF discount rate, further adjusted by country and company size." },
  { term: "Risk-Free Rate",        cat: "Analysis",       def: "The theoretical return on an investment with zero risk, typically proxied by a country's government bond yield (e.g. the US 10-year Treasury). The starting point for CAPM — every other required return is priced as this rate plus a premium for taking on risk." },
  { term: "Equity Risk Premium",   cat: "Analysis",       def: "The extra return investors demand for holding stocks instead of risk-free government bonds, compensating for the additional risk. Typically 4-6% in developed markets, higher in emerging markets. A core building block of CAPM and DCF discount rates." },
  { term: "Beta Adjustment",       cat: "Analysis",       def: "How a stock's own volatility (beta) shifts its required rate of return in CAPM. A beta above 1.0 means more volatile than the market, so investors demand a higher return — raising the DCF discount rate and lowering the intrinsic value estimate. Below 1.0 works in reverse." },
  { term: "Net Debt Adjustment",   cat: "Analysis",       def: "In a DCF, total debt minus cash is subtracted from the calculated business value before dividing by shares outstanding. This converts a whole-company valuation into per-share equity value — what a shareholder's stake is actually worth, not the value of the business including its debt." },
  { term: "Size Premium",          cat: "Analysis",       def: "An extra required return added to smaller, less-established companies' DCF discount rate, reflecting their genuinely higher risk and uncertainty compared to a mega-cap with an identical beta." },
  { term: "Growth Fade",           cat: "Analysis",       def: "In a DCF, gradually reducing a company's assumed growth rate each year toward its long-run terminal growth rate, rather than assuming high growth continues unchanged for a decade and then suddenly drops. Avoids overvaluing high-growth stocks with unrealistic sustained-growth assumptions." },
  /* Macro */
  { term: "Interest Rates",        cat: "Macro",          def: "The cost of borrowing money, set by central banks. Rising rates increase borrowing costs and reduce the present value of future earnings — negative for growth stocks." },
  { term: "Bond Yields",           cat: "Macro",          def: "The return an investor earns holding a government or corporate bond to maturity. Rising yields make bonds more attractive relative to stocks and raise the discount rate used in DCF valuations, generally pressuring stock valuations lower." },
  { term: "Inflation",             cat: "Macro",          def: "The rate at which prices rise. High inflation erodes purchasing power and often prompts rate rises. Companies with pricing power are better protected." },
  { term: "Recession",             cat: "Macro",          def: "A period of economic contraction, typically defined as two consecutive quarters of negative GDP growth. Cyclical stocks are most exposed." },
  { term: "GDP",                   cat: "Macro",          def: "Gross Domestic Product. The total value of goods and services produced in a country. Strong GDP growth generally supports corporate earnings and equity markets." },
  { term: "Yield Curve",           cat: "Macro",          def: "A chart showing interest rates across different bond maturities. An inverted yield curve (short rates > long rates) has historically preceded recessions." },
  { term: "QE / QT",              cat: "Macro",          def: "Quantitative Easing (QE) = central bank buys bonds, injecting money. Quantitative Tightening (QT) = the reverse. QE is generally bullish for equities." },
  { term: "CPI",                   cat: "Macro",          def: "Consumer Price Index. The main measure of inflation, tracking a basket of goods and services. Monthly CPI releases move markets significantly." },
  { term: "Central Bank",          cat: "Macro",          def: "Institution that sets monetary policy. The US Federal Reserve, UK Bank of England, and ECB's decisions on interest rates directly impact stock valuations globally." },
  /* Classification */
  { term: "Sector",                cat: "Classification", def: "The broad industry a company operates in (e.g. Technology, Healthcare, Energy). Stocks in the same sector share similar drivers and are benchmarked against each other." },
  { term: "ETF",                   cat: "Classification", def: "Exchange-Traded Fund. A basket of securities trading on an exchange like a single stock. ETFs like VOO or QQQ offer instant diversification at very low cost." },
  { term: "Blue Chip",             cat: "Classification", def: "A large, well-established, financially stable company with a long track record. Examples: Apple, HSBC, Unilever. Generally lower risk but slower growth." },
  { term: "Growth Stock",          cat: "Classification", def: "A company expected to grow revenues and earnings significantly faster than the market. Often trades at a high P/E. Examples: Nvidia, Shopify in their early years." },
  { term: "Value Stock",           cat: "Classification", def: "A company trading at a low valuation relative to its fundamentals — low P/E, P/B, or high dividend yield. Often in mature industries or temporarily out of favour." },
  { term: "Cyclical Stock",        cat: "Classification", def: "A company whose performance is closely tied to the economic cycle. Does well in booms, poorly in recessions. Examples: airlines, luxury goods, construction." },
  { term: "Defensive Stock",       cat: "Classification", def: "A company whose earnings are relatively stable regardless of the economic cycle. Examples: utilities, healthcare, supermarkets. Lower growth but more stable." },
  { term: "Small Cap",             cat: "Classification", def: "A company with a market cap typically between $300M and $2B. Higher growth potential but less analyst coverage, lower liquidity, and higher risk than large caps." },
  { term: "IPO",                   cat: "Classification", def: "Initial Public Offering. When a private company first lists on a stock exchange. IPOs can be very volatile — the first-day pop is often followed by a pullback." },
  { term: "ADR",                   cat: "Classification", def: "American Depositary Receipt. A way for US investors to buy shares in foreign companies without using foreign exchanges. E.g. BABA is the ADR for Alibaba." },
  /* Zoltra Methodology */
  { term: "Composite Score",       cat: "Zoltra",        def: "Zoltra's overall 0-100 valuation score — a weighted blend of the Value, Quality, and Altman Z-Score pillars, plus a small market-regime adjustment, weighted differently depending on sector. Drives the Undervalued / Fairly Valued / Overvalued verdict shown at the top of a stock's page — tap any of the three pillar circles below it to see the full signal breakdown." },
  { term: "Value Score",           cat: "Zoltra",        def: "One of the three pillar scores shown on a stock's detail page. Blends how attractively priced the stock is — PEG ratio, forward and trailing P/E vs sector, DCF fair-value upside, analyst price targets, and price momentum — into one 0-100 score. Different from the Composite Score, which also folds in Quality and the Altman Z-Score." },
  { term: "Quality Score",         cat: "Zoltra",        def: "Zoltra's own 0-100 measure of business fundamentals: return on equity, free cash flow margin, earnings growth, revenue growth, and debt levels. Feeds into the overall composite score, weighted more heavily when other valuation signals are limited or unavailable." },
  { term: "Altman Z-Score",        cat: "Zoltra",        def: "A financial-distress risk measure, created by Edward Altman in 1968, calculated from the balance sheet — retained earnings, working capital, leverage, and profitability relative to total assets. Above 3.0 is generally a safe zone, 1.8-3.0 a grey zone worth watching, below 1.8 signals elevated distress risk. Not shown for banks and financial companies, whose balance sheets the model wasn't designed for. Zoltra displays a version rescaled to 0-100 for visual comparison alongside Value and Quality, with the real Z-score always shown too." },
  { term: "Sector-Relative Valuation", cat: "Zoltra",    def: "Comparing a stock's P/E to the average for its own sector, rather than judging it in isolation. A 25x P/E might be expensive for a utility but cheap for a software company — sector context matters more than the raw multiple." },
  { term: "Market Regime",         cat: "Zoltra",        def: "Zoltra's check on whether the broader market (S&P 500) is currently expensive or cheap relative to its own historical average valuation, applying a small adjustment to individual stock scores. Exists because 'cheap vs sector' can still mean expensive in absolute terms during a broad market rally." },
  { term: "Confidence Level",      cat: "Zoltra",        def: "How much the underlying signals agree with each other for a given verdict — High means multiple factors clearly point the same direction; Low means signals conflict or data is sparse. Shown alongside every verdict so you know how much weight to put on it." },
  { term: "Weekly Deep Dive",      cat: "Zoltra",        def: "A different company from a different region (US, UK, India, Japan, Europe) featured each week with a fuller write-up than a standard verdict — competitive landscape, growth drivers, risks, and the underlying thesis." },
];

/* ── AI Pick fallback pool — only used if the live server-side pick fails
   entirely. Same 165 verified, region-balanced stocks used for Browse and
   the real daily-pick generation, so even this fallback isn't US-skewed. ── */
var AI_PICK_POOL = [
  "AAPL","NVDA","MSFT","TSLA","GOOG","AMZN","META","JPM","V","JNJ","WMT","NFLX","ORCL","AMD","INTC",
  "DIS","PG","KO","PEP","MA","BAC","XOM","CVX","UNH","PFE","HD","MCD","NKE","ADBE","CRM","QCOM","IBM",
  "HON","VZ","GE","FCX","GRMN","BA","NOW","RL","EA","SHEL.L","AZN.L","BP.L","HSBA.L","ULVR.L","VOD.L",
  "LSEG.L","GSK.L","RIO.L","BARC.L","DGE.L","REL.L","NG.L","BATS.L","PRU.L","LLOY.L","NWG.L","AAL.L",
  "GLEN.L","CPG.L","TSCO.L","SBRY.L","IMB.L","SSE.L","CRH.L","III.L","ANTO.L","SGE.L","BA.L","RR.L",
  "STAN.L","AML.L","ASOS.L","IQE.L","ITV.L","BNZL.L","BRBY.L","TM17.L","RTO.L","CRDA.L","TRST.L",
  "RELIANCE.NS","TCS.NS","HDFCBANK.NS","INFY.NS","ICICIBANK.NS","SBIN.NS","WIPRO.NS","BHARTIARTL.NS",
  "BAJFINANCE.NS","MARUTI.NS","ADANIENT.NS","KOTAKBANK.NS","HINDUNILVR.NS","ITC.NS","LT.NS",
  "AXISBANK.NS","ASIANPAINT.NS","SUNPHARMA.NS","TITAN.NS","ULTRACEMCO.NS","NESTLEIND.NS","ONGC.NS",
  "NTPC.NS","POWERGRID.NS","TATASTEEL.NS","TATAMOTORS.NS","HCLTECH.NS","TECHM.NS","BAJAJFINSV.NS",
  "HDFCLIFE.NS","SBILIFE.NS","DRREDDY.NS","CIPLA.NS","GRASIM.NS","JSWSTEEL.NS","COALINDIA.NS",
  "INDUSINDBK.NS","NYKAA.NS","DIXON.NS","VEDL.NS","HAL.NS","HAVELLS.NS","PERSISTENT.NS","ZEEL.NS",
  "NAZARA.NS","DMART.NS","7203.T","6758.T","9984.T","6861.T","9432.T","7974.T","6501.T","8306.T",
  "6098.T","8035.T","6902.T","7267.T","7201.T","4063.T","6367.T","8058.T","8031.T","8001.T","4502.T",
  "4568.T","9433.T","9983.T","8316.T","8411.T","6273.T","6503.T","6178.T","4661.T","9020.T","5108.T",
  "7011.T","1605.T","4755.T","5713.T","4452.T","4911.T","4684.T","6954.T","8604.T","7269.T","ASML.AS",
  "SAP.DE","MC.PA","SIE.DE","NESN.SW","NOVN.SW","OR.PA","AIR.PA","TTE.PA","SAN.PA","BNP.PA","KER.PA",
  "DG.PA","ALV.DE","DTE.DE","BAS.DE","BMW.DE","MBG.DE","VOW3.DE","DBK.DE","IBE.MC","SAN.MC","ITX.MC",
  "UCG.MI","ENI.MI","ISP.MI","RACE.MI","ABI.BR","ADYEN.AS","PHIA.AS","INGA.AS","ROG.SW","UBSG.SW",
  "ZURN.SW","ZAL.DE","MT.AS","BOL.PA","UBI.PA","LOGN.SW","DHL.DE","VIV.PA",
];
var N="#081B33",B="#2554D9",BW="#EAF0FE",TX="#0F172A",TX2="#334155",TXM="#64748B",TX3="#94A3B8",BG="#EEF2F8",WH="#FFF",BD="#E4E9F0",OK="#16A34A",OKS="#E9F9EF",OKT="#0F7A34",ER="#DC2626",ERS="#FDEDED",ERT="#A31515",WN="#D97706",WNS="#FFF6E5",AC="#0D9488",ACS="#E3F6F3",ACT="#0A6C63",MONO="SFMono-Regular, Consolas, monospace",CS$={USD:"$",GBP:"£",INR:"₹",JPY:"¥",EUR:"€",HKD:"HK$",CAD:"C$",AUD:"A$"};

/* ── Normalise currency code from Yahoo (handles GBp pence → GBP etc) ── */
function normCurrency(raw) {
  if (!raw) return null;
  var u = raw.toUpperCase();
  if (u === "GBP" || raw === "GBp") return "GBP";
  return u;
}

/* ── Logo ── */
function Logo({size, full, dark}){
  var s = size || 40;
  if (full) {
    return (
      <img
        src="/logo192.png"
        alt="Zoltra"
        style={{ height: s, width: "auto", maxWidth: "100%", display: "block", objectFit: "contain" }}
      />
    );
  }
  if (dark) {
    return (
      <img src="/icon-192.png" alt="Zoltra" style={{ width: s, height: s, objectFit: "contain", display: "block", flexShrink: 0 }} />
    );
  }
  return (
    <img
      src="/icon-192.png"
      alt="Q"
      style={{ height: s, width: s, maxWidth: s, display: "block", objectFit: "contain", borderRadius: s > 30 ? 8 : 4 }}
    />
  );
}

/* ── Ticker Lookup — maps common names to Yahoo tickers ── */
var NAMES = {
  apple:"AAPL",nvidia:"NVDA",tesla:"TSLA",microsoft:"MSFT",google:"GOOG",alphabet:"GOOG",amazon:"AMZN",
  berkshire:"BRK-B","berkshire hathaway":"BRK-B",meta:"META",facebook:"META",
  jpmorgan:"JPM","jp morgan":"JPM",visa:"V",mastercard:"MA",walmart:"WMT",netflix:"NFLX",disney:"DIS",
  "coca cola":"KO",cocacola:"KO",pepsi:"PEP",nike:"NKE","mcdonalds":"MCD","mcdonald's":"MCD",
  oracle:"ORCL",salesforce:"CRM",adobe:"ADBE",qualcomm:"QCOM",intel:"INTC",amd:"AMD",ibm:"IBM",
  exxon:"XOM",exxonmobil:"XOM",chevron:"CVX",pfizer:"PFE","johnson and johnson":"JNJ","johnson & johnson":"JNJ",
  unitedhealth:"UNH","home depot":"HD",homedepot:"HD",
  shell:"SHEL.L",astrazeneca:"AZN.L",reliance:"RELIANCE.NS",tcs:"TCS.NS",tata:"TCS.NS",
  toyota:"7203.T",sony:"6758.T",asml:"ASML.AS",sap:"SAP.DE",voo:"VOO",
  // Indian stocks — smart .NS suffix
  hdfc:"HDFCBANK.NS","hdfc bank":"HDFCBANK.NS",infosys:"INFY.NS",wipro:"WIPRO.NS",
  "solar industries":"SOLARINDS.NS",solarinds:"SOLARINDS.NS",
  icici:"ICICIBANK.NS",sbi:"SBIN.NS",bharti:"BHARTIARTL.NS",airtel:"BHARTIARTL.NS",
  bajaj:"BAJFINANCE.NS",kotak:"KOTAKBANK.NS",maruti:"MARUTI.NS",
  itc:"ITC.NS",hul:"HINDUNILVR.NS",adani:"ADANIENT.NS",
  // UK
  bp:"BP.L",vodafone:"VOD.L",unilever:"ULVR.L",hsbc:"HSBA.L",
  // Europe
  "lvmh":"MC.PA",siemens:"SIE.DE",nestle:"NESN.SW"
};

/* ── Popular stocks for Browse section ── */
var BROWSE_STOCKS = {
  US: [
    { t:"AAPL", n:"Apple", s:"Technology" , d:"apple.com" },
    { t:"NVDA", n:"Nvidia", s:"Semiconductors" , d:"nvidia.com" },
    { t:"MSFT", n:"Microsoft", s:"Technology" , d:"microsoft.com" },
    { t:"TSLA", n:"Tesla", s:"Automotive" , d:"tesla.com" },
    { t:"GOOG", n:"Alphabet", s:"Technology" , d:"google.com" },
    { t:"AMZN", n:"Amazon", s:"E-Commerce" , d:"amazon.com" },
    { t:"META", n:"Meta", s:"Communication Services" , d:"meta.com" },
    { t:"JPM", n:"JPMorgan", s:"Financials" , d:"jpmorganchase.com" },
    { t:"V", n:"Visa", s:"Financials" , d:"visa.com" },
    { t:"JNJ", n:"Johnson & Johnson", s:"Healthcare" , d:"jnj.com" },
    { t:"WMT", n:"Walmart", s:"Retail" , d:"walmart.com" },
    { t:"NFLX", n:"Netflix", s:"Entertainment" , d:"netflix.com" },
    { t:"ORCL", n:"Oracle", s:"Technology" , d:"oracle.com" },
    { t:"AMD", n:"AMD", s:"Semiconductors" , d:"amd.com" },
    { t:"INTC", n:"Intel", s:"Semiconductors" , d:"intel.com" },
    { t:"DIS", n:"Disney", s:"Entertainment" , d:"disney.com" },
    { t:"PG", n:"Procter & Gamble", s:"Consumer Goods" , d:"pg.com" },
    { t:"KO", n:"Coca-Cola", s:"Consumer Goods" , d:"coca-cola.com" },
    { t:"PEP", n:"PepsiCo", s:"Consumer Goods" , d:"pepsico.com" },
    { t:"MA", n:"Mastercard", s:"Financials" , d:"mastercard.com" },
    { t:"BAC", n:"Bank of America", s:"Financials" , d:"bankofamerica.com" },
    { t:"XOM", n:"ExxonMobil", s:"Energy" , d:"exxonmobil.com" },
    { t:"CVX", n:"Chevron", s:"Energy" , d:"chevron.com" },
    { t:"UNH", n:"UnitedHealth", s:"Healthcare" , d:"unitedhealthgroup.com" },
    { t:"PFE", n:"Pfizer", s:"Healthcare" , d:"pfizer.com" },
    { t:"HD", n:"Home Depot", s:"Retail" , d:"homedepot.com" },
    { t:"MCD", n:"McDonald's", s:"Retail" , d:"mcdonalds.com" },
    { t:"NKE", n:"Nike", s:"Consumer Goods" , d:"nike.com" },
    { t:"ADBE", n:"Adobe", s:"Technology" , d:"adobe.com" },
    { t:"CRM", n:"Salesforce", s:"Technology" , d:"salesforce.com" },
    { t:"QCOM", n:"Qualcomm", s:"Semiconductors" , d:"qualcomm.com" },
    { t:"IBM", n:"IBM", s:"Technology" , d:"ibm.com" },
    { t:"HON", n:"Honeywell", s:"Industrial" , d:"honeywell.com" },
    { t:"VZ", n:"Verizon", s:"Telecom" , d:"verizon.com" },
    { t:"GE", n:"General Electric", s:"Conglomerate" , d:"ge.com" },
    { t:"FCX", n:"Freeport-McMoRan", s:"Mining" , d:"fcx.com" },
    { t:"GRMN", n:"Garmin", s:"Consumer Tech" , d:"garmin.com" },
    { t:"BA", n:"Boeing", s:"Aerospace" , d:"boeing.com" },
    { t:"NOW", n:"ServiceNow", s:"Enterprise Software" , d:"servicenow.com" },
    { t:"RL", n:"Ralph Lauren", s:"Luxury" , d:"ralphlauren.com" },
    { t:"EA", n:"Electronic Arts", s:"Gaming" , d:"ea.com" },
  ],
  UK: [
    { t:"SHEL.L", n:"Shell", s:"Energy" , d:"shell.com" },
    { t:"AZN.L", n:"AstraZeneca", s:"Healthcare" , d:"astrazeneca.com" },
    { t:"BP.L", n:"BP", s:"Energy" , d:"bp.com" },
    { t:"HSBA.L", n:"HSBC", s:"Financials" , d:"hsbc.com" },
    { t:"ULVR.L", n:"Unilever", s:"Consumer Goods" , d:"unilever.com" },
    { t:"VOD.L", n:"Vodafone", s:"Telecom" , d:"vodafone.com" },
    { t:"LSEG.L", n:"London Stock Exch.", s:"Financials" , d:"lseg.com" },
    { t:"GSK.L", n:"GSK", s:"Healthcare" , d:"gsk.com" },
    { t:"RIO.L", n:"Rio Tinto", s:"Mining" , d:"riotinto.com" },
    { t:"BARC.L", n:"Barclays", s:"Financials" , d:"barclays.com" },
    { t:"DGE.L", n:"Diageo", s:"Consumer Goods" , d:"diageo.com" },
    { t:"REL.L", n:"RELX", s:"Technology" , d:"relx.com" },
    { t:"NG.L", n:"National Grid", s:"Energy" , d:"nationalgrid.com" },
    { t:"BATS.L", n:"British American Tobacco", s:"Consumer Goods" , d:"bat.com" },
    { t:"PRU.L", n:"Prudential", s:"Financials" , d:"prudentialplc.com" },
    { t:"LLOY.L", n:"Lloyds Banking Group", s:"Financials" , d:"lloydsbankinggroup.com" },
    { t:"NWG.L", n:"NatWest Group", s:"Financials" , d:"natwestgroup.com" },
    { t:"AAL.L", n:"Anglo American", s:"Mining" , d:"angloamerican.com" },
    { t:"GLEN.L", n:"Glencore", s:"Mining" , d:"glencore.com" },
    { t:"CPG.L", n:"Compass Group", s:"Retail" , d:"compass-group.com" },
    { t:"TSCO.L", n:"Tesco", s:"Retail" , d:"tesco.com" },
    { t:"SBRY.L", n:"Sainsbury's", s:"Retail" , d:"sainsburys.co.uk" },
    { t:"IMB.L", n:"Imperial Brands", s:"Consumer Goods" , d:"imperialbrandsplc.com" },
    { t:"SSE.L", n:"SSE", s:"Energy" , d:"sse.com" },
    { t:"CRH.L", n:"CRH", s:"Industrial" , d:"crh.com" },
    { t:"III.L", n:"3i Group", s:"Financials" , d:"3i.com" },
    { t:"ANTO.L", n:"Antofagasta", s:"Mining" , d:"antofagasta.co.uk" },
    { t:"SGE.L", n:"Sage Group", s:"Enterprise Software" , d:"sage.com" },
    { t:"BA.L", n:"BAE Systems", s:"Aerospace" , d:"baesystems.com" },
    { t:"RR.L", n:"Rolls-Royce Holdings", s:"Industrial" , d:"rolls-royce.com" },
    { t:"STAN.L", n:"Standard Chartered", s:"Financials" , d:"sc.com" },
    { t:"AML.L", n:"Aston Martin", s:"Automotive" , d:"astonmartinlagonda.com" },
    { t:"ASOS.L", n:"ASOS", s:"E-Commerce" , d:"asosplc.com" },
    { t:"IQE.L", n:"IQE plc", s:"Semiconductors" , d:"iqep.com" },
    { t:"ITV.L", n:"ITV", s:"Entertainment" , d:"itvplc.com" },
    { t:"BNZL.L", n:"Bunzl", s:"Conglomerate" , d:"bunzl.com" },
    { t:"BRBY.L", n:"Burberry", s:"Luxury" , d:"burberryplc.com" },
    { t:"TM17.L", n:"Team17 Group", s:"Gaming" , d:"team17group.com" },
    { t:"RTO.L", n:"Rentokil Initial", s:"Industrial" , d:"rentokil-initial.com" },
    { t:"CRDA.L", n:"Croda International", s:"Consumer Goods" , d:"croda.com" },
    { t:"TRST.L", n:"Trustpilot Group", s:"Consumer Tech" , d:"business.trustpilot.com" },
  ],
  India: [
    { t:"RELIANCE.NS", n:"Reliance", s:"Conglomerate" , d:"ril.com" },
    { t:"TCS.NS", n:"TCS", s:"Technology" , d:"tcs.com" },
    { t:"HDFCBANK.NS", n:"HDFC Bank", s:"Financials" , d:"hdfcbank.com" },
    { t:"INFY.NS", n:"Infosys", s:"Technology" , d:"infosys.com" },
    { t:"ICICIBANK.NS", n:"ICICI Bank", s:"Financials" , d:"icicibank.com" },
    { t:"SBIN.NS", n:"State Bank", s:"Financials" , d:"sbi.co.in" },
    { t:"WIPRO.NS", n:"Wipro", s:"Technology" , d:"wipro.com" },
    { t:"BHARTIARTL.NS", n:"Airtel", s:"Telecom" , d:"airtel.in" },
    { t:"BAJFINANCE.NS", n:"Bajaj Finance", s:"Financials" , d:"bajajfinserv.in" },
    { t:"MARUTI.NS", n:"Maruti Suzuki", s:"Automotive" , d:"marutisuzuki.com" },
    { t:"ADANIENT.NS", n:"Adani Enterprises", s:"Conglomerate" , d:"adani.com" },
    { t:"KOTAKBANK.NS", n:"Kotak Bank", s:"Financials" , d:"kotak.com" },
    { t:"HINDUNILVR.NS", n:"Hindustan Unilever", s:"Consumer Goods" , d:"hul.co.in" },
    { t:"ITC.NS", n:"ITC Limited", s:"Conglomerate" , d:"itcportal.com" },
    { t:"LT.NS", n:"Larsen & Toubro", s:"Industrial" , d:"larsentoubro.com" },
    { t:"AXISBANK.NS", n:"Axis Bank", s:"Financials" , d:"axisbank.com" },
    { t:"ASIANPAINT.NS", n:"Asian Paints", s:"Consumer Goods" , d:"asianpaints.com" },
    { t:"SUNPHARMA.NS", n:"Sun Pharmaceutical", s:"Healthcare" , d:"sunpharma.com" },
    { t:"TITAN.NS", n:"Titan Company", s:"Luxury" , d:"titancompany.in" },
    { t:"ULTRACEMCO.NS", n:"UltraTech Cement", s:"Industrial" , d:"ultratechcement.com" },
    { t:"NESTLEIND.NS", n:"Nestlé India", s:"Consumer Goods" , d:"nestle.in" },
    { t:"ONGC.NS", n:"Oil & Natural Gas Corp", s:"Energy" , d:"ongcindia.com" },
    { t:"NTPC.NS", n:"NTPC", s:"Energy" , d:"ntpc.co.in" },
    { t:"POWERGRID.NS", n:"Power Grid Corp", s:"Energy" , d:"powergrid.in" },
    { t:"TATASTEEL.NS", n:"Tata Steel", s:"Industrial" , d:"tatasteel.com" },
    { t:"TATAMOTORS.NS", n:"Tata Motors", s:"Automotive" , d:"tatamotors.com" },
    { t:"HCLTECH.NS", n:"HCL Technologies", s:"Technology" , d:"hcltech.com" },
    { t:"TECHM.NS", n:"Tech Mahindra", s:"Technology" , d:"techmahindra.com" },
    { t:"BAJAJFINSV.NS", n:"Bajaj Finserv", s:"Financials" , d:"bajajfinserv.in" },
    { t:"HDFCLIFE.NS", n:"HDFC Life Insurance", s:"Financials" , d:"hdfclife.com" },
    { t:"SBILIFE.NS", n:"SBI Life Insurance", s:"Financials" , d:"sbilife.co.in" },
    { t:"DRREDDY.NS", n:"Dr. Reddy's Laboratories", s:"Healthcare" , d:"drreddys.com" },
    { t:"CIPLA.NS", n:"Cipla", s:"Healthcare" , d:"cipla.com" },
    { t:"GRASIM.NS", n:"Grasim Industries", s:"Industrial" , d:"grasim.com" },
    { t:"JSWSTEEL.NS", n:"JSW Steel", s:"Industrial" , d:"jsw.in" },
    { t:"COALINDIA.NS", n:"Coal India", s:"Energy" , d:"coalindia.in" },
    { t:"INDUSINDBK.NS", n:"IndusInd Bank", s:"Financials" , d:"indusind.com" },
    { t:"NYKAA.NS", n:"Nykaa", s:"E-Commerce" , d:"nykaa.com" },
    { t:"DIXON.NS", n:"Dixon Technologies", s:"Semiconductors" , d:"dixoninfo.com" },
    { t:"VEDL.NS", n:"Vedanta", s:"Mining" , d:"vedantalimited.com" },
    { t:"HAL.NS", n:"Hindustan Aeronautics", s:"Aerospace" , d:"hal-india.co.in" },
    { t:"HAVELLS.NS", n:"Havells India", s:"Consumer Tech" , d:"havells.com" },
    { t:"PERSISTENT.NS", n:"Persistent Systems", s:"Enterprise Software" , d:"persistent.com" },
    { t:"ZEEL.NS", n:"Zee Entertainment", s:"Entertainment" , d:"zee.com" },
    { t:"NAZARA.NS", n:"Nazara Technologies", s:"Gaming" , d:"nazara.com" },
    { t:"DMART.NS", n:"Avenue Supermarts", s:"Retail" , d:"dmartindia.com" },
    { t:"TRENT.NS", n:"Trent", s:"Retail" , d:"trentlimited.com" },
    { t:"EICHERMOT.NS", n:"Eicher Motors", s:"Automotive" , d:"eicher.in" },
    { t:"BAJAJ-AUTO.NS", n:"Bajaj Auto", s:"Automotive" , d:"bajajauto.com" },
  ],
  Japan: [
    { t:"7203.T", n:"Toyota", s:"Automotive" , d:"toyota.com" },
    { t:"6758.T", n:"Sony", s:"Consumer Tech" , d:"sony.com" },
    { t:"9984.T", n:"SoftBank", s:"Technology" , d:"group.softbank" },
    { t:"6861.T", n:"Keyence", s:"Industrial" , d:"keyence.com" },
    { t:"9432.T", n:"NTT", s:"Telecom" , d:"ntt.com" },
    { t:"7974.T", n:"Nintendo", s:"Gaming" , d:"nintendo.com" },
    { t:"6501.T", n:"Hitachi", s:"Industrial" , d:"hitachi.com" },
    { t:"8306.T", n:"Mitsubishi UFJ", s:"Financials" , d:"mufg.jp" },
    { t:"6098.T", n:"Recruit Holdings", s:"Technology" , d:"recruit-holdings.com" },
    { t:"8035.T", n:"Tokyo Electron", s:"Semiconductors" , d:"tel.com" },
    { t:"6902.T", n:"Denso", s:"Automotive" , d:"denso.com" },
    { t:"7267.T", n:"Honda Motor", s:"Automotive" , d:"honda.com" },
    { t:"7201.T", n:"Nissan Motor", s:"Automotive" , d:"nissan-global.com" },
    { t:"4063.T", n:"Shin-Etsu Chemical", s:"Industrial" , d:"shinetsu.co.jp" },
    { t:"6367.T", n:"Daikin Industries", s:"Industrial" , d:"daikin.com" },
    { t:"8058.T", n:"Mitsubishi Corp", s:"Conglomerate" , d:"mitsubishicorp.com" },
    { t:"8031.T", n:"Mitsui & Co", s:"Conglomerate" , d:"mitsui.com" },
    { t:"8001.T", n:"Itochu Corp", s:"Conglomerate" , d:"itochu.co.jp" },
    { t:"4502.T", n:"Takeda Pharmaceutical", s:"Healthcare" , d:"takeda.com" },
    { t:"4568.T", n:"Daiichi Sankyo", s:"Healthcare" , d:"daiichisankyo.com" },
    { t:"9433.T", n:"KDDI", s:"Telecom" , d:"kddi.com" },
    { t:"9983.T", n:"Fast Retailing", s:"Retail" , d:"fastretailing.com" },
    { t:"8316.T", n:"Sumitomo Mitsui Financial", s:"Financials" , d:"smfg.co.jp" },
    { t:"8411.T", n:"Mizuho Financial Group", s:"Financials" , d:"mizuhogroup.com" },
    { t:"6273.T", n:"SMC Corp", s:"Industrial" , d:"smcworld.com" },
    { t:"6503.T", n:"Mitsubishi Electric", s:"Industrial" , d:"mitsubishielectric.com" },
    { t:"6178.T", n:"Japan Post Holdings", s:"Financials" , d:"japanpost.jp" },
    { t:"4661.T", n:"Oriental Land", s:"Entertainment" , d:"olc.co.jp" },
    { t:"9020.T", n:"East Japan Railway", s:"Industrial" , d:"jreast.co.jp" },
    { t:"5108.T", n:"Bridgestone", s:"Automotive" , d:"bridgestone.com" },
    { t:"7011.T", n:"Mitsubishi Heavy Industries", s:"Aerospace" , d:"mhi.com" },
    { t:"1605.T", n:"INPEX", s:"Energy" , d:"inpex.co.jp" },
    { t:"4755.T", n:"Rakuten", s:"E-Commerce" , d:"rakuten.com" },
    { t:"5713.T", n:"Sumitomo Metal Mining", s:"Mining" , d:"smm.co.jp" },
    { t:"4452.T", n:"Kao Corporation", s:"Consumer Goods" , d:"kao.com" },
    { t:"4911.T", n:"Shiseido", s:"Luxury" , d:"shiseido.com" },
    { t:"4684.T", n:"Obic", s:"Enterprise Software" , d:"obic.co.jp" },
    { t:"6954.T", n:"Fanuc", s:"Industrial" , d:"fanuc.co.jp" },
    { t:"8604.T", n:"Nomura Holdings", s:"Financials" , d:"nomura.com" },
    { t:"7269.T", n:"Suzuki Motor", s:"Automotive" , d:"globalsuzuki.com" },
  ],
  Europe: [
    { t:"ASML.AS", n:"ASML", s:"Semiconductors" , d:"asml.com" },
    { t:"SAP.DE", n:"SAP", s:"Enterprise Software" , d:"sap.com" },
    { t:"MC.PA", n:"LVMH", s:"Luxury" , d:"lvmh.com" },
    { t:"SIE.DE", n:"Siemens", s:"Industrial" , d:"siemens.com" },
    { t:"NESN.SW", n:"Nestlé", s:"Consumer Goods" , d:"nestle.com" },
    { t:"NOVN.SW", n:"Novartis", s:"Healthcare" , d:"novartis.com" },
    { t:"OR.PA", n:"L'Oréal", s:"Consumer Goods" , d:"loreal.com" },
    { t:"AIR.PA", n:"Airbus", s:"Aerospace" , d:"airbus.com" },
    { t:"TTE.PA", n:"TotalEnergies", s:"Energy" , d:"totalenergies.com" },
    { t:"SAN.PA", n:"Sanofi", s:"Healthcare" , d:"sanofi.com" },
    { t:"BNP.PA", n:"BNP Paribas", s:"Financials" , d:"bnpparibas.com" },
    { t:"KER.PA", n:"Kering", s:"Luxury" , d:"kering.com" },
    { t:"DG.PA", n:"Vinci", s:"Industrial" , d:"vinci.com" },
    { t:"ALV.DE", n:"Allianz", s:"Financials" , d:"allianz.com" },
    { t:"DTE.DE", n:"Deutsche Telekom", s:"Telecom" , d:"telekom.com" },
    { t:"BAS.DE", n:"BASF", s:"Industrial" , d:"basf.com" },
    { t:"BMW.DE", n:"BMW", s:"Automotive" , d:"bmw.com" },
    { t:"MBG.DE", n:"Mercedes-Benz Group", s:"Automotive" , d:"mercedes-benz.com" },
    { t:"VOW3.DE", n:"Volkswagen", s:"Automotive" , d:"volkswagen.com" },
    { t:"DBK.DE", n:"Deutsche Bank", s:"Financials" , d:"db.com" },
    { t:"IBE.MC", n:"Iberdrola", s:"Energy" , d:"iberdrola.com" },
    { t:"SAN.MC", n:"Banco Santander", s:"Financials" , d:"santander.com" },
    { t:"ITX.MC", n:"Inditex", s:"Retail" , d:"inditex.com" },
    { t:"UCG.MI", n:"UniCredit", s:"Financials" , d:"unicredit.eu" },
    { t:"ENI.MI", n:"Eni", s:"Energy" , d:"eni.com" },
    { t:"ISP.MI", n:"Intesa Sanpaolo", s:"Financials" , d:"intesasanpaolo.com" },
    { t:"RACE.MI", n:"Ferrari", s:"Luxury" , d:"ferrari.com" },
    { t:"ABI.BR", n:"Anheuser-Busch InBev", s:"Consumer Goods" , d:"ab-inbev.com" },
    { t:"ADYEN.AS", n:"Adyen", s:"Technology" , d:"adyen.com" },
    { t:"PHIA.AS", n:"Philips", s:"Healthcare" , d:"philips.com" },
    { t:"INGA.AS", n:"ING Group", s:"Financials" , d:"ing.com" },
    { t:"ROG.SW", n:"Roche", s:"Healthcare" , d:"roche.com" },
    { t:"UBSG.SW", n:"UBS Group", s:"Financials" , d:"ubs.com" },
    { t:"ZURN.SW", n:"Zurich Insurance", s:"Financials" , d:"zurich.com" },
    { t:"ZAL.DE", n:"Zalando", s:"E-Commerce" , d:"zalando.com" },
    { t:"MT.AS", n:"ArcelorMittal", s:"Mining" , d:"arcelormittal.com" },
    { t:"BOL.PA", n:"Bollore", s:"Conglomerate" , d:"bollore.com" },
    { t:"UBI.PA", n:"Ubisoft Entertainment", s:"Gaming" , d:"ubisoft.com" },
    { t:"LOGN.SW", n:"Logitech International", s:"Consumer Tech" , d:"logitech.com" },
    { t:"DHL.DE", n:"Deutsche Post DHL", s:"Industrial" , d:"dpdhl.com" },
    { t:"VIV.PA", n:"Vivendi", s:"Entertainment" , d:"vivendi.com" },
  ],
  etf: [
    { t:"VOO", n:"Vanguard S&P 500", s:"US Large Cap", d:"vanguard.com" },
    { t:"QQQ", n:"Invesco Nasdaq 100", s:"US Tech", d:"invesco.com" },
    { t:"SPY", n:"SPDR S&P 500", s:"US Large Cap", d:"ssga.com" },
    { t:"VTI", n:"Vanguard Total Mkt", s:"US Total Mkt", d:"vanguard.com" },
    { t:"ARKK", n:"ARK Innovation", s:"Disruptive Tech", d:"ark-invest.com" },
    { t:"GLD", n:"SPDR Gold Shares", s:"Commodities", d:"ssga.com" },
  ],
};

// Rotates the "Browse > All" picks daily, Monday-Friday — 3 stocks each from
// US, UK, India, Europe and Japan (15 total), deterministically chosen from
// that day's weekday so every user sees the same picks on the same day
// rather than a per-session random shuffle. Weekends reuse Friday's set.
// Choosing a specific region filter still shows that region's full curated
// list, unaffected by rotation — this only changes the "All" default view.
function getDailyBrowsePicks() {
  var day = new Date().getDay(); // 0 = Sun ... 6 = Sat
  var weekdayIndex = (day === 0 || day === 6) ? 4 : day - 1; // clamp weekends to Friday's picks
  var regions = ["US", "UK", "India", "Europe", "Japan"];
  var picks = [];
  regions.forEach(function(r) {
    var pool = BROWSE_STOCKS[r] || [];
    if (pool.length === 0) return;
    for (var i = 0; i < 3; i++) {
      picks.push(pool[(weekdayIndex * 3 + i) % pool.length]);
    }
  });
  return picks;
}

/* ── Local search index — instant results before API fires ── */
/* ── Detect currency and flag from ticker/exchange ── */
function detectCurrency(ticker, exchange) {
  var t = (ticker || "").toUpperCase();
  var ex = (exchange || "").toUpperCase();
  if (t.endsWith(".NS") || t.endsWith(".BO") || t.endsWith(".BSE") || ex.includes("NSE") || ex.includes("BSE") || ex.includes("NSI") || ex.includes("BOM") || ex.includes("BOMBAY") || ex.includes("MUMBAI") || ex.includes("NATIONAL STOCK EXCHANGE")) return "INR";
  if (t.endsWith(".L") || ex.includes("LSE") || ex.includes("LON")) return "GBP";
  if (t.endsWith(".T") || ex.includes("TSE") || ex.includes("TYO") || ex.includes("JPX")) return "JPY";
  if (ex.includes("XETRA") || ex.includes("FRA") || ex.includes("EURONEXT") || ex.includes("AMS") || ex.includes("PAR")) return "EUR";
  // Other German regional exchanges — same currency as Xetra/.DE, just a
  // different trading venue. Confirmed real gap: DAIKIN.DU (Düsseldorf) was
  // falling through to the USD default since only .DE was recognized here.
  if (t.endsWith(".DU") || t.endsWith(".BE") || t.endsWith(".MU") || t.endsWith(".HM") || t.endsWith(".HA") || t.endsWith(".SG")) return "EUR";
  // Same class of gap, found while auditing further: these markets were
  // only checked via exchange-string substrings above, never by ticker
  // suffix directly — but detectFlag (the sibling function) already checks
  // ticker suffix for all of these, and is frequently called with an empty
  // exchange string. That mismatch meant a Dutch, French, Swiss, Italian,
  // Spanish, or Belgian stock could show the correct flag but still price
  // in USD instead of its real currency — the exact DAIKIN.DU bug, just not
  // yet reported for these markets.
  if (t.endsWith(".AS") || t.endsWith(".PA")) return "EUR";
  if (t.endsWith(".SW")) return "CHF";
  if (t.endsWith(".MI")) return "EUR";
  if (t.endsWith(".MC")) return "EUR";
  if (t.endsWith(".BR")) return "EUR";
  if (ex.includes("HKG") || ex.includes("HKSE")) return "HKD";
  if (ex.includes("TSX") || ex.includes("TOR")) return "CAD";
  if (ex.includes("ASX") || ex.includes("SYD")) return "AUD";
  return "USD";
}
// Maps a Browse-section sector string to a fitting icon and accent color —
// keyword-matched since BROWSE_STOCKS has free-text sector labels, not a
// fixed enum.
function sectorVisual(sector) {
  var s = (sector || "").toLowerCase();
  if (s.indexOf("semiconductor") !== -1) return { Icon: MdMemory, bg: ACS, color: AC };
  if (s.indexOf("disruptive tech") !== -1) return { Icon: MdRocketLaunch, bg: ACS, color: AC };
  if (s.indexOf("technology") !== -1 || s.indexOf("software") !== -1 || s.indexOf("enterprise") !== -1) return { Icon: MdCode, bg: BW, color: B };
  if (s.indexOf("consumer tech") !== -1) return { Icon: MdMemory, bg: BW, color: B };
  if (s.indexOf("automotive") !== -1) return { Icon: MdDirectionsCar, bg: BW, color: B };
  if (s.indexOf("e-commerce") !== -1 || s.indexOf("retail") !== -1) return { Icon: MdShoppingCart, bg: ACS, color: AC };
  if (s.indexOf("consumer goods") !== -1) return { Icon: MdShoppingBag, bg: BW, color: B };
  if (s.indexOf("financ") !== -1 || s.indexOf("bank") !== -1) return { Icon: MdAccountBalanceWallet, bg: BW, color: B };
  if (s.indexOf("energy") !== -1) return { Icon: MdBolt, bg: WNS, color: WN };
  if (s.indexOf("health") !== -1 || s.indexOf("pharma") !== -1) return { Icon: MdLocalHospital, bg: ACS, color: AC };
  if (s.indexOf("conglomerate") !== -1) return { Icon: MdDomain, bg: BW, color: B };
  if (s.indexOf("communication services") !== -1 || s.indexOf("social media") !== -1) return { Icon: MdGroups, bg: ACS, color: AC };
  if (s.indexOf("telecom") !== -1) return { Icon: MdCellTower, bg: BW, color: B };
  if (s.indexOf("aerospace") !== -1) return { Icon: MdFlightTakeoff, bg: BW, color: B };
  if (s.indexOf("industrial") !== -1) return { Icon: MdFactory, bg: BW, color: B };
  if (s.indexOf("mining") !== -1) return { Icon: MdTerrain, bg: WNS, color: WN };
  if (s.indexOf("commodities") !== -1) return { Icon: MdInventory2, bg: WNS, color: WN };
  if (s.indexOf("luxury") !== -1) return { Icon: MdStyle, bg: ACS, color: AC };
  if (s.indexOf("entertainment") !== -1) return { Icon: MdMovie, bg: ACS, color: AC };
  if (s.indexOf("gaming") !== -1) return { Icon: MdSportsEsports, bg: ACS, color: AC };
  if (s.indexOf("etf") !== -1 || s.indexOf("us large cap") !== -1 || s.indexOf("us tech") !== -1 || s.indexOf("us total mkt") !== -1) return { Icon: MdBarChart, bg: ACS, color: AC };
  return { Icon: MdShowChart, bg: BW, color: B };
}

// Strips a trailing par-value/security-class suffix some raw company names
// include (e.g. "AstraZeneca PLC ORD SHS $0.25", or just "BP PLC $0.25" with
// no "ORD SHS" wording at all) — this is the share's nominal/accounting par
// value, a completely different concept from its market price, and
// displaying it as-is reads confusingly like a second, contradictory price
// sitting right next to the real one. Handles both a currency symbol
// ($/£/€) and a bare currency code (EUR/USD/GBP/GBX/JPY) directly against
// the number, with or without "ORD"/"ORD SHS" wording in between.
function cleanCompanyName(name) {
  if (!name) return name;
  return name.replace(/\s+(ORD(INARY)?\s*(SH(ARE)?S?)?\s*)?[A-Z]{0,3}([$£€]\s*\d+(\.\d+)?|(EUR|USD|GBP|GBX|JPY)\s*\d+(\.\d+)?)\s*$/i, "").trim();
}

function detectFlag(ticker, exchange) {
  var t = (ticker || "").toUpperCase();
  var ex = (exchange || "").toUpperCase();
  if (t.endsWith(".NS") || t.endsWith(".BO") || t.endsWith(".BSE") || ex.includes("NSE") || ex.includes("BSE") || ex.includes("NSI") || ex.includes("BOM") || ex.includes("BOMBAY") || ex.includes("MUMBAI") || ex.includes("NATIONAL STOCK EXCHANGE")) return "🇮🇳";
  if (t.endsWith(".L") || ex.includes("LSE") || ex.includes("LON")) return "🇬🇧";
  if (t.endsWith(".T") || ex.includes("TSE") || ex.includes("TYO") || ex.includes("JPX")) return "🇯🇵";
  // European suffixes — checked on the ticker itself, not just the exchange
  // string, since detectFlag is frequently called with an empty exchange
  // (e.g. building the search index), which meant these never matched at all.
  if (t.endsWith(".DE") || ex.includes("XETRA") || ex.includes("FRA")) return "🇩🇪";
  // Other German regional exchanges — same country/flag as Xetra/.DE, just a
  // different trading venue. Confirmed real gap: DAIKIN.DU (Düsseldorf) was
  // showing a US flag since only .DE was recognized here.
  if (t.endsWith(".DU") || t.endsWith(".BE") || t.endsWith(".MU") || t.endsWith(".HM") || t.endsWith(".HA") || t.endsWith(".SG")) return "🇩🇪";
  if (t.endsWith(".AS") || ex.includes("AMS")) return "🇳🇱";
  if (t.endsWith(".PA") || ex.includes("PAR")) return "🇫🇷";
  if (t.endsWith(".SW") || ex.includes("SIX") || ex.includes("SWISS")) return "🇨🇭";
  if (t.endsWith(".MI") || ex.includes("MIL") || ex.includes("BORSA")) return "🇮🇹";
  if (t.endsWith(".MC") || ex.includes("BME") || ex.includes("MADRID")) return "🇪🇸";
  if (t.endsWith(".BR") || ex.includes("BRU") || ex.includes("BRUSSELS")) return "🇧🇪";
  if (ex.includes("HKG") || ex.includes("HKSE")) return "🇭🇰";
  if (ex.includes("TSX") || ex.includes("TOR")) return "🇨🇦";
  if (ex.includes("ASX") || ex.includes("SYD")) return "🇦🇺";
  return "🇺🇸";
}

// Real, permanent fix for flag rendering: Unicode flag emoji (built from
// pairs of "regional indicator" characters) are notoriously inconsistent on
// Windows specifically — many Windows/browser combinations fall back to
// showing the raw two-letter country code as plain text instead of an
// actual flag, since it depends entirely on whichever font the OS/browser
// happens to resolve for those characters. No CSS font-family fallback can
// guarantee this works, since it requires the OS to actually have a
// compatible font installed at all — on some Windows setups, none does.
// The only rendering method that's genuinely consistent everywhere is a
// real image, which doesn't depend on font/emoji support existing at all.
//
// detectFlag() itself is left completely unchanged — it's used throughout
// the app to compute and store a flag value in state (watchlist items,
// search results, etc.), and changing its return type would break all of
// that. This is purely a display-time conversion: given the emoji string
// already being passed around, look up the matching ISO country code and
// render a real image instead.
var EMOJI_TO_COUNTRY_CODE = {
  "🇺🇸": "us", "🇮🇳": "in", "🇬🇧": "gb", "🇯🇵": "jp", "🇩🇪": "de", "🇳🇱": "nl",
  "🇫🇷": "fr", "🇨🇳": "cn", "🇭🇰": "hk", "🇨🇦": "ca", "🇦🇺": "au", "🇰🇷": "kr",
  "🇹🇼": "tw", "🇧🇷": "br", "🇨🇭": "ch", "🇸🇪": "se", "🇮🇪": "ie", "🇮🇱": "il",
  "🇸🇬": "sg", "🇪🇸": "es", "🇮🇹": "it", "🇧🇪": "be", "🇵🇹": "pt", "🇩🇰": "dk",
  "🇳🇴": "no", "🇫🇮": "fi", "🇵🇱": "pl", "🇦🇹": "at", "🇪🇺": "eu",
};
function FlagImg({ emoji, size, style }) {
  var code = EMOJI_TO_COUNTRY_CODE[emoji] || "us";
  var h = size || 14;
  return (
    <img
      src={"https://flagcdn.com/24x18/" + code + ".png"}
      srcSet={"https://flagcdn.com/48x36/" + code + ".png 2x"}
      alt=""
      style={Object.assign({ height: h, width: Math.round(h * 1.333), objectFit: "cover", borderRadius: 2, display: "inline-block", verticalAlign: "middle", flexShrink: 0 }, style || {})}
    />
  );
}

// Simple edit-distance function — used only for typo-tolerant ticker
// matching (e.g. "APPL" -> AAPL) as a local, instant fallback when no
// exact/prefix match is found. Deliberately checked only against the
// (short) curated ticker list, not full company names — company-name typo
// tolerance is handled server-side by Yahoo's fuzzy search instead, which
// has a much larger index to work with.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  var prev = [];
  for (var j = 0; j <= b.length; j++) prev[j] = j;
  for (var i = 1; i <= a.length; i++) {
    var cur = [i];
    for (var j2 = 1; j2 <= b.length; j2++) {
      var cost = a[i - 1] === b[j2 - 1] ? 0 : 1;
      cur[j2] = Math.min(prev[j2] + 1, cur[j2 - 1] + 1, prev[j2 - 1] + cost);
    }
    prev = cur;
  }
  return prev[b.length];
}

var LOCAL_SEARCH_INDEX = (function() {
  var seen = {};
  var idx = [];
  // From BROWSE_STOCKS — all regions
  Object.values(BROWSE_STOCKS).forEach(function(arr) {
    arr.forEach(function(s) {
      if (!seen[s.t]) {
        seen[s.t] = true;
        var flag = detectFlag(s.t, "");
        var cur = detectCurrency(s.t, "");
        idx.push({ t: s.t, n: s.n, f: flag, cur: cur, search: (s.t + " " + s.n).toLowerCase() });
      }
    });
  });
  // From NAMES map
  Object.keys(NAMES).forEach(function(k) {
    var ticker = NAMES[k];
    if (!seen[ticker]) {
      seen[ticker] = true;
      var flag = detectFlag(ticker, "");
      var cur = detectCurrency(ticker, "");
      var name = k.charAt(0).toUpperCase() + k.slice(1);
      idx.push({ t: ticker, n: name, f: flag, cur: cur, search: (ticker + " " + name).toLowerCase() });
    }
  });
  return idx;
})();

function lookup(q) {
  var u = q.trim().toUpperCase();
  if (u.includes(".") || u.length <= 6) {
    var l = q.trim().toLowerCase();
    for (var k in NAMES) { if (l === k || l.includes(k)) return NAMES[k]; }
    if (!u.includes(".") && u.length > 4) {
      var testNS = l;
      for (var k2 in NAMES) { if (testNS.includes(k2)) return NAMES[k2]; }
    }
    return u;
  }
  var l2 = q.trim().toLowerCase();
  for (var k3 in NAMES) { if (l2.includes(k3)) return NAMES[k3]; }
  return u;
}

function pct(v) { return (v >= 0 ? "+" : "") + v.toFixed(2) + "%"; }

// Real market trading hours (local time, own timezone) — DST is handled
// automatically by Intl's timeZone conversion rather than hardcoded UTC
// offsets, so this stays correct across the UK/US/EU clock changes without
// needing manual updates.
var MARKET_SESSIONS = [
  { name: "S&P 500", tz: "America/New_York", open: 9 * 60 + 30, close: 16 * 60 },
  { name: "FTSE 100", tz: "Europe/London", open: 8 * 60, close: 16 * 60 + 30 },
  { name: "Nifty 50", tz: "Asia/Kolkata", open: 9 * 60 + 15, close: 15 * 60 + 30 },
  { name: "Sensex", tz: "Asia/Kolkata", open: 9 * 60 + 15, close: 15 * 60 + 30 },
  { name: "Nikkei", tz: "Asia/Tokyo", open: 9 * 60, close: 15 * 60 },
  { name: "DAX", tz: "Europe/Berlin", open: 9 * 60, close: 17 * 60 + 30 },
];

function isMarketOpen(session) {
  var parts = new Intl.DateTimeFormat("en-GB", { timeZone: session.tz, hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short" }).formatToParts(new Date());
  var map = {};
  parts.forEach(function(p) { map[p.type] = p.value; });
  if (map.weekday === "Sat" || map.weekday === "Sun") return false;
  var nowMinutes = parseInt(map.hour, 10) * 60 + parseInt(map.minute, 10);
  return nowMinutes >= session.open && nowMinutes < session.close;
}

function biggestMoverName(list) {
  if (!list || list.length === 0) return null;
  var best = list[0];
  list.forEach(function(d) { if (Math.abs(d.change) > Math.abs(best.change)) best = d; });
  return best.name;
}

// Decides which market index to visually lead with: whichever market is
// currently open; if several overlap, whichever has moved the most today;
// if none are open (e.g. between sessions), falls back to the day's biggest
// mover across all tracked indices.
function getLeadMarketName(marketData) {
  var openNames = MARKET_SESSIONS.filter(isMarketOpen).map(function(m) { return m.name; });
  if (openNames.length === 0) return biggestMoverName(marketData);
  if (openNames.length === 1) return openNames[0];
  var openData = marketData.filter(function(d) { return openNames.indexOf(d.name) !== -1; });
  return biggestMoverName(openData) || openNames[0];
}


function formatRevEst(val, cur) {
  if (!val) return "N/A";
  var sym = { GBP:"£", INR:"₹", JPY:"¥", EUR:"€" }[cur] || "$";
  var abs = Math.abs(val);
  if (abs >= 1e12) return sym + (abs / 1e12).toFixed(2) + "T";
  if (abs >= 1e9)  return sym + (abs / 1e9).toFixed(2) + "B";
  if (abs >= 1e6)  return sym + (abs / 1e6).toFixed(1) + "M";
  return sym + val.toFixed(0);
}

/* ── Chart ── */
function ChartSkeleton() {
  return (
    <div>
      <div style={{ position: "relative", height: 160, borderRadius: 8, overflow: "hidden", background: "#F8FAFC" }}>
        <svg viewBox="0 0 320 160" style={{ width: "100%", height: "100%" }} preserveAspectRatio="none">
          <path d="M0,110 C30,95 50,120 80,100 C110,80 130,105 160,90 C190,75 210,95 240,80 C270,65 290,85 320,70"
            fill="none" stroke="#D8DEE6" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        <div className="skeleton" style={{ position: "absolute", inset: 0, opacity: 0.5 }} />
      </div>
      <div style={{ textAlign: "center", fontSize: 12, color: TX3, marginTop: 10 }}>Loading price history…</div>
    </div>
  );
}

function Chart({ data, color, currency, benchmarkData }) {
  var [tooltip, setTooltip] = useState(null);
  if (!data || data.length < 2) return null;
  var pr = data.map(function(d) { return d.price; });
  var mn = Math.min.apply(null, pr), mx = Math.max.apply(null, pr), rn = mx - mn || 1;
  var W = 320, H = 160, padL = 42, padB = 20, padT = 8, padR = 8;
  var cW = W - padL - padR, cH = H - padT - padB;
  var sym = { GBP:"£", INR:"₹", JPY:"¥", EUR:"€", HKD:"HK$", CAD:"C$", AUD:"A$" }[currency] || "$";

  var pts = data.map(function(d, i) {
    var x = padL + (i / (data.length - 1)) * cW;
    var y = padT + cH - ((d.price - mn) / rn) * cH;
    return { x: x, y: y, price: d.price, date: d.date };
  });

  var pathD = pts.map(function(p, i) { return (i === 0 ? "M" : "L") + p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
  var areaD = pathD + " L" + pts[pts.length - 1].x.toFixed(1) + "," + (padT + cH) + " L" + padL + "," + (padT + cH) + " Z";

  var yLabels = [];
  for (var g = 0; g <= 3; g++) {
    var gy = padT + (g / 3) * cH;
    var gv = mx - (g / 3) * rn;
    var label = gv >= 10000 ? (gv / 1000).toFixed(0) + "k" : gv >= 1000 ? (gv / 1000).toFixed(1) + "k" : gv.toFixed(gv < 10 ? 2 : 0);
    yLabels.push({ y: gy, label: label });
  }

  var xLabels = [];
  var step = Math.max(1, Math.floor(data.length / 4));
  for (var i = 0; i < data.length; i += step) {
    xLabels.push({ x: padL + (i / (data.length - 1)) * cW, label: data[i].date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }) });
  }

  function getClosestPoint(svgX) {
    var relX = svgX - padL;
    var idx = Math.round((relX / cW) * (pts.length - 1));
    return pts[Math.max(0, Math.min(pts.length - 1, idx))];
  }

  function handleTouch(e) {
    e.preventDefault();
    var rect = e.currentTarget.getBoundingClientRect();
    var touch = e.touches[0] || e.changedTouches[0];
    var relX = (touch.clientX - rect.left) / rect.width * W;
    setTooltip(getClosestPoint(relX));
  }

  function handleMouse(e) {
    var rect = e.currentTarget.getBoundingClientRect();
    var relX = (e.clientX - rect.left) / rect.width * W;
    setTooltip(getClosestPoint(relX));
  }

  var tt = tooltip;

  return (
    <svg viewBox={"0 0 " + W + " " + H} style={{ width: "100%", height: "auto", display: "block", touchAction: "none" }}
      onTouchMove={handleTouch} onTouchEnd={function() { setTooltip(null); }}
      onMouseMove={handleMouse} onMouseLeave={function() { setTooltip(null); }}>
      <defs>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".10" />
          <stop offset="100%" stopColor={color} stopOpacity=".01" />
        </linearGradient>
      </defs>
      {yLabels.map(function(yl, i) {
        return (
          <g key={i}>
            <line x1={padL} y1={yl.y} x2={padL + cW} y2={yl.y} stroke="#E8ECF1" strokeWidth=".5" />
            <text x={padL - 4} y={yl.y + 3} textAnchor="end" fill="#8A95A5" fontSize="8" fontFamily="Inter,sans-serif">{yl.label}</text>
          </g>
        );
      })}
      <path d={areaD} fill="url(#chartFill)" />
      <path d={pathD} fill="none" stroke={tt ? "#C4CAD4" : color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />

      {/* Tooltip crosshair */}
      {tt && (
        <g>
          <line x1={tt.x} y1={padT} x2={tt.x} y2={padT + cH} stroke={color} strokeWidth="1" strokeDasharray="3,2" />
          <circle cx={tt.x} cy={tt.y} r="4" fill={color} stroke="#fff" strokeWidth="2" />
          {/* Tooltip box */}
          {(function() {
            var boxW = 90, boxH = 34, boxX = tt.x + 6;
            if (boxX + boxW > W - padR) boxX = tt.x - boxW - 6;
            var boxY = Math.max(padT, tt.y - boxH / 2);
            if (boxY + boxH > padT + cH) boxY = padT + cH - boxH;
            return (
              <g>
                <rect x={boxX} y={boxY} width={boxW} height={boxH} rx="4" fill="#0F2D5C" opacity=".92" />
                <text x={boxX + boxW / 2} y={boxY + 13} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="Inter,sans-serif">
                  {sym}{tt.price >= 1000 ? tt.price.toLocaleString("en", { maximumFractionDigits: 0 }) : tt.price.toFixed(2)}
                </text>
                <text x={boxX + boxW / 2} y={boxY + 26} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="8" fontFamily="Inter,sans-serif">
                  {tt.date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </text>
              </g>
            );
          })()}
        </g>
      )}

      {!tt && <circle cx={pts[pts.length - 1].x} cy={pts[pts.length - 1].y} r="3" fill={color} stroke="#fff" strokeWidth="1.5" />}

      {/* Benchmark overlay — normalised relative strength line */}
      {benchmarkData && benchmarkData.length > 1 && (function() {
        var benchVals = benchmarkData.map(function(x) { return x.p; });
        var bMin = Math.min.apply(null, benchVals), bMax = Math.max.apply(null, benchVals);
        var bRange = bMax - bMin || 1;
        var tStart = data[0].date.getTime(), tEnd = data[data.length - 1].date.getTime();
        var tRange = tEnd - tStart || 1;
        var benchPts = benchmarkData.map(function(b) {
          var xFrac = Math.min(Math.max((b.d - tStart) / tRange, 0), 1);
          var x = padL + xFrac * cW;
          var y = padT + cH - ((b.p - bMin) / bRange) * cH;
          return { x: x, y: y };
        });
        var benchPath = benchPts.map(function(p, i) { return (i === 0 ? "M" : "L") + p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
        return <path d={benchPath} fill="none" stroke="#8A95A5" strokeWidth="1" strokeDasharray="3,2" strokeLinejoin="round" opacity="0.7" />;
      })()}
      {xLabels.map(function(xl, i) {
        return <text key={i} x={xl.x} y={H - 4} textAnchor="middle" fill="#8A95A5" fontSize="8" fontFamily="Inter,sans-serif">{xl.label}</text>;
      })}
    </svg>
  );
}

/* ── Badge ── */
/* ── Score Ring ── */
function ScoreRing({ score, size, animate }) {
  var s = size || 60, r = (s - 8) / 2, ci = 2 * Math.PI * r;
  var [displayed, setDisplayed] = useState(animate ? 0 : score);
  useEffect(function() {
    if (!animate) { setDisplayed(score); return; }
    setDisplayed(0);
    var start = null, duration = 900;
    function step(ts) {
      if (!start) start = ts;
      var prog = Math.min((ts - start) / duration, 1);
      var ease = 1 - Math.pow(1 - prog, 3);
      setDisplayed(Math.round(ease * score));
      if (prog < 1) requestAnimationFrame(step);
    }
    var raf = requestAnimationFrame(step);
    return function() { cancelAnimationFrame(raf); };
  }, [animate]); // eslint-disable-line react-hooks/exhaustive-deps
  var off = ci - (displayed / 100) * ci;
  var col = displayed >= 65 ? OK : displayed >= 40 ? WN : ER;
  return (
    <div style={{ position: "relative", width: s, height: s }}>
      <svg width={s} height={s}>
        <circle cx={s/2} cy={s/2} r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
        <circle cx={s/2} cy={s/2} r={r} fill="none" stroke={col} strokeWidth="4" strokeLinecap="round" strokeDasharray={ci} strokeDashoffset={off} transform={"rotate(-90 " + s/2 + " " + s/2 + ")"} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: s * 0.32, fontWeight: 800, color: col }}>{displayed}</div>
    </div>
  );
}

// Small circle used in place of ScoreRing when a pillar genuinely doesn't
// apply (Safety on banks) or can't be computed (missing balance-sheet data).
// Deliberately not a number-shaped placeholder ("N/A", "0") — those read as
// a broken/missing score. A dashed ring + dash + a tappable info badge reads
// as "this doesn't apply here", with the explanation one tap away rather
// than guessed at.
// Small circle used in place of ScoreRing when a pillar genuinely doesn't
// apply (Altman Z-Score on banks) or can't be computed (missing balance-
// sheet data). Deliberately not a number-shaped placeholder ("N/A", "0") —
// those read as a broken/missing score. Just a dashed ring + dash; the
// explanation lives in the single ⓘ next to the label below, not on the
// ring itself, so there's exactly one tap target per pillar, not two.
function EmptyPillarRing({ size }) {
  var s = size || 60, r = (s - 8) / 2;
  return (
    <div style={{ position: "relative", width: s, height: s }}>
      <svg width={s} height={s}>
        <circle cx={s/2} cy={s/2} r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" strokeDasharray="4 4" />
        <text x={s/2} y={s/2 + s*0.11} textAnchor="middle" fontSize={s * 0.3} fill="rgba(255,255,255,0.35)">—</text>
      </svg>
    </div>
  );
}

// A label + ⓘ button used under each pillar circle — same tappable pattern
// for all three, so Value/Quality/Altman Z-Score behave identically rather
// than only Safety having an explanation as before.
function PillarLabel({ text, muted, onTap }) {
  return (
    <button onClick={onTap} style={{ fontSize: 11, color: muted ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.7)", marginTop: 4, fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 3, justifyContent: "center", width: "100%", lineHeight: 1.3 }}>
      <span>{text}</span>
      <span style={{ width: 13, height: 13, borderRadius: "50%", border: "1px solid " + (muted ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.35)"), fontSize: 9, fontWeight: 700, color: muted ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>i</span>
    </button>
  );
}

// Value / Quality / Altman Z-Score pillar row — sits directly under the
// verdict header, above the price chart, so the "why" behind the verdict
// is visible without scrolling past the chart into collapsed cards. Always
// renders all three slots (never 2 vs 3 depending on the stock) so an
// excluded Altman Z-Score pillar reads as an explained state, not a layout
// inconsistency. Named "Altman Z-Score" rather than "Safety" — the plain-
// language name it started with wasn't the most precise one, and since the
// tooltip already explains the actual term, naming the circle after it
// directly makes the label self-documenting rather than needing translation.
function PillarRow({ stock, openSections, setOpenSections, jumpToDcf, jumpToJustifiedPB }) {
  var p = stock.pillars;
  if (!p) return null;
  var zExcluded = p.safety == null;

  var TIP_KEYS = ["pillar_value_tip", "pillar_quality_tip", "pillar_z_tip"];
  function selectTip(key) {
    setOpenSections(function(prev) {
      var wasOpen = !!prev[key];
      var next = Object.assign({}, prev);
      TIP_KEYS.forEach(function(k) { next[k] = false; });
      next[key] = !wasOpen; // tapping an already-open tip closes it; tapping any other opens it and closes the rest
      return next;
    });
  }

  // Value drill-down — the actual signals feeding the pillar, not a
  // paragraph describing them. Everything in stock.sb except Quality Score,
  // Financial Safety (those have their own pillars) and Market Regime (a
  // separate market-wide adjustment, not a signal about this stock).
  var valueSignals = (stock.sb || []).filter(function(f) {
    return f.factor !== "Quality Score" && f.factor !== "Financial Safety" && f.factor !== "Market Regime";
  });

  // Quality drill-down — the same factor list the standalone Quality Score
  // card used to show, now living in the one place Quality information
  // actually belongs, instead of duplicated in two places.
  var qualityFactors = (stock.qs && stock.qs.factors) || [];

  var zDefinition = "The Altman Z-Score is a financial-distress risk measure calculated from the balance sheet — retained earnings, working capital, leverage, and profitability relative to total assets. Above 3.0 is generally a safe zone, 1.8–3.0 a grey zone worth watching, below 1.8 signals elevated distress risk.";
  var zExtremeCaveat = (p.safetyRaw && p.safetyRaw.z > 10)
    ? " This is far above the typical safe-zone range — usually a sign of unusually low debt relative to market cap (common for asset-light, low-debt companies). The Altman Z model was built mainly for manufacturing companies with more typical debt levels, so it can produce extreme, less meaningful scores for businesses like this."
    : "";
  var zNote = zExcluded
    ? zDefinition + " " + (p.safetyExcludeReason === "sector"
        ? "Not shown here for banks and financial companies specifically — it wasn't designed for their balance sheets, where deposits aren't debt in the normal sense."
        : "Data not available for this specific stock.")
    : zDefinition
      + (p.safetyRaw ? " The " + p.safety + "/100 shown here is that score rescaled to sit visually alongside Value and Quality — the actual Altman Z-Score is " + p.safetyRaw.z + " (" + (p.safetyRaw.zone === "safe" ? "Safe zone" : p.safetyRaw.zone === "grey" ? "Grey zone" : "Distress zone") + ")." : "")
      + zExtremeCaveat;

  function DrillRow(props) {
    var f = props.f;
    var isDcf = f.factor === "DCF Upside";
    var isJustifiedPB = f.factor === "Justified P/B Upside";
    // Used only to decide which plain-language message fits — never shown
    // to the user as a raw number. A percentage like "10%" assumes someone
    // already understands there's a weighted composite score underneath,
    // which most users won't have — the insight that matters is "this
    // signal counts less here," not the mechanics of exactly how much.
    var weightPct = (isDcf && props.totalWeight) ? (f.weight / props.totalWeight) * 100 : null;
    return (
      <div style={{ padding: "5px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <span style={{ fontSize: 12.5, color: TXM, flexShrink: 0 }}>{f.factor === "Forward P/E" || f.factor === "Trailing P/E" ? "P/E vs Sector" : f.factor}</span>
          <span style={{ textAlign: "right" }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: TX }}>{f.value}</span>
            {f.label && <span style={{ fontSize: 11, color: TX3, marginLeft: 6 }}>{f.label}</span>}
          </span>
        </div>
        {isDcf && jumpToDcf && (
          <button onClick={jumpToDcf} style={{ fontSize: 10.5, color: B, background: BW, border: "none", borderRadius: 6, padding: "5px 8px", marginTop: 4, cursor: "pointer", fontWeight: 600, display: "block", width: "100%", textAlign: "left" }}>
            {weightPct != null && weightPct < 15 ? "Weighted less for high-growth stocks like this — see why below ↓" : "Full breakdown in the DCF card below ↓"}
          </button>
        )}
        {isJustifiedPB && jumpToJustifiedPB && (
          <button onClick={jumpToJustifiedPB} style={{ fontSize: 10.5, color: B, background: BW, border: "none", borderRadius: 6, padding: "5px 8px", marginTop: 4, cursor: "pointer", fontWeight: 600, display: "block", width: "100%", textAlign: "left" }}>
            Full breakdown in the 1-Year Target card below ↓
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {p.value != null ? <ScoreRing score={p.value} size={68} /> : <EmptyPillarRing size={68} />}
          <PillarLabel text="Value" onTap={function() { selectTip("pillar_value_tip"); }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {p.quality != null ? <ScoreRing score={p.quality} size={68} /> : <EmptyPillarRing size={68} />}
          <PillarLabel text="Quality" onTap={function() { selectTip("pillar_quality_tip"); }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {!zExcluded ? <ScoreRing score={p.safety} size={68} /> : <EmptyPillarRing size={68} />}
          <PillarLabel text="Altman Z-Score" muted={zExcluded} onTap={function() { selectTip("pillar_z_tip"); }} />
          {p.safetyRaw && p.safetyRaw.zone === "distress" && (
            <div style={{ fontSize: 10, fontWeight: 700, color: "#F0997B", background: "rgba(240,153,123,0.15)", borderRadius: 6, padding: "2px 6px", marginTop: 3, display: "inline-block" }}>Distress risk</div>
          )}
        </div>
      </div>

      {openSections.pillar_value_tip && (
        <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", marginTop: 10 }}>
          {valueSignals.length > 0 ? (function() {
            var totalWeight = valueSignals.reduce(function(sum, s) { return sum + (s.weight || 0); }, 0);
            return valueSignals.map(function(f, i) {
              return <div key={i} style={{ borderTop: i ? "1px solid " + BD : "none" }}><DrillRow f={f} totalWeight={totalWeight} /></div>;
            });
          })() : <div style={{ fontSize: 11, color: TX3 }}>Not enough data to break this down yet.</div>}
        </div>
      )}

      {openSections.pillar_quality_tip && (
        <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", marginTop: 10 }}>
          {qualityFactors.length > 0 ? qualityFactors.map(function(f, i) {
            var good = f.rating === "excellent" || f.rating === "low debt";
            var ok = f.rating === "good" || f.rating === "manageable";
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: good ? OK : ok ? WN : ER, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, color: TXM, flex: 1 }}>{f.label}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: TX }}>{f.value}</span>
                <span style={{ fontSize: 11, color: good ? OKT : ok ? WN : ERT }}>{f.rating}</span>
              </div>
            );
          }) : <div style={{ fontSize: 11, color: TX3 }}>Not enough data to break this down yet.</div>}
        </div>
      )}

      {openSections.pillar_z_tip && (
        <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.5, marginTop: 10 }}>{zNote}</div>
      )}
    </div>
  );
}

/* ── Collapsible section card ── */
function SecCard({ id, title, badge, badgeColor, open, onToggle, children }) {
  var badgeBg = badgeColor === "green" ? OKS : badgeColor === "red" ? ERS : badgeColor === "amber" ? WNS : BW;
  var badgeTx = badgeColor === "green" ? OKT : badgeColor === "red" ? ERT : badgeColor === "amber" ? WN : B;
  return (
    <div id={id} style={{ background: WH, borderRadius: 16, overflow: "hidden", marginBottom: 12, scrollMarginTop: 64, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 8px rgba(15,23,42,0.05)" }}>
      <div onClick={onToggle} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: N }}>{title}</span>
          {badge && <span style={{ fontSize: 11.5, fontWeight: 700, background: badgeBg, color: badgeTx, borderRadius: 20, padding: "4px 10px", whiteSpace: "nowrap" }}>{badge}</span>}
        </div>
        <span style={{ fontSize: 11, color: TX3, transform: open ? "rotate(180deg)" : "none", display: "inline-block", transition: "transform .2s" }}>▼</span>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid #F1F5F9", padding: "14px 16px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* GroupCard — top-level entry point for the detail page. Wraps 2-5 related
   SecCards so a new user sees 4 groups instead of 15 flat sections. Closed
   groups show an icon + short subtitle; open groups reveal their nested
   SecCards, unchanged, below. If none of a group's sections have data for
   this stock, hasData=false shows a plain message instead of an empty box —
   the group itself still stays visible and tappable either way. */
/* TabHeader — icon-in-accent-circle + title, used at the top of every main
   tab (Watchlist, Screener, Compare, Portfolio, Learn) so each screen carries
   the same signature-teal visual language as the detail page. */
function TabHeader({ icon, title, subtitle, badge }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: subtitle ? 4 : 12 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: N, flex: 1 }}>{title}</div>
      {badge && <div style={{ fontSize: 12, fontWeight: 700, color: badge.atLimit ? ER : TXM, background: badge.atLimit ? ERS : BG, borderRadius: 6, padding: "3px 9px" }}>{badge.text}</div>}
    </div>
  );
}

function GroupCard({ id, icon, title, subtitle, open, onToggle, hasData, badge, badgeColor, children }) {
  var badgeBg = badgeColor === "green" ? OKS : badgeColor === "red" ? ERS : badgeColor === "amber" ? WNS : BW;
  var badgeTx = badgeColor === "green" ? OKT : badgeColor === "red" ? ERT : badgeColor === "amber" ? WN : B;
  return (
    <div id={id} style={{ background: WH, border: open ? "1.5px solid " + B : "none", borderRadius: 16, overflow: "hidden", gridColumn: open ? "1 / -1" : "auto", scrollMarginTop: 64, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 8px rgba(15,23,42,0.05)" }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", cursor: "pointer", userSelect: "none" }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: N }}>{title}</div>
          {!open && (badge ? (
            <span style={{ fontSize: 10, fontWeight: 700, background: badgeBg, color: badgeTx, borderRadius: 20, padding: "3px 9px", marginTop: 4, display: "inline-block" }}>{badge}</span>
          ) : (
            <div style={{ fontSize: 11, color: TX3, marginTop: 1 }}>{subtitle}</div>
          ))}
        </div>
        <MdChevronRight size={18} color={TX3} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s", flexShrink: 0 }} />
      </div>
      {open && (
        <div style={{ borderTop: "1px solid #F1F5F9", padding: "10px 10px 2px" }}>
          {hasData === false ? (
            <div style={{ padding: "14px 4px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: TX3 }}>Data not available for {title.toLowerCase()} on this stock yet.</div>
            </div>
          ) : children}
        </div>
      )}
    </div>
  );
}

var _swipeMap = {};

// Splash screen orbital icons — two layers (back: behind the logo, front:
// in front of it) for the depth effect, same idea as before, now driven by
// the same orbital motion + glassmorphic styling as the welcome screen.
// Splash screen icons — real orbital motion (CSS keyframes, matching
// Waltra's actual index.html splash technique exactly, not the small
// sin/cos bob used elsewhere). 5 icons: 3 orbit clockwise at 110px radius,
// 2 counter-clockwise at 80px radius, staggered delays — see
// SPLASH_ORBIT_KEYFRAMES and SplashScreen below.
var SPLASH_ORBIT_ICONS = [
  { Icon: MdShowChart,           color: "#D4AF37", anim: "zorbit",  duration: "12s", delay: "0s" },   // Undervalued Picks
  { Icon: MdLightbulb,           color: "#FFFFFF", anim: "zorbit",  duration: "12s", delay: "-4s" },  // AI Investment Thesis
  { Icon: MdBarChart,            color: "#FFFFFF", anim: "zorbit",  duration: "12s", delay: "-8s" },  // Stock Comparisons
  { Icon: MdAccountBalanceWallet, color: "#D4AF37", anim: "zorbitR", duration: "8s",  delay: "0s" },  // Mock Investing
  { Icon: MdMenuBook,            color: "#FFFFFF", anim: "zorbitR", duration: "8s",  delay: "-4s" },  // Financial Dictionary
];

// Orbital-motion floating icons for the welcome screen — same real
// orbital CSS technique as the splash screen (see SPLASH_ORBIT_ICONS),
// radius scaled down for this smaller hero section. Colors reuse Zoltra's
// own existing accent palette (AC teal, B blue).
var WELCOME_ORBIT_ICONS = [
  { Icon: MdShowChart,   color: AC, anim: "worbit",  duration: "12s", delay: "0s" },
  { Icon: MdTrendingUp,  color: B,  anim: "worbit",  duration: "12s", delay: "-4s" },
  { Icon: MdBarChart,    color: B,  anim: "worbit",  duration: "12s", delay: "-8s" },
  { Icon: MdPublic,      color: AC, anim: "worbitR", duration: "8s",  delay: "0s" },
  { Icon: MdGpsFixed,    color: B,  anim: "worbitR", duration: "8s",  delay: "-4s" },
];

/* ── MAIN ── */
// ── Welcome screen — its own component, not an inline branch inside Zoltra()
// below. This matters structurally, not just for organization: the orbital
// icon motion needs its own useState/useRef/useEffect hooks, and calling
// hooks conditionally inside one giant component's early-return branches
// is a real React Rules-of-Hooks violation — hook call order changes the
// moment `view` moves away from "welcome" (e.g. after sign-in), which is
// exactly the kind of mismatch that produces a hard-to-diagnose "visible
// artifact" rather than a clean crash. Keeping this as a fully separate
// component sidesteps that: it mounts and unmounts cleanly with its own
// consistent hook order, the same way Waltra's AuthScreen already does.
// ── Splash screen — same reasoning as WelcomeScreen above: its own
// component so the orbital-motion hooks have a safe, consistent place to
// live, rather than being called conditionally inside Zoltra()'s early
// returns (the likely actual cause of the original "visible artifact").
function SplashScreen({ closing }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "linear-gradient(135deg, #1E1240, #2D1B54)", fontFamily: "Inter, -apple-system, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)", zIndex: 9999, opacity: closing ? 0 : 1, transition: "opacity .3s ease" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}
          @keyframes splashIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
          @keyframes splashPulse{0%,100%{opacity:.35}50%{opacity:1}}
          .sdot{animation:splashPulse 1.1s ease-in-out infinite}
          @keyframes zorbit{0%{transform:rotate(0deg) translateX(150px) rotate(0deg)}100%{transform:rotate(360deg) translateX(150px) rotate(-360deg)}}
          @keyframes zorbitR{0%{transform:rotate(0deg) translateX(110px) rotate(0deg)}100%{transform:rotate(-360deg) translateX(110px) rotate(360deg)}}
        `}</style>

        {/* Gold glow blobs — same device as the Weekly Deep Dive tile */}
        <div style={{ position: "absolute", top: -70, right: -70, width: 280, height: 280, borderRadius: "50%", background: "rgba(212,175,55,0.18)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -90, left: -70, width: 300, height: 300, borderRadius: "50%", background: "rgba(212,175,55,0.10)", pointerEvents: "none" }} />

        {/* Orbital icons — genuine circular orbit around the center point
            (where the logo/text sits), not a small independent bob near a
            fixed position. Confirmed against Waltra's actual index.html
            splash and approved via side-by-side mockup — this was a real
            motion-pattern mismatch, not just a speed/amplitude tuning
            issue like the earlier attempts assumed. */}
        <div style={{ position: "absolute", top: "50%", left: "50%", marginTop: -190, marginLeft: -190, width: 380, height: 380, zIndex: 0, pointerEvents: "none" }} aria-hidden="true">
          {SPLASH_ORBIT_ICONS.map(function(f, i) {
            var isWhite = f.color === "#FFFFFF";
            var bgOp = isWhite ? "40" : "26";
            var borderOp = isWhite ? "B0" : "80";
            var glowOp = isWhite ? "B0" : "80";
            return (
              <div key={i} style={{ position: "absolute", top: "50%", left: "50%", transformOrigin: "0 0", animation: f.anim + " " + f.duration + " linear infinite", animationDelay: f.delay }}>
                <div style={{ width: 48, height: 48, marginTop: -24, marginLeft: -24, borderRadius: 15, background: f.color + bgOp, border: "1.5px solid " + f.color + borderOp, display: "flex", alignItems: "center", justifyContent: "center", filter: "drop-shadow(0 0 8px " + f.color + glowOp + ")", color: f.color }}>
                  <f.Icon size={24} />
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ position: "relative", zIndex: 1, textAlign: "center", animation: "splashIn .35s ease both" }}>
          <div style={{ marginBottom: 30, display: "flex", justifyContent: "center" }}>
            <Logo size={68} dark />
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, color: WH, letterSpacing: -0.3, marginBottom: 18 }}>Zoltra.</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#D4AF37", letterSpacing: 1, marginBottom: 26 }}>AI-POWERED STOCK ANALYSIS</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 34 }}>
            <FlagImg emoji="🇺🇸" size={16} />
            <FlagImg emoji="🇬🇧" size={16} />
            <FlagImg emoji="🇮🇳" size={16} />
            <FlagImg emoji="🇯🇵" size={16} />
            <FlagImg emoji="🇪🇺" size={16} />
          </div>
          <div style={{ marginBottom: 34 }}>
            <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 8 }}>
              {["Undervalued Picks", "Stock Comparisons", "Mock Investing"].map(function(label, i) {
                return (
                  <div key={i} style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 20, padding: "5px 10px", whiteSpace: "nowrap" }}>
                    {label}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
              {["AI Investment Thesis", "Financial Dictionary"].map(function(label, i) {
                return (
                  <div key={i} style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 20, padding: "5px 10px", whiteSpace: "nowrap" }}>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: "flex", gap: 7, justifyContent: "center" }}>
            <div className="sdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "#D4AF37" }} />
            <div className="sdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "#D4AF37", animationDelay: ".15s" }} />
            <div className="sdot" style={{ width: 7, height: 7, borderRadius: "50%", background: "#D4AF37", animationDelay: ".3s" }} />
          </div>
        </div>
    </div>
  );
}

function WelcomeScreen({
  authMode, setAuthMode, emailInput, setEmailInput, passwordInput, setPasswordInput,
  ageConfirmed, setAgeConfirmed, authError, setAuthError, handleEmailAuth, handleForgotPassword,
}) {
  return (
    <div style={{ minHeight: "100dvh", background: WH, fontFamily: "Inter, -apple-system, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", overflowX: "hidden", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)", paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}@keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}.wu0{animation:fadeUp .5s ease both}.wu1{animation:fadeUp .5s .08s ease both}.wu2{animation:fadeUp .5s .16s ease both}.wu3{animation:fadeUp .5s .24s ease both}.wu4{animation:fadeUp .5s .32s ease both}.wfeat{background:#F8F9FB;border:1px solid #E8ECF1;border-radius:12px;padding:14px 12px}.wcta{background:#0F2D5C;color:#fff;border:none;border-radius:12px;padding:14px 32px;font-size:16px;font-weight:700;cursor:pointer;font-family:inherit;width:100%}
        @keyframes worbit{0%{transform:rotate(0deg) translateX(90px) rotate(0deg)}100%{transform:rotate(360deg) translateX(90px) rotate(-360deg)}}
        @keyframes worbitR{0%{transform:rotate(0deg) translateX(65px) rotate(0deg)}100%{transform:rotate(-360deg) translateX(65px) rotate(360deg)}}
      `}</style>

      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        {/* Top bar */}
        <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid " + BD }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Logo size={32} />
            <span style={{ fontSize: 16, fontWeight: 800, color: N, letterSpacing: -0.2 }}>Zoltra.</span>
          </div>
          <span style={{ visibility: "hidden" }}>placeholder</span>
        </div>

        {/* Hero */}
        <div style={{ padding: "40px 24px 32px", textAlign: "center", position: "relative", overflow: "hidden" }}>

          {/* Floating icon background — genuine CSS orbital motion, same
              technique confirmed against Waltra's real index.html splash
              (not the small sin/cos bob this used before). Radius scaled
              down from the splash screen's 110px/80px since this hero
              section is a bounded area, not a full viewport — keeps the
              motion visible within it rather than mostly clipped outside. */}
          <div style={{ position: "absolute", top: 80, left: "50%", marginTop: -160, marginLeft: -160, width: 320, height: 320, zIndex: 0, pointerEvents: "none" }} aria-hidden="true">
            {WELCOME_ORBIT_ICONS.map(function(f, i) {
              return (
                <div key={i} style={{ position: "absolute", top: "50%", left: "50%", transformOrigin: "0 0", animation: f.anim + " " + f.duration + " linear infinite", animationDelay: f.delay }}>
                  <div style={{ width: 40, height: 40, marginTop: -20, marginLeft: -20, borderRadius: 13, background: "rgba(15,45,92,0.05)", border: "1.5px solid rgba(15,45,92,0.14)", display: "flex", alignItems: "center", justifyContent: "center", filter: "drop-shadow(0 0 6px rgba(15,45,92,0.1))", color: f.color }}>
                    <f.Icon size={20} />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
          <div className="wu0" style={{ marginBottom: 20 }}>
            <img src="/zoltra-icon.png" alt="Zoltra" style={{ width: 80, height: 80, objectFit: "contain" }} />
            <div style={{ fontSize: 22, fontWeight: 800, color: N, marginTop: 10, letterSpacing: -0.3 }}>Zoltra.</div>
          </div>
          <div className="wu1" style={{ fontSize: 28, fontWeight: 800, color: N, marginBottom: 8, lineHeight: 1.2 }}>
            AI-Powered<br />Stock Analysis
          </div>
          <p className="wu2" style={{ fontSize: 15, color: TXM, lineHeight: 1.65, marginBottom: 20 }}>
            Real-time valuations, earnings data, and AI verdicts across global markets.
          </p>

          {/* Market coverage — a real trust signal for a new visitor before
              asking them to sign up, not just a line buried in the features
              grid further down. Uses detectFlag/the app's own color tokens
              rather than new hardcoded values, so it stays visually
              consistent with how markets are represented everywhere else. */}
          <div className="wu2" style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 24 }}>
            {[
              { region: "US", exch: "NYSE · Nasdaq", flag: detectFlag("AAPL"), c: AC, bg: ACS },
              { region: "UK", exch: "LSE", flag: detectFlag("SHEL.L"), c: B, bg: BW },
              { region: "India", exch: "NSE · BSE", flag: detectFlag("RELIANCE.NS"), c: AC, bg: ACS },
              { region: "Japan", exch: "TSE", flag: detectFlag("7203.T"), c: B, bg: BW },
              { region: "Europe", exch: "Xetra · Euronext", flag: detectFlag("SAP.DE"), c: AC, bg: ACS },
            ].map(function(m, i) {
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, background: m.bg, border: "1px solid " + m.c + "40", borderRadius: 20, padding: "6px 12px" }}>
                  <FlagImg emoji={m.flag} size={15} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: N }}>{m.region}</span>
                  <span style={{ fontSize: 10.5, color: TXM }}>{m.exch}</span>
                </div>
              );
            })}
          </div>

          {/* Inline sign-in form */}
          <div className="wu3" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: N, marginBottom: 12, padding: "8px 14px", background: BW, borderRadius: 10, display: "inline-block" }}>
              {authMode === "signin" ? "Welcome back — sign in" : "Create your account"}
            </div>
            <input
              type="email"
              value={emailInput}
              onChange={function(e) { setEmailInput(e.target.value); setAuthError(""); }}
              placeholder="Email address"
              style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1.5px solid " + BD, fontSize: 16, color: N, background: WH, marginBottom: 8, fontFamily: "Inter, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif" }}
            />
            <input
              type="password"
              value={passwordInput}
              onChange={function(e) { setPasswordInput(e.target.value); setAuthError(""); }}
              onKeyDown={function(e) { if (e.key === "Enter") handleEmailAuth(); }}
              placeholder="Password (min 6 characters)"
              style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: "1.5px solid " + BD, fontSize: 16, color: N, background: WH, marginBottom: 8, fontFamily: "Inter, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif" }}
            />
            {authMode === "signup" && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8, padding: "0 2px", cursor: "pointer" }}>
                <input type="checkbox" checked={ageConfirmed} onChange={function(e) { setAgeConfirmed(e.target.checked); setAuthError(""); }}
                  style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0, accentColor: N }} />
                <span style={{ fontSize: 12, color: TXM, lineHeight: 1.5, textAlign: "left" }}>I confirm I am 18 years of age or older</span>
              </label>
            )}
            {authError ? <div style={{ fontSize: 12, color: ER, marginBottom: 8, paddingLeft: 4 }}>{authError}</div> : null}
            <button className="wcta" onClick={handleEmailAuth}>
              {authMode === "signup" ? "Create account & start →" : "Sign in & start →"}
            </button>
            <div style={{ textAlign: "center", marginTop: 12 }}>
              {authMode === "signin" ? (
                <span style={{ fontSize: 13, color: TXM }}>No account? <button onClick={function() { setAuthMode("signup"); setAuthError(""); }} style={{ color: B, background: "none", border: "none", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Create one</button></span>
              ) : (
                <span style={{ fontSize: 13, color: TXM }}>Already have an account? <button onClick={function() { setAuthMode("signin"); setAuthError(""); }} style={{ color: B, background: "none", border: "none", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>Sign in</button></span>
              )}
            </div>
            {authMode === "signin" && (
              <div style={{ textAlign: "center", marginTop: 6 }}>
                <button onClick={handleForgotPassword} style={{ color: TXM, background: "none", border: "none", fontSize: 12, cursor: "pointer" }}>Forgot password?</button>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6, justifyContent: "center", marginTop: 16, padding: "0 16px" }}>
              <MdLock size={13} color={TX3} style={{ flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 11, color: TX3, lineHeight: 1.5, margin: 0, textAlign: "left" }}>No credit card required to sign up. The Free plan is genuinely free — choose your plan on the next screen.</p>
            </div>
          </div>
          </div>
        </div>

        {/* Features */}
        <div className="wu4" style={{ padding: "0 20px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
            {[
              { i: <MdGpsFixed size={20} />, t: "AI Verdicts", d: "Over/undervalued with confidence score" },
              { i: <MdBarChart size={20} />, t: "Earnings", d: "EPS estimates vs actuals, beat/miss" },
              { i: <MdPublic size={20} />, t: "Global Markets", d: "US, UK, India, Japan, Europe" },
              { i: <MdShowChart size={20} />, t: "Price History", d: "5-year chart with period returns" },
              { i: <MdBalance size={20} />, t: "Compare", d: "Side-by-side stock analysis" },
              { i: <MdAccountBalanceWallet size={20} />, t: "Portfolio", d: "Paper-trade and track P&L" },
            ].map(function(f, i) {
              return (
                <div key={i} className="wfeat">
                  <div style={{ color: AC, marginBottom: 6, display: "flex" }}>{f.i}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: N, marginBottom: 2 }}>{f.t}</div>
                  <div style={{ color: TXM, fontSize: 12, lineHeight: 1.4 }}>{f.d}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: "0 20px 32px", textAlign: "center" }}>
          <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
        </div>
      </div>
    </div>
  );
}

export default function Zoltra() {
  var [view, setView] = useState("welcome");

  // Reset scroll to top on every view change. Without this, navigating to a
  // new tab or the detail page inherits whatever scroll position the
  // previous view was left at. A bare scrollTo(0,0) fired too early (before
  // the new view has actually painted) — double-rAF pushes it to after the
  // browser has genuinely rendered the new content, which is what a naive
  // synchronous call was racing against.
  useEffect(function() {
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        window.scrollTo(0, 0);
      });
    });
  }, [view]);
  var [query, setQuery] = useState("");
  var [loadingName, setLoadingName] = useState(null);
  var [loading, setLoading] = useState(false);
  var [loadStep, setLoadStep] = useState("Analysing…");
  var [loadingChartData, setLoadingChartData] = useState(null); // { points, changePct, price } — real data, populated as soon as getStockHistory resolves (independent of the slower AI call)
  var [loadingTicker, setLoadingTicker] = useState(null); // resolved ticker symbol, known well before the full analysis — used for the loading screen's logo and flag
  var [stock, setStock] = useState(null);
  var [hist, setHist] = useState(null);
  var [watchlist, setWatchlist] = useState([]);
  var [portfolio, setPf] = useState([]);
  var [homeCurrency, setHomeCurrency] = useState(function() {
    try { return localStorage.getItem("q_home_currency") || "GBP"; } catch(e) { return "GBP"; }
  });
  var [fxRates, setFxRates] = useState({ GBP: 1 });
  var [fxLoading, setFxLoading] = useState(false);
  var [validationData, setValidationData] = useState(null);
  var [validationLoading, setValidationLoading] = useState(false);
  var [validationExpandedWeek, setValidationExpandedWeek] = useState(null);
  var [notes, setNotes] = useState({});
  var [thesis, setThesis] = useState({});
  var [thesisChallenge, setThesisChallenge] = useState({});
  var [thesisChallenging, setThesisChallenging] = useState(false);
  var [alerts, setAlerts] = useState([]);
  var [noteText, setNoteText] = useState("");
  var [aboutExpanded, setAboutExpanded] = useState(false);
  var [alertPrice, setAlertPrice] = useState("");
  var [alertDir, setAlertDir] = useState("above");
  var [onboardStep, setOnboardStep] = useState(0);
  var [emailInput, setEmailInput] = useState("");
  var [passwordInput, setPasswordInput] = useState("");
  var [authMode, setAuthMode] = useState("signin");
  var [authError, setAuthError] = useState("");
  var [stockNews, setStockNews] = useState([]);
  var [screenerResults, setScreenerResults] = useState([]);
  var [screenerRaw, setScreenerRaw] = useState([]);
  var [screenerLoading, setScreenerLoading] = useState(false);
  var [screenerRegion, setScreenerRegion] = useState("all");
  var [screenerHasRun, setScreenerHasRun] = useState(false);
  var [showSectorRotation, setShowSectorRotation] = useState(false);
  var [screenerFilters, setScreenerFilters] = useState({ valuation: null, quality: false, bullish: false, sector: null });
  var [screenerSort, setScreenerSort] = useState(null); // null = default order; "value" | "quality" | "safety" | "change"
  var [wlScores, setWlScores] = useState([]);
  var [wlScoring, setWlScoring] = useState(false);
  var [prevView, setPrevView] = useState("home");
  var [openSections, setOpenSections] = useState({ ai: true, earnings: true, value: true, sector: false, dcf: false, quality: false, momentum: false, valhistory: false, earnquality: false, shortint: false, vh_tip: false, eq_tip: false, si_tip: false, mos_tip: false, qs_tip: false, pillar_value_tip: false, pillar_quality_tip: false, pillar_z_tip: false, thesis: false, notes: false, alert: false, bullbear: false, catalysts: false, news: false, dcf_country_tip: false });
  var [openGroups, setOpenGroups] = useState({ valuation: false, financials: false, risk: false, ai: false });
  function tog(id) { setOpenSections(function(prev) { var n = Object.assign({}, prev); n[id] = !n[id]; return n; }); }
  function openSection(id) { setOpenSections(function(prev) { var n = Object.assign({}, prev); n[id] = true; return n; }); }
  function togGroup(id) { setOpenGroups(function(prev) { var n = Object.assign({}, prev); n[id] = !n[id]; return n; }); }
  function openGroup(id) { setOpenGroups(function(prev) { var n = Object.assign({}, prev); n[id] = true; return n; }); }
  var [pfHistory, setPfHistory] = useState(function() {
    try { var h = localStorage.getItem("q_pf_history"); return h ? JSON.parse(h) : []; } catch(e) { return []; }
  });
  var [spyBaseline, setSpyBaseline] = useState(function() {
    try { var s = localStorage.getItem("q_spy_baseline"); return s ? JSON.parse(s) : null; } catch(e) { return null; }
  });
  var [cmpList, setCmp] = useState([]);
  var [cmpQuery, setCmpQuery] = useState("");
  var [cmpSearchResults, setCmpSearchResults] = useState([]);
  var [cmpStocks, setCmpStocks] = useState({});
  var [cmpShowMore, setCmpShowMore] = useState(false);
  var [showInvest, setShowInvest] = useState(false);
  var [investAmt, setInvestAmt] = useState("100");
  var [toast, setToast] = useState("");
  var [chartRange, setChartRange] = useState(90);
  var [metricTab, setMetricTab] = useState(function() { try { return sessionStorage.getItem("q_metric_tab") || "val"; } catch(e) { return "val"; } });
  var [marketFilter, setMarketFilter] = useState("all");
  var [recent, setRecent] = useState(function() {
    try { var r = localStorage.getItem("q_recent"); return r ? JSON.parse(r) : []; } catch(e) { return []; }
  });
  var [searchResults, setSearchResults] = useState([]);
  var [searchingApi, setSearchingApi] = useState(false);
  var [user, setUser] = useState(null);
  var [authLoading, setAuthLoading] = useState(true); // true until Firebase resolves any cached session — gates the initial render so logged-in users never see the login screen flash
  var [splashMinTimeDone, setSplashMinTimeDone] = useState(false); // forces the splash to hold for at least 2s even when auth resolves instantly, so it reads as a deliberate brand moment rather than a flicker
  var [showInstallGuide, setShowInstallGuide] = useState(false);
  var [notifPrefEnabled, setNotifPrefEnabled] = useState(true);
  var [showDcfMethodology, setShowDcfMethodology] = useState(false);
  var [showPushPrompt, setShowPushPrompt] = useState(false);
  var [searchLimitInfo, setSearchLimitInfo] = useState(null);
  var [thesisLimitInfo, setThesisLimitInfo] = useState(null);
  var [showLimitModal, setShowLimitModal] = useState(null); // null | "search" | "thesis"
  var [stickyToast, setStickyToast] = useState(null); // doesn't auto-dismiss — cleared explicitly when the real work finishes
  var [showEditProfile, setShowEditProfile] = useState(false);
  var [editName, setEditName] = useState("");
  var [editAvatarColor, setEditAvatarColor] = useState(null);
  var [editBusy, setEditBusy] = useState(false);
  var [ageConfirmed, setAgeConfirmed] = useState(false);
  var isNewSignupRef = useRef(false);
  var [setupStep, setSetupStep] = useState(0);
  var [setupName, setSetupName] = useState("");
  var [setupAvatarColor, setSetupAvatarColor] = useState(AC);
  var [setupTier, setSetupTier] = useState("free");
  var [avatarColor, setAvatarColor] = useState(N);
  var [installPlatform, setInstallPlatform] = useState(function() {
    try { return /iphone|ipad|ipod/i.test(navigator.userAgent) ? "ios" : "android"; } catch(e) { return "ios"; }
  });
  var [profileTab, setProfileTab] = useState("account"); // "account" | "plans"
  var [userPlan, setUserPlan] = useState({ tier: "free", source: null, expiresAt: null });
  var [hasUsedTrial, setHasUsedTrial] = useState(false);
  var [monthlyDigestOn, setMonthlyDigestOn] = useState(true);
  var [digestNoteDismissed, setDigestNoteDismissed] = useState(function() {
    try { return !!localStorage.getItem("q_digest_note_dismissed"); } catch(e) { return false; }
  });
  var [billingCycle, setBillingCycle] = useState("annual"); // "monthly" | "annual"
  var [giftEmail, setGiftEmail] = useState("");
  var [giftName, setGiftName] = useState("");
  var [giftDuration, setGiftDuration] = useState("lifetime"); // "1month" | "1year" | "lifetime"
  var [giftBusy, setGiftBusy] = useState(false);
  var [giftMsg, setGiftMsg] = useState("");
  var [showDeleteAccount, setShowDeleteAccount] = useState(false);
  var [deletePassword, setDeletePassword] = useState("");
  var [deleteBusy, setDeleteBusy] = useState(false);
  var [deleteError, setDeleteError] = useState("");
  var [aiPick, setAiPick] = useState(null);
  var [weeklyInsight, setWeeklyInsight] = useState(null);
  var [deepDiveStreak, setDeepDiveStreak] = useState(0);
  var [spotlightTab, setSpotlightTab] = useState("pick");
  var [activityTab, setActivityTab] = useState("recent");
  var [wlSummaryTab, setWlSummaryTab] = useState("movers");
  var [weeklyProposal, setWeeklyProposal] = useState(null);
  var [weeklyOverrideInput, setWeeklyOverrideInput] = useState("");
  var [shufflingWeekly, setShufflingWeekly] = useState(false);
  var [confirmPushRefresh, setConfirmPushRefresh] = useState(false);
  var [pushRefreshSending, setPushRefreshSending] = useState(false);
  var [learnQuery, setLearnQuery] = useState("");
  var [learnCat, setLearnCat] = useState("All");
  var [refreshingWL, setRefreshingWL] = useState(false);
  var [refreshingPF, setRefreshingPF] = useState(false);
  var [priceFlash, setPriceFlash] = useState(null); // "up" | "down" | null
  var [benchmarkData, setBenchmarkData] = useState(null);
  var [benchmarkName, setBenchmarkName] = useState(null);
  var [showRelStrength, setShowRelStrength] = useState(false);
  var inputRef = useRef(null);
  var searchDropRef = useRef(null);
  var cmpDropRef = useRef(null);
  var [marketData, setMarketData] = useState([
    { name: "S&P 500", price: 0, change: 0, up: true },
    { name: "FTSE 100", price: 0, change: 0, up: true },
    { name: "Nifty 50", price: 0, change: 0, up: true },
    { name: "Sensex", price: 0, change: 0, up: true },
    { name: "Nikkei", price: 0, change: 0, up: true },
    { name: "DAX", price: 0, change: 0, up: true }
  ]);

  // Load live market indices on mount
  useEffect(function() {
    getMarketIndicesFn({}).then(function(result) {
      if (result.data && result.data.length > 0) {
        setMarketData(result.data);
      }
    }).catch(function(err) {
      console.error("Market data error:", err);
    });
  }, []);

  // Load live AI Pick of the Day on mount
  useEffect(function() {
    // Calculate today's UK date (BST aware)
    var now = new Date();
    var year = now.getUTCFullYear();
    function lastSunday(month) {
      var d = new Date(Date.UTC(year, month, 31));
      d.setUTCDate(31 - d.getUTCDay());
      return d;
    }
    var bstStart = lastSunday(2);
    var bstEnd = lastSunday(9);
    var ukOffset = (now >= bstStart && now < bstEnd) ? 60 : 0;
    var ukTime = new Date(now.getTime() + ukOffset * 60000);
    var ukDateStr = ukTime.getUTCFullYear() + "-" +
      String(ukTime.getUTCMonth() + 1).padStart(2, "0") + "-" +
      String(ukTime.getUTCDate()).padStart(2, "0");

    // Check localStorage — same pick all day, no extra calls. Version bumped
    // alongside DAILY_PICK_CACHE_VERSION on the server whenever a stale pick
    // needs invalidating (e.g. the ASML.AS ticker fix) — old cached entries
    // from before the bump simply won't match and get refetched fresh.
    var DAILY_PICK_CLIENT_CACHE_VERSION = 2;
    try {
      var cached = localStorage.getItem("q_daily_pick");
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed.date === ukDateStr && parsed.version === DAILY_PICK_CLIENT_CACHE_VERSION && parsed.pickData) {
          setAiPick(parsed.pickData);
          return;
        }
      }
    } catch(e) {}

    // Call backend — it picks via Claude and caches in Firestore
    getDailyPickFn({}).then(function(result) {
      var pick = result.data;
      var pickTicker = pick.ticker;
      var pickReason = pick.reason;

      // Now fetch the full stock analysis for the chosen ticker
      return analyseStockFn({ ticker: pickTicker }).then(function(stockResult) {
        var d = stockResult.data;
        var cur = normCurrency(d.currency) || detectCurrency(d.ticker || pickTicker, d.exchange);
        var flag = d.flag || countryToFlag(d.country) || detectFlag(d.ticker || pickTicker, d.exchange);
        var pickData = {
          t: d.ticker || pickTicker,
          n: d.company || pickTicker,
          f: flag, p: d.price || 0, cur: cur,
          v: d.verdict || "FAIRLY VALUED",
          ai: d.summary || pickReason,
          reason: pickReason,
          cp: d.changePct || 0,
          live: true
        };
        setAiPick(pickData);
        try { localStorage.setItem("q_daily_pick", JSON.stringify({ date: ukDateStr, version: DAILY_PICK_CLIENT_CACHE_VERSION, pickData: pickData })); } catch(e) {}
      });
    }).catch(function(err) {
      console.error("AI Pick error:", err);
      // Graceful fallback — use deterministic rotation from pool
      var seed = parseInt(ukDateStr.replace(/-/g, ""), 10) || 0;
      var fallbackTicker = AI_PICK_POOL[seed % AI_PICK_POOL.length];
      setAiPick({ t: fallbackTicker, n: fallbackTicker, f: detectFlag(fallbackTicker, ""), p: 0, cur: "USD", v: "—", ai: "", cp: 0, live: false, error: true });
    });
  }, []);

  // ── Weekly Deep Dive — fetch the currently published insight ──
  useEffect(function() {
    getWeeklyInsightFn({}).then(function(result) {
      if (result.data) {
        setWeeklyInsight(result.data);
      }
    }).catch(function(err) { console.error("Weekly insight fetch error:", err); });
  }, []);

  // ── Weekly Deep Dive — read receipt + streak. Fires only when the user
  // actually opens the detail view (view === "weeklyInsight"), not just
  // when the tile is visible on Discover — this measures reading it, not
  // seeing it. markDeepDiveRead is idempotent for the same weekId, so this
  // is safe to fire every time the view mounts.
  useEffect(function() {
    if (view !== "weeklyInsight" || !weeklyInsight || !weeklyInsight.weekId || !user) return;
    markDeepDiveReadFn({ weekId: weeklyInsight.weekId }).then(function(r) {
      if (r.data) setDeepDiveStreak(r.data.streak || 0);
    }).catch(function(err) { console.error("markDeepDiveRead error:", err); });
  }, [view, weeklyInsight, user]);

  // ── Portfolio FX rates — fetches conversion rates for every currency
  // present in the mock investment portfolio, to the chosen home currency,
  // so the total shown is a genuine converted sum rather than raw numbers
  // added across currencies (which was silently wrong whenever a portfolio
  // held more than one currency). Only re-fetches when the actual set of
  // currencies in play changes, not on every portfolio edit.
  var portfolioCurrencyKey = Array.from(new Set(portfolio.map(function(p) { return p.cur || "GBP"; }))).sort().join(",");
  useEffect(function() {
    var currencies = portfolioCurrencyKey ? portfolioCurrencyKey.split(",") : [];
    if (currencies.length <= 1) return; // nothing to convert
    setFxLoading(true);
    getExchangeRatesFn({ currencies: currencies, base: homeCurrency }).then(function(r) {
      setFxRates(Object.assign({}, r.data || {}, { [homeCurrency]: 1 }));
      setFxLoading(false);
    }).catch(function() { setFxLoading(false); });
  }, [portfolioCurrencyKey, homeCurrency]);

  // ── Splash minimum display time — the orbital icon animation runs on a
  // slow, deliberate 12s/8s-per-revolution cycle (confirmed matching
  // Waltra's real splash technique) — at a short minimum display time,
  // the splash fades out before that motion is even visible, which reads
  // as "everything moves fast" even though the underlying animation
  // itself is slow and smooth. 2s gives it a chance to actually be seen.
  useEffect(function() {
    var t = setTimeout(function() { setSplashMinTimeDone(true); }, 3000);
    return function() { clearTimeout(t); };
  }, []);

  // ── Splash fade-out — the moment we're actually ready to leave the
  // splash, don't unmount it immediately (an abrupt cut from the dark
  // splash to a fully different-colored screen is exactly what read as
  // "two separate loading screens"). Keep it mounted for one more fade
  // duration, transitioning its opacity to 0, then swap away — same
  // technique as a proper cross-fade, without needing to restructure how
  // the rest of the view-switching in this component works.
  var [splashClosing, setSplashClosing] = useState(false);
  var [splashDone, setSplashDone] = useState(false);
  var splashClosingTriggeredRef = useRef(false);
  useEffect(function() {
    if (!authLoading && splashMinTimeDone && !splashClosingTriggeredRef.current) {
      splashClosingTriggeredRef.current = true;
      setSplashClosing(true);
      var t = setTimeout(function() { setSplashDone(true); }, 320);
      return function() { clearTimeout(t); };
    }
  }, [authLoading, splashMinTimeDone]);

  // ── Body background sync — this is THE fix for the iOS safe-area
  // white-band bug (same root cause, same fix that resolved it on Waltra).
  // WebKit extends the <html>/<body> element's OWN background-color to
  // paint the full device canvas on standalone PWAs, including the
  // home-indicator chrome region at the very bottom of the screen — a
  // child <div>, even position:fixed with a deliberate overscan past the
  // viewport edge, does NOT get this treatment; it's specific to the root
  // elements. index.html sets the same dark purple as a CSS default so
  // there's no gap during the pre-mount skeleton phase either; this effect
  // keeps it in sync with whichever screen React is actually showing.
  // useLayoutEffect (not useEffect) so this runs before the browser paints
  // the new screen, closing any timing gap between screens.
  useLayoutEffect(function() {
    document.body.style.background = !splashDone ? "#1E1240" : BG;
    document.documentElement.style.background = !splashDone ? "#1E1240" : BG;
  }, [splashDone]);

  // ── Auth listener ──
  useEffect(function() {
    var unsub = onAuthStateChanged(auth, function(u) {
      setUser(u);
      setAuthLoading(false);
      if (u) {
        loadUserData(u.uid);
        setView(function(v) {
          if (v === "setup") return v; // don't yank the user out of an active setup flow
          if (isNewSignupRef.current) { isNewSignupRef.current = false; return "setup"; }
          return (v === "welcome" || v === "onboard") ? "home" : v;
        });
      }
    });
    return function() { unsub(); };
  }, []);

  // ── Load watchlist & portfolio from Firestore ──
  async function loadUserData(uid) {
    try {
      var wlDoc = await getDoc(doc(firestore, "users", uid, "data", "watchlist"));
      var cloudWL = wlDoc.exists() ? (wlDoc.data().items || []) : null;
      if (cloudWL) setWatchlist(cloudWL);

      var pfDoc = await getDoc(doc(firestore, "users", uid, "data", "portfolio"));
      var cloudPF = pfDoc.exists() ? (pfDoc.data().items || []) : null;
      if (cloudPF) setPf(cloudPF);

      var notesDoc = await getDoc(doc(firestore, "users", uid, "data", "notes"));
      if (notesDoc.exists()) setNotes(notesDoc.data().items || {});
      var thesisDoc = await getDoc(doc(firestore, "users", uid, "data", "thesis"));
      if (thesisDoc.exists()) setThesis(thesisDoc.data().items || {});
      var alertsDoc = await getDoc(doc(firestore, "users", uid, "data", "alerts"));
      if (alertsDoc.exists()) setAlerts(alertsDoc.data().items || []);

      var profileDoc = await getDoc(doc(firestore, "users", uid, "data", "profile"));
      if (profileDoc.exists() && profileDoc.data().avatarColor) setAvatarColor(profileDoc.data().avatarColor);

      var searchLimitDoc = await getDoc(doc(firestore, "users", uid, "data", "searchLimit"));
      if (searchLimitDoc.exists()) setSearchLimitInfo(searchLimitDoc.data());

      var thesisLimitDoc = await getDoc(doc(firestore, "users", uid, "data", "thesisLimit"));
      if (thesisLimitDoc.exists()) setThesisLimitInfo(thesisLimitDoc.data());

      // Load subscription plan, and check whether a founder gift was waiting
      // for this email before they signed up (applied automatically, once).
      var planDoc = await getDoc(doc(firestore, "users", uid, "data", "plan"));
      if (planDoc.exists()) {
        var pd = planDoc.data();
        setUserPlan({ tier: pd.tier || "free", source: pd.source || null, expiresAt: pd.expiresAt || null });
      }
      var trialStatusDoc = await getDoc(doc(firestore, "users", uid, "data", "trialStatus"));
      setHasUsedTrial(trialStatusDoc.exists() && !!trialStatusDoc.data().hasUsedTrial);
      var emailPrefsDoc = await getDoc(doc(firestore, "users", uid, "data", "emailPrefs"));
      setMonthlyDigestOn(!emailPrefsDoc.exists() || emailPrefsDoc.data().monthlyDigest !== false);
      applyPendingGiftFn().then(function(r) {
        if (r.data && r.data.applied) {
          setUserPlan({ tier: r.data.tier || "pro", source: r.data.source || "founder_gift", expiresAt: r.data.expiresAt || null });
          setToast("A Zoltra Pro gift was waiting for you — applied to your account!");
        }
      }).catch(function() {});
    } catch (e) { console.error("Load error:", e); }
  }

  // ── Save watchlist to Firestore ──
  useEffect(function() {
    if (!user) return;
    var timer = setTimeout(function() {
      setDoc(doc(firestore, "users", user.uid, "data", "watchlist"), { items: watchlist }).catch(console.error);
    }, 500);
    return function() { clearTimeout(timer); };
  }, [watchlist, user]);

  // ── Save portfolio to Firestore ──
  useEffect(function() {
    if (!user) return;
    var timer = setTimeout(function() {
      setDoc(doc(firestore, "users", user.uid, "data", "portfolio"), { items: portfolio }).catch(console.error);
    }, 500);
    return function() { clearTimeout(timer); };
  }, [portfolio, user]);

  // ── Save notes to Firestore when they change ──
  useEffect(function() {
    if (!user) return;
    var timer = setTimeout(function() {
      setDoc(doc(firestore, "users", user.uid, "data", "notes"), { items: notes }).catch(console.error);
    }, 1000);
    return function() { clearTimeout(timer); };
  }, [notes, user]);

  useEffect(function() {
    if (!user) return;
    var timer = setTimeout(function() {
      setDoc(doc(firestore, "users", user.uid, "data", "thesis"), { items: thesis }).catch(console.error);
    }, 1000);
    return function() { clearTimeout(timer); };
  }, [thesis, user]);

  // ── Save alerts to Firestore when they change ──
  useEffect(function() {
    if (!user) return;
    var timer = setTimeout(function() {
      setDoc(doc(firestore, "users", user.uid, "data", "alerts"), { items: alerts }).catch(console.error);
    }, 500);
    return function() { clearTimeout(timer); };
  }, [alerts, user]);

  // ── Persist recent searches to localStorage ──
  useEffect(function() {
    try { localStorage.setItem("q_recent", JSON.stringify(recent.slice(0, 10))); } catch(e) {}
  }, [recent]);

  // ── Keep "Recently viewed" prices live ──
  // recent[] stores a price snapshot from the moment each stock was last
  // opened, and that snapshot doesn't change on its own — so returning to
  // Discover later kept showing whatever the price was back then, which
  // could quietly drift from the live price shown on the stock's own detail
  // page (the actual bug: SanDisk's carousel tile showing $1096 while its
  // detail page correctly showed $1015 for the same moment in time).
  // Refreshes via screenStocks (computeStockSnapshot) rather than
  // analyseStock deliberately — it's the same live price/quote data, but
  // free and instant, with no Claude API cost and no weekly analysis-limit
  // consumption for something that's just a price refresh, not a new
  // analysis.
  useEffect(function() {
    if (view !== "home" || recent.length === 0) return;
    var tickers = recent.map(function(r) { return r.t; });
    screenStocksFn({ tickers: tickers }).then(function(r) {
      var results = (r.data && r.data.results) || [];
      var byTicker = {};
      results.forEach(function(res) { byTicker[res.t] = res; });
      setRecent(function(prev) {
        return prev.map(function(item) {
          var fresh = byTicker[item.t];
          if (!fresh || fresh.p == null) return item;
          return Object.assign({}, item, { p: fresh.p, cp: fresh.cp != null ? fresh.cp : item.cp });
        });
      });
    }).catch(function() {}); // silent — a stale price shown briefly is fine, a broken carousel isn't
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep Watchlist and Portfolio prices live on open ──
  // Same staleness pattern as "Recently viewed" above, on the same two
  // screens that previously only refreshed when the user tapped the manual
  // "Refresh" button. Calling refreshWatchlist/refreshPortfolio here is
  // safe despite them being declared later in this file — function
  // declarations are hoisted within the component's closure, the same
  // pattern already relied on elsewhere in this file (see doSearch's
  // comment on this above).
  useEffect(function() {
    if (view === "watchlist") refreshWatchlist();
    else if (view === "portfolio") refreshPortfolio();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FCM push notifications ──
  // The actual permission request is now ONLY triggered by an explicit user
  // action (the push-opt-in prompt shown after their first watchlist add, or
  // a settings toggle later) — never fired blindly the moment someone signs
  // in, which is jarring and depresses opt-in rates.
  function requestNotificationPermission(silent) {
    if (!messagingInstance || !user) return;
    Notification.requestPermission().then(function(permission) {
      if (permission !== "granted") return;
      // VAPID key: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates → Key pair
      getToken(messagingInstance, {
        vapidKey: "BFYaIjBRuIUHcx9DNhNQPT3rgJih78wMKzaOQI_shiZ9T-FzRduhTnC2h6GAuEoibRgI3qDUb1Qg97TKVNaFZVY"
      }).then(function(token) {
        if (token) {
          var payload = { token: token };
          // Only set `enabled: true` explicitly when this came from a real
          // user action (toggle or opt-in prompt) — the silent background
          // refresh (permission already granted from a past session) omits
          // it deliberately, so it can't silently undo a previous in-app
          // off-toggle. Same reasoning as before, just now paired with also
          // updating the UI state below for the non-silent case — that part
          // was missing entirely, which is why the toggle stayed grey until
          // a full refresh re-read the real state from scratch.
          if (!silent) payload.enabled = true;
          setDoc(doc(firestore, "users", user.uid, "data", "fcmToken"), payload, { merge: true }).catch(console.error);
          if (!silent) setNotifPrefEnabled(true);
        }
      }).catch(function(e) { console.log("FCM token error:", e.message); });
    }).catch(function() {});
  }

  // "Latest ref" pattern — keeps the FCM message listener below subscribed
  // once (not re-subscribing on every keystroke, which doSearch would
  // otherwise force via its `query` dependency) while still always calling
  // the current version of each function, not a stale one from whenever the
  // listener first subscribed.
  var doSearchRef = useRef(null);
  var requestNotifRef = useRef(null);
  useEffect(function() { doSearchRef.current = doSearch; requestNotifRef.current = requestNotificationPermission; });

  // Load the user's existing in-app notification preference — separate from
  // the browser permission itself. Missing/undefined defaults to enabled,
  // matching the backend's `enabled !== false` check.
  useEffect(function() {
    if (!user) return;
    getDoc(doc(firestore, "users", user.uid, "data", "fcmToken")).then(function(snap) {
      if (snap.exists()) setNotifPrefEnabled(snap.data().enabled !== false);
    }).catch(function() {});
  }, [user]);

  function toggleNotifPref(turnOn) {
    if (!user) return;
    if (turnOn && (typeof Notification === "undefined" || Notification.permission !== "granted")) {
      // Not yet granted at the browser level — go through the full prompt,
      // which also sets up the token for the first time.
      requestNotificationPermission();
      return;
    }
    setNotifPrefEnabled(turnOn);
    setDoc(doc(firestore, "users", user.uid, "data", "fcmToken"), { enabled: turnOn }, { merge: true }).catch(console.error);
  }

  useEffect(function() {
    if (!messagingInstance || !user) return;
    // If permission was already granted in a previous session, silently
    // refresh the token — no need to ask again or show any prompt.
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      requestNotifRef.current(true);
    }

    // Handle foreground messages (app is open)
    var unsub = onMessage(messagingInstance, function(payload) {
      var n = payload.notification || {};
      var d = payload.data || {};
      if (d.type === "thesis_break" && d.ticker) {
        try { localStorage.setItem("q_thesis_link", JSON.stringify({ ticker: d.ticker, ts: Date.now() })); } catch(e) {}
        setToast("Thesis alert: " + d.ticker + " — tap to view");
        setTimeout(function() {
          setOpenSections(function(prev) { return Object.assign({}, prev, { thesis: true }); });
          doSearchRef.current(d.ticker);
        }, 800);
      } else {
        setToast((n.title || "Alert") + (n.body ? " — " + n.body : ""));
      }
    });
    return unsub;
  }, [user]);

  // ── Push opt-in prompt: shown once, after the user's first-ever watchlist
  // add (not during onboarding, when they haven't seen any value yet). ──
  useEffect(function() {
    if (!user) return;
    if (watchlist.length !== 1) return; // only fires on the transition to their first stock
    if (typeof Notification === "undefined" || Notification.permission !== "default") return; // already asked, granted, or denied
    var alreadyShown = false;
    try { alreadyShown = localStorage.getItem("q_push_prompt_shown") === "1"; } catch(e) {}
    if (alreadyShown) return;
    try { localStorage.setItem("q_push_prompt_shown", "1"); } catch(e) {}
    setShowPushPrompt(true);
  }, [watchlist, user]);



  // ── Sign in / out ──
  async function handleForgotPassword() {
    if (!emailInput || !emailInput.includes("@")) { setAuthError("Enter your email address above first."); return; }
    try {
      await sendPasswordResetEmail(auth, emailInput);
      setToast("Password reset email sent — check your inbox.");
      setAuthError("");
    } catch (e) {
      if (e.code === "auth/user-not-found") setAuthError("No account found with that email.");
      else setAuthError("Could not send reset email. Try again.");
    }
  }

  async function handleEmailAuth() {
    setAuthError("");
    if (!emailInput || !emailInput.includes("@")) { setAuthError("Please enter a valid email address."); return; }
    if (!passwordInput || passwordInput.length < 6) { setAuthError("Password must be at least 6 characters."); return; }
    if (authMode === "signup" && !ageConfirmed) { setAuthError("Please confirm you are 18 or older to continue."); return; }
    try {
      if (authMode === "signup") {
        await createUserWithEmailAndPassword(auth, emailInput, passwordInput);
        isNewSignupRef.current = true;
      } else {
        await signInWithEmailAndPassword(auth, emailInput, passwordInput);
      }
      setEmailInput("");
      setPasswordInput("");
      setAuthError("");
    } catch (e) {
      var msg = "Something went wrong. Please try again.";
      if (e.code === "auth/user-not-found" || e.code === "auth/wrong-password" || e.code === "auth/invalid-credential") msg = "Incorrect email or password.";
      else if (e.code === "auth/email-already-in-use") msg = "An account with this email already exists. Sign in instead.";
      else if (e.code === "auth/too-many-requests") msg = "Too many attempts. Please try again later.";
      else if (e.code === "auth/invalid-email") msg = "Please enter a valid email address.";
      setAuthError(msg);
    }
  }
  function haptic(pattern) { try { navigator.vibrate && navigator.vibrate(pattern || 10); } catch(e) {} }

  async function handleSignOut() {
    await signOut(auth);
    setUser(null);
    setWatchlist([]);
    setPf([]);
    setNotes({});
    setThesis({});
    setThesisChallenge({});
    setAlerts([]);
    setSearchLimitInfo(null);
    setThesisLimitInfo(null);
    setView("welcome");
    setToast("Signed out");
  }

  async function saveProfileEdit() {
    if (!user) return;
    var finalName = editName.trim();
    if (!finalName) { setToast("Enter a name."); return; }
    setEditBusy(true);
    try {
      await updateProfile(auth.currentUser, { displayName: finalName });
      setUser(Object.assign({}, auth.currentUser));
      await setDoc(doc(firestore, "users", user.uid, "data", "profile"), { avatarColor: editAvatarColor, displayName: finalName }, { merge: true });
      setAvatarColor(editAvatarColor);
      setEditBusy(false);
      setShowEditProfile(false);
      setToast("Profile updated");
    } catch (e) {
      console.error("Profile update error:", e.code || e.message || e);
      setEditBusy(false);
      setToast("Could not update profile — please try again.");
    }
  }

  async function handleDeleteAccount() {
    if (!user) return;
    if (!deletePassword.trim()) { setDeleteError("Enter your password to confirm."); return; }
    setDeleteBusy(true);
    setDeleteError("");
    try {
      // Firebase requires a recent sign-in before allowing account deletion —
      // re-authenticate with their password first.
      var credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      // Delete all of their app data before deleting the account itself —
      // once the auth account is gone they'd no longer have permission to
      // delete their own Firestore documents (per firestore.rules).
      var uid = user.uid;
      var docNames = ["watchlist", "portfolio", "notes", "thesis", "alerts", "fcmToken", "plan", "profile", "deepDiveStreak", "trialStatus", "emailPrefs", "digestSnapshot"];
      await Promise.all(docNames.map(function(name) {
        return deleteDoc(doc(firestore, "users", uid, "data", name)).catch(function() {});
      }));

      await deleteUser(user);

      setUser(null);
      setWatchlist([]);
      setPf([]);
      setNotes({});
      setThesis({});
      setThesisChallenge({});
      setAlerts([]);
      setUserPlan({ tier: "free", source: null, expiresAt: null });

      setShowDeleteAccount(false);
      setDeletePassword("");
      setDeleteBusy(false);
      setView("welcome");
      setToast("Your account has been deleted.");
    } catch (e) {
      setDeleteBusy(false);
      if (e.code === "auth/wrong-password" || e.code === "auth/invalid-credential") {
        setDeleteError("Incorrect password.");
      } else if (e.code === "auth/too-many-requests") {
        setDeleteError("Too many attempts. Please try again later.");
      } else {
        setDeleteError("Something went wrong. Please try again.");
      }
    }
  }

  useEffect(function() { if (view === "home" && inputRef.current) inputRef.current.blur(); }, [view]);

  // Load note text when stock changes. Deliberately excludes `notes` from
  // deps — including it would re-fire and overwrite whatever the user is
  // actively typing whenever `notes` updates elsewhere in the app.
  useEffect(function() {
    if (stock && stock.t) setNoteText(notes[stock.t] || "");
    setAboutExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock]);

  // Check price alerts whenever stock loads. Deliberately excludes `alerts`
  // and `user` — this should fire on new stock data, not whenever alerts
  // changes (which includes this effect's own setAlerts call above).
  useEffect(function() {
    if (!stock || !stock.p || stock.p === 0) return;
    alerts.forEach(function(alert) {
      if (alert.t !== stock.t || alert.triggered) return;
      var crossed = (alert.dir === "above" && stock.p >= alert.price) ||
                    (alert.dir === "below" && stock.p <= alert.price);
      if (crossed) {
        var alertMsg = alert.t + " has " + (alert.dir === "above" ? "risen above" : "fallen below") + " " + (CS$[stock.cur] || "$") + alert.price;
        setToast(alertMsg);
        setAlerts(function(prev) { return prev.map(function(a) { return a === alert ? Object.assign({}, a, { triggered: true }) : a; }); });
        try { navigator.vibrate && navigator.vibrate([100, 50, 100]); } catch(e) {}
        if (user) sendAlertFn({ ticker: alert.t, message: alertMsg, uid: user.uid }).catch(function() {});
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock]);

  // Restore state after returning from WhatsApp (iOS PWA reloads on window.open)
  useEffect(function() {
    try {
      var saved = localStorage.getItem("q_wa_return");
      if (saved) {
        var parsed = JSON.parse(saved);
        // Only restore if saved within the last 5 minutes
        if (parsed.ts && Date.now() - parsed.ts < 300000 && parsed.stock) {
          localStorage.removeItem("q_wa_return");
          setStock(parsed.stock);
          if (parsed.hist) setHist(parsed.hist.map(function(h) { return { date: new Date(h.date), price: h.price }; }));
          setView("result");
        } else {
          localStorage.removeItem("q_wa_return");
        }
      }
      // Handle thesis-break notification deep link
      var thesisLink = localStorage.getItem("q_thesis_link");
      if (thesisLink) {
        localStorage.removeItem("q_thesis_link");
        var tl = JSON.parse(thesisLink);
        if (tl.ticker && Date.now() - tl.ts < 300000) {
          setOpenSections(function(prev) { return Object.assign({}, prev, { thesis: true }); });
          setTimeout(function() { doSearchRef.current(tl.ticker); }, 500);
        }
      }
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(function() { if (!toast) return; var t = setTimeout(function() { setToast(""); }, Math.max(2500, toast.length * 60)); return function() { clearTimeout(t); }; }, [toast]);

  // Auto-update, layer 1 of 2: polls a small version marker (regenerated
  // fresh on every deploy) and silently reloads the moment a new build is
  // detected. This alone isn't fully reliable on iOS home-screen PWAs, since
  // it depends on this page's own JS still being alive to run the poll — see
  // the service worker registered below for the second, more robust layer.
  useEffect(function() {
    var initialVersion = null;
    var reloading = false;

    // Guards against racing with the WhatsApp-return restore logic above —
    // both listen for the same visibilitychange/pageshow events, and if the
    // update-checker fires its own reload at the same moment iOS is already
    // reloading the PWA after window.open, the two can collide and leave
    // the app stuck on a blank screen requiring a manual force-close.
    function recentlyReturnedFromWhatsApp() {
      try {
        var saved = localStorage.getItem("q_wa_return");
        if (!saved) return false;
        var parsed = JSON.parse(saved);
        return parsed.ts && (Date.now() - parsed.ts) < 4000;
      } catch (e) { return false; }
    }

    function checkVersion() {
      if (reloading || recentlyReturnedFromWhatsApp()) return;
      fetch("/version.txt?_=" + Date.now(), { cache: "no-store" })
        .then(function(r) { return r.ok ? r.text() : null; })
        .then(function(v) {
          if (!v) return;
          v = v.trim();
          if (initialVersion === null) { initialVersion = v; return; }
          if (v !== initialVersion && !reloading && !recentlyReturnedFromWhatsApp()) {
            reloading = true;
            setToast("Updating Zoltra to the latest version…");
            setTimeout(function() { window.location.reload(); }, 1200);
          }
        })
        .catch(function() {});
    }

    checkVersion(); // establish baseline as soon as the app loads
    var interval = setInterval(checkVersion, 60 * 1000); // re-check every 60 seconds
    function onVisible() { if (document.visibilityState === "visible") checkVersion(); }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", checkVersion);
    // iOS home-screen PWAs often resume from a frozen snapshot rather than a
    // real reload — `pageshow` with `persisted: true` is the standard signal
    // for exactly that case, and can fire in situations visibilitychange
    // sometimes doesn't on iOS specifically.
    function onPageShow(e) { if (e.persisted) checkVersion(); }
    window.addEventListener("pageshow", onPageShow);

    return function() {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", checkVersion);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // Auto-update, layer 2 of 2: a service worker. This is the more robust
  // layer — it runs in its own thread, independent of whether this page's
  // own JS is currently frozen (which is exactly what iOS does to installed
  // home-screen PWAs when backgrounded, and is the actual root cause behind
  // "I deleted and reinstalled the app and now I see the update"). The SW's
  // script content is rewritten on every build (see package.json's prebuild
  // script), so the browser detects it as changed, installs it, activates
  // immediately, and messages this page to reload — all without needing
  // this page to have been alive and polling the whole time.
  useEffect(function() {
    if (!("serviceWorker" in navigator)) return;
    var reg = null;
    navigator.serviceWorker.register("/sw.js").then(function(r) { reg = r; }).catch(function() {});
    function recentlyReturnedFromWhatsApp() {
      try {
        var saved = localStorage.getItem("q_wa_return");
        if (!saved) return false;
        var parsed = JSON.parse(saved);
        return parsed.ts && (Date.now() - parsed.ts) < 4000;
      } catch (e) { return false; }
    }
    function onMessage(event) {
      if (event.data && event.data.type === "SW_UPDATED" && !recentlyReturnedFromWhatsApp()) {
        setToast("Updating Zoltra to the latest version…");
        setTimeout(function() { window.location.reload(); }, 1200);
      }
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    // Browsers rate-limit their own automatic SW update checks to roughly
    // once per 24h, on navigation, regardless of cache headers — fine for a
    // freshly-opened tab, not fine for an app that stays open. Force an
    // explicit check on the same cadence as the version.txt poll, plus
    // whenever the app becomes visible again.
    function forceUpdateCheck() { if (reg && !recentlyReturnedFromWhatsApp()) reg.update().catch(function() {}); }
    var swInterval = setInterval(forceUpdateCheck, 60 * 1000);
    function onVisible() { if (document.visibilityState === "visible") forceUpdateCheck(); }
    document.addEventListener("visibilitychange", onVisible);
    return function() {
      navigator.serviceWorker.removeEventListener("message", onMessage);
      clearInterval(swInterval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Two-layer search: instant local results + API results after debounce
  useEffect(function() {
    if (!query.trim() || query.length < 1) { setSearchResults([]); setSearchingApi(false); return; }
    if (loading) { setSearchResults([]); setSearchingApi(false); return; }

    var q = query.trim().toLowerCase();

    // Layer 1 — instant local results, ranked: ticker prefix > name prefix > contains
    var tier1 = [], tier2 = [], tier3 = [], tier4 = [];
    LOCAL_SEARCH_INDEX.forEach(function(s) {
      var tl = s.t.toLowerCase(), nl = s.n.toLowerCase();
      if (tl.startsWith(q) || tl.replace(/\.[a-z]+$/, "").startsWith(q)) tier1.push(s);
      else if (nl.startsWith(q)) tier2.push(s);
      else if (tl.includes(q) || nl.includes(q)) tier3.push(s);
    });
    // Tier 4 — typo tolerance, only tried when nothing else matched at all,
    // and only for ticker-like queries (short, no spaces) since that's what
    // this is for: catching things like "APPL" -> AAPL, "GOOGL" -> GOOG.
    if (tier1.length === 0 && tier2.length === 0 && tier3.length === 0 && q.length >= 3 && q.length <= 6 && !/\s/.test(q)) {
      var maxDist = q.length <= 4 ? 1 : 2;
      LOCAL_SEARCH_INDEX.forEach(function(s) {
        var bareTicker = s.t.toLowerCase().replace(/\.[a-z]+$/, "");
        if (levenshtein(q, bareTicker) <= maxDist) tier4.push(s);
      });
    }
    var localMatches = tier1.concat(tier2).concat(tier3).concat(tier4).slice(0, 6);
    // Always set — even when empty. Previously this only updated the dropdown
    // when localMatches had something, so for an obscure/small-cap name (never
    // in the curated local index) a stale result list from a prior keystroke
    // sat on screen through the whole debounce + API round trip, making the
    // search look frozen/unresponsive. Clearing it here lets the empty state
    // below correctly show the "Searching…" indicator while Layer 2 runs.
    setSearchResults(localMatches);

    // Layer 2 — API results after 200ms debounce
    if (query.trim().length < 2) { setSearchingApi(false); return; }
    var cancelled = false;
    setSearchingApi(true);
    var timer = setTimeout(function() {
      searchStocksFn({ query: query.trim() }).then(function(result) {
        if (cancelled) return;
        setSearchingApi(false);
        if (result.data && result.data.length > 0) {
          var apiResults = result.data.map(function(r) {
            var cur = r.currency || detectCurrency(r.ticker, r.exchange);
            var flag = detectFlag(r.ticker, r.exchange);
            return { t: r.ticker, n: r.name, f: flag, p: 0, cur: cur };
          });
          // Merge: keep local results that aren't in API, add API results
          var seen = {};
          var merged = [];
          apiResults.forEach(function(r) { seen[r.t] = true; merged.push(r); });
          localMatches.forEach(function(r) { if (!seen[r.t]) merged.push(r); });
          setSearchResults(merged.slice(0, 6));
        } else if (localMatches.length === 0) {
          setSearchResults([]); // genuinely no matches anywhere — lets the empty state show
        }
      }).catch(function() { if (!cancelled) setSearchingApi(false); }); // keep local results on API failure
    }, 200);

    return function() { cancelled = true; clearTimeout(timer); };
  }, [query, loading, view]);

  // Close autocomplete when clicking outside
  useEffect(function() {
    function handleClick(e) {
      if (searchDropRef.current && !searchDropRef.current.contains(e.target)) {
        setSearchResults([]);
      }
      if (cmpDropRef.current && !cmpDropRef.current.contains(e.target)) {
        setCmpSearchResults([]);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return function() { document.removeEventListener("mousedown", handleClick); };
  }, []);

  // Add earnings to calendar (.ics download)
  function addToCalendar(ticker, name, ts) {
    var dt = new Date(ts);
    var pad = function(n) { return String(n).padStart(2, "0"); };
    var dateStr = dt.getFullYear() + pad(dt.getMonth() + 1) + pad(dt.getDate()) + "T090000";
    var ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Zoltra//EN", "BEGIN:VEVENT",
      "DTSTART:" + dateStr, "DTEND:" + dateStr,
      "SUMMARY:" + ticker + " Earnings Report",
      "DESCRIPTION:" + name + " quarterly earnings release.",
      "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    var blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url; a.download = ticker + "-earnings.ics"; a.click();
    URL.revokeObjectURL(url);
  }

  // Refresh all watchlist prices
  // Refresh all watchlist prices — uses screenStocks (computeStockSnapshot),
  // not analyseStock: this is a price refresh, not a new analysis, so it
  // shouldn't cost real Claude API money or eat into the user's weekly
  // analysis limit just to update numbers already being displayed. Note:
  // earnings data isn't refreshed here (screenStocks doesn't return it) —
  // entries keep whatever earnings info they had from when last analysed.
  function refreshWatchlist() {
    if (refreshingWL || watchlist.length === 0) return;
    setRefreshingWL(true);
    screenStocksFn({ tickers: watchlist.map(function(w) { return w.t; }) }).then(function(r) {
      var results = (r.data && r.data.results) || [];
      var byTicker = {};
      results.forEach(function(res) { byTicker[res.t] = res; });
      var updated = watchlist.map(function(w) {
        var fresh = byTicker[w.t];
        if (!fresh || fresh.p == null) return w;
        return Object.assign({}, w, { p: fresh.p, cp: fresh.cp != null ? fresh.cp : w.cp });
      });
      setWatchlist(updated);
      setRefreshingWL(false);
      setToast("Watchlist updated");
    }).catch(function() { setRefreshingWL(false); });
  }

  // Refresh all portfolio prices — same reasoning as refreshWatchlist above:
  // screenStocks instead of analyseStock, and the SPY benchmark is now
  // folded into the same single batched call instead of a separate request.
  function refreshPortfolio() {
    if (refreshingPF || portfolio.length === 0) return;
    setRefreshingPF(true);
    var tickers = portfolio.map(function(p) { return p.t; }).concat(["SPY"]);
    screenStocksFn({ tickers: tickers }).then(function(r) {
      var results = (r.data && r.data.results) || [];
      var byTicker = {};
      results.forEach(function(res) { byTicker[res.t] = res; });
      var updated = portfolio.map(function(p) {
        var fresh = byTicker[p.t];
        if (!fresh || fresh.p == null) return p;
        return Object.assign({}, p, { cp: fresh.p });
      });
      var spyPrice = byTicker.SPY ? byTicker.SPY.p : null;
      setPf(updated);
      setRefreshingPF(false);
      setToast("Portfolio updated");
      // Save SPY baseline on first snapshot
      if (spyPrice) {
        setSpyBaseline(function(prev) {
          if (!prev) {
            var baseline = { price: spyPrice, ts: Date.now() };
            try { localStorage.setItem("q_spy_baseline", JSON.stringify(baseline)); } catch(e) {}
            return baseline;
          }
          return prev;
        });
      }
      // Save portfolio snapshot for performance chart — converted to a
      // single currency the same way the total card is, so history stays
      // internally consistent even if the currency mix changes over time.
      var totalInv = 0, totalCurr = 0;
      var pfCurrencies = [...new Set(updated.map(function(p) { return p.cur || "GBP"; }))];
      var pfMixed = pfCurrencies.length > 1;
      updated.forEach(function(p) {
        var rate = pfMixed ? (fxRates[p.cur || "GBP"] != null ? fxRates[p.cur || "GBP"] : 1) : 1;
        totalInv += (p.inv || 0) * rate;
        totalCurr += (p.sh * p.cp) * rate;
      });
      var snap = { ts: Date.now(), val: totalCurr, inv: totalInv, spy: spyPrice };
      setPfHistory(function(prev) {
        var next = prev.concat([snap]).slice(-90);
        try { localStorage.setItem("q_pf_history", JSON.stringify(next)); } catch(e) {}
        return next;
      });
    }).catch(function() { setRefreshingPF(false); });
  }

  // Re-apply filters reactively whenever raw results or filters change
  useEffect(function() {
    if (screenerRaw.length === 0) return;
    var filtered = screenerRaw.slice();
    if (screenerFilters.valuation) filtered = filtered.filter(function(s) { return s.verdict === screenerFilters.valuation; });
    if (screenerFilters.quality) filtered = filtered.filter(function(s) { return s.qualityScore != null && s.qualityScore >= 75; });
    if (screenerFilters.bullish) filtered = filtered.filter(function(s) { return s.momentum === "bullish"; });
    if (screenerFilters.safe) filtered = filtered.filter(function(s) { return s.pillars && s.pillars.safetyRaw && s.pillars.safetyRaw.zone === "safe"; });
    if (screenerFilters.positiveTarget) filtered = filtered.filter(function(s) { return s.oneYearUpside != null && s.oneYearUpside > 0; });
    if (screenerFilters.sector) filtered = filtered.filter(function(s) { return s.sector === screenerFilters.sector; });
    if (screenerSort) {
      filtered.sort(function(a, b) {
        var av = screenerSort === "value" ? (a.pillars && a.pillars.value) : screenerSort === "quality" ? a.qualityScore : screenerSort === "safety" ? (a.pillars && a.pillars.safety) : screenerSort === "target" ? a.oneYearUpside : a.cp;
        var bv = screenerSort === "value" ? (b.pillars && b.pillars.value) : screenerSort === "quality" ? b.qualityScore : screenerSort === "safety" ? (b.pillars && b.pillars.safety) : screenerSort === "target" ? b.oneYearUpside : b.cp;
        if (av == null && bv == null) return 0;
        if (av == null) return 1; // missing data sinks to the bottom, never sorts unpredictably
        if (bv == null) return -1;
        return bv - av; // descending — highest score / biggest gainer first
      });
    }
    setScreenerResults(filtered);
  }, [screenerRaw, screenerFilters, screenerSort]);

  // Compare tab search — two-layer: instant local + API
  useEffect(function() {
    if (!cmpQuery.trim() || cmpQuery.length < 1) { setCmpSearchResults([]); return; }
    var q = cmpQuery.trim().toLowerCase();
    // Layer 1 — instant local, ranked
    var tier1 = [], tier2 = [], tier3 = [];
    LOCAL_SEARCH_INDEX.forEach(function(s) {
      var tl = s.t.toLowerCase(), nl = s.n.toLowerCase();
      if (tl.startsWith(q) || tl.replace(/\.[a-z]+$/, "").startsWith(q)) tier1.push(s);
      else if (nl.startsWith(q)) tier2.push(s);
      else if (tl.includes(q) || nl.includes(q)) tier3.push(s);
    });
    var localMatches = tier1.concat(tier2).concat(tier3).slice(0, 5);
    if (localMatches.length > 0) setCmpSearchResults(localMatches.map(function(s) { return { t: s.t, n: s.n, f: s.f }; }));
    // Layer 2 — API after 200ms
    if (cmpQuery.trim().length < 2) return;
    var cancelled = false;
    var timer = setTimeout(function() {
      searchStocksFn({ query: cmpQuery.trim() }).then(function(result) {
        if (cancelled) return;
        if (result.data && result.data.length > 0) {
          var seen = {};
          var merged = [];
          result.data.forEach(function(r) { seen[r.ticker] = true; merged.push({ t: r.ticker, n: r.name, f: detectFlag(r.ticker, r.exchange) }); });
          localMatches.forEach(function(s) { if (!seen[s.t]) merged.push({ t: s.t, n: s.n, f: s.f }); });
          setCmpSearchResults(merged.slice(0, 5));
        }
      }).catch(function() {});
    }, 200);
    return function() { cancelled = true; clearTimeout(timer); };
  }, [cmpQuery]);

  // Fetch stock data when added to compare list
  function addToCompare(ticker) {
    var compareLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? 6 : 3;
    if (cmpList.includes(ticker)) return;
    if (cmpList.length >= compareLimit) {
      setToast(effectivePlan === "free" ? "Standard is capped at 3 stocks to compare — upgrade to Pro for 6." : "You've reached the compare limit.");
      return;
    }
    setCmp(function(c) { return c.concat([ticker]); });
    setCmpQuery("");
    setCmpSearchResults([]);
    // Fetch data if not already loaded
    if (!cmpStocks[ticker]) {
      analyseStockFn({ ticker: ticker }).then(function(result) {
        var d = result.data;
        setCmpStocks(function(prev) {
          var next = Object.assign({}, prev);
          next[d.ticker || ticker] = {
            t: d.ticker || ticker, n: d.company || ticker,
            f: d.flag || detectFlag(ticker, ""),
            p: d.price || 0, cur: d.currency || "USD",
            cp: d.changePct || 0,
            v: d.verdict || "—", sc: d.score || 50, pillars: d.pillars || null,
            m: { pe: d.pe, fpe: d.forwardPE, peg: d.peg, mc: d.marketCap, bt: d.beta, dy: d.dividendYield, hi: d.high52w, lo: d.low52w, roe: d.roe, roa: d.roa, rg: d.revenueGrowth, eg: d.epsGrowth, gm: d.grossMargin, om: d.operatingMargin, de: d.debtToEquity, pb: d.pb, ps: d.ps, at: d.analystTarget }
          };
          return next;
        });
      }).catch(function() {});
    }
  }

  // Detect currency and flag from ticker suffix or exchange
  function countryToFlag(country) {
    if (!country) return null;
    var c = country.toUpperCase();
    var map = { "US": "🇺🇸", "USA": "🇺🇸", "UNITED STATES": "🇺🇸", "IN": "🇮🇳", "INDIA": "🇮🇳", "GB": "🇬🇧", "UK": "🇬🇧", "UNITED KINGDOM": "🇬🇧", "JP": "🇯🇵", "JAPAN": "🇯🇵", "DE": "🇩🇪", "GERMANY": "🇩🇪", "NL": "🇳🇱", "NETHERLANDS": "🇳🇱", "FR": "🇫🇷", "FRANCE": "🇫🇷", "CN": "🇨🇳", "CHINA": "🇨🇳", "HK": "🇭🇰", "HONG KONG": "🇭🇰", "CA": "🇨🇦", "CANADA": "🇨🇦", "AU": "🇦🇺", "AUSTRALIA": "🇦🇺", "KR": "🇰🇷", "SOUTH KOREA": "🇰🇷", "TW": "🇹🇼", "TAIWAN": "🇹🇼", "BR": "🇧🇷", "BRAZIL": "🇧🇷", "CH": "🇨🇭", "SWITZERLAND": "🇨🇭", "SE": "🇸🇪", "SWEDEN": "🇸🇪", "IE": "🇮🇪", "IRELAND": "🇮🇪", "IL": "🇮🇱", "ISRAEL": "🇮🇱", "SG": "🇸🇬", "SINGAPORE": "🇸🇬" };
    return map[c] || null;
  }
  function countryToCurrency(country) {
    if (!country) return null;
    var c = country.toUpperCase();
    var map = { "US": "USD", "USA": "USD", "UNITED STATES": "USD", "IN": "INR", "INDIA": "INR", "GB": "GBP", "UK": "GBP", "UNITED KINGDOM": "GBP", "JP": "JPY", "JAPAN": "JPY", "DE": "EUR", "GERMANY": "EUR", "NL": "EUR", "NETHERLANDS": "EUR", "FR": "EUR", "FRANCE": "EUR", "HK": "HKD", "HONG KONG": "HKD", "CA": "CAD", "CANADA": "CAD", "AU": "AUD", "AUSTRALIA": "AUD" };
    return map[c] || null;
  }

  // Moved here (was previously declared after doSearch) so doSearch can list
  // these in its dependency array without triggering no-use-before-define —
  // this was always safe at runtime (JS closures + hoisting), but ESLint
  // doesn't know that, so fixing the actual declaration order is cleaner
  // than suppressing the warning.
  var isFounder = !!(user && user.email && user.email.toLowerCase() === FOUNDER_EMAIL.toLowerCase());

  useEffect(function() {
    if (!isFounder) return;
    getWeeklyProposalFn({}).then(function(r) { setWeeklyProposal(r.data); }).catch(function() {});
  }, [isFounder]);
  var planExpired = !!(userPlan.expiresAt && Date.now() > userPlan.expiresAt);
  var effectivePlan = isFounder ? "founder" : (userPlan.tier === "pro" && !planExpired ? "pro" : "free");

  var doSearch = useCallback(function(q2, knownName, pinnedData) {
    var sq = q2 || query;
    if (!sq.trim()) return;

    setPrevView(view);
    setSearchResults([]);
    setQuery(sq);
    setLoadingName(knownName || null);
    setLoading(true);
    setLoadingChartData(null);
    setLoadingTicker(null);
    var searchStartTime = Date.now();
    setLoadStep("Fetching price data…");
    var stepTimer1 = setTimeout(function() { setLoadStep("Checking valuation model…"); }, 1800);
    var stepTimer2 = setTimeout(function() { setLoadStep("Comparing to sector peers…"); }, 3600);
    var stepTimer3 = setTimeout(function() { setLoadStep("Running AI analysis…"); }, 5500);
    var stepTimer4 = setTimeout(function() { setLoadStep("Almost there…"); }, 9500);

    var ticker = lookup(sq);

    // Pick country-aware benchmark using ticker suffix (country unknown before fetch)
    function getBenchmark(ticker) {
      var t = ticker.toUpperCase();
      if (t.endsWith(".NS") || t.endsWith(".BO")) return "^NSEI";
      if (t.endsWith(".L")) return "^FTSE";
      if (t.endsWith(".T")) return "^N225";
      if (t.endsWith(".PA") || t.endsWith(".DE") || t.endsWith(".AS") || t.endsWith(".SW")) return "^GDAXI";
      return "^GSPC";
    }

    setShowRelStrength(false);
    setBenchmarkData(null);
    setOpenSections({ ai: true, earnings: true, value: true, sector: false, dcf: false, quality: false, momentum: false, valhistory: false, earnquality: false, shortint: false, vh_tip: false, eq_tip: false, si_tip: false, mos_tip: false, qs_tip: false, pillar_value_tip: false, pillar_quality_tip: false, pillar_z_tip: false, thesis: false, notes: false, alert: false, bullbear: false, catalysts: false, news: false, dcf_country_tip: false });
    setOpenGroups({ valuation: false, financials: false, risk: false, ai: false });

    // If lookup() couldn't resolve the query against our small curated NAMES map,
    // it falls back to returning the raw uppercased text (e.g. "MICRON", "OCCIDENTAL PETROLEUM").
    // That is not a real ticker, so analyseStockFn would fail for any company name we
    // haven't hand-mapped. Resolve unmapped queries via the live search API first —
    // this is the same Yahoo-backed lookup that powers the autocomplete dropdown —
    // and only fall back to the raw text if the API finds nothing.
    //
    // Only do this for genuine free text, though — every click-through entry
    // point in the app (watchlist, search results, similar companies, etc.)
    // already passes a real, known ticker directly as sq. lookup() returns an
    // already-valid ticker unchanged too (it has no NAME mapping to find for
    // something that's already a ticker), which used to look identical to an
    // unresolved query and triggered this same network round-trip on nearly
    // every click — adding a real, avoidable delay before the ticker (and so
    // the loading screen's logo/flag) became known.
    var rawUpper = sq.trim().toUpperCase();
    var looksLikeTicker = /^[A-Z0-9]{1,6}(\.[A-Z]{1,3})?$/.test(rawUpper);
    var resolveTicker = (!looksLikeTicker && ticker === rawUpper)
      ? searchStocksFn({ query: sq.trim() }).then(function(r) {
          var top = r.data && r.data[0];
          return top ? top.ticker : ticker;
        }).catch(function() { return ticker; })
      : Promise.resolve(ticker);

    // Fetch live analysis and historical chart data in parallel
    resolveTicker.then(function(resolved) {
      ticker = resolved;
      setLoadingTicker(resolved);
      var histPromise = getStockHistoryFn({ ticker: ticker, benchmark: getBenchmark(ticker) }).catch(function() { return { data: { data: [], benchmark: null } }; });

      // Loading screen's chart uses its own separate, lightweight fetch
      // (last ~30 days, no benchmark) rather than piggybacking on the full
      // 5-year+benchmark call above — that call has to fetch two entire
      // 5-year daily histories before either use sees any data, which is
      // what made the loading screen's chart only appear right at the end
      // of the sequence instead of within the first second or two.
      getStockHistoryFn({ ticker: ticker, quickPreview: true }).then(function(qp) {
        var hd = (qp.data && qp.data.data) || [];
        if (hd.length >= 2) {
          var recent = hd.slice(-10);
          var last = recent[recent.length - 1];
          var prev = recent[recent.length - 2];
          setLoadingChartData({
            points: recent,
            price: last.p,
            changePct: prev.p ? Math.round(((last.p - prev.p) / prev.p) * 1000) / 10 : 0,
          });
        }
      }).catch(function() {});

      return Promise.all([
        analyseStockFn({ ticker: ticker }),
        histPromise
      ]);
    }).then(function(results) {
      var d = results[0].data;
      var histResult = results[1].data || {};
      // histResult = { data: [...], benchmark: [...], benchmarkTicker: "..." }
      var rawHist = histResult.data || (Array.isArray(histResult) ? histResult : []);
      var rawBench = histResult.benchmark || null;

      var cur = normCurrency(d.currency) || countryToCurrency(d.country) || detectCurrency(d.ticker || ticker, d.exchange);
      var flag = d.flag || countryToFlag(d.country) || detectFlag(d.ticker || ticker, d.exchange);
      function safeMetric(v) { return (!v || v === "0.0" || v === "N/A") ? "N/A" : v; }
      setStock({
        t: d.ticker || ticker,
        n: d.company || ticker,
        s: d.sector || "Unknown",
        x: d.exchange || "Unknown",
        c: d.country || "—",
        f: flag,
        p: (pinnedData && pinnedData.price != null) ? pinnedData.price : (d.price || 0),
        cur: cur,
        ch: d.change || 0,
        cp: (pinnedData && pinnedData.changePct != null) ? pinnedData.changePct : (d.changePct || 0),
        m: {
          pe: d.pe, fpe: d.forwardPE, peg: d.peg, mc: d.marketCap,
          bt: d.beta, dy: d.dividendYield, hi: d.high52w, lo: d.low52w,
          at: d.analystTarget || "N/A", roe: d.roe, roa: d.roa,
          pb: safeMetric(d.pb), ps: safeMetric(d.ps),
          rg: d.revenueGrowth || "N/A", eg: d.epsGrowth || "N/A",
          fcf: d.fcf || "N/A", gm: d.grossMargin || "N/A",
          om: d.operatingMargin || "N/A", nm: d.netProfitMargin || "N/A",
          de: d.debtToEquity || "N/A", evEbitda: d.evEbitda || "N/A"
        },
        pillars: d.pillars || null,
        v: (pinnedData && pinnedData.verdict) ? pinnedData.verdict : (d.verdict || "FAIRLY VALUED"),
        cn: d.confidence || "LOW",
        cr: d.confidence_reason || "",
        sc: (pinnedData && pinnedData.score != null) ? pinnedData.score : (d.score || 50),
        ai: d.summary || null,
        bl: d.bull_case || null,
        br: d.bear_case || null,
        ct: d.catalysts || [],
        er: d.earnings || [],
        sb: d.score_breakdown || [],
        at: d.auto_thesis || null,
        aiOff: d.ai_unavailable || false,
        aiOffReason: d.ai_unavailable_reason || null,
        sc2: d.sectorComparison || null, similar: d.similarCompanies || [],
        dcf: d.dcf || null,
        justifiedPB: d.justifiedPB || null,
        qs: d.qualityScore || null,
        mom: d.momentum || null,
        vh: d.valuationHistory || null,
        eq: d.earningsQuality || null,
        si: d.shortInterest || null,
        ac: d.analystConsensus || null,
        ins: d.insider || null,
        sc_chg: d.sharesChange || null,
        industry: d.industry || null,
        about: d.businessSummary || null,
        employees: d.employees || null,
        website: d.website || null,
        fetchedAt: d.fetchedAt || Date.now()
      });
      setBenchmarkData(null);
      if (rawHist.length > 10) {
        setHist(rawHist.map(function(h) { return { date: new Date(h.d), price: h.p, norm: h.n }; }));
        if (rawBench && rawBench.length > 10) {
          setBenchmarkData(rawBench);
          var bNames = { "^GSPC": "S&P 500", "^FTSE": "FTSE 100", "^NSEI": "Nifty 50", "^N225": "Nikkei 225", "^GDAXI": "DAX" };
          setBenchmarkName(bNames[histResult.benchmarkTicker] || histResult.benchmarkTicker || "Index");
        }
      } else {
        setHist([]);
      }
      setRecent(function(prev) {
        var filtered = prev.filter(function(r) { return r.t !== (d.ticker || ticker); });
        return [{ t: d.ticker || ticker, n: d.company || ticker, f: flag, p: d.price || 0, cur: cur, cp: d.changePct || 0 }].concat(filtered).slice(0, 5);
      });
      setStockNews([]);
      getStockNewsFn({ ticker: ticker }).then(function(r) {
        if (r.data && r.data.news) setStockNews(r.data.news);
      }).catch(function() {});

      // Loading screen always plays the complete 5-step sequence through to
      // the end, regardless of how fast the real data actually arrives —
      // deliberately, not just as a smoothing touch. A cache hit resolving
      // early and cutting the sequence off partway through (jumping straight
      // to the result after only 2-3 of 5 steps) read as broken rather than
      // fast, even though nothing was actually wrong. 13500ms matches the
      // step timeline's own total length (1800+1800+1900+4000+4000, the
      // point at which the 5th step's ring visually finishes), so the
      // animation is never left visibly incomplete.
      var MIN_LOADING_MS = 13500;
      var elapsed = Date.now() - searchStartTime;
      var remaining = Math.max(0, MIN_LOADING_MS - elapsed);
      setTimeout(function() {
        clearTimeout(stepTimer1); clearTimeout(stepTimer2); clearTimeout(stepTimer3); clearTimeout(stepTimer4);
        setView("result");
        setLoading(false);
        setQuery("");
        setSearchResults([]);
        if (!isFounder) {
          setSearchLimitInfo(function(prev) {
            var limit = (effectivePlan === "pro") ? 25 : 5;
            if (prev && prev.count != null) return Object.assign({}, prev, { count: prev.count + 1, limit: limit });
            return { count: 1, limit: limit };
          });
        }
      }, remaining);
    }).catch(function(err) {
      clearTimeout(stepTimer1); clearTimeout(stepTimer2); clearTimeout(stepTimer3); clearTimeout(stepTimer4);
      console.error("Live API failed:", err);
      setLoading(false);
      if (err && err.code === "functions/resource-exhausted") {
        setView(prevView);
        setQuery("");
        setSearchResults([]);
        setShowLimitModal("search");
        return;
      }
      var fallbackCur = detectCurrency(ticker, "");
      var fallbackFlag = detectFlag(ticker, "");
      // Only a confirmed "functions/not-found" from the backend (which
      // already retried once itself) means the ticker genuinely doesn't
      // exist. Anything else — network blip, timeout, cold start — isn't
      // the same thing as a wrong ticker, and telling someone to check
      // their exchange suffix when they typed a perfectly valid one is
      // actively misleading about what actually went wrong.
      var isGenuineNotFound = err && err.code === "functions/not-found";
      setStock({ t: ticker, n: null, s: "—", x: "—", c: "—", f: fallbackFlag, p: 0, cur: fallbackCur, ch: 0, cp: 0, m: {}, v: "FAIRLY VALUED", cn: "LOW", sc: 50, ai: null, bl: "—", br: "—", ct: [], er: [], notFound: true, notFoundReason: isGenuineNotFound ? "invalid" : "error" });
      setHist([]);
      setView("result");
      setQuery("");
      setSearchResults([]);
    });
  }, [query, prevView, view, effectivePlan, isFounder]);

  // Swipe-to-add from the search dropdown — fetches a real analysis in the
  // background (same call doSearch makes) so the watchlist card has actual
  // price/verdict data immediately, but stays on the current screen instead
  // of navigating to the stock detail page. Counts against the weekly
  // analysis limit exactly the same as tapping through normally would.
  var quickAddingTickers = useRef({});
  function quickAddFromSearch(ticker, name) {
    if (watchlist.some(function(w) { return w.t === ticker; })) { setToast((name || ticker) + " is already on your watchlist"); return; }
    var wlLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? Infinity : 3;
    if (watchlist.length >= wlLimit) { setToast("Standard watchlist holds 3 stocks at a time — remove one to add another, or upgrade to Pro for unlimited."); return; }
    if (quickAddingTickers.current[ticker]) return; // already in flight
    quickAddingTickers.current[ticker] = true;
    setToast("Adding " + ticker + "…");
    analyseStockFn({ ticker: ticker }).then(function(r) {
      delete quickAddingTickers.current[ticker];
      var d = r.data;
      var cur = normCurrency(d.currency) || countryToCurrency(d.country) || detectCurrency(d.ticker || ticker, d.exchange);
      var flag = d.flag || countryToFlag(d.country) || detectFlag(d.ticker || ticker, d.exchange);
      var entry = {
        t: d.ticker || ticker, n: d.company || name || ticker, s: d.sector || "Unknown", x: d.exchange || "Unknown",
        c: d.country || "—", f: flag, p: d.price || 0, cur: cur, ch: d.change || 0, cp: d.changePct || 0,
        v: d.verdict || "FAIRLY VALUED", cn: d.confidence || "LOW", sc: d.score || 50,
        ai: d.summary || null, bl: d.bull_case || null, br: d.bear_case || null, ct: d.catalysts || [],
        er: d.earnings || [], earnings: d.earnings || [], fetchedAt: d.fetchedAt || Date.now()
      };
      setWatchlist(function(w) { return [entry].concat(w); });
      setToast(entry.n + " added to watchlist");
      haptic([10, 50, 10]);
    }).catch(function() {
      delete quickAddingTickers.current[ticker];
      setToast("Couldn't add " + ticker + " — try again");
    });
  }

  // ── Data export — a plain client-side blob download, no backend call
  // needed since this data is already loaded in state. Two forms:
  // exportMyData() is a full structured JSON export (all the account data
  // that syncs to Firestore), aimed at data-portability requests; the CSV
  // exports are per-tab, aimed at people who just want their numbers in a
  // spreadsheet for their own records or taxes.
  function downloadBlob(filename, content, mime) {
    var blob = new Blob([content], { type: mime });
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
  }
  function csvEscape(v) {
    var s = (v == null) ? "" : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }
  function exportMyData() {
    var payload = {
      exportedAt: new Date().toISOString(),
      account: { name: user ? user.displayName : null, email: user ? user.email : null },
      watchlist: watchlist, portfolio: portfolio, notes: notes, thesis: thesis, alerts: alerts,
    };
    downloadBlob("zoltra-data-" + new Date().toISOString().slice(0,10) + ".json", JSON.stringify(payload, null, 2), "application/json");
    setToast("Your data has been downloaded");
  }
  function exportWatchlistCsv() {
    if (watchlist.length === 0) { setToast("Your watchlist is empty — nothing to export"); return; }
    var rows = [["Ticker","Name","Price","Currency","Change %","Verdict","Score"]];
    watchlist.forEach(function(w) { rows.push([w.t, w.n, w.p, w.cur, w.cp, w.v, w.sc]); });
    var csv = rows.map(function(r) { return r.map(csvEscape).join(","); }).join("\n");
    downloadBlob("zoltra-watchlist-" + new Date().toISOString().slice(0,10) + ".csv", csv, "text/csv");
    setToast("Watchlist exported");
  }
  function exportPortfolioCsv() {
    if (portfolio.length === 0) { setToast("Your mock investment portfolio is empty — nothing to export"); return; }
    var rows = [["Ticker","Shares","Currency","Amount Invested","Current Price","Current Value","P&L"]];
    portfolio.forEach(function(p) {
      var val = p.sh * p.cp;
      rows.push([p.t, p.sh, p.cur, p.inv, p.cp, val.toFixed(2), (val - p.inv).toFixed(2)]);
    });
    var csv = rows.map(function(r) { return r.map(csvEscape).join(","); }).join("\n");
    downloadBlob("zoltra-portfolio-" + new Date().toISOString().slice(0,10) + ".csv", csv, "text/csv");
    setToast("Portfolio exported");
  }

  var isWatched = stock && watchlist.some(function(w) { return w.t === stock.t; });
  var chartData = useMemo(function() {
    if (!hist || hist.length === 0) return [];
    var cutoff = Date.now() - chartRange * 86400000;
    return hist.filter(function(h) { return h.date.getTime() >= cutoff; });
  }, [hist, chartRange]);

  var periodChange = useMemo(function() {
    if (!chartData || chartData.length < 2 || !stock || !stock.p) return null;
    // Find the data point closest to exactly chartRange days ago (handles weekends/holidays)
    var targetTs = Date.now() - chartRange * 86400000;
    var startPoint = chartData.reduce(function(best, h) {
      return Math.abs(h.date.getTime() - targetTs) < Math.abs(best.date.getTime() - targetTs) ? h : best;
    }, chartData[0]);
    // Use live current price as end — history cache can be up to 24h old
    var f = startPoint.price, l = stock.p;
    if (!f || f === 0) return null;
    return { pct: ((l - f) / f) * 100 };
  }, [chartData, stock, chartRange]);

  var metricGroups = {
    val: [["P/E", "pe"], ["Fwd P/E", "fpe"], ["PEG", "peg"], ["P/B", "pb"], ["P/S", "ps"], ["EV/EBITDA", "evEbitda"], ["Mkt Cap", "mc"], ["Div Yield", "dy"], ["Analyst Target", "at"]],
    grow: [["Rev Growth", "rg"], ["EPS Growth", "eg"], ["FCF", "fcf"], ["Gross Margin", "gm"], ["Op Margin", "om"], ["Net Margin", "nm"]],
    risk: [["Beta", "bt"], ["Debt/Equity", "de"], ["ROE", "roe"], ["ROA", "roa"], ["52w High", "hi"], ["52w Low", "lo"]]
  };

  var dailyBrowsePicks = useMemo(getDailyBrowsePicks, []);
  var filteredStocks = marketFilter === "all" ? dailyBrowsePicks : (BROWSE_STOCKS[marketFilter] || []);

  var rangeMap = { 30: "1M", 90: "3M", 180: "6M", 365: "1Y", 1825: "5Y" };

  // Styles
  var pad = { padding: "0 12px" };
  var card = { background: WH, border: "none", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 8px rgba(15,23,42,0.05)" };
  var segBtn = function(active) {
    return { padding: "6px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: active ? 700 : 400, cursor: "pointer", background: active ? WH : "transparent", color: active ? N : TX3 };
  };

  /* ═══ SPLASH — shown only while Firebase is resolving whether there's a
     cached session (a few hundred ms, occasionally longer on a cold PWA
     launch). This is what a returning, already-logged-in user sees instead
     of the Welcome/login screen — it never shows the sign-in form for a
     split second before yanking them to Discover. Logged-out users see this
     too, briefly, then fall through to Welcome below once the check
     resolves. Styled to match the Weekly Deep Dive tile — same deep
     purple gradient and gold accent — since that's the app's most premium,
     editorial moment and makes a strong first impression either way.

     Note on the iOS safe-area white-band bug: the fix is NOT anything in
     this component — a position:fixed div, even overscanned past the
     viewport edge, does not get the treatment needed to paint under the
     home-indicator chrome region on iOS standalone PWAs. The actual fix is
     the body-background sync effect (search "body background sync" in this
     file) plus the matching html/body rule in index.html — WebKit extends
     the ROOT element's own background-color to the full device canvas
     including that chrome region, which is specific to html/body and does
     not apply to child elements. Confirmed working (this is the same fix
     that resolved the identical bug on Waltra). ═══ */
  if (!splashDone) {
    return <SplashScreen closing={splashClosing} />;
  }

  /* ═══ SETUP (new signup: name, avatar, tier — then into the tour) ═══ */
  if (view === "setup") {
    var avatarColors = [N, B, AC, OK, WN, "#7C3AED"];

    var finishSetup = function(goToTour) {
      var finalName = setupName.trim();
      if (finalName && user) {
        // updateProfile needs the live Firebase SDK User instance, not React's
        // `user` state — that becomes a plain object after we refresh it below
        // (for the avatar-initial display), and a plain object breaks any
        // *subsequent* call to updateProfile with an opaque error.
        updateProfile(auth.currentUser, { displayName: finalName }).then(function() {
          setUser(Object.assign({}, auth.currentUser));
        }).catch(function() {});
      }
      if (user) {
        setDoc(doc(firestore, "users", user.uid, "data", "profile"), { avatarColor: setupAvatarColor, displayName: finalName }).catch(console.error);
      }
      setAvatarColor(setupAvatarColor);

      // Combines the trial-claim result (if any) with the tour/skip choice
      // into one toast — doing these separately risked the async trial-claim
      // toast silently overwriting a "check Profile for the tour" message
      // fired right before it.
      function proceed(trialMsg) {
        if (goToTour) {
          setView("onboard");
          if (trialMsg) setToast(trialMsg);
        } else {
          setView("home");
          setToast(trialMsg ? trialMsg + " You can take the app tour any time from Profile." : "You can take the app tour any time from Profile \u2014 Profile \u2192 Take the app tour.");
        }
      }

      if (setupTier === "pro") {
        claimTrialFn({}).then(function(r) {
          if (r.data && r.data.granted) {
            proceed("Your 7-Day Pro Trial has started — no card needed.");
          } else {
            proceed("You're on Standard for now — you can start a trial or upgrade any time from Profile.");
          }
        }).catch(function(e) {
          console.error("claimTrial error:", e.message);
          proceed("Couldn't start your trial — you're on Standard for now, try again from Profile.");
        });
      } else {
        proceed(null);
      }
    };

    return (
      <div style={{ minHeight: "100dvh", background: WH, fontFamily: "Inter, -apple-system, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", overflowX: "hidden", display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <style>{`*{box-sizing:border-box}input:focus,button:focus{outline:none}input:focus-visible,button:focus-visible,a:focus-visible,select:focus-visible{outline:2px solid #2554D9;outline-offset:-2px}.su-input:focus{border-color:#2554D9}@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}.su-fade{animation:fadeUp .4s ease both}`}</style>
        <div style={{ maxWidth: 480, margin: "0 auto", width: "100%", flex: 1, display: "flex", flexDirection: "column", padding: "0 24px" }}>

          <div style={{ display: "flex", justifyContent: "center", gap: 6, paddingTop: 32, paddingBottom: 8 }}>
            {[0, 1, 2].map(function(i) {
              return <div key={i} style={{ width: i === setupStep ? 20 : 8, height: 8, borderRadius: 4, background: i === setupStep ? N : BD, transition: "width .2s" }} />;
            })}
          </div>

          <div key={setupStep} className="su-fade" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingBottom: 40, overflowY: "auto" }}>

            {setupStep === 0 && (
              <div>
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <MdPerson size={32} />
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: N, marginBottom: 8, textAlign: "center" }}>Your Name</div>
                <p style={{ fontSize: 14, color: TXM, textAlign: "center", marginBottom: 28, lineHeight: 1.5 }}>Please enter your full name.</p>
                <input className="su-input" value={setupName} onChange={function(e) { setSetupName(e.target.value); }} placeholder="Full name" autoFocus
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: "2px solid " + (setupName.trim() ? AC : BD), fontSize: 16, color: N, textAlign: "center", fontFamily: "inherit", background: setupName.trim() ? ACS : WH, transition: "border-color .15s, background .15s" }} />
              </div>
            )}

            {setupStep === 1 && (
              <div>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: setupAvatarColor, color: WH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 700, margin: "0 auto 24px" }}>
                  {(setupName.trim()[0] || "?").toUpperCase()}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: N, marginBottom: 8, textAlign: "center" }}>Pick your color</div>
                <p style={{ fontSize: 14, color: TXM, textAlign: "center", marginBottom: 28 }}>This is your avatar around the app.</p>
                <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
                  {avatarColors.map(function(c) {
                    return <button key={c} onClick={function() { setSetupAvatarColor(c); }} style={{ width: 40, height: 40, borderRadius: "50%", background: c, border: "3px solid " + (setupAvatarColor === c ? c : "transparent"), boxShadow: setupAvatarColor === c ? "0 0 0 2px " + BD : "none", cursor: "pointer" }} />;
                  })}
                </div>
              </div>
            )}

            {setupStep === 2 && (
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: N, marginBottom: 8, textAlign: "center" }}>Choose your plan</div>
                <p style={{ fontSize: 14, color: TXM, textAlign: "center", marginBottom: 20 }}>You can always change this later in your Profile.</p>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
                  {[
                    {
                      key: "free", label: "Standard", price: "Free",
                      rows: ["5 analyses/wk", "3 thesis/wk", "3 watchlist", "3 portfolio", "Compare 3"],
                      accent: N, bg: WH, ac: null,
                    },
                    {
                      key: "pro", label: "7-Day Trial", price: "Free", badge: "No card needed",
                      rows: ["25 analyses/wk", "10 thesis/wk", "Unlimited watchlist", "Unlimited portfolio", "Compare 6"],
                      accent: AC, bg: ACS, ac: AC,
                      footnote: "Doesn't auto-renew — reverts to Standard after 7 days unless you subscribe to keep Pro.",
                    },
                    {
                      key: "buy", label: "Pro", price: "£4.99/mo", badge: "Coming soon",
                      rows: ["25 analyses/wk", "10 thesis/wk", "Unlimited watchlist", "Unlimited portfolio", "Compare 6"],
                      accent: B, bg: BW, ac: B,
                      footnote: "Direct Pro sign-up is launching soon — start the free trial today to use Pro features now.",
                    },
                  ].map(function(t) {
                    var isSel = t.key === "pro" ? setupTier === "pro" : t.key === "free" ? setupTier === "free" : false;
                    return (
                      <button key={t.key}
                        onClick={function() {
                          if (t.key === "buy") { setSetupTier("pro"); setToast("Direct Pro sign-up is coming soon — starting your free trial instead."); }
                          else { setSetupTier(t.key); }
                        }}
                        style={{ textAlign: "left", padding: "14px 11px", borderRadius: 14, border: "2px solid " + (isSel ? t.accent : BD), background: isSel ? t.bg : WH, cursor: "pointer", display: "flex", flexDirection: "column", minHeight: 268 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: N, lineHeight: 1.3 }}>{t.label}</span>
                          {isSel && <MdCheckCircle size={18} color={t.accent} style={{ flexShrink: 0 }} />}
                        </div>
                        <div style={{ fontSize: 19, fontWeight: 800, color: t.accent || N, marginBottom: 12 }}>{t.price}</div>
                        <div style={{ flex: 1 }}>
                          {t.rows.map(function(r, i) {
                            return (
                              <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start", marginBottom: 7 }}>
                                <MdCheckCircle size={13} color={t.ac || TX3} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span style={{ fontSize: 12, color: TXM, lineHeight: 1.35 }}>{r}</span>
                              </div>
                            );
                          })}
                        </div>
                        {t.badge && <div style={{ fontSize: 11, fontWeight: 700, color: t.accent, marginTop: 8 }}>{t.badge}</div>}
                        {t.footnote && <div style={{ fontSize: 10.5, color: TXM, lineHeight: 1.4, marginTop: 5 }}>{t.footnote}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

          <div style={{ paddingBottom: 32 }}>
            {setupStep < 2 ? (
              <button onClick={function() { setSetupStep(function(s) { return s + 1; }); }} disabled={setupStep === 0 && !setupName.trim()}
                style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: (setupStep === 0 && !setupName.trim()) ? BD : N, color: WH, fontSize: 16, fontWeight: 800, cursor: (setupStep === 0 && !setupName.trim()) ? "default" : "pointer" }}>
                Continue →
              </button>
            ) : (
              <div>
                <button onClick={function() { finishSetup(true); }} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: N, color: WH, fontSize: 16, fontWeight: 800, cursor: "pointer", marginBottom: 10 }}>
                  {setupTier === "pro" ? "Start trial & take the tour →" : "Continue & take the tour →"}
                </button>
                <button onClick={function() { finishSetup(false); }} style={{ width: "100%", padding: 12, borderRadius: 12, border: "none", background: "none", color: TXM, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Skip to Discover
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }

  /* ═══ ONBOARDING ═══ */
  if (view === "onboard") {
    var steps = [
      { icon: <MdShowChart size={40} />, title: "AI-powered stock analysis", body: "Get instant AI valuations, earnings data, and verdicts across US, UK, Indian, Japanese and European markets." },
      { icon: <MdGpsFixed size={40} />, title: "Understand the verdict", body: "Every stock gets Undervalued, Fairly Valued, or Overvalued, based on three scores — Value, Quality, and Altman Z-Score. Tap any of the three on a stock's page to see exactly what's behind it." },
      { icon: <MdStar size={40} />, title: "Build your watchlist", body: "Watch stocks, set price alerts, add personal notes, and track a mock portfolio. Sign in to sync everything across your devices." },
    ];

    return (
      <div style={{ minHeight: "100dvh", background: WH, fontFamily: "Inter, -apple-system, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", overflowX: "hidden", display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0}@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}.ob-fade{animation:fadeUp .4s ease both}`}</style>
        <div style={{ maxWidth: 480, margin: "0 auto", width: "100%", flex: 1, display: "flex", flexDirection: "column", padding: "0 24px" }}>

          {/* Logo */}
          <div style={{ paddingTop: 40, paddingBottom: 32, textAlign: "center" }}>
            <img src="/zoltra-icon.png" alt="Zoltra" style={{ width: 56, height: 56, objectFit: "contain" }} />
            <div style={{ fontSize: 17, fontWeight: 800, color: N, marginTop: 8, letterSpacing: -0.3 }}>Zoltra.</div>
          </div>

          {/* Step content */}
          <div key={onboardStep} className="ob-fade" style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center", paddingBottom: 40 }}>
            <div style={{ width: 88, height: 88, borderRadius: "50%", background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>{steps[onboardStep].icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: N, marginBottom: 12, lineHeight: 1.2 }}>{steps[onboardStep].title}</div>
            <p style={{ fontSize: 15, color: TXM, lineHeight: 1.7 }}>{steps[onboardStep].body}</p>
          </div>

          {/* Step dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 24 }}>
            {steps.map(function(_, i) {
              return <div key={i} style={{ width: i === onboardStep ? 20 : 8, height: 8, borderRadius: 4, background: i === onboardStep ? N : BD, transition: "width .2s" }} />;
            })}
          </div>

          {/* CTA */}
          <div style={{ paddingBottom: 32 }}>
            {onboardStep < steps.length - 1 ? (
              <div style={{ display: "flex", gap: 10 }}>
                {onboardStep === 0 ? (
                  <button onClick={function() { try { localStorage.setItem("q_onboarded", "1"); } catch(e) {} setView(user ? "home" : "welcome"); }}
                    style={{ flex: 1, padding: 12, borderRadius: 12, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 14, cursor: "pointer" }}>
                    Skip
                  </button>
                ) : (
                  <button onClick={function() { setOnboardStep(function(s) { return s - 1; }); }}
                    style={{ flex: 1, padding: 12, borderRadius: 12, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 14, cursor: "pointer" }}>
                    ← Back
                  </button>
                )}
                <button onClick={function() { setOnboardStep(function(s) { return s + 1; }); }}
                  style={{ flex: 2, padding: 12, borderRadius: 12, border: "none", background: N, color: WH, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  Next →
                </button>
              </div>
            ) : (
              <div>
                <button onClick={function() { setOnboardStep(function(s) { return s - 1; }); }}
                  style={{ width: "100%", padding: 10, marginBottom: 10, borderRadius: 12, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 13, cursor: "pointer" }}>
                  ← Back
                </button>
                <button onClick={function() { try { localStorage.setItem("q_onboarded", "1"); } catch(e) {} setView(user ? "home" : "welcome"); }}
                  style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: N, color: WH, fontSize: 16, fontWeight: 800, cursor: "pointer" }}>
                  Start analysing →
                </button>
                <button onClick={function() {
                  try { localStorage.setItem("q_onboarded", "1"); } catch(e) {}
                  if (user) { setView("home"); doSearch("AAPL", "Apple"); }
                  else { setView("welcome"); }
                }} style={{ width: "100%", padding: 12, marginTop: 10, borderRadius: 12, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                  Or see a live example: Apple (AAPL) →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ WELCOME ═══ */
  if (view === "welcome") {
    return (
      <WelcomeScreen
        authMode={authMode} setAuthMode={setAuthMode}
        emailInput={emailInput} setEmailInput={setEmailInput}
        passwordInput={passwordInput} setPasswordInput={setPasswordInput}
        ageConfirmed={ageConfirmed} setAgeConfirmed={setAgeConfirmed}
        authError={authError} setAuthError={setAuthError}
        handleEmailAuth={handleEmailAuth} handleForgotPassword={handleForgotPassword}
      />
    );
  }

  /* ═══ APP ═══ */
  return (
    <div style={{ minHeight: "100dvh", background: BG, color: TX, fontFamily: "Inter, -apple-system, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", overflowX: "hidden", paddingBottom: "env(safe-area-inset-bottom)", paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');*{box-sizing:border-box;margin:0;padding:0;overflow-anchor:none}html{-webkit-text-size-adjust:100%;text-size-adjust:100%}img{max-width:100%;display:block}input:focus,button:focus{outline:none}input:focus-visible,button:focus-visible,a:focus-visible,select:focus-visible{outline:2px solid #2554D9;outline-offset:2px}@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}@keyframes spin{to{transform:rotate(360deg)}}.fi{animation:fadeIn .3s ease both}@keyframes flashUp{0%{background:#E6F5ED}50%{background:#C8E6C9}100%{background:transparent}}@keyframes flashDn{0%{background:#FCE8E8}50%{background:#FFCDD2}100%{background:transparent}}@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}.skeleton{background:linear-gradient(90deg,#E8ECF1 25%,#F3F5F7 50%,#E8ECF1 75%);background-size:200% 100%;animation:shimmer 1.4s linear infinite;border-radius:6px}@keyframes qdot{0%,100%{opacity:0.25;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}@keyframes pulseRing{0%{transform:scale(0.8);opacity:0.8}100%{transform:scale(1.3);opacity:0}}`}</style>

      {/* Datalist for native browser autocomplete on all search inputs */}
      <datalist id="stock-suggestions">
        {(function() {
          var seen = {};
          var opts = [];
          // All browse stocks — name and ticker as options
          Object.values(BROWSE_STOCKS).forEach(function(arr) {
            arr.forEach(function(s) {
              if (!seen[s.t]) { seen[s.t] = true; opts.push({ val: s.t, label: s.n + " (" + s.t + ")" }); }
              if (!seen[s.n]) { seen[s.n] = true; opts.push({ val: s.n, label: s.n + " — " + s.t }); }
            });
          });
          // Known name aliases from NAMES map
          Object.keys(NAMES).forEach(function(k) {
            var display = k.charAt(0).toUpperCase() + k.slice(1);
            if (!seen[k]) { seen[k] = true; opts.push({ val: display, label: display + " (" + NAMES[k] + ")" }); }
          });
          return opts.map(function(o, i) { return <option key={i} value={o.val}>{o.label}</option>; });
        })()}
      </datalist>
    <div style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}>

      {/* Nav */}
      <nav style={{ background: WH, borderBottom: "1px solid " + BD, position: "sticky", top: 0, zIndex: 50, padding: "0 12px", paddingTop: "env(safe-area-inset-top)" }}>
        <div style={{ display: "flex", alignItems: "center", height: 52, gap: 8 }}>
          <button style={{ cursor: "pointer", width: 34, height: 34, flexShrink: 0, overflow: "hidden", border: "none", background: "none", padding: 0 }} onClick={function() { setView("home"); setStock(null); setQuery(""); setSearchResults([]); }} aria-label="Zoltra home">
            <img src="/zoltra-icon.png" alt="" style={{ width: 34, height: 34, objectFit: "contain", display: "block" }} />
          </button>
          <div style={{ display: "flex", background: BG, borderRadius: 8, padding: "2px 4px 2px 2px", overflowX: "auto", flex: 1, minWidth: 0 }}>
            {[
              { k: "home", l: "Discover", Icon: MdAutoAwesome },
              { k: "screener", l: "Screener", Icon: MdTune },
              { k: "compare", l: "Compare", Icon: MdBalance },
              { k: "watchlist", l: "Watchlist", Icon: MdStar },
              { k: "portfolio", l: "Portfolio", Icon: MdAccountBalanceWallet },
              { k: "learn", l: "Learn", Icon: MdMenuBook },
            ].map(function(tab) {
              var active = (tab.k === "home" && (view === "home" || view === "result")) || view === tab.k;
              return (
                <button key={tab.k} onClick={function() { if (tab.k === "home") { setView("home"); setStock(null); setQuery(""); setSearchResults([]); } else setView(tab.k); }}
                  aria-label={tab.l} aria-current={active ? "page" : undefined}
                  style={{ display: "flex", alignItems: "center", gap: 4, padding: active ? "6px 10px" : "6px 8px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: active ? 700 : 400, cursor: "pointer", background: active ? WH : "transparent", color: active ? N : TX3, whiteSpace: "nowrap", flexShrink: 0 }}>
                  <tab.Icon size={16} />
                  {active && tab.l}
                </button>
              );
            })}
          </div>
          {user ? (
            <button style={{ cursor: "pointer", width: 28, height: 28, borderRadius: "50%", background: avatarColor, color: WH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, border: "none", padding: 0 }}
              onClick={function() { setView("profile"); }} aria-label="Profile">
              {user.displayName ? user.displayName[0].toUpperCase() : "U"}
            </button>
          ) : (
            <button onClick={function() { setView("profile"); }} aria-label="Profile / Sign in"
              style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid " + BD, background: WH, color: TXM, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0 }}>
              <MdPerson size={15} />
            </button>
          )}
        </div>
      </nav>

      {/* First-ever-search banner — extra reassurance for brand new users, since
          real AI analysis genuinely takes longer than most app interactions. */}
      {/* Loading takeover — replaces old content entirely rather than layering
          skeleton blocks over still-visible page content underneath. Shown
          for every search, not just the first, since the "up to 30 seconds"
          reassurance is useful every time, not only on someone's first ever
          search. */}
      {loading && (function() {
        var stepLabels = ["Fetching price data", "Checking valuation model", "Comparing to sector peers", "Running AI analysis", "Finalising verdict"];
        var stepMap = { "Fetching": 0, "Checking": 1, "Comparing": 2, "Running": 3, "Almost": 4 };
        var stepIndex = stepMap[loadStep.split(" ")[0]] != null ? stepMap[loadStep.split(" ")[0]] : 0;
        var stepDurations = [1800, 1800, 1900, 4000, 4000]; // ms — last one has no fixed endpoint (matches loadStep timers), used only for the ring's own transition length
        var CIRC = 57; // 2*PI*9

        var lc = loadingChartData; // { points, price, changePct } | null
        var chartW = 260, chartH = 84;
        var chartGeom = null;
        if (lc && lc.points && lc.points.length >= 2) {
          var prices = lc.points.map(function(p) { return p.p; });
          var min = Math.min.apply(null, prices), max = Math.max.apply(null, prices);
          var range = (max - min) || 1;
          var n = lc.points.length;
          var coords = lc.points.map(function(p, i) {
            var x = (i / (n - 1)) * chartW;
            var y = chartH - 8 - ((p.p - min) / range) * (chartH - 18);
            return [Math.round(x), Math.round(y)];
          });
          var lineD = "M" + coords.map(function(c) { return c.join(","); }).join(" L");
          var areaD = lineD + " L" + chartW + "," + chartH + " L0," + chartH + " Z";
          var up = lc.changePct >= 0;
          var col = up ? "#5DCAA5" : "#F0997B";
          var fmtDate = function(ts) { return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
          chartGeom = { lineD: lineD, areaD: areaD, col: col, up: up, from: fmtDate(lc.points[0].d), to: fmtDate(lc.points[n - 1].d) };
        }

        var tick = loadingTicker || "";
        var displayName = loadingName || query || tick || "your stock";

        return (
          <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: N, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", paddingTop: "calc(24px + env(safe-area-inset-top))", paddingBottom: "calc(20px + env(safe-area-inset-bottom))", paddingLeft: 24, paddingRight: 24, overflowY: "auto" }}>
            <style>{`@keyframes stepFade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:translateY(0)}}`}</style>

            <div style={{ width: "100%", maxWidth: 340, display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>

              <div style={{ width: 88, height: 88, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, flexShrink: 0, position: "relative" }}>
                {tick && (
                  <img src={"/logos/" + tick + ".png"} alt="" loading="eager"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    onError={function(e) {
                      e.target.style.display = "none";
                      var mono = e.target.nextSibling;
                      if (mono) mono.style.display = "flex";
                    }}
                  />
                )}
                <div style={{ display: "none", width: "100%", height: "100%", borderRadius: 18, alignItems: "center", justifyContent: "center", position: "absolute", inset: 0, background: B, color: WH, fontSize: 24, fontWeight: 800, letterSpacing: 0.3 }}>
                  {tick.replace(/\..*/, "").slice(0, 4) || "?"}
                </div>
              </div>

              <span style={{ fontSize: 21, fontWeight: 700, color: WH, textAlign: "center", marginBottom: 3 }}>{displayName}</span>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 22 }}>
                {tick && <FlagImg emoji={detectFlag(tick)} size={14} />}
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{tick || "\u00A0"}</span>
              </div>

              <div style={{ minHeight: 32, display: "flex", alignItems: "baseline", gap: 9, marginBottom: 20, opacity: lc ? 1 : 0, transition: "opacity .3s" }}>
                <span style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 24, fontWeight: 700, color: WH, fontVariantNumeric: "tabular-nums" }}>
                  {lc ? (CS$[detectCurrency(tick)] || "$") + lc.price.toFixed(2) : "\u00A0"}
                </span>
                {lc && (
                  <span style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 14, color: chartGeom.col, fontVariantNumeric: "tabular-nums" }}>
                    {chartGeom.up ? "+" : ""}{lc.changePct}%
                  </span>
                )}
              </div>

              <div style={{ minHeight: chartH + 24, display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 26 }}>
                {chartGeom ? (
                  <>
                    <svg width={chartW} height={chartH}>
                      <defs>
                        <linearGradient id="loadChartFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={chartGeom.col} stopOpacity="0.35" />
                          <stop offset="100%" stopColor={chartGeom.col} stopOpacity="0" />
                        </linearGradient>
                        <clipPath id="loadChartReveal"><rect x="0" y="0" width={chartW} height={chartH} style={{ transition: "width 1.1s ease" }} /></clipPath>
                      </defs>
                      <line x1="0" y1={chartH * 0.25} x2={chartW} y2={chartH * 0.25} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                      <line x1="0" y1={chartH * 0.6} x2={chartW} y2={chartH * 0.6} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                      <g clipPath="url(#loadChartReveal)">
                        <path d={chartGeom.areaD} fill="url(#loadChartFill)" />
                        <path d={chartGeom.lineD} fill="none" stroke={chartGeom.col} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </g>
                    </svg>
                    <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.4)", marginTop: 10 }}>{chartGeom.from} – {chartGeom.to} · real closing prices</div>
                  </>
                ) : (
                  <div style={{ width: chartW, height: chartH, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <svg viewBox={"0 0 " + chartW + " " + chartH} style={{ width: chartW, height: chartH, position: "absolute", top: 0, left: 0 }} preserveAspectRatio="none">
                      <path d={"M0," + (chartH*0.65) + " C" + (chartW*0.1) + "," + (chartH*0.55) + " " + (chartW*0.2) + "," + (chartH*0.72) + " " + (chartW*0.32) + "," + (chartH*0.58) + " C" + (chartW*0.45) + "," + (chartH*0.42) + " " + (chartW*0.55) + "," + (chartH*0.6) + " " + (chartW*0.68) + "," + (chartH*0.5) + " C" + (chartW*0.8) + "," + (chartH*0.4) + " " + (chartW*0.9) + "," + (chartH*0.5) + " " + chartW + "," + (chartH*0.38)}
                        fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.08) 50%, transparent 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s linear infinite" }} />
                  </div>
                )}
              </div>

              {!chartGeom && <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.4)", marginTop: -18, marginBottom: 26 }}>Loading price history…</div>}

              <div style={{ width: "100%", maxWidth: 300, display: "flex", flexDirection: "column", gap: 18 }}>
                {stepLabels.map(function(label, i) {
                  var isActive = i === stepIndex;
                  var isPast = i < stepIndex;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ position: "relative", width: 20, height: 20, flexShrink: 0 }}>
                        {isPast ? (
                          <div style={{ width: 20, height: 20, borderRadius: "50%", background: AC, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MdCheck size={14} color={N} />
                          </div>
                        ) : (
                          <svg width="20" height="20" key={isActive ? "active-" + i : "pending-" + i}>
                            <circle cx="10" cy="10" r="9" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="2.5" />
                            {isActive && (
                              <circle cx="10" cy="10" r="9" fill="none" stroke={AC} strokeWidth="2.5" strokeLinecap="round"
                                strokeDasharray={CIRC} strokeDashoffset={CIRC} transform="rotate(-90 10 10)"
                                style={{ animation: "ringFill" + i + " " + stepDurations[i] + "ms linear forwards" }} />
                            )}
                          </svg>
                        )}
                      </div>
                      <span style={{ fontSize: 16, fontWeight: isActive ? 600 : 400, color: isPast || isActive ? WH : "rgba(255,255,255,0.4)", transition: "color .3s" }}>{label}</span>
                    </div>
                  );
                })}
                <style>{stepDurations.map(function(d, i) { return "@keyframes ringFill" + i + "{to{stroke-dashoffset:0}}"; }).join(" ")}</style>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexShrink: 0, paddingTop: 20 }}>
              <MdSchedule size={12} color="rgba(255,255,255,0.55)" />
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>Usually takes 10 to 20 seconds</span>
            </div>
          </div>
        );
      })()}

      <div style={pad}>
        {/* Search */}
        {(view === "home" || view === "result") && (
          <div style={{ paddingTop: 12, paddingBottom: 8 }}>
            {view === "result" && stock && (
              <button onClick={function() {
                if (prevView === "screener") { setView("screener"); setStock(null); setQuery(""); setSearchResults([]); }
                else if (prevView === "watchlist") { setView("watchlist"); setStock(null); setQuery(""); setSearchResults([]); }
                else if (prevView === "portfolio") { setView("portfolio"); setStock(null); setQuery(""); setSearchResults([]); }
                else { setView("home"); setStock(null); setQuery(""); setSearchResults([]); }
              }} style={{ background: "none", border: "none", color: B, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 8, padding: 0 }}>
                ← Back to {prevView === "screener" ? "screener" : prevView === "watchlist" ? "watchlist" : prevView === "portfolio" ? "portfolio" : "search"}
              </button>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <input
                ref={inputRef}
                value={query}
                onChange={function(e) { setQuery(e.target.value); }}
                onKeyDown={function(e) { if (e.key === "Enter") doSearch(); }}
                placeholder="Search by name, ticker, or sector..."
                list="stock-suggestions"
                style={{ flex: 1, minWidth: 0, background: WH, border: "2px solid " + BD, borderRadius: 12, padding: "12px 14px", fontSize: 16, color: TX }}
              />
              <button onClick={function() { doSearch(); }} disabled={loading || !query.trim()} style={{ padding: "12px 14px", borderRadius: 12, border: "none", background: N, color: WH, fontSize: loading ? 11 : 15, fontWeight: 700, cursor: "pointer", opacity: (!query.trim() || loading) ? 0.6 : 1, minWidth: 56, textAlign: "center", lineHeight: 1.2 }}>
                {loading ? loadStep.replace("…","").split(" ")[0] + "…" : "Go"}
              </button>
            </div>

            {/* Ambient usage note — quiet, and only shown once someone's
                actually close to their limit (2 or fewer left), rather than
                a persistent progress bar competing for attention on the
                app's most-visited screen every single time regardless of
                how much room they have left. */}
            {view === "home" && !isFounder && (function() {
              var limit = searchLimitInfo && searchLimitInfo.limit ? searchLimitInfo.limit : (effectivePlan === "pro" ? 25 : 5);
              var used = Math.min(searchLimitInfo && searchLimitInfo.count ? searchLimitInfo.count : 0, limit);
              var remaining = Math.max(limit - used, 0);
              if (remaining > 2) return null;
              return (
                <div onClick={function() { setProfileTab("account"); setView("profile"); }} style={{ fontSize: 11, color: TX3, marginBottom: 16, paddingLeft: 2, cursor: "pointer" }}>
                  {remaining === 0
                    ? <>You've used all {limit} analyses this week{effectivePlan !== "pro" ? <> — <span style={{ color: B, fontWeight: 700 }}>Upgrade</span> for unlimited</> : null}</>
                    : <>You have <strong style={{ color: B }}>{remaining} analys{remaining === 1 ? "is" : "es"}</strong> left this week{effectivePlan !== "pro" ? <> — <span style={{ color: B, fontWeight: 700 }}>Upgrade</span> for unlimited</> : null}</>}
                </div>
              );
            })()}

            {/* Search autocomplete dropdown — shown on both home and result views.
                Swipe left on a row to reveal a quick "+ Watch" action —
                same touch mechanics as the existing swipe-to-delete on
                Watchlist/Portfolio, just green and additive instead of red. */}
            {searchResults.length > 0 && query.trim() && (
              <div ref={searchDropRef} style={{ background: WH, border: "1px solid " + BD, borderRadius: 10, marginTop: 6, marginBottom: 4, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
                <style>{`.sr-card{position:relative;overflow:hidden}.sr-add{position:absolute;right:0;top:0;bottom:0;width:72px;background:${OK};display:flex;align-items:center;justify-content:center;color:#fff;font-size:12px;font-weight:700;cursor:pointer}.sr-inner{position:relative;transition:transform .2s ease;background:${WH}}`}</style>
                {searchResults.map(function(s, i) {
                  var isWatchedS = watchlist.some(function(w) { return w.t === s.t; });
                  return (
                    <div key={s.t + i} className="sr-card"
                      style={{ borderTop: i > 0 ? "1px solid " + BD : "none" }}
                      onTouchStart={function(e) { _swipeMap["sr_" + s.t] = e.touches[0].clientX; }}
                      onTouchMove={function(e) {
                        var startX = _swipeMap["sr_" + s.t];
                        if (startX === undefined) return;
                        var dx = e.touches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".sr-inner");
                        if (dx < 0) inner.style.transform = "translateX(" + Math.max(dx, -72) + "px)";
                      }}
                      onTouchEnd={function(e) {
                        var startX = _swipeMap["sr_" + s.t];
                        if (startX === undefined) return;
                        var dx = e.changedTouches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".sr-inner");
                        if (dx < -40 && !isWatchedS) { inner.style.transform = "translateX(0)"; quickAddFromSearch(s.t, s.n); }
                        else { inner.style.transform = "translateX(0)"; }
                        delete _swipeMap["sr_" + s.t];
                      }}>
                      {!isWatchedS && (
                        <div className="sr-add" onClick={function() { quickAddFromSearch(s.t, s.n); }}>
                          <MdStarBorder size={14} style={{ marginRight: 3 }} /> Watch
                        </div>
                      )}
                      <div className="sr-inner" onClick={function() { setSearchResults([]); doSearch(s.t, s.n); }}
                        style={{ padding: "11px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        onMouseEnter={function(e) { e.currentTarget.style.background = BG; }}
                        onMouseLeave={function(e) { e.currentTarget.style.background = WH; }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: N, marginRight: 6 }}><FlagImg emoji={s.f} /> {s.t}</span>
                          <span style={{ color: TXM, fontSize: 13 }}>{s.n}</span>
                        </div>
                        {isWatchedS ? (
                          <span style={{ fontSize: 11, color: OKT, flexShrink: 0, marginLeft: 8, display: "flex", alignItems: "center", gap: 3 }}><MdStar size={12} /> Watching</span>
                        ) : (
                          <span style={{ fontSize: 12, color: B, flexShrink: 0, marginLeft: 8 }}>Search →</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Searching / no-matches feedback — without this, a genuine
                zero-result search (or the ~200ms wait for the API layer)
                just looks like nothing happened, which reads as broken.
                No-matches also suggests a few tickers rather than just
                stating the problem, so it's not a dead end. */}
            {searchResults.length === 0 && query.trim().length >= 2 && !loading && (
              <div style={{ marginTop: 6, marginBottom: 4, padding: "10px 14px 12px" }}>
                {searchingApi ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: TXM }}>
                    {[0,0.15,0.3].map(function(d){return <div key={d} style={{width:5,height:5,borderRadius:"50%",background:TX3,animation:"qdot 1s "+d+"s ease-in-out infinite"}} />;})}<span>Searching…</span>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, color: TXM, marginBottom: 8 }}>No matches for "{query.trim()}" — try the exact ticker, or one of these:</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {[{ t: "AAPL", n: "Apple" }, { t: "NVDA", n: "Nvidia" }, { t: "MSFT", n: "Microsoft" }].map(function(s) {
                        return (
                          <button key={s.t} onClick={function() { setSearchResults([]); doSearch(s.t, s.n); }}
                            style={{ padding: "6px 12px", borderRadius: 20, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            {s.t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {view === "home" && (
              <div style={{ marginTop: 12 }}>

                {/* Market ticker — quiet, ambient context rather than a row
                    of competing cards. The open market (or today's biggest
                    mover on overlap) gets a subtle dark pill; everything
                    else is plain text. */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, overflowX: "auto", paddingBottom: 4, marginBottom: 18 }}>
                  {(function() {
                    var leadName = getLeadMarketName(marketData);
                    var ordered = marketData.slice().sort(function(a, b) {
                      if (a.name === leadName) return -1;
                      if (b.name === leadName) return 1;
                      return 0;
                    });
                    return ordered.map(function(idx) {
                      var priceStr = idx.price > 0 ? idx.price.toLocaleString("en", { maximumFractionDigits: 0 }) : "—";
                      var changeStr = idx.price > 0 ? (idx.up || idx.change >= 0 ? "+" : "") + (typeof idx.change === "number" ? idx.change.toFixed(2) : idx.change) + "%" : "—";
                      var isUp = idx.up || (typeof idx.change === "number" && idx.change >= 0);
                      var isLead = idx.name === leadName;
                      return (
                        <div key={idx.name} style={{ display: "flex", alignItems: "baseline", gap: 6, flexShrink: 0, fontSize: 12.5, background: isLead ? N : "transparent", color: isLead ? WH : undefined, padding: isLead ? "5px 12px" : 0, borderRadius: isLead ? 20 : 0 }}>
                          <span style={{ color: isLead ? "rgba(255,255,255,0.6)" : TXM }}>{idx.name}</span>
                          <span style={{ fontWeight: 700, color: isLead ? WH : TX, fontVariantNumeric: "tabular-nums" }}>{priceStr}</span>
                          <span style={{ fontWeight: 700, color: isLead ? (isUp ? "#5DCAA5" : "#F09595") : (isUp ? OK : ER) }}>{changeStr}</span>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Spotlight — one signature dark card instead of two
                    separate, differently-colored ones (navy AI Pick +
                    purple/gold Deep Dive) competing for the same attention.
                    Tabs let someone choose which they want to see, which
                    also replaces the old one-way "dismiss" behavior on the
                    Deep Dive card — staying on the Today's Pick tab already
                    achieves the same thing more cleanly. */}
                <div style={{ background: N, borderRadius: 18, padding: 18, marginBottom: 18, color: WH, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: -30, right: -30, width: 130, height: 130, borderRadius: "50%", background: "rgba(13,148,136,0.18)" }} />
                  <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: 3, marginBottom: 16, position: "relative" }}>
                    <div onClick={function() { setSpotlightTab("pick"); }} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 8, fontSize: 12, fontWeight: 700, color: spotlightTab === "pick" ? WH : "rgba(255,255,255,0.55)", background: spotlightTab === "pick" ? "rgba(255,255,255,0.14)" : "transparent", cursor: "pointer" }}>
                      Today's pick
                    </div>
                    <div onClick={function() { setSpotlightTab("deepdive"); }} style={{ flex: 1, textAlign: "center", padding: "7px 0", borderRadius: 8, fontSize: 12, fontWeight: 700, color: spotlightTab === "deepdive" ? WH : "rgba(255,255,255,0.55)", background: spotlightTab === "deepdive" ? "rgba(255,255,255,0.14)" : "transparent", cursor: "pointer" }}>
                      Weekly deep dive
                    </div>
                  </div>

                  {spotlightTab === "pick" && (!aiPick ? (
                    <div style={{ position: "relative" }}>
                      <div style={{ height: 18, width: 120, background: "rgba(255,255,255,0.15)", borderRadius: 6, marginBottom: 8 }} />
                      <div style={{ height: 14, width: 180, background: "rgba(255,255,255,0.1)", borderRadius: 6, marginBottom: 8 }} />
                      <div style={{ height: 12, width: "80%", background: "rgba(255,255,255,0.08)", borderRadius: 6 }} />
                      <div style={{ marginTop: 10, height: 32, background: "rgba(255,255,255,0.1)", borderRadius: 8 }} />
                    </div>
                  ) : (function() {
                    var pickCur = CS$[aiPick.cur] || "$";
                    var pickUp = aiPick.live && aiPick.cp >= 0;
                    return (
                      <div style={{ position: "relative" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 22, fontWeight: 800 }}><FlagImg emoji={aiPick.f} size={20} /> {aiPick.t}</div>
                            <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{aiPick.n}</div>
                            {aiPick.live && <span style={{ display: "inline-block", background: "rgba(93,202,165,0.2)", color: "#5DCAA5", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20 }}>{aiPick.v}</span>}
                            {aiPick.reason && (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.5, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 2 }}>Why today's pick</div>
                                <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.5, fontStyle: "italic" }}>{aiPick.reason}</div>
                              </div>
                            )}
                            {aiPick.live && !aiPick.reason && <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8, lineHeight: 1.5 }}>{aiPick.ai}</div>}
                            {!aiPick.live && aiPick.error && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>AI Pick unavailable right now — try reopening the app shortly.</div>}
                            {!aiPick.live && !aiPick.error && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.5 }}>Loading live analysis...</div>}
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                            {aiPick.live && <div style={{ fontSize: 26, fontWeight: 800 }}>{pickCur}{aiPick.p.toLocaleString("en-US")}</div>}
                            {!aiPick.live && !aiPick.error && <div style={{ fontSize: 14, opacity: 0.5 }}>Loading...</div>}
                            {!aiPick.live && aiPick.error && <div style={{ fontSize: 14, opacity: 0.5 }}>—</div>}
                            {aiPick.live && <div style={{ fontSize: 12, fontWeight: 700, color: pickUp ? "#5DCAA5" : "#F09595" }}>{pct(aiPick.cp)}</div>}
                          </div>
                        </div>
                        <button onClick={function() { doSearch(aiPick.t, aiPick.n); }}
                          style={{ marginTop: 14, background: WH, border: "none", borderRadius: 10, padding: "11px 14px", color: N, fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%" }}>
                          View analysis →
                        </button>
                      </div>
                    );
                  })())}

                  {spotlightTab === "deepdive" && (!weeklyInsight ? (
                    <div style={{ fontSize: 12.5, opacity: 0.6, position: "relative", padding: "8px 0" }}>This week's deep dive isn't ready yet — check back shortly.</div>
                  ) : (
                    <div onClick={function() { setView("weeklyInsight"); }} style={{ cursor: "pointer", position: "relative" }}>
                      <div style={{ fontSize: 20, fontWeight: 800 }}><FlagImg emoji={detectFlag(weeklyInsight.ticker, "")} size={18} /> {cleanCompanyName(weeklyInsight.name)}</div>
                      <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 10 }}>{weeklyInsight.ticker} · {weeklyInsight.region}</div>
                      {weeklyInsight.content && weeklyInsight.content.hook && (
                        <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5, marginBottom: 10, fontStyle: "italic" }}>{weeklyInsight.content.hook}</div>
                      )}
                      <div style={{ fontSize: 11, opacity: 0.55, lineHeight: 1.5, marginBottom: 12 }}>Every week we pick a different company from a different region — US, UK, India, Japan, Europe — and go deeper than a valuation score: competitive landscape, growth drivers, risks, and the real thesis.</div>
                      <button style={{ background: "#D4AF37", border: "none", borderRadius: 10, padding: "11px 14px", color: "#1E1240", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%" }}>
                        Read this week's deep dive →
                      </button>
                    </div>
                  ))}
                </div>

                {recent.length === 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: TXM, lineHeight: 1.5 }}>Search any stock, ETF or index. Try a ticker like <span style={{ color: B, fontWeight: 700 }}>AAPL</span>, <span style={{ color: B, fontWeight: 700 }}>SHEL.L</span> or <span style={{ color: B, fontWeight: 700 }}>RELIANCE.NS</span>.</div>
                </div>
                )}

                {/* Activity — Recently Viewed and Watchlist merged into one
                    tabbed section instead of two separate headers back to
                    back saying essentially the same thing ("stuff related
                    to you"). Tabs only appear when both have content; with
                    only one, that one shows directly with no redundant
                    switcher; with neither (a brand new user), the whole
                    section is skipped rather than showing empty tabs. */}
                {(recent.length > 0 || watchlist.length > 0) && (function() {
                  var showTabs = recent.length > 0 && watchlist.length > 0;
                  var effectiveTab = showTabs ? activityTab : (recent.length > 0 ? "recent" : "watchlist");
                  return (
                    <div style={{ marginBottom: 18 }}>
                      {showTabs ? (
                        <div style={{ display: "flex", gap: 16, marginBottom: 12, borderBottom: "1px solid " + BD }}>
                          <div onClick={function() { setActivityTab("recent"); }} style={{ fontSize: 13, fontWeight: 700, color: effectiveTab === "recent" ? N : TX3, paddingBottom: 8, borderBottom: effectiveTab === "recent" ? "2px solid " + N : "2px solid transparent", cursor: "pointer" }}>Recent</div>
                          <div onClick={function() { setActivityTab("watchlist"); }} style={{ fontSize: 13, fontWeight: 700, color: effectiveTab === "watchlist" ? N : TX3, paddingBottom: 8, borderBottom: effectiveTab === "watchlist" ? "2px solid " + N : "2px solid transparent", cursor: "pointer" }}>Watchlist</div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <span style={{ fontSize: 15, fontWeight: 800, color: N }}>{effectiveTab === "recent" ? "Recently viewed" : "Your watchlist"}</span>
                          {effectiveTab === "watchlist" && <button onClick={function() { setView("watchlist"); }} style={{ background: "none", border: "none", color: B, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>View all →</button>}
                        </div>
                      )}

                      {effectiveTab === "recent" && (
                        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
                          {recent.map(function(r) {
                            var rUp = r.cp >= 0;
                            return (
                              <div key={r.t} onClick={function() { doSearch(r.t, r.n); }}
                                style={{ background: WH, borderRadius: 14, padding: 13, minWidth: 128, cursor: "pointer", flexShrink: 0, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 8px rgba(15,23,42,0.05)" }}>
                                <div style={{ fontWeight: 800, fontSize: 13.5, color: N }}><FlagImg emoji={r.f} /> {r.t}</div>
                                <div style={{ fontSize: 11, color: TXM, margin: "3px 0 6px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.n}</div>
                                <div style={{ fontSize: 15, fontWeight: 800, color: TX, fontVariantNumeric: "tabular-nums" }}>{(CS$[r.cur] || "$") + r.p.toFixed(2)}</div>
                                <div style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, fontWeight: 700, color: rUp ? OKT : ERT, background: rUp ? OKS : ERS, borderRadius: 6, padding: "2px 7px", marginTop: 5 }}>
                                  {rUp ? <MdTrendingUp size={11} /> : <MdTrendingDown size={11} />}{pct(r.cp)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {effectiveTab === "watchlist" && watchlist.slice(0, showTabs ? 5 : 3).map(function(w) {
                        return (
                          <div key={w.t} onClick={function() { doSearch(w.t, w.n); }}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + BD, cursor: "pointer" }}>
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontWeight: 800, color: N, fontSize: 14 }}><FlagImg emoji={w.f} /> {w.t}</span>
                              <span style={{ color: TXM, fontSize: 12, marginLeft: 8 }}>{w.n}</span>
                            </div>
                            <span style={{ fontSize: 14, fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{(CS$[w.cur] || "$") + w.p.toFixed(2)}</span>
                          </div>
                        );
                      })}
                      {showTabs && effectiveTab === "watchlist" && watchlist.length > 5 && (
                        <button onClick={function() { setView("watchlist"); }} style={{ background: "none", border: "none", color: B, fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: 8, padding: 0 }}>View all {watchlist.length} →</button>
                      )}
                    </div>
                  );
                })()}

                {/* Browse by Market */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: N, marginBottom: 10 }}>Browse</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {[{ k: "all", l: "All", f: null }, { k: "US", l: "US", f: "🇺🇸" }, { k: "UK", l: "UK", f: "🇬🇧" }, { k: "India", l: "India", f: "🇮🇳" }, { k: "Japan", l: "Japan", f: "🇯🇵" }, { k: "Europe", l: "Europe", f: "🇪🇺" }, { k: "etf", l: "ETFs", f: null }].map(function(m) {
                      var active = marketFilter === m.k;
                      return (
                        <button key={m.k} onClick={function() { setMarketFilter(m.k); }}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 13px", borderRadius: 20, border: "none", background: active ? N : WH, color: active ? WH : TXM, fontSize: 12, fontWeight: 600, cursor: "pointer", boxShadow: active ? "none" : "0 1px 2px rgba(15,23,42,0.04), 0 1px 6px rgba(15,23,42,0.04)" }}>
                          {m.f && <FlagImg emoji={m.f} size={12} />}
                          {m.l}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
                    {filteredStocks.map(function(stk) {
                      var flag = detectFlag(stk.t, "");
                      var vis = sectorVisual(stk.s);
                      return (
                        <div key={stk.t} onClick={function() { doSearch(stk.t, stk.n); }}
                          style={{ background: WH, border: "none", borderRadius: 14, padding: 14, cursor: "pointer", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 1px 8px rgba(15,23,42,0.05)" }}
                          onTouchStart={function(e) { e.currentTarget.style.background = BG; }}
                          onTouchEnd={function(e) { e.currentTarget.style.background = WH; }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 9, background: stk.mono ? "#F2E9D8" : vis.bg, color: stk.mono ? "#8A6D1D" : vis.color, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative", flexShrink: 0 }}>
                              {stk.mono ? (
                                // Monogram badge — used when we've confirmed no
                                // external logo/favicon service reliably resolves
                                // this company (tried Google favicons, DuckDuckGo
                                // icons, and a direct image URL for SoftBank
                                // specifically — none rendered). Pure CSS/text,
                                // so this can never fail to display, unlike a
                                // third-party image request.
                                <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3 }}>{stk.mono}</span>
                              ) : stk.d ? (
                                <img src={"/logos/" + stk.t + ".png"} alt="" loading="lazy"
                                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: 5, background: WH }}
                                  onLoad={function(e) {
                                    if (e.target.naturalWidth > 0 && e.target.naturalWidth < 16) {
                                      e.target.dataset.local = "failed";
                                      e.target.src = "https://www.google.com/s2/favicons?domain=" + stk.d + "&sz=64";
                                    }
                                  }}
                                  onError={function(e) {
                                    // Local file missing (404) — this only
                                    // happens for a company added after the
                                    // last logo-download batch. Fall back to
                                    // the same live-fetch chain used before:
                                    // Google favicons, then DuckDuckGo, then
                                    // the generic sector icon. Google's
                                    // favicon service doesn't always error out
                                    // when it can't resolve a domain — for a
                                    // lot of "hit or miss" cases it instead
                                    // returns a normal 200 with a tiny
                                    // generic/blank placeholder, which
                                    // onError alone can't catch, hence the
                                    // naturalWidth check in onLoad too.
                                    if (e.target.dataset.local !== "failed" && !e.target.dataset.fallback) {
                                      e.target.dataset.local = "failed";
                                      e.target.dataset.fallback = "1";
                                      e.target.src = "https://www.google.com/s2/favicons?domain=" + stk.d + "&sz=64";
                                    } else if (!e.target.dataset.ddg) {
                                      e.target.dataset.ddg = "1";
                                      e.target.src = "https://icons.duckduckgo.com/ip3/" + stk.d + ".ico";
                                    } else {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display = "flex";
                                    }
                                  }} />
                              ) : null}
                              <div style={{ width: "100%", height: "100%", display: (stk.mono || stk.d) ? "none" : "flex", alignItems: "center", justifyContent: "center", position: (stk.mono || stk.d) ? "absolute" : "static", top: 0, left: 0 }}>
                                <vis.Icon size={17} />
                              </div>
                            </div>
                            <span style={{ fontSize: 15 }}><FlagImg emoji={flag} /></span>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: N, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{stk.n}</div>
                          <div style={{ fontSize: 11, color: TX3, marginBottom: 8 }}>{stk.t}</div>
                          <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, color: TXM, background: BG, borderRadius: 20, padding: "3px 9px" }}>{stk.s}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ padding: "22px 10px 4px", textAlign: "center" }}>
                  <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
                </div>

              </div>
            )}
          </div>
        )}

        {/* Stock Result */}
        {view === "result" && stock && !loading && (
          <div className="fi" style={{ paddingBottom: 32 }}>

            {/* Not found / error state */}
            {stock.notFound && stock.notFoundReason === "error" && (
              <div style={Object.assign({}, card, { padding: 28, textAlign: "center", marginBottom: 12 })}>
                <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><MdRefresh size={36} color={TX3} /></div>
                <div style={{ fontSize: 17, fontWeight: 700, color: N, marginBottom: 8 }}>Something went wrong</div>
                <p style={{ fontSize: 13, color: TXM, lineHeight: 1.65, marginBottom: 20 }}>
                  We had trouble reaching live data just now — this is usually temporary and not a problem with "{stock.t}" itself.
                </p>
                <button onClick={function() { setStock(null); doSearch(stock.t); }} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Try again
                </button>
              </div>
            )}
            {stock.notFound && stock.notFoundReason !== "error" && (
              <div style={Object.assign({}, card, { padding: 28, textAlign: "center", marginBottom: 12 })}>
                <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><MdSearchOff size={36} color={TX3} /></div>
                <div style={{ fontSize: 17, fontWeight: 700, color: N, marginBottom: 8 }}>"{stock.t}" not found</div>
                <p style={{ fontSize: 13, color: TXM, lineHeight: 1.65, marginBottom: 20 }}>
                  We couldn't find live data for this ticker. Make sure you're using the correct exchange suffix:
                </p>
                <div style={{ background: BG, borderRadius: 10, padding: 14, textAlign: "left", marginBottom: 20 }}>
                  {[["🇮🇳 Indian stocks", "add .NS — e.g. HDFCBANK.NS, RELIANCE.NS"],["🇬🇧 UK stocks","add .L — e.g. SHEL.L, AZN.L"],["🇯🇵 Japanese stocks","add .T — e.g. 7203.T"],["🇺🇸 US stocks","no suffix needed — e.g. AAPL, MSFT"]].map(function(r,i){
                    return <div key={i} style={{ fontSize: 12, color: TX2, padding: "4px 0", borderBottom: i < 3 ? "1px solid " + BD : "none" }}><strong>{r[0]}:</strong> {r[1]}</div>;
                  })}
                </div>
                <button onClick={function() { setView("home"); setStock(null); setQuery(""); }} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Try another search
                </button>
              </div>
            )}

            {/* Normal result — only show when found */}
            {!stock.notFound && (<div>
            <div style={{ background: N, borderRadius: 20, padding: "26px 20px 24px", marginBottom: 12, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%", background: "rgba(13,148,136,0.25)", pointerEvents: "none" }} />
              <div style={{ position: "absolute", bottom: -80, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(37,84,217,0.18)", pointerEvents: "none" }} />

              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, position: "relative" }}>
                    <img src={"/logos/" + stock.t + ".png"} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
                      onError={function(e) { e.target.style.display = "none"; var m = e.target.nextSibling; if (m) m.style.display = "flex"; }} />
                    <div style={{ display: "none", width: "100%", height: "100%", alignItems: "center", justifyContent: "center", position: "absolute", inset: 0, background: B, color: WH, fontSize: 15, fontWeight: 800 }}>
                      {stock.t.replace(/\..*/, "").slice(0, 4)}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <FlagImg emoji={stock.f} size={15} />
                      <span style={{ fontSize: 17, fontWeight: 700, color: WH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stock.n}</span>
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{[stock.t, stock.x, stock.s].filter(function(v) { return v && v !== "Unknown"; }).join(" · ")}</div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 34, fontWeight: 700, color: WH, borderRadius: 6, animation: priceFlash === "up" ? "flashUp 1.2s ease" : priceFlash === "down" ? "flashDn 1.2s ease" : "none" }}>
                    {(CS$[stock.cur] || "$") + stock.p.toFixed(2)}
                  </span>
                  <span style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontSize: 15, fontWeight: 600, color: stock.cp >= 0 ? "#5DCAA5" : "#F0997B" }}>
                    {stock.cp >= 0 ? "▲" : "▼"} {Math.abs(stock.ch).toFixed(2)} ({pct(stock.cp)})
                  </span>
                </div>

                {(function() {
                  var fv = stock.dcf || stock.justifiedPB;
                  if (!fv || fv.intrinsicValue == null) return null;
                  var fvSym = CS$[stock.cur] || "$", fvUp = fv.upside >= 0;
                  return (
                    <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>
                      Est. intrinsic value: <span style={{ fontFamily: "SFMono-Regular, Consolas, monospace", fontWeight: 700, color: fvUp ? "#5DCAA5" : "#F0997B" }}>{fvSym}{fv.intrinsicValue.toFixed(2)}</span>
                      <span style={{ color: fvUp ? "#5DCAA5" : "#F0997B" }}> ({fvUp ? "+" : ""}{fv.upside}%)</span>
                    </div>
                  );
                })()}

                {(function() {
                  var vCol = stock.v === "UNDERVALUED" ? "#5DCAA5" : stock.v === "OVERVALUED" ? "#F0997B" : "#F0C36B";
                  var vLabel = stock.v === "UNDERVALUED" ? "Undervalued" : stock.v === "OVERVALUED" ? "Overvalued" : "Fairly Valued";
                  var isLow = stock.cn === "LOW", isMed = stock.cn === "MEDIUM";
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          borderRadius: 8, padding: "6px 14px", fontSize: 14, fontWeight: 700, color: vCol,
                          background: isLow ? "transparent" : vCol + "26",
                          border: isLow ? "1px dashed " + vCol + "60" : isMed ? "1px solid " + vCol + "60" : "1px solid " + vCol + "40",
                          opacity: isLow ? 0.8 : 1,
                        }}>{vLabel}</span>
                        <span style={{ fontSize: 11.5, color: isLow ? "#F0C36B" : "rgba(255,255,255,0.45)", fontWeight: isLow ? 700 : 400 }}>
                          {isLow ? "⚠ Low confidence" : isMed ? "Medium confidence" : "High confidence"}
                        </span>
                      </div>
                      <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>Based on the three scores below</div>
                      {stock.cr && <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,0.55)", lineHeight: 1.5, fontStyle: "italic" }}>{stock.cr}</div>}
                      {(function() {
                        // Co-located with the verdict pill itself, not just
                        // buried inside the DCF/Justified P/B card — this is
                        // where a user's eye actually lands first, so this is
                        // where the "why don't these agree" explanation needs
                        // to be. Fires whenever DCF/Justified P/B's own label
                        // disagrees with the overall verdict above, in either
                        // direction (not just full-opposite-poles).
                        var fv2 = stock.dcf || stock.justifiedPB;
                        if (!fv2 || fv2.upside == null) return null;
                        var fvLabel = fv2.upside > 20 ? "UNDERVALUED" : fv2.upside > 0 ? "FAIRLY VALUED" : "OVERVALUED";
                        if (fvLabel === stock.v) return null;
                        var fvName = stock.dcf ? "DCF" : "Justified P/B";
                        var fvLabelText = fvLabel === "UNDERVALUED" ? "undervalued" : fvLabel === "OVERVALUED" ? "overvalued" : "fairly valued";
                        return (
                          <div style={{ marginTop: 8, fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
                            {fvName} alone points to {fvLabelText} — the verdict above weighs it alongside PEG, sector P/E, analyst consensus and more, not just this one signal. <a href={stock.dcf ? "#dcf" : "#justifiedpb"} onClick={function(e) {
                              e.preventDefault();
                              var targetId = stock.dcf ? "dcf" : "justifiedpb";
                              openGroup("valuation");
                              openSection(targetId);
                              requestAnimationFrame(function() {
                                requestAnimationFrame(function() {
                                  var el = document.getElementById(targetId);
                                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                });
                              });
                            }} style={{ color: "#5DCAA5" }}>See the full breakdown ↓</a>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {stock.ins && (
                  <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4, background: stock.ins.signal === "buying" ? "rgba(93,202,165,0.15)" : "rgba(240,153,123,0.15)", borderRadius: 8, padding: "3px 8px" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: stock.ins.signal === "buying" ? "#5DCAA5" : "#F0997B", display: "inline-block" }} />
                    <span style={{ fontSize: 10, fontWeight: 600, color: stock.ins.signal === "buying" ? "#5DCAA5" : "#F0997B" }}>{stock.ins.label}</span>
                  </div>
                )}
                {stock.fetchedAt && (
                  <div style={{ marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                    Updated {(function() {
                      var diff = Date.now() - stock.fetchedAt;
                      var mins = Math.round(diff / 60000);
                      if (mins < 1) return "just now";
                      if (mins < 60) return mins + "m ago";
                      return Math.round(mins / 60) + "h ago";
                    })()}
                  </div>
                )}

                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 18, paddingTop: 18 }}>
                  <PillarRow stock={stock} openSections={openSections} setOpenSections={setOpenSections}
                    jumpToDcf={function() {
                      openGroup("valuation");
                      openSection("dcf");
                      requestAnimationFrame(function() {
                        requestAnimationFrame(function() {
                          var el = document.getElementById("dcf");
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      });
                    }}
                    jumpToJustifiedPB={function() {
                      openGroup("valuation");
                      openSection("justifiedpb");
                      requestAnimationFrame(function() {
                        requestAnimationFrame(function() {
                          var el = document.getElementById("justifiedpb");
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      });
                    }}
                  />
                </div>
              </div>
            </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button onClick={function() {
                  if (!stock) return;
                  if (isWatched) { setWatchlist(function(w) { return w.filter(function(x) { return x.t !== stock.t; }); }); setToast("Removed"); haptic(10); }
                  else {
                    var wlLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? Infinity : 3;
                    if (watchlist.length >= wlLimit) { setToast("Standard watchlist holds 3 stocks at a time — remove one to add another, or upgrade to Pro for unlimited."); return; }
                    setWatchlist(function(w) { return [Object.assign({}, stock, { earnings: stock.er || [] })].concat(w); }); setToast("Added to watchlist"); haptic([10, 50, 10]);
                  }
                }} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid " + (isWatched ? OK : BD), background: isWatched ? OKS : WH, color: isWatched ? OKT : TXM, fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {isWatched ? <MdStar size={14} /> : <MdStarBorder size={14} />} {isWatched ? "Watching" : "Watch"}
                </button>
                <button onClick={function() {
                  if (!stock) return;
                  setStickyToast("Refreshing…");
                  analyseStockFn({ ticker: stock.t, forceRefresh: true }).then(function(result) {
                    var d = result.data;
                    var cur = normCurrency(d.currency) || detectCurrency(d.ticker || stock.t, d.exchange);
                    var flag = d.flag || detectFlag(d.ticker || stock.t, d.exchange);
                    function safeMetric(v) { return (!v || v === "0.0" || v === "N/A") ? "N/A" : v; }
                    setStock({
                      t: d.ticker || stock.t, n: d.company || stock.t, s: d.sector || "Unknown", x: d.exchange || "Unknown", c: d.country || "—", f: flag, p: d.price || 0, cur: cur, ch: d.change || 0, cp: d.changePct || 0,
                      m: { pe: d.pe, fpe: d.forwardPE, peg: d.peg, mc: d.marketCap, bt: d.beta, dy: d.dividendYield, hi: d.high52w, lo: d.low52w, at: d.analystTarget || "N/A", roe: d.roe, roa: d.roa, pb: safeMetric(d.pb), ps: safeMetric(d.ps), rg: d.revenueGrowth || "N/A", eg: d.epsGrowth || "N/A", fcf: d.fcf || "N/A", gm: d.grossMargin || "N/A", om: d.operatingMargin || "N/A", nm: d.netProfitMargin || "N/A", de: d.debtToEquity || "N/A", evEbitda: d.evEbitda || "N/A" },
                      pillars: d.pillars || null,
                      v: d.verdict || "FAIRLY VALUED", cn: d.confidence || "LOW", cr: d.confidence_reason || "", sc: d.score || 50, ai: d.summary || null, bl: d.bull_case || null, br: d.bear_case || null, ct: d.catalysts || [], er: d.earnings || [], sb: d.score_breakdown || [],
                      at: d.auto_thesis || null, aiOff: d.ai_unavailable || false, aiOffReason: d.ai_unavailable_reason || null,
                      sc2: d.sectorComparison || null, similar: d.similarCompanies || [], dcf: d.dcf || null, justifiedPB: d.justifiedPB || null, qs: d.qualityScore || null, mom: d.momentum || null,
                      vh: d.valuationHistory || null, eq: d.earningsQuality || null, si: d.shortInterest || null,
                      ac: d.analystConsensus || null,
                      ins: d.insider || null, sc_chg: d.sharesChange || null,
                      industry: d.industry || null, about: d.businessSummary || null, employees: d.employees || null, website: d.website || null,
                      fetchedAt: d.fetchedAt || Date.now()
                    });
                    // Flash price animation
                    setPriceFlash(d.changePct >= 0 ? "up" : "down");
                    setTimeout(function() { setPriceFlash(null); }, 1200);
                    setStickyToast(null);
                    setToast(d.ai_unavailable ? "Updated — AI analysis unavailable this time" : "Updated!");
                  }).catch(function() { setStickyToast(null); setToast("Refresh failed"); });
                }} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 13, cursor: "pointer" }}>
                  ↻ Refresh
                </button>
                <button onClick={function() {
                  if (!stock) return;
                  var sym = CS$[stock.cur] || "$";
                  var shareText = stock.t + " — " + stock.v + " (Score: " + stock.sc + "/100)\n" + stock.n + " · " + sym + stock.p.toFixed(2) + " · " + (stock.cp >= 0 ? "+" : "") + stock.cp.toFixed(2) + "%\n\n" + (stock.ai || "") + "\n\nView on Zoltra: https://zoltra.app";
                  if (navigator.share) {
                    navigator.share({ title: stock.t + " on Zoltra", text: shareText }).catch(function() {});
                  } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(shareText).then(function() { setToast("Copied to clipboard"); }).catch(function() {});
                  }
                }} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 16, cursor: "pointer" }} title="Share">
                  <MdShare size={16} />
                </button>
                <button onClick={function() {
                  if (!stock) return;
                  var sym = CS$[stock.cur] || "$";
                  var msg = "📊 *" + stock.t + " — " + stock.v + "* (Score: " + stock.sc + "/100)\n" + stock.n + " · " + sym + stock.p.toFixed(2) + " · " + (stock.cp >= 0 ? "+" : "") + stock.cp.toFixed(2) + "%\n\n" + (stock.ai || "") + "\n\nView on Zoltra: https://zoltra.app";
                  try {
                    localStorage.setItem("q_wa_return", JSON.stringify({
                      stock: stock,
                      hist: hist ? hist.map(function(h) { return { date: h.date.getTime(), price: h.price }; }) : null,
                      ts: Date.now()
                    }));
                  } catch(e) {}
                  // Same fix used on Waltra for the identical issue: prefer
                  // the native OS share sheet when available (lets the user
                  // pick WhatsApp themselves, sidesteps wa.me entirely), and
                  // when falling back to wa.me, use a real clicked <a>
                  // element rather than window.open/location.href — this is
                  // what actually avoided the white-screen-with-close-button
                  // issue on Waltra, where both of those didn't.
                  if (navigator.share) {
                    navigator.share({ text: msg }).catch(function() {});
                    return;
                  }
                  var a = document.createElement("a");
                  a.href = "https://wa.me/?text=" + encodeURIComponent(msg);
                  a.target = "_blank"; a.rel = "noopener noreferrer";
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                }} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: "#25D366", fontSize: 16, cursor: "pointer" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={function() {
                  setShowInvest(true);
                }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Mock Invest
                </button>
              </div>


            {/* Quick nav — jumps directly to a section, force-opening its group first
                if collapsed, then scrolling once the DOM has actually expanded
                (double-rAF avoids racing the expand, same fix as the earlier
                view-change scroll-reset issue). */}
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, marginBottom: 14 }}>
              {[
                { l: "Valuation", group: "valuation", target: "group-valuation" },
                { l: "Financials", group: "financials", target: "group-financials" },
                { l: "Additional", group: "risk", target: "group-additional" },
                { l: "AI Analysis", group: "ai", target: "ai" },
                { l: "Notes", section: "notes", target: "notes" },
              ].map(function(n) {
                return (
                  <button key={n.target} onClick={function() {
                    if (n.group) openGroup(n.group);
                    if (n.section) openSection(n.section);
                    requestAnimationFrame(function() {
                      requestAnimationFrame(function() {
                        var el = document.getElementById(n.target);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      });
                    });
                  }} style={{ flexShrink: 0, padding: "6px 13px", borderRadius: 20, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {n.l}
                  </button>
                );
              })}
            </div>

            {/* About the company — foundational context, shown before the deeper analysis tiles */}
            {stock.about && (function() {
              var truncateAt = 260;
              var isLong = stock.about.length > truncateAt;
              var displayText = aboutExpanded || !isLong ? stock.about : stock.about.slice(0, truncateAt) + "…";
              return (
                <div style={Object.assign({}, card, { padding: 16, marginBottom: 8 })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <MdDomain size={16} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: N }}>About {stock.n}</div>
                  </div>
                  <p style={{ fontSize: 13, color: TX2, lineHeight: 1.6, margin: "0 0 10px" }}>{displayText}</p>
                  {isLong && (
                    <button onClick={function() { setAboutExpanded(!aboutExpanded); }} style={{ background: "none", border: "none", color: B, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0, marginBottom: 10 }}>
                      {aboutExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {stock.industry && <span style={{ fontSize: 11, fontWeight: 700, color: TXM, background: BG, borderRadius: 20, padding: "3px 10px" }}>{stock.industry}</span>}
                    {stock.employees && <span style={{ fontSize: 11, fontWeight: 700, color: TXM, background: BG, borderRadius: 20, padding: "3px 10px" }}>{stock.employees.toLocaleString("en-US")} employees</span>}
                    {stock.website && <a href={stock.website.startsWith("http") ? stock.website : "https://" + stock.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 700, color: B, background: BW, borderRadius: 20, padding: "3px 10px", textDecoration: "none" }}>Website ↗</a>}
                  </div>
                </div>
              );
            })()}

            {/* -- Grouped research sections -- */}

            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 8 }}>

            <GroupCard id="group-valuation" icon={<MdShowChart size={17} />} title="Valuation deep-dive" subtitle="Price history, key metrics, sector comparison, fair value" open={openGroups.valuation} onToggle={function() { togGroup("valuation"); }}>
            {/* Chart */}
            <div style={Object.assign({}, card, { padding: 14, marginBottom: 8 })}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: N }}>Price</span>
                  {periodChange && <span style={{ fontSize: 13, fontWeight: 600, color: periodChange.pct >= 0 ? OK : ER }}>{pct(periodChange.pct)} ({rangeMap[chartRange]})</span>}
                </div>
                <div style={{ display: "flex", background: BG, borderRadius: 6, padding: 2 }}>
                  {[{ l: "1M", v: 30 }, { l: "3M", v: 90 }, { l: "6M", v: 180 }, { l: "1Y", v: 365 }, { l: "5Y", v: 1825 }].map(function(r) {
                    return <button key={r.v} onClick={function() { setChartRange(r.v); }} style={segBtn(chartRange === r.v)}>{r.l}</button>;
                  })}
                </div>
              </div>
              {hist === null ? (
                <ChartSkeleton />
              ) : (
                <Chart data={chartData} color={periodChange && periodChange.pct >= 0 ? OK : ER} currency={stock.cur} benchmarkData={showRelStrength ? (function() {
                  if (!benchmarkData) return null;
                  var cutoff = Date.now() - chartRange * 86400000;
                  return benchmarkData.filter(function(b) { return b.d >= cutoff; });
                })() : null} />
              )}
              {benchmarkData && benchmarkName && (
                <button onClick={function() { setShowRelStrength(function(v) { return !v; }); }}
                  style={{ marginTop: 8, fontSize: 11, color: showRelStrength ? B : TX3, background: showRelStrength ? BW : "none", border: "1px solid " + (showRelStrength ? B : BD), borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontWeight: 600 }}>
                  {showRelStrength ? "vs " + benchmarkName + " ✓" : "vs " + benchmarkName}
                </button>
              )}
            </div>
            {/* Metrics */}
            <div style={Object.assign({}, card, { padding: 14, marginBottom: 8 })}>
              <div style={{ display: "flex", background: BG, borderRadius: 6, padding: 2, marginBottom: 10 }}>
                {[{ k: "val", l: "Valuation" }, { k: "grow", l: "Growth" }, { k: "risk", l: "Risk" }].map(function(t) {
                  return <button key={t.k} onClick={function() { setMetricTab(t.k); try { sessionStorage.setItem("q_metric_tab", t.k); } catch(e) {} }} style={segBtn(metricTab === t.k)}>{t.l}</button>;
                })}
              </div>
              {(metricGroups[metricTab] || []).map(function(m) {
                var val = stock.m ? stock.m[m[1]] : null;
                var na = !val || val === "N/A";
                var extra = null;

                // Analyst target — show upside/downside %
                if (m[1] === "at" && !na && stock.p) {
                  var tgt = parseFloat(val);
                  if (!isNaN(tgt) && tgt > 0) {
                    var upside = ((tgt - stock.p) / stock.p) * 100;
                    extra = <span style={{ fontSize: 11, color: upside >= 0 ? OK : ER, fontWeight: 600, marginLeft: 6 }}>({upside >= 0 ? "+" : ""}{upside.toFixed(1)}%)</span>;
                  }
                }

                // Dividend yield — add colour
                if (m[1] === "dy" && !na) {
                  var dyNum = parseFloat(val);
                  extra = dyNum > 0 ? <span style={{ fontSize: 11, color: OKT, marginLeft: 6 }}>●</span> : null;
                }

                return (
                  <div key={m[1]} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + BD }}>
                    <span style={{ color: TXM, fontSize: 14 }}>{m[0]}</span>
                    <span style={{ color: na ? TX3 : TX, fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center" }}>
                      {na ? "—" : val}{extra}
                    </span>
                  </div>
                );
              })}

              {/* 52w Range bar — always shown at bottom of any tab */}
              {stock.m && stock.m.hi && stock.m.lo && stock.p && (function() {
                var hi = parseFloat(stock.m.hi), lo = parseFloat(stock.m.lo);
                var sym = CS$[stock.cur] || "$";
                if (isNaN(hi) || isNaN(lo) || hi <= lo) return null;
                var pos = Math.min(Math.max((stock.p - lo) / (hi - lo), 0), 1);
                return (
                  <div style={{ paddingTop: 10, marginTop: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: TXM }}>52-week range</span>
                      <span style={{ fontSize: 11, color: TX3 }}>{sym}{lo} — {sym}{hi}</span>
                    </div>
                    <div style={{ position: "relative", height: 6, background: BD, borderRadius: 3 }}>
                      <div style={{ position: "absolute", left: 0, width: (pos * 100) + "%", height: "100%", background: pos > 0.7 ? OK : pos < 0.3 ? ER : WN, borderRadius: 3 }} />
                      <div style={{ position: "absolute", left: (pos * 100) + "%", top: -3, transform: "translateX(-50%)", width: 12, height: 12, borderRadius: "50%", background: N, border: "2px solid " + WH, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: TX3 }}>52w Low</span>
                      <span style={{ fontSize: 10, color: N, fontWeight: 600 }}>{Math.round(pos * 100)}% of range</span>
                      <span style={{ fontSize: 10, color: TX3 }}>52w High</span>
                    </div>
                  </div>
                );
              })()}

              {/* Analyst consensus bar — shown if available */}
              {stock.ac && stock.ac.total > 0 && (function() {
                var ac = stock.ac;
                var buyPct = Math.round(((ac.strongBuy + ac.buy) / ac.total) * 100);
                var holdPct = Math.round((ac.hold / ac.total) * 100);
                var sellPct = 100 - buyPct - holdPct;
                return (
                  <div style={{ paddingTop: 10, marginTop: 4, borderTop: "1px solid " + BD }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: TXM }}>Analyst consensus</span>
                      <span style={{ fontSize: 11, color: TX3 }}>{ac.total} analysts</span>
                    </div>
                    <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", marginBottom: 5 }}>
                      <div style={{ width: buyPct + "%", background: OK }} />
                      <div style={{ width: holdPct + "%", background: WN }} />
                      <div style={{ width: sellPct + "%", background: ER }} />
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                      <span style={{ fontSize: 10, color: OKT }}>{buyPct}% Buy</span>
                      <span style={{ fontSize: 10, color: WN }}>{holdPct}% Hold</span>
                      <span style={{ fontSize: 10, color: ERT }}>{sellPct}% Sell</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Sticky section jump-nav — sticks below the main app header once
                scrolled past this point. With 16 possible sections on this
                page (even collapsed), scrolling to find "DCF" vs "Momentum"
                by hand gets old fast. Uses SecCard's own id (already a real
                DOM id, already has scrollMarginTop set for this exact
                purpose) rather than needing a separate ref per section. */}
            <div style={{ position: "sticky", top: 52, zIndex: 40, background: BG, marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", padding: "8px 0", WebkitOverflowScrolling: "touch" }}>
                {[
                  ["sector", "Sector"], ["valhistory", "Val. History"], ["dcf", "DCF"],
                  ["earnings", "Earnings"], ["pillar_quality_tip", "Quality"], ["ai", "Momentum"],
                  ["ai", "AI Analysis"], ["thesis", "Thesis"],
                ].map(function(s) {
                  return (
                    <button key={s[0]} onClick={function() {
                      setOpenSections(function(prev) { return Object.assign({}, prev, { [s[0]]: true }); });
                      requestAnimationFrame(function() {
                        requestAnimationFrame(function() {
                          var el = document.getElementById(s[0]);
                          if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
                          else { window.scrollTo({ top: 0, behavior: "smooth" }); } // Quality's tip has no DOM id of its own — it lives in the hero at the top of the page
                        });
                      });
                    }} style={{ flexShrink: 0, padding: "6px 12px", borderRadius: 20, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                      {s[1]}
                    </button>
                  );
                })}
              </div>
            </div>

            {stock.sc2 && stock.sc2.metrics && stock.sc2.metrics.length > 0 && (
              <SecCard id="sector" title="Sector Comparison" badge={stock.sc2.percentile != null ? "top " + (100 - stock.sc2.percentile) + "%" : null} badgeColor={stock.sc2.percentile >= 75 ? "green" : stock.sc2.percentile >= 50 ? "amber" : "red"} open={openSections.sector} onToggle={function() { tog("sector"); }}>
                {stock.sc2.percentile != null && <div style={{ fontSize: 13, fontWeight: 700, color: TX, marginBottom: 4 }}>Better than {stock.sc2.percentile}% of {stock.sc2.sector} peers on these metrics.</div>}
                <div style={{ fontSize: 11, color: TXM, marginBottom: stock.sc2.peerTickers && stock.sc2.peerTickers.length > 0 ? 4 : 10 }}>
                  {stock.sc2.isRegional
                    ? "vs " + stock.sc2.region + " " + stock.sc2.sector + " peers (" + stock.sc2.sampleSize + " stocks tracked)"
                    : "vs " + stock.sc2.sector + " sector averages (global)"}
                </div>
                {stock.sc2.peerTickers && stock.sc2.peerTickers.length > 0 && (
                  <div style={{ fontSize: 11, color: TX3, marginBottom: 10 }}>Includes: {stock.sc2.peerTickers.join(", ")}{stock.sc2.sampleSize > stock.sc2.peerTickers.length ? " and others" : ""}</div>
                )}
                {stock.sc2.metrics.map(function(m, i) {
                  return (
                    <div key={i} style={{ paddingBottom: 8, marginBottom: i < stock.sc2.metrics.length - 1 ? 4 : 0, borderBottom: i < stock.sc2.metrics.length - 1 ? "1px solid " + BD : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: TXM }}>{m.label}</span>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: TX, fontFamily: MONO }}>{m.stock}</span>
                          <span style={{ fontSize: 11, color: TX3, fontFamily: MONO }}>vs {m.avg}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, background: m.better ? OKS : ERS, color: m.better ? OKT : ERT, borderRadius: 4, padding: "1px 6px" }}>{m.better ? "better" : "worse"}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </SecCard>
            )}

            <SecCard id="valhistory" title="Valuation History" badge={stock.vh && stock.vh.metrics && stock.vh.metrics.length > 0 ? stock.vh.summary : "limited data"} badgeColor={stock.vh && stock.vh.metrics && stock.vh.metrics.length > 0 ? stock.vh.summaryColor : "amber"} open={openSections.valhistory} onToggle={function() { tog("valhistory"); }}>
              {stock.vh && stock.vh.metrics && stock.vh.metrics.length > 0 ? (
                <div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: TXM, flex: 1 }}>Current metrics vs this stock's own {stock.vh.yearsUsed}-year average — cheap or expensive for itself?</span>
                  <button onClick={function(e2) { e2.stopPropagation(); tog("vh_tip"); }} style={{ width: 17, height: 17, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>i</button>
                </div>
                {openSections.vh_tip && (
                  <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.6, marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, color: TX }}>How it's calculated: </span>Real historical prices (matched to each fiscal year-end) divided by that year's actual EPS, revenue-per-share, and book-value-per-share — averaged across the last {stock.vh.yearsUsed} years of annual filings available. Not a forecast, and share-count changes between years (buybacks, issuance) aren't fully adjusted for — use as a directional guide, not an exact figure.
                  </div>
                )}
                {stock.vh.metrics.map(function(m, i) {
                  var range = (m.high - m.low) || 1;
                  var pos = Math.min(Math.max((m.current - m.low) / range, 0), 1);
                  var avgPos = Math.min(Math.max((m.avg - m.low) / range, 0), 1);
                  var barCol = m.cheaper ? OK : m.pricier ? ER : TXM;
                  return (
                    <div key={i} style={{ marginBottom: i < stock.vh.metrics.length - 1 ? 12 : 0, paddingBottom: i < stock.vh.metrics.length - 1 ? 12 : 0, borderBottom: i < stock.vh.metrics.length - 1 ? "1px solid " + BD : "none" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: TXM }}>{m.label}</span>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: TX, fontFamily: MONO }}>{m.current}</span>
                          <span style={{ fontSize: 11, color: TX3, fontFamily: MONO }}>vs avg {m.avg}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, background: m.cheaper ? OKS : m.pricier ? ERS : BG, color: m.cheaper ? OKT : m.pricier ? ERT : TXM, borderRadius: 4, padding: "1px 6px" }}>
                            {m.diffPct !== null ? (m.diffPct > 0 ? "+" : "") + m.diffPct + "%" : "inline"}
                          </span>
                        </div>
                      </div>
                      <div style={{ height: 5, background: BG, borderRadius: 3, position: "relative", overflow: "visible" }}>
                        <div style={{ height: "100%", width: (pos * 100) + "%", background: barCol, borderRadius: 3 }} />
                        <div style={{ position: "absolute", top: -2, left: (avgPos * 100) + "%", width: 2, height: 9, background: TX3, borderRadius: 1 }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                        <span style={{ fontSize: 10, color: TX3 }}>low {m.low}</span>
                        <span style={{ fontSize: 10, color: TX3 }}>▲ {stock.vh.yearsUsed}yr avg</span>
                        <span style={{ fontSize: 10, color: TX3 }}>high {m.high}</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10, color: TX3, marginTop: 10 }}>▲ marks the {stock.vh.yearsUsed}-year average from actual filings. Bar left of marker = cheaper than usual.</div>
                {((stock.v === "UNDERVALUED" && stock.vh.summary === "pricier than usual") || (stock.v === "OVERVALUED" && stock.vh.summary === "cheaper than usual")) && (
                  <div style={{ marginTop: 8, background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 10.5, color: TXM, lineHeight: 1.5 }}>
                    Valuation History compares this stock only to its own past — it doesn't feed into the overall verdict, which weighs several other factors together (sector-relative pricing, analyst consensus, momentum, quality, fair value estimate). It's possible for a stock to be pricier than its own history while still scoring as undervalued relative to its sector or fair value — and vice versa.
                  </div>
                )}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "12px 8px" }}>
                  <div style={{ fontSize: 13, color: TXM, marginBottom: 6 }}>Not enough historical data for {stock.t} yet.</div>
                  <div style={{ fontSize: 11, color: TX3, lineHeight: 1.6 }}>
                    This needs at least 2 years of real matched price-and-earnings history to show honestly — common for recently-listed stocks or ones with limited filing history. This isn't a bug; showing a made-up average here would be worse than showing nothing.
                  </div>
                </div>
              )}
            </SecCard>

            {stock.dcf && (function() {
              var d = stock.dcf, sym = CS$[stock.cur] || "$", upside = d.upside >= 0;
              var oneYearUpside = d.oneYearUpside != null ? d.oneYearUpside >= 0 : upside;
              var badgeUp = d.oneYearUpside != null ? oneYearUpside : upside;
              // Soften the badge to amber (instead of alarm-red) when this
              // DCF carries little weight in the overall verdict for this
              // stock's sector, or the model itself flagged low confidence
              // — a big red "-41.8% Overvalued" next to a "Fairly Valued"
              // verdict pill reads as a broken/contradictory app rather
              // than the deliberate weighting choice it actually is.
              var dcfSig = stock.sb && stock.sb.find(function(f) { return f.factor === "DCF Upside"; });
              var dcfTotW = (stock.sb || []).reduce(function(sum, s) { return sum + (s.weight || 0); }, 0);
              var dcfWPct = (dcfSig && dcfTotW) ? (dcfSig.weight / dcfTotW) * 100 : null;
              var dcfMuted = !badgeUp && ((dcfWPct != null && dcfWPct < 15) || d.confidence === "low");
              return (
                <SecCard id="dcf" title="1-Year Target" badge={d.oneYearUpside != null ? ((oneYearUpside ? "↑ " : "↓ ") + Math.abs(d.oneYearUpside) + "% " + (oneYearUpside ? "upside" : "downside")) : ((upside ? "↑ " : "↓ ") + Math.abs(d.upside) + "% " + (upside ? "upside" : "downside"))} badgeColor={badgeUp ? "green" : (dcfMuted ? "amber" : "red")} open={openSections.dcf} onToggle={function() { tog("dcf"); }}>
                  <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>
                    Based on a discounted cash flow (DCF) model — projecting the company's future cash flows and discounting them back to today's value. The 1-year target assumes the price moves partway toward that fair value over the next year, not all at once — markets re-rate gradually, not instantly.
                  </div>
                  {d.confidence === "low" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#FFF4E5", border: "1px solid #F0C36D", borderRadius: 8, padding: "7px 10px", marginBottom: 10 }}>
                      <span style={{ fontSize: 14 }}>⚠️</span>
                      <span style={{ fontSize: 11, color: "#8A5A00", lineHeight: 1.4 }}>Lower confidence: {d.isProjected ? "cash flow data was too sparse, so this uses a revenue-margin proxy instead" : "the growth assumption is pinned at its ceiling for this stock"} — treat this figure as a rougher estimate.</span>
                    </div>
                  )}
                  <button onClick={function(e2) { e2.stopPropagation(); setShowDcfMethodology(true); }}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: BG, border: "none", borderRadius: 8, padding: "7px 10px", marginBottom: 10, cursor: "pointer", width: "100%" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: "1px solid " + B, color: B, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>i</div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: B }}>How we calculate this — full formula &amp; assumptions</span>
                  </button>
                  {d.oneYearTarget != null && (
                    <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                      <div style={{ flex: 1, background: oneYearUpside ? OKS : ERS, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: oneYearUpside ? OKT : ERT, marginBottom: 2 }}>1-year target</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: oneYearUpside ? OKT : ERT, fontFamily: MONO }}>{sym}{d.oneYearTarget.toFixed(2)}</div>
                      </div>
                      <div style={{ flex: 1, background: BG, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: TXM, marginBottom: 2 }}>current price</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: TX, fontFamily: MONO }}>{sym}{d.currentPrice.toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: TXM, marginBottom: 2 }}>Intrinsic value today</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: upside ? OKT : ERT, fontFamily: MONO }}>{sym}{d.intrinsicValue.toFixed(2)}</div>
                      <div style={{ fontSize: 11, color: upside ? OKT : ERT, fontFamily: MONO }}>{upside ? "↑" : "↓"} {Math.abs(d.upside)}% vs current {sym}{d.currentPrice.toFixed(2)}</div>
                    </div>
                    <div style={{ background: upside ? OKS : ERS, borderRadius: 10, padding: "8px 12px", textAlign: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", marginBottom: 2 }}>
                        <div style={{ fontSize: 10, color: upside ? OKT : ERT }}>margin of safety</div>
                        <button onClick={function(e2) { e2.stopPropagation(); tog("mos_tip"); }} style={{ width: 15, height: 15, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>i</button>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: upside ? OKT : ERT, fontFamily: MONO }}>{d.marginOfSafety.toFixed(1)}%</div>
                    </div>
                  </div>
                  {openSections.mos_tip && <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>The gap between the DCF fair value and the current price. A 20%+ margin means you're buying £1 of value for 80p — protecting you if the growth assumptions turn out to be too optimistic. Benjamin Graham's golden rule: never invest without one.<br /><br /><strong>Why this differs from the {Math.abs(d.upside)}% shown above:</strong> both come from the same {sym}{Math.abs(d.intrinsicValue - d.currentPrice).toFixed(2)} gap between intrinsic value and price — Upside divides it by the current price ({sym}{d.currentPrice.toFixed(2)}), Margin of Safety divides it by the intrinsic value ({sym}{d.intrinsicValue.toFixed(2)}) instead. Dividing by the larger number always gives a smaller percentage, so Margin of Safety is the more conservative of the two figures.</div>}
                  {d.range && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: TXM, marginBottom: 4 }}>Valuation range (±1.5pt discount rate, ±1pt growth)</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <div style={{ flex: 1, background: BG, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: TXM }}>Bear</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: TX, fontFamily: MONO }}>{sym}{d.range.bear.toFixed(2)}</div>
                        </div>
                        <div style={{ flex: 1, background: BG, borderRadius: 8, padding: "6px 8px", textAlign: "center", border: "1px solid " + BD }}>
                          <div style={{ fontSize: 9, color: TXM }}>Base</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: TX, fontFamily: MONO }}>{sym}{d.range.base.toFixed(2)}</div>
                        </div>
                        <div style={{ flex: 1, background: BG, borderRadius: 8, padding: "6px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: TXM }}>Bull</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: TX, fontFamily: MONO }}>{sym}{d.range.bull.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ borderTop: "1px solid " + BD, paddingTop: 8 }}>
                    {[
                      ["Growth rate used", d.growthRateUsed + "%"],
                      ["Discount rate", d.discountRate + "%"],
                      ["Terminal growth", (d.terminalGrowth || 2.5) + "%"],
                      ["Model", "10-yr DCF + terminal value"],
                      ["Net debt adjusted", d.netDebtAdj ? "Yes" : "No"],
                    ].map(function(r, i) {
                      return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span style={{ fontSize: 11, color: TXM }}>{r[0]}</span><span style={{ fontSize: 11, color: TX, fontFamily: MONO }}>{r[1]}</span></div>;
                    })}
                  </div>
                  {/* Country assumptions note */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 10, background: BG, borderRadius: 8, padding: "8px 10px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: TX2 }}>Country-adjusted assumptions</span>
                        <button onClick={function(e2) { e2.stopPropagation(); tog("dcf_country_tip"); }} style={{ width: 15, height: 15, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>i</button>
                      </div>
                      <div style={{ fontSize: 10, color: TXM, lineHeight: 1.5 }}>
                        We assumed {d.growthRateUsed}% growth fading toward a {d.terminalGrowth || 2.5}% long-run rate over 10 years, discounted at {d.discountRate}% — both calibrated for {stock.c !== "Unknown" ? stock.c : "this market"} based on local risk-free rates and long-run GDP growth expectations.
                      </div>
                    </div>
                  </div>
                  {openSections.dcf_country_tip && (
                    <div style={{ background: BW, borderRadius: 8, padding: "10px 12px", fontSize: 11, color: TXM, lineHeight: 1.6, marginTop: 6 }}>
                      <div style={{ fontWeight: 700, color: TX, marginBottom: 6 }}>Why rates vary by country</div>
                      <div style={{ marginBottom: 4 }}><strong>Discount rate</strong> — reflects the local cost of capital: risk-free rate (government bond yield) + equity risk premium + beta adjustment. India uses ~13% because RBI rates are ~6.5% vs the US Fed at ~4.5%. Japan uses ~7% due to near-zero interest rates.</div>
                      <div style={{ marginBottom: 4 }}><strong>Terminal growth</strong> — the assumed long-run growth rate after year 10. Anchored to each country's long-run nominal GDP growth. India at 5%, Japan at 0.8%, US/UK at ~2.5%.</div>
                      <div><strong>Net debt</strong> — total debt minus cash is subtracted from the enterprise value before dividing by shares, giving a more accurate equity value per share.</div>
                    </div>
                  )}
                  {stock.dcf.isProjected && (
                    <div style={{ background: WNS, border: "1px solid " + WN, borderRadius: 8, padding: "8px 12px", marginTop: 8, fontSize: 11, color: WN, fontWeight: 600, display: "flex", gap: 6, alignItems: "flex-start" }}>
                      <MdWarningAmber size={14} style={{ flexShrink: 0, marginTop: 1 }} /><span>Projected DCF — this stock has negative current FCF. Estimate based on gross margin and revenue growth trajectory. Higher uncertainty than usual.</span>
                    </div>
                  )}
                  {(function() {
                    // The Value pillar's DCF row deliberately stays short
                    // ("-82%, Overvalued") — this is where the fuller context
                    // lives instead, when DCF is weighted less heavily due to
                    // being a growth-classified stock. Previously gated on a
                    // string match ("Weighted less heavily") that never
                    // appeared in the backend's actual reason text ("Counts
                    // for much less..."), so this note silently never
                    // rendered for any stock — fixed to reuse the same
                    // weight-share check the badge softening above uses.
                    if (!dcfSig || dcfWPct == null || dcfWPct >= 15) return null;
                    return (
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 8, background: WNS, border: "1px solid #F0C36D", borderRadius: 8, padding: "8px 10px" }}>
                        <span style={{ fontSize: 13 }}>⚠️</span>
                        <span style={{ fontSize: 10.5, color: "#8A5A00", lineHeight: 1.5 }}>{dcfSig.reason}</span>
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 10, color: TX3, marginTop: 8 }}>DCF is sensitive to assumptions. Treat as a guide, not a guarantee.</div>
                  {(function() {
                    // Broadened from only the full-opposite-poles case
                    // (UNDERVALUED vs a downside DCF, or vice versa) to any
                    // case where DCF's own label disagrees with the overall
                    // verdict — including the common "DCF says Overvalued,
                    // overall verdict says Fairly Valued" case, which the
                    // old condition missed entirely (confirmed real case:
                    // Netflix — DCF -41.8%/Overvalued, overall verdict
                    // Fairly Valued, and no explanatory note appeared here
                    // at all under the old logic).
                    var dcfLabel = d.upside > 20 ? "UNDERVALUED" : d.upside > 0 ? "FAIRLY VALUED" : "OVERVALUED";
                    return dcfLabel !== stock.v;
                  })() && (
                    <div style={{ marginTop: 8, background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 10.5, color: TXM, lineHeight: 1.5 }}>
                      Our overall verdict weighs several factors together — PEG, sector-relative P/E, analyst consensus, momentum, quality, and this DCF — not just this one. It's possible for the combined verdict to point one way while DCF points the other, especially when the other factors are strong enough to outweigh it.
                    </div>
                  )}
                </SecCard>
              );
            })()}

            {stock.justifiedPB && (function() {
              var jp = stock.justifiedPB, sym = CS$[stock.cur] || "$", upside = jp.upside >= 0;
              var oneYearUpside = jp.oneYearUpside != null ? jp.oneYearUpside >= 0 : upside;
              return (
                <SecCard id="justifiedpb" title="1-Year Target" badge={(oneYearUpside ? "↑ " : "↓ ") + Math.abs(jp.oneYearUpside != null ? jp.oneYearUpside : jp.upside) + "% " + (oneYearUpside ? "upside" : "downside")} badgeColor={oneYearUpside ? "green" : "red"} open={openSections.justifiedpb} onToggle={function() { tog("justifiedpb"); }}>
                  <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>
                    Banks don't fit a cash-flow-based model well — this uses a justified P/B approach instead, comparing what the stock's price-to-book ratio should be (given its return on equity and cost of capital) against what it actually is. The 1-year target assumes the price moves partway toward that fair value over the next year, not all at once.
                  </div>
                  {jp.confidence === "low" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#FFF4E5", border: "1px solid #F0C36D", borderRadius: 8, padding: "7px 10px", marginBottom: 10 }}>
                      <span style={{ fontSize: 14 }}>⚠️</span>
                      <span style={{ fontSize: 11, color: "#8A5A00", lineHeight: 1.4 }}>Lower confidence: the reported ROE was outside a realistic range and had to be capped — treat this figure as a rougher estimate.</span>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                    <div style={{ flex: 1, background: oneYearUpside ? OKS : ERS, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: oneYearUpside ? OKT : ERT, marginBottom: 2 }}>1-year target</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: oneYearUpside ? OKT : ERT, fontFamily: MONO }}>{sym}{jp.targetPrice.toFixed(2)}</div>
                    </div>
                    <div style={{ flex: 1, background: BG, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: TXM, marginBottom: 2 }}>current price</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: TX, fontFamily: MONO }}>{sym}{jp.currentPrice.toFixed(2)}</div>
                    </div>
                  </div>
                  {jp.intrinsicValue != null && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: TXM, marginBottom: 2 }}>Intrinsic value today</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: upside ? OKT : ERT, fontFamily: MONO }}>{sym}{jp.intrinsicValue.toFixed(2)}</div>
                        <div style={{ fontSize: 11, color: upside ? OKT : ERT, fontFamily: MONO }}>{upside ? "↑" : "↓"} {Math.abs(jp.upside)}% vs current {sym}{jp.currentPrice.toFixed(2)}</div>
                      </div>
                      <div style={{ background: upside ? OKS : ERS, borderRadius: 10, padding: "8px 12px", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center", marginBottom: 2 }}>
                          <div style={{ fontSize: 10, color: upside ? OKT : ERT }}>margin of safety</div>
                          <button onClick={function(e2) { e2.stopPropagation(); tog("mos_tip"); }} style={{ width: 15, height: 15, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>i</button>
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: upside ? OKT : ERT, fontFamily: MONO }}>{jp.marginOfSafety.toFixed(1)}%</div>
                      </div>
                    </div>
                  )}
                  {openSections.mos_tip && <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>The gap between the justified P/B fair value and the current price. A 20%+ margin means you're buying £1 of value for 80p — protecting you if the ROE/growth assumptions turn out to be too optimistic. Benjamin Graham's golden rule: never invest without one.<br /><br /><strong>Why this differs from the {Math.abs(jp.upside)}% shown above:</strong> both come from the same {sym}{Math.abs(jp.intrinsicValue - jp.currentPrice).toFixed(2)} gap between intrinsic value and price — Upside divides it by the current price ({sym}{jp.currentPrice.toFixed(2)}), Margin of Safety divides it by the intrinsic value ({sym}{jp.intrinsicValue.toFixed(2)}) instead. Dividing by the larger number always gives a smaller percentage, so Margin of Safety is the more conservative of the two figures.</div>}
                  <div style={{ background: BG, borderRadius: 8, padding: "8px 10px" }}>
                    {[
                      ["Justified P/B", jp.justifiedPBRatio + "x"],
                      ["Current P/B", jp.currentPBRatio + "x"],
                      ["ROE used", jp.roeUsed + "%"],
                      ["Cost of equity", jp.costOfEquity + "%"],
                      ["Growth used", jp.growthUsed + "%"],
                    ].map(function(r, i) {
                      return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span style={{ fontSize: 11, color: TXM }}>{r[0]}</span><span style={{ fontSize: 11, color: TX, fontFamily: MONO }}>{r[1]}</span></div>;
                    })}
                  </div>
                  <div style={{ fontSize: 10, color: TX3, marginTop: 8 }}>Justified P/B is sensitive to ROE and growth assumptions, same as DCF is to its own. Treat as a guide, not a guarantee.</div>
                  {((stock.v === "UNDERVALUED" && !upside) || (stock.v === "OVERVALUED" && upside)) && (
                    <div style={{ marginTop: 8, background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 10.5, color: TXM, lineHeight: 1.5 }}>
                      Our overall verdict weighs several factors together — PEG, sector-relative P/E, analyst consensus, momentum, quality, and this model — not just this one. It's possible for the combined verdict to point one way while this specific model points the other, especially when the other factors are strong enough to outweigh it.
                    </div>
                  )}
                </SecCard>
              );
            })()}

            {!stock.dcf && !stock.justifiedPB && (
              <div style={Object.assign({}, card, { padding: 14, marginBottom: 8 })}>
                <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 4 }}>Fair value estimate — not shown for this stock</div>
                <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>The estimate fell outside a reasonable sanity range and was suppressed rather than shown as misleading, or there wasn't enough data (ROE, P/B, or cash flow) to compute one confidently.</div>
                <button onClick={function() { setShowDcfMethodology(true); }}
                  style={{ display: "flex", alignItems: "center", gap: 6, background: BG, border: "none", borderRadius: 8, padding: "7px 10px", cursor: "pointer", width: "100%" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", border: "1px solid " + B, color: B, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>i</div>
                  <span style={{ fontSize: 11, fontWeight: 600, color: B }}>How our valuation models work — full formula &amp; assumptions</span>
                </button>
              </div>
            )}

            </GroupCard>

            <GroupCard id="group-financials" icon={<MdBarChart size={17} />} title="Financials" subtitle="Earnings history and estimates" open={openGroups.financials} onToggle={function() { togGroup("financials"); }} hasData={!!(stock.er && stock.er.length > 0)}>
            {stock.er && stock.er.length > 0 && (function() {
              var reported = stock.er.filter(function(e) { return e.a !== null; });
              var pendingQ = stock.er.filter(function(e) { return e.a === null; });
              var nextPending = pendingQ.length > 0 ? pendingQ[0] : null;
              var daysToEarnings = null, calendarTs = null;
              if (nextPending) {
                if (nextPending.ts && nextPending.ts > Date.now()) { calendarTs = nextPending.ts; daysToEarnings = Math.round((nextPending.ts - Date.now()) / 86400000); }
                else if (nextPending.d) { var parsedD = new Date(nextPending.d); if (!isNaN(parsedD.getTime()) && parsedD > new Date()) { calendarTs = parsedD.getTime(); daysToEarnings = Math.round((calendarTs - Date.now()) / 86400000); } }
              }
              var chartEr = reported.slice(-3).concat(pendingQ.slice(0, 1));
              var allVals = chartEr.reduce(function(acc, e) { if (e.e && e.e !== "N/A") acc.push(Math.abs(parseFloat(e.e) || 0)); if (e.a !== null) acc.push(Math.abs(parseFloat(e.a) || 0)); return acc; }, []);
              var maxVal = Math.max.apply(null, allVals) || 1;
              var BAR_H = 100, CHART_H = BAR_H + 70;
              function barH(v2) { return Math.max(4, (Math.abs(parseFloat(v2) || 0) / maxVal) * BAR_H); }
              var lastReported2 = reported.length > 0 ? reported[reported.length - 1] : null;
              var beatBadge = lastReported2 ? (lastReported2.b === true ? "BEAT" : lastReported2.b === false ? "MISS" : "MET") : null;
              var beatColor = lastReported2 ? (lastReported2.b === true ? "green" : lastReported2.b === false ? "red" : null) : null;
              var nextBadge = daysToEarnings !== null ? daysToEarnings + "d" : (nextPending ? "upcoming" : null);
              return (
                <SecCard id="earnings" title="Earnings" badge={beatBadge || nextBadge} badgeColor={beatColor || "blue"} open={openSections.earnings} onToggle={function() { tog("earnings"); }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: TXM }}>EPS — estimate vs actual</div>
                    {nextPending && daysToEarnings !== null && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <div style={{ background: BW, border: "1px solid " + BD, borderRadius: 8, padding: "4px 10px", textAlign: "center" }}>
                          <div style={{ fontSize: 10, color: TXM }}>next earnings</div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: B, fontFamily: MONO }}>{daysToEarnings}d</div>
                        </div>
                        {calendarTs && <button onClick={function() { addToCalendar(stock.t, stock.n, calendarTs); }} style={{ fontSize: 11, color: B, background: BW, border: "1px solid " + BD, borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center" }}><MdCalendarToday size={13} /></button>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: CHART_H }}>
                    {chartEr.map(function(e, i) {
                      var isPending = e.a === null;
                      var beat = e.b === true, miss = e.b === false;
                      var actualColor = beat ? OK : miss ? ER : TXM;
                      var estH = e.e && e.e !== "N/A" ? barH(e.e) : 0;
                      var actH = !isPending && e.a ? barH(e.a) : 0;
                      var surpSign = e.s ? parseFloat(e.s) >= 0 : null;
                      return (
                        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: CHART_H }}>
                          <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 3 }}>
                            {!isPending && estH > 0 && <div style={{ width: "40%", background: "#D4D9E0", borderRadius: "3px 3px 0 0", height: estH, flexShrink: 0 }} />}
                            {isPending && (
                              <div style={{ width: "60%", border: "2px dashed " + B, borderRadius: "3px 3px 0 0", height: Math.max(estH, 40), position: "relative", flexShrink: 0, boxSizing: "border-box" }}>
                                <div style={{ position: "absolute", top: -(e.e && e.e !== "N/A" ? 36 : 16), left: "50%", transform: "translateX(-50%)", textAlign: "center", whiteSpace: "nowrap" }}>
                                  {e.e && e.e !== "N/A" ? <><div style={{ fontSize: 11, fontWeight: 700, color: B, fontFamily: MONO }}>{(CS$[stock.cur] || "$")}{parseFloat(e.e).toFixed(2)}</div><div style={{ fontSize: 10, color: TXM }}>est. EPS</div></> : <div style={{ fontSize: 10, color: TXM }}>est. TBC</div>}
                                </div>
                              </div>
                            )}
                            {!isPending && actH > 0 && (
                              <div style={{ width: "40%", background: actualColor, borderRadius: "3px 3px 0 0", height: actH, position: "relative", flexShrink: 0 }}>
                                {e.s && <div style={{ position: "absolute", top: -15, left: "50%", transform: "translateX(-50%)", fontSize: 10, color: actualColor, whiteSpace: "nowrap", fontWeight: 600, fontFamily: MONO }}>{(surpSign ? "+" : "") + e.s + "%"}</div>}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: TX2, textAlign: "center" }}>{e.q}</div>
                          {isPending ? <div style={{ fontSize: 10, color: B, background: BW, border: "0.5px solid " + BD, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>NEXT</div>
                            : <div style={{ fontSize: 10, background: beat ? OKS : miss ? ERS : BG, color: beat ? OKT : miss ? ERT : TXM, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>{beat ? "BEAT" : miss ? "MISS" : "MET"}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 14, marginTop: 8, paddingTop: 8, borderTop: "1px solid " + BD }}>
                    {[["#D4D9E0","Estimate"],[OK,"Beat"],[ER,"Miss"]].map(function(l) { return <div key={l[1]} style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 9, height: 9, borderRadius: 2, background: l[0] }} /><span style={{ fontSize: 10, color: TXM }}>{l[1]}</span></div>; })}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 9, height: 9, borderRadius: 2, border: "1.5px dashed " + B }} /><span style={{ fontSize: 10, color: TXM }}>Upcoming</span></div>
                  </div>
                  {reported.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: TX3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>
                        History
                        {(function() {
                          var rev = reported.slice().reverse();
                          if (rev.length < 2) return null;
                          var trend = 0;
                          for (var i = 1; i < Math.min(rev.length, 4); i++) {
                            var a = parseFloat(rev[i].a), b = parseFloat(rev[i-1].a);
                            if (!isNaN(a) && !isNaN(b)) trend += (b > a ? 1 : b < a ? -1 : 0);
                          }
                          return <span style={{ marginLeft: 8, fontSize: 12, color: trend > 0 ? OKT : trend < 0 ? ERT : TXM }}>{trend > 0 ? "↑ Accelerating" : trend < 0 ? "↓ Decelerating" : "→ Stable"}</span>;
                        })()}
                      </div>
                      {reported.slice().reverse().slice(0, 4).map(function(e, i) {
                        var beat = e.b === true, miss = e.b === false;
                        var sym = CS$[stock.cur] || "$";
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                            <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, color: TX }}>{e.q}</div></div>
                            <div style={{ textAlign: "right", marginRight: 10 }}>
                              {e.e && e.e !== "N/A" && <div style={{ fontSize: 11, color: TX3, fontFamily: MONO }}>{sym}{e.e} est.</div>}
                              {e.a && <div style={{ fontSize: 13, fontWeight: 600, color: beat ? OKT : miss ? ERT : TX, fontFamily: MONO }}>{sym}{e.a}</div>}
                            </div>
                            <span style={{ background: beat ? OKS : miss ? ERS : BG, color: beat ? OKT : miss ? ERT : TXM, borderRadius: 8, padding: "3px 8px", fontSize: 11, fontWeight: 700 }}>{beat ? "BEAT" : miss ? "MISS" : "MET"}</span>
                            {e.s && <div style={{ fontSize: 11, color: beat ? OKT : miss ? ERT : TXM, marginLeft: 6, fontFamily: MONO }}>{(parseFloat(e.s) >= 0 ? "+" : "") + e.s + "%"}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Upcoming revenue estimate */}
                  {nextPending && typeof nextPending.revEst === 'number' && nextPending.revEst > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + BD, display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, color: TXM }}>Revenue estimate ({nextPending.q})</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: TX, fontFamily: MONO }}>{formatRevEst(nextPending.revEst, stock.cur)}</span>
                    </div>
                  )}
                </SecCard>
              );
            })()}

            </GroupCard>

            {(!!(stock.si && stock.si.pct !== null) || !!(stock.eq && stock.eq.years && stock.eq.years.length > 0)) && (
            <GroupCard id="group-additional" icon={<MdInsights size={17} />} title="Additional Signals" subtitle="Earnings track record, short interest" open={openGroups.risk} onToggle={function() { togGroup("risk"); }} hasData={true}
              badge={(function() {
                // Worst-item-wins: if anything here is a real concern, that's
                // what the collapsed summary should say — not diluted into a
                // vague "some good, some caution" that could mislead someone
                // who never opens it. Only shown when both members are known;
                // an incomplete badge would be worse than none.
                var colors = [];
                if (stock.eq && stock.eq.years && stock.eq.years.length > 0) colors.push(stock.eq.labelColor);
                if (stock.si && stock.si.pct !== null) colors.push(stock.si.levelColor);
                if (colors.length === 0) return null;
                return colors.indexOf("red") !== -1 ? "Needs a look" : colors.indexOf("amber") !== -1 ? "1 to watch" : "All clear";
              })()}
              badgeColor={(function() {
                var colors = [];
                if (stock.eq && stock.eq.years && stock.eq.years.length > 0) colors.push(stock.eq.labelColor);
                if (stock.si && stock.si.pct !== null) colors.push(stock.si.levelColor);
                return colors.indexOf("red") !== -1 ? "red" : colors.indexOf("amber") !== -1 ? "amber" : "green";
              })()}
            >
            {(function() {
              var hasEq = !!(stock.eq && stock.eq.years && stock.eq.years.length > 0);
              var hasSi = !!(stock.si && stock.si.pct !== null);
              // When only one signal is actually available, auto-expand it —
              // otherwise opening the group just leads to a second, pointless
              // tap to reveal the only thing that was ever going to be there.
              // Confirmed real case: Oracle only has Short Interest (Earnings
              // Track Record needs matching income-statement and cash-flow
              // years, which Yahoo doesn't always have — common enough that
              // "just one signal" isn't a rare edge case worth ignoring).
              var onlyOne = hasEq !== hasSi;
              return (
            <>
            {hasEq && (
              <SecCard id="earnquality" title="Earnings Track Record" badge={stock.eq.label} badgeColor={stock.eq.labelColor} open={openSections.earnquality || onlyOne} onToggle={function() { tog("earnquality"); }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: TXM, flex: 1 }}>Are reported profits backed by real cash? Compares operating cash flow vs net income each year.</span>
                  <button onClick={function(e2) { e2.stopPropagation(); tog("eq_tip"); }} style={{ width: 17, height: 17, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>i</button>
                </div>
                {openSections.eq_tip && (
                  <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.6, marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, color: TX }}>What this means: </span>Companies can report high net income through accounting choices, but cash flow is harder to manipulate — it's the actual money that came in. A ratio above 1.0x means the business generates more cash than it reports as profit, which is a strong sign of earnings quality. A ratio consistently below 0.7x suggests profits may be inflated relative to real cash generation — a common warning sign before earnings disappointments.
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  {stock.eq.years.map(function(y, i) {
                    var col = y.quality === "high" ? OK : y.quality === "medium" ? WN : y.ratio !== null ? ER : TXM;
                    var bg = y.quality === "high" ? OKS : y.quality === "low" ? ERS : WNS;
                    return (
                      <div key={i} style={{ flex: 1, background: bg, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: TXM, marginBottom: 4 }}>{y.year}</div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: col, fontFamily: MONO }}>{y.ratio !== null ? y.ratio + "x" : "—"}</div>
                        <div style={{ fontSize: 10, color: TXM, marginTop: 2 }}>CF / income</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ background: stock.eq.labelColor === "green" ? OKS : stock.eq.labelColor === "red" ? ERS : WNS, borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: stock.eq.labelColor === "green" ? OK : stock.eq.labelColor === "red" ? ER : WN, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: stock.eq.labelColor === "green" ? OKT : stock.eq.labelColor === "red" ? ERT : WN }}>{stock.eq.summary}</span>
                </div>
                <div style={{ fontSize: 10, color: TX3, marginTop: 8 }}>Above 1.0x is healthy. Below 0.7x for multiple years is a warning sign of aggressive accounting.</div>
              </SecCard>
            )}

            {hasSi && (
              <SecCard id="shortint" title="Short Interest" badge={stock.si.level + (stock.si.pct !== null ? " — " + stock.si.pct + "%" : "")} badgeColor={stock.si.levelColor} open={openSections.shortint || onlyOne} onToggle={function() { tog("shortint"); }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: TXM, flex: 1 }}>What % of shares are being bet against by professional short sellers.</span>
                  <button onClick={function(e2) { e2.stopPropagation(); tog("si_tip"); }} style={{ width: 17, height: 17, borderRadius: "50%", border: "1px solid " + BD, background: BW, color: B, fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>i</button>
                </div>
                {openSections.si_tip && (
                  <div style={{ background: BW, borderRadius: 8, padding: "8px 10px", fontSize: 11, color: TXM, lineHeight: 1.6, marginBottom: 10 }}>
                    <span style={{ fontWeight: 700, color: TX }}>What this means: </span>Short sellers borrow shares and sell them, hoping to buy back cheaper later. When professionals short a stock they are betting real money that it will fall. Low short interest (&lt;3%) means few are betting against it. High short interest (&gt;10%) means significant bearish conviction — worth understanding why. "Days to cover" is how many days of normal trading volume it would take to unwind all short positions — high numbers can amplify a price move if shorts are forced to buy back quickly (a short squeeze).
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 10 }}>
                  <div style={{ background: stock.si.levelColor === "green" ? OKS : stock.si.levelColor === "amber" ? WNS : ERS, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 10, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT, marginBottom: 4 }}>Short % of float</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT, fontFamily: MONO }}>{stock.si.pct}%</div>
                    <div style={{ fontSize: 10, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT }}>{stock.si.level}</div>
                  </div>
                  {stock.si.daysTocover !== null && (
                    <div style={{ background: stock.si.levelColor === "green" ? OKS : stock.si.levelColor === "amber" ? WNS : ERS, borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT, marginBottom: 4 }}>Days to cover</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT, fontFamily: MONO }}>{stock.si.daysTocover}d</div>
                      <div style={{ fontSize: 10, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT }}>{stock.si.daysTocover <= 2 ? "minimal pressure" : stock.si.daysTocover <= 5 ? "moderate" : "high pressure"}</div>
                    </div>
                  )}
                </div>
                <div style={{ background: stock.si.levelColor === "green" ? OKS : stock.si.levelColor === "amber" ? WNS : ERS, borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: stock.si.levelColor === "green" ? OK : stock.si.levelColor === "amber" ? WN : ER, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: stock.si.levelColor === "green" ? OKT : stock.si.levelColor === "amber" ? WN : ERT }}>{stock.si.summary}</span>
                </div>
                {stock.si.trend && <div style={{ fontSize: 11, color: TXM, marginTop: 8 }}>Short interest is {stock.si.trend} vs prior month.</div>}
                <div style={{ fontSize: 10, color: TX3, marginTop: 6 }}>Above 10% = meaningful bearish conviction. Above 20% = high risk or potential short squeeze. Days to cover = time to unwind all short positions at normal volume.</div>
              </SecCard>
            )}
            </>
              );
            })()}

            </GroupCard>
            )}

            <GroupCard id="group-ai" icon={<MdAutoAwesome size={17} />} title="AI thesis" subtitle="Full analysis, bull/bear case, catalysts, news" open={openGroups.ai} onToggle={function() { togGroup("ai"); }}>
            <SecCard id="ai" title="AI Analysis" badge={stock.v} badgeColor={stock.v === "UNDERVALUED" ? "green" : stock.v === "OVERVALUED" ? "red" : "amber"} open={openSections.ai} onToggle={function() { tog("ai"); }}>
              {stock.aiOff ? (
                <div>
                  <div style={{ background: WNS, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: WN, marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}><MdWarningAmber size={14} /> AI analysis temporarily unavailable</div>
                    <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5 }}>
                      {stock.aiOffReason === "billing" ? "The AI service is currently offline. Financial data and score are still fully available." :
                       stock.aiOffReason === "quota" ? "AI request limit reached. Try again in a few minutes." :
                       stock.aiOffReason === "parse_error" ? "The AI's response couldn't be read this time. Hit ↻ Refresh to try again — this is usually a one-off." :
                       "AI analysis timed out. Hit ↻ Refresh to try again. Score below is calculated from live financial data."}
                    </div>
                  </div>
                  {/* Deterministic score breakdown */}
                  <div style={{ fontSize: 12, fontWeight: 700, color: N, marginBottom: 10 }}>Score breakdown — {stock.t}</div>
                  {(function() {
                    var signals = [];
                    // PEG
                    if (stock.m && stock.m.peg && stock.m.peg !== "N/A") {
                      var peg = parseFloat(stock.m.peg);
                      var pegS = peg < 0.75 ? 90 : peg < 1.0 ? 78 : peg < 1.5 ? 65 : peg < 2.0 ? 52 : peg < 3.0 ? 38 : 22;
                      var pegLbl = peg < 1.0 ? "Attractive" : peg < 1.5 ? "Fair" : peg < 2.0 ? "Moderate" : "Expensive";
                      signals.push({ label: "PEG Ratio", value: peg.toFixed(2), score: pegS, max: 100, color: pegS >= 65 ? OK : pegS >= 42 ? WN : ER, desc: pegLbl });
                    }
                    // Forward P/E
                    if (stock.m && stock.m.fpe && stock.m.fpe !== "N/A") {
                      var fpe = parseFloat(stock.m.fpe);
                      var fpeS = fpe < 15 ? 80 : fpe < 22 ? 65 : fpe < 30 ? 52 : fpe < 45 ? 40 : 28;
                      signals.push({ label: "Forward P/E", value: fpe.toFixed(1) + "×", score: fpeS, max: 100, color: fpeS >= 65 ? OK : fpeS >= 42 ? WN : ER, desc: fpe < 22 ? "Cheap" : fpe < 30 ? "Fair" : "Rich" });
                    }
                    // Analyst consensus
                    if (stock.ac && stock.ac.bullishPct != null) {
                      var acS = stock.ac.bullishPct >= 80 ? 82 : stock.ac.bullishPct >= 60 ? 68 : stock.ac.bullishPct >= 40 ? 52 : 32;
                      signals.push({ label: "Analyst consensus", value: stock.ac.bullishPct + "% buy", score: acS, max: 100, color: acS >= 65 ? OK : acS >= 42 ? WN : ER, desc: stock.ac.bullishPct >= 70 ? "Strong buy" : stock.ac.bullishPct >= 50 ? "Moderate buy" : "Mixed" });
                    }
                    // DCF
                    if (stock.dcf && stock.dcf.upside != null) {
                      var dcfS = stock.dcf.upside > 40 ? 88 : stock.dcf.upside > 20 ? 75 : stock.dcf.upside > 0 ? 62 : stock.dcf.upside > -20 ? 45 : 28;
                      signals.push({ label: "DCF upside", value: (stock.dcf.upside >= 0 ? "+" : "") + stock.dcf.upside + "%", score: dcfS, max: 100, color: dcfS >= 65 ? OK : dcfS >= 42 ? WN : ER, desc: stock.dcf.upside > 20 ? "Undervalued" : stock.dcf.upside > 0 ? "Slight upside" : "Overvalued" });
                    }
                    // Momentum
                    if (stock.mom) {
                      var momS = stock.mom.signal === "bullish" ? 70 : stock.mom.signal === "bearish" ? 35 : 52;
                      signals.push({ label: "Momentum", value: stock.mom.signal, score: momS, max: 100, color: momS >= 65 ? OK : momS >= 42 ? WN : ER, desc: stock.mom.signal === "bullish" ? "Above MAs" : stock.mom.signal === "bearish" ? "Below MAs" : "Mixed" });
                    }
                    // Quality
                    if (stock.qs) {
                      var qsS = stock.qs.score;
                      signals.push({ label: "Quality score", value: qsS + "/100", score: qsS, max: 100, color: qsS >= 65 ? OK : qsS >= 42 ? WN : ER, desc: stock.qs.label });
                    }
                    if (signals.length === 0) return <div style={{ fontSize: 12, color: TXM }}>No signal data available. Tap ↻ Refresh.</div>;
                    return signals.map(function(s, i) {
                      return (
                        <div key={i} style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                            <span style={{ fontSize: 12, color: TX2, fontWeight: 600 }}>{s.label}</span>
                            <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value} · {s.desc}</span>
                          </div>
                          <div style={{ height: 5, background: BD, borderRadius: 3 }}>
                            <div style={{ height: 5, background: s.color, borderRadius: 3, width: s.score + "%" }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div>
                  <p style={{ color: TX2, fontSize: 14, lineHeight: 1.7, margin: 0 }}>{stock.ai || "Tap ↻ Refresh to generate AI analysis for this stock."}</p>
                  {stock.ai && (
                    <div style={{ marginTop: 10, fontSize: 10.5, color: TX3 }}>Analysis based on Claude models, using data from Yahoo Finance. For informational purposes only — not financial advice.</div>
                  )}
                </div>
              )}
            </SecCard>

            <SecCard id="bullbear" title="Bull &amp; Bear Case" open={openSections.bullbear} onToggle={function() { tog("bullbear"); }}>
              {stock.aiOff || (!stock.bl && !stock.br) ? (
                <div style={{ fontSize: 13, color: TXM, fontStyle: "italic" }}>AI analysis unavailable — tap ↻ Refresh to generate.</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
                  <div><div style={{ fontSize: 12, fontWeight: 700, color: OKT, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><MdTrendingUp size={14} /> Bull</div><p style={{ color: TX2, fontSize: 12, lineHeight: 1.6, margin: 0 }}>{stock.bl}</p></div>
                  <div><div style={{ fontSize: 12, fontWeight: 700, color: ERT, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}><MdTrendingDown size={14} /> Bear</div><p style={{ color: TX2, fontSize: 12, lineHeight: 1.6, margin: 0 }}>{stock.br}</p></div>
                </div>
              )}
            </SecCard>

            {stock.ct && stock.ct.length > 0 && (
              <SecCard id="catalysts" title="Upcoming Catalysts" badge={stock.ct.length + ""} badgeColor="blue" open={openSections.catalysts} onToggle={function() { tog("catalysts"); }}>
                {stock.ct.map(function(c, i) {
                  var name, date;
                  if (c && typeof c === "object" && !Array.isArray(c)) { name = c.name; date = c.date || null; }
                  else if (Array.isArray(c)) { name = c[0]; date = c[1] || null; }
                  else { name = c; date = null; }
                  return <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: i ? "1px solid " + BD : "none" }}><span style={{ fontSize: 13, color: TX2 }}>{name}</span>{date && <span style={{ fontSize: 12, color: TX3 }}>{date}</span>}</div>;
                })}
              </SecCard>
            )}

            {stockNews.length > 0 && (
              <SecCard id="news" title="Latest News" badge={stockNews.length + ""} badgeColor="blue" open={openSections.news} onToggle={function() { tog("news"); }}>
                <div style={{ fontSize: 11, color: TX3, marginBottom: 10 }}>
                  News via Yahoo Finance — tapping opens in your browser.
                </div>
                {stockNews.map(function(n, i) {
                  var ago = n.time ? (function() { var diff = Date.now() - n.time, h = Math.floor(diff / 3600000); if (h < 1) return Math.floor(diff / 60000) + "m ago"; if (h < 24) return h + "h ago"; return Math.floor(h / 24) + "d ago"; })() : "";
                  return (
                    <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                      style={{ display: "block", padding: "9px 0", borderTop: i ? "1px solid " + BD : "none", cursor: "pointer", textDecoration: "none" }}>
                      <div style={{ fontSize: 13, color: N, fontWeight: 600, lineHeight: 1.4, marginBottom: 4 }}>{n.title}</div>
                      <div style={{ display: "flex", gap: 8 }}><span style={{ fontSize: 11, color: TXM }}>{n.publisher}</span>{ago && <span style={{ fontSize: 11, color: TX3 }}>{ago}</span>}</div>
                    </a>
                  );
                })}
              </SecCard>
            )}

            <SecCard id="thesis" title="Investment Thesis"
              badge={stock.at ? "AI generated" : null}
              badgeColor="blue"
              open={openSections.thesis}
              onToggle={function() { tog("thesis"); }}>
              {(function() {
                var t = thesis[stock.t] || {};
                var sym = CS$[stock.cur] || "$";
                var challenge = thesisChallenge[stock.t];

                // AI auto-thesis section
                var autoThesis = stock.at;
                var autoFields = autoThesis ? [
                  { label: "Competitive moat", value: autoThesis.moat },
                  { label: "Growth driver", value: autoThesis.growth },
                  { label: "Key assumptions", value: autoThesis.assumptions },
                  { label: "Key risks", value: autoThesis.risks },
                  { label: "Watch for thesis breaks", value: autoThesis.watch_for },
                ] : [];

                var manualFields = [
                  { k: "moat", label: "Your view on the moat", placeholder: "e.g. Network effects, switching costs, brand strength…" },
                  { k: "growth", label: "Your growth driver", placeholder: "e.g. Cloud expansion, new product line, emerging markets…" },
                  { k: "assumptions", label: "What needs to go right", placeholder: "e.g. Margins expand to 25%, revenue grows 15%+ annually…" },
                  { k: "exit", label: "What would make you sell", placeholder: "e.g. Margins falling, founder leaves, competitor wins…" },
                ];
                function updateThesis(key, val) {
                  setThesis(function(prev) {
                    var next = Object.assign({}, prev);
                    next[stock.t] = Object.assign({}, next[stock.t] || {}, { [key]: val });
                    return next;
                  });
                }
                function dismissAlert() {
                  dismissThesisAlertFn({ ticker: stock.t }).then(function() {
                    setThesis(function(prev) {
                      var next = Object.assign({}, prev);
                      if (next[stock.t]) next[stock.t] = Object.assign({}, next[stock.t], { activeAlert: null });
                      return next;
                    });
                  }).catch(function(e) { setToast(e.message || "Couldn't dismiss alert."); });
                }
                var hasManualThesis = t.moat || t.growth || t.assumptions || t.exit;

                return (
                  <div>
                    {/* Thesis-break alert — persists in-app even if the push
                        notification was missed. Not AI-generated: this is a
                        deterministic check (target price, moving averages,
                        large single-day move) against the thesis you wrote
                        below, run periodically in the background. */}
                    {t.activeAlert && (
                      <div style={{ background: WNS, border: "1px solid " + WN, borderRadius: 10, padding: "12px 14px", marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <MdWarningAmber size={16} color={WN} style={{ flexShrink: 0, marginTop: 1 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: WN, marginBottom: 2 }}>Thesis check on {stock.t}</div>
                          <div style={{ fontSize: 12, color: TX2, lineHeight: 1.5 }}>{t.activeAlert.message}</div>
                        </div>
                        <button onClick={dismissAlert} style={{ flexShrink: 0, background: "none", border: "none", color: WN, fontSize: 11, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Dismiss</button>
                      </div>
                    )}
                    {/* AI-generated thesis */}
                    {autoThesis && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: TX3, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>AI Generated</div>
                        {autoFields.filter(function(f) { return f.value; }).map(function(f, i) {
                          return (
                            <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: B, marginBottom: 2 }}>{f.label}</div>
                              <div style={{ fontSize: 13, color: TX2, lineHeight: 1.55 }}>{f.value}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!autoThesis && (
                      <div style={{ background: BG, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: TXM, marginBottom: 14 }}>
                        AI thesis generates automatically when you load or refresh this stock.
                      </div>
                    )}

                    {/* Divider */}
                    <div style={{ borderTop: "1px solid " + BD, paddingTop: 14, marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: TX3, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>Your Thesis</div>

                      {/* Manual thesis fields */}
                      {manualFields.map(function(f) {
                        return (
                          <div key={f.k} style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: TX2, marginBottom: 4 }}>{f.label}</div>
                            {user ? (
                              <textarea value={t[f.k] || ""} onChange={function(ev) { updateThesis(f.k, ev.target.value.slice(0, 300)); }}
                                placeholder={f.placeholder}
                                style={{ width: "100%", minHeight: 52, background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "8px 10px", fontSize: 12, color: TX, fontFamily: "Inter, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", resize: "none", lineHeight: 1.5 }} />
                            ) : (
                              <div style={{ fontSize: 12, color: TX3, fontStyle: "italic" }}>Sign in to add your view</div>
                            )}
                          </div>
                        );
                      })}

                      {/* Target price + timeframe */}
                      {user && (
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 14 }}>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: TX2, marginBottom: 4 }}>Target price ({sym})</div>
                            <input type="number" value={t.targetPrice || ""} onChange={function(ev) { updateThesis("targetPrice", ev.target.value); }}
                              placeholder={"e.g. " + (stock.p * 1.3).toFixed(0)}
                              style={{ width: "100%", background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "8px 10px", fontSize: 16, color: N }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: TX2, marginBottom: 4 }}>Timeframe</div>
                            <select value={t.timeframe || ""} onChange={function(ev) { updateThesis("timeframe", ev.target.value); }}
                              style={{ width: "100%", background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: N }}>
                              <option value="">Select…</option>
                              <option value="6 months">6 months</option>
                              <option value="1 year">1 year</option>
                              <option value="2 years">2 years</option>
                              <option value="3–5 years">3–5 years</option>
                              <option value="5+ years">5+ years</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Challenge button */}
                      {user && hasManualThesis && (
                        <button onClick={function() {
                          if (thesisChallenging) return;
                          setThesisChallenging(true);
                          challengeThesisFn({
                            ticker: stock.t,
                            stockData: { verdict: stock.v, score: stock.sc, dcf: stock.dcf, qs: stock.qs, mom: stock.mom, sc2: stock.sc2, ai: stock.ai, m: stock.m },
                            thesis: t
                          }).then(function(r) {
                            setThesisChallenge(function(prev) { var next = Object.assign({}, prev); next[stock.t] = r.data; return next; });
                            setThesisChallenging(false);
                            if (!isFounder) {
                              setThesisLimitInfo(function(prev) {
                                var limit = (effectivePlan === "pro") ? 10 : 3;
                                if (prev && prev.count != null) return Object.assign({}, prev, { count: prev.count + 1, limit: limit });
                                return { count: 1, limit: limit };
                              });
                            }
                          }).catch(function(err) {
                            setThesisChallenging(false);
                            if (err && err.code === "functions/resource-exhausted") { setShowLimitModal("thesis"); return; }
                            setToast("Challenge failed — try again");
                          });
                        }} disabled={thesisChallenging}
                          style={{ width: "100%", padding: "10px 0", borderRadius: 8, border: "none", background: thesisChallenging ? BG : N, color: thesisChallenging ? TXM : WH, fontSize: 13, fontWeight: 700, cursor: thesisChallenging ? "default" : "pointer", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          {thesisChallenging ? "Challenging your thesis…" : <><MdSmartToy size={15} /> Challenge my thesis</>}
                        </button>
                      )}
                    </div>

                    {/* Challenge results */}
                    {challenge && (
                      <div style={{ background: BW, borderRadius: 10, padding: 14, border: "1px solid " + BD }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: N }}>AI verdict on your thesis</div>
                            <div style={{ fontSize: 10, color: TX3, marginTop: 1 }}>Analysis based on Claude models</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ fontSize: 11, color: TXM }}>Conviction</div>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", border: "2.5px solid " + (challenge.conviction >= 70 ? OK : challenge.conviction >= 45 ? WN : ER), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: challenge.conviction >= 70 ? OKT : challenge.conviction >= 45 ? WN : ERT }}>
                              {challenge.conviction}
                            </div>
                          </div>
                        </div>
                        <p style={{ fontSize: 13, color: TX2, lineHeight: 1.6, marginBottom: 10 }}>{challenge.summary}</p>
                        {challenge.challenges && challenge.challenges.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: TX3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>Specific challenges</div>
                            {challenge.challenges.map(function(c, i) {
                              return (
                                <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                                  <span style={{ flexShrink: 0, display: "flex", marginTop: 1 }}>{c.valid ? <MdCheckCircle size={15} color={OK} /> : <MdWarningAmber size={15} color={WN} />}</span>
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: TX }}>{c.assumption}</div>
                                    <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5 }}>{c.challenge}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {challenge.watchFor && (
                          <div style={{ paddingTop: 8, borderTop: "1px solid " + BD }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: TX3, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.6 }}>Watch for</div>
                            <div style={{ fontSize: 12, color: TXM, lineHeight: 1.5 }}>{challenge.watchFor}</div>
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: TX3, marginTop: 8 }}>Last challenged: {new Date(challenge.ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}</div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </SecCard>

            </GroupCard>

            </div>

            {/* Personal -- always visible, not grouped */}
            <SecCard id="alert" title="Price Alert" badge={alerts.find(function(a) { return a.t === stock.t && !a.triggered; }) ? "active" : null} badgeColor="green" open={openSections.alert} onToggle={function() { tog("alert"); }}>
              <div style={{ fontSize: 11, color: TXM, marginBottom: 10 }}>Get notified when {stock.t} crosses a price you set.</div>
              {(function() {
                var existingAlert = alerts.find(function(a) { return a.t === stock.t && !a.triggered; });
                var sym = CS$[stock.cur] || "$";
                return existingAlert ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: BW, borderRadius: 8, padding: "10px 12px" }}>
                    <div><div style={{ fontSize: 13, fontWeight: 600, color: N }}>{existingAlert.dir === "above" ? "Above" : "Below"} {sym}{existingAlert.price}</div><div style={{ fontSize: 11, color: TXM }}>Current: {sym}{stock.p.toFixed(2)}</div></div>
                    <button onClick={function() { setAlerts(function(prev) { return prev.filter(function(a) { return !(a.t === stock.t && !a.triggered); }); }); setToast("Alert removed"); }} style={{ background: ERS, border: "none", borderRadius: 6, padding: "4px 10px", color: ER, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>Remove</button>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      {["above", "below"].map(function(dir) {
                        return <button key={dir} onClick={function() { setAlertDir(dir); }} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1px solid " + (alertDir === dir ? N : BD), background: alertDir === dir ? N : WH, color: alertDir === dir ? WH : TXM, fontSize: 12, fontWeight: alertDir === dir ? 700 : 400, cursor: "pointer" }}>{dir === "above" ? "▲ Rises above " + sym : "▼ Falls below " + sym}</button>;
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: TXM, flexShrink: 0 }}>{sym}</span>
                      <input type="number" value={alertPrice} onChange={function(ev) { setAlertPrice(ev.target.value); }} placeholder={"e.g. " + (stock.p * 1.1).toFixed(2)} style={{ flex: 1, background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "9px 12px", fontSize: 16, color: N }} />
                      <button onClick={function() {
                        var p = parseFloat(alertPrice); if (!p || p <= 0) { setToast("Enter a valid price"); return; }
                        var activeAlerts = alerts.filter(function(a) { return !a.triggered; }).length;
                        var alertLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? Infinity : 3;
                        if (activeAlerts >= alertLimit) { setToast("Standard holds 3 active alerts at a time — remove one to add another, or upgrade to Pro for unlimited."); return; }
                        setAlerts(function(prev) { return prev.concat([{ t: stock.t, price: p, dir: alertDir, triggered: false, cur: stock.cur }]); }); setAlertPrice(""); setToast("Alert set for " + stock.t); haptic([10, 50, 10]);
                      }} style={{ padding: "9px 16px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Set</button>
                    </div>
                  </div>
                );
              })()}
            </SecCard>

            <SecCard id="notes" title="My Notes" badge={notes[stock.t] ? "saved" : null} badgeColor="green" open={openSections.notes} onToggle={function() { tog("notes"); }}>
              <div style={{ fontSize: 11, color: TXM, marginBottom: 8 }}>Private notes for {stock.t} — only visible to you.</div>
              <div>
                  <textarea
                    value={noteText}
                    onChange={function(e) { setNoteText(e.target.value.slice(0, 500)); }}
                    placeholder={"e.g. Watching for Q4 earnings beat, margin improvement, insider buying..."}
                    style={{ width: "100%", minHeight: 80, background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "9px 12px", fontSize: 13, color: TX, fontFamily: "Inter, 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif", resize: "none", lineHeight: 1.5 }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                    <span style={{ fontSize: 10, color: TX3 }}>{noteText.length}/500</span>
                    <button onClick={function() {
                      setNotes(function(prev) { var next = Object.assign({}, prev); if (noteText.trim()) { next[stock.t] = noteText.trim(); } else { delete next[stock.t]; } return next; });
                      setToast(noteText.trim() ? "Note saved" : "Note cleared");
                    }} style={{ padding: "6px 16px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Save</button>
                  </div>
                </div>
            </SecCard>


            {/* Similar Companies — real competitors when we have curated data for
                this ticker (higher quality), falling back to same-sector matches
                from the Browse pool otherwise. Up to 5 either way. */}
            {(function() {
              var usingCompetitors = stock.similar && stock.similar.length > 0;
              var similar = [];
              if (usingCompetitors) {
                similar = stock.similar.slice(0, 5).map(function(c) { return { t: c.t, n: c.n }; });
              } else if (stock.s && stock.s !== "—") {
                var sectorKey = stock.s;
                Object.values(BROWSE_STOCKS).forEach(function(arr) {
                  arr.forEach(function(stk) {
                    if (stk.t !== stock.t && stk.s === sectorKey && similar.length < 5) {
                      if (!similar.find(function(x) { return x.t === stk.t; })) similar.push(stk);
                    }
                  });
                });
              }
              if (similar.length === 0) return null;
              return (
                <div style={Object.assign({}, card, { padding: 14, marginBottom: 8 })}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 2 }}>{usingCompetitors ? "Similar Companies" : "Similar " + stock.s + " stocks"}</div>
                  <div style={{ fontSize: 11, color: TXM, marginBottom: 10 }}>{usingCompetitors ? "Direct competitors" : "Other stocks in this sector"}</div>
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                    {similar.map(function(stk) {
                      var f = detectFlag(stk.t, "");
                      return (
                        <div key={stk.t} onClick={function() { doSearch(stk.t, stk.n); }}
                          style={{ background: BG, border: "1px solid " + BD, borderRadius: 10, padding: "10px 12px", minWidth: 110, flexShrink: 0, cursor: "pointer" }}>
                          <div style={{ fontSize: 10, color: TX3, marginBottom: 3 }}>{f} {stk.t}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: N, whiteSpace: "nowrap" }}>{stk.n}</div>
                          <div style={{ fontSize: 11, color: B, marginTop: 4, fontWeight: 600 }}>Analyse →</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Compare nudge */}
            {stock.s && stock.s !== "—" && (
              <div style={{ marginTop: 4, marginBottom: 8 }}>
                <div onClick={function() {
                  setView("compare");
                  setStock(null);
                  setQuery("");
                }} style={{ background: BW, border: "1px solid " + BD, borderRadius: 12, padding: "12px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: N }}>How does {stock.t} compare?</div>
                    <div style={{ fontSize: 11, color: TXM, marginTop: 2 }}>Compare it side-by-side with similar {stock.s} stocks →</div>
                  </div>
                  <span style={{ fontSize: 18, color: B }}>⇄</span>
                </div>
              </div>
            )}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>

            </div>)}
          </div>
        )}

                {/* Watchlist */}
        {view === "watchlist" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <TabHeader icon={<MdStar size={16} />} title="Watchlist" badge={effectivePlan === "pro" || isFounder ? null : { text: watchlist.length + "/3", atLimit: watchlist.length >= 3 }} />
              <div style={{ display: "flex", gap: 6 }}>
                {watchlist.length > 0 && (
                  <button onClick={function() {
                    if (wlScoring) return;
                    setWlScoring(true); setWlScores([]);
                    screenStocksFn({ tickers: watchlist.map(function(w) { return w.t; }) }).then(function(r) {
                      setWlScores(r.data.results || []);
                      setWlScoring(false);
                    }).catch(function() { setWlScoring(false); setToast("Scoring failed — try again"); });
                  }} disabled={wlScoring}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + BD, background: wlScoring ? BG : N, color: wlScoring ? TXM : WH, fontSize: 12, fontWeight: 600, cursor: wlScoring ? "default" : "pointer" }}>
                    {wlScoring ? "Scoring…" : "⭐ Score all"}
                  </button>
                )}
                {watchlist.length > 0 && (
                  <button onClick={exportWatchlistCsv} title="Export as CSV" aria-label="Export watchlist as CSV"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, padding: "6px 0", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, cursor: "pointer" }}>
                    <MdDownload size={14} />
                  </button>
                )}
                {watchlist.length > 0 && (
                  <button onClick={refreshWatchlist} disabled={refreshingWL}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: refreshingWL ? TX3 : TXM, fontSize: 12, fontWeight: 600, cursor: refreshingWL ? "default" : "pointer" }}>
                    {refreshingWL ? (
                      <>{[0,0.2,0.4].map(function(d){return <div key={d} style={{width:5,height:5,borderRadius:"50%",background:N,animation:"qdot 1s "+d+"s ease-in-out infinite"}} />;})}<span>Refreshing</span></>
                    ) : "↻ Prices"}
                  </button>
                )}
              </div>
            </div>

            {watchlist.length > 0 && !digestNoteDismissed && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: BW, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                <MdMailOutline size={15} color={B} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1, fontSize: 12, color: TX2, lineHeight: 1.5 }}>You'll get a monthly email summarising how these stocks moved — turn it off any time from Profile.</div>
                <button onClick={function() { try { localStorage.setItem("q_digest_note_dismissed", "1"); } catch(e) {} setDigestNoteDismissed(true); }} aria-label="Dismiss this note" style={{ background: "none", border: "none", color: TX3, cursor: "pointer", flexShrink: 0, padding: 0, display: "flex" }}>
                  <MdClose size={15} />
                </button>
              </div>
            )}

            {/* Watchlist summary — Movers and Earnings merged into one
                tabbed card instead of two separate ones (the old "Today's
                Summary" and "Upcoming Earnings" cards showed a stock's
                earnings date twice when it fell within 7 days — once in
                each card). Same tabs-only-when-both-have-content pattern
                as Discover's Activity section. */}
            {watchlist.length > 0 && (function() {
              var bigMovers = watchlist.filter(function(w) { return w.cp !== undefined && Math.abs(w.cp) >= 3; }).sort(function(a, b) { return Math.abs(b.cp) - Math.abs(a.cp); }).slice(0, 3);
              var earningsList = [];
              watchlist.forEach(function(w) {
                if (w.earnings && w.earnings.length > 0) {
                  var upcomingE = w.earnings.filter(function(e) { return e.a === null && e.ts && e.ts > Date.now(); });
                  if (upcomingE.length > 0) {
                    var next = upcomingE[0];
                    var days = Math.round((next.ts - Date.now()) / 86400000);
                    earningsList.push({ t: w.t, n: w.n, f: w.f, q: next.q, d: next.d, days: days, ts: next.ts, e: next.e, cur: w.cur });
                  }
                }
              });
              earningsList.sort(function(a, b) { return a.ts - b.ts; });
              if (bigMovers.length === 0 && earningsList.length === 0) return null;
              var showTabs = bigMovers.length > 0 && earningsList.length > 0;
              var effectiveTab = showTabs ? wlSummaryTab : (bigMovers.length > 0 ? "movers" : "earnings");
              return (
                <div style={Object.assign({}, card, { padding: 14, marginBottom: 12, background: N, color: WH })}>
                  {showTabs ? (
                    <div style={{ display: "flex", gap: 4, background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: 3, marginBottom: 12 }}>
                      <div onClick={function() { setWlSummaryTab("movers"); }} style={{ flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 8, fontSize: 11.5, fontWeight: 700, color: effectiveTab === "movers" ? WH : "rgba(255,255,255,0.55)", background: effectiveTab === "movers" ? "rgba(255,255,255,0.14)" : "transparent", cursor: "pointer" }}>Movers</div>
                      <div onClick={function() { setWlSummaryTab("earnings"); }} style={{ flex: 1, textAlign: "center", padding: "6px 0", borderRadius: 8, fontSize: 11.5, fontWeight: 700, color: effectiveTab === "earnings" ? WH : "rgba(255,255,255,0.55)", background: effectiveTab === "earnings" ? "rgba(255,255,255,0.14)" : "transparent", cursor: "pointer" }}>Earnings</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.6, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.8 }}>{effectiveTab === "movers" ? "Today's Movers" : "Upcoming Earnings"}</div>
                  )}

                  {effectiveTab === "movers" && bigMovers.map(function(w) {
                    var wUp = w.cp >= 0;
                    return (
                      <div key={w.t} onClick={function() { doSearch(w.t, w.n); }} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, cursor: "pointer" }}>
                        <span style={{ fontWeight: 700 }}><FlagImg emoji={w.f} size={13} /> {w.t}</span>
                        <span style={{ fontWeight: 700, color: wUp ? "#5DCAA5" : "#F0997B" }}>{wUp ? "+" : ""}{w.cp.toFixed(1)}%</span>
                      </div>
                    );
                  })}

                  {effectiveTab === "earnings" && earningsList.map(function(e, i) {
                    return (
                      <div key={e.t} onClick={function() { doSearch(e.t, e.n); }} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderTop: i ? "1px solid rgba(255,255,255,0.1)" : "none", cursor: "pointer" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}><FlagImg emoji={e.f} size={13} /> {e.t}</div>
                          <div style={{ fontSize: 11, opacity: 0.6 }}>{e.q} · {e.e !== "N/A" ? "est. " + (CS$[e.cur] || "$") + e.e + " EPS" : "EPS est. TBC"}</div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: e.days <= 7 ? "#F0997B" : e.days <= 14 ? "#F0C36B" : "#8FA8D9" }}>{e.days}d</div>
                          <div style={{ fontSize: 10, opacity: 0.5 }}>{e.d}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {wlScores.length > 0 && (
              <div style={Object.assign({}, card, { padding: 14, marginBottom: 14 })}>
                <div style={{ fontSize: 14, fontWeight: 700, color: N, marginBottom: 4 }}>Watchlist Ranking</div>
                <div style={{ fontSize: 11, color: TXM, marginBottom: 10 }}>Ranked by the same Value, Quality, and Altman Z-Score blend shown on every stock's page.</div>
                {wlScores.map(function(s, i) {
                  var scoreCol = s.composite >= 65 ? OK : s.composite >= 40 ? WN : ER;
                  return (
                    <div key={s.t} onClick={function() { doSearch(s.t, s.n); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: i ? "1px solid " + BD : "none", cursor: "pointer" }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: scoreCol + "20", border: "1.5px solid " + scoreCol, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: scoreCol, flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: N }}>{s.t}</div>
                        <div style={{ fontSize: 11, color: TXM, display: "flex", gap: 6 }}>
                          {s.dcfUpside != null && <span style={{ color: s.dcfUpside >= 0 ? OKT : ERT }}>{s.dcfUpside >= 0 ? "↑" : "↓"}{Math.abs(s.dcfUpside)}% DCF</span>}
                          {s.qualityScore != null && <span>Q:{s.qualityScore}</span>}
                          {s.momentum && <span style={{ color: s.momentum === "bullish" ? OKT : s.momentum === "bearish" ? ERT : WN }}>{s.momentum}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: scoreCol }}>{s.composite}</div>
                        <div style={{ fontSize: 10, color: TXM }}>score</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {watchlist.length === 0 ? (
              <div style={Object.assign({}, card, { padding: 24, textAlign: "center" })}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>⭐</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: N, marginBottom: 6 }}>Your watchlist is empty</div>
                <p style={{ fontSize: 13, color: TXM, marginBottom: 20, lineHeight: 1.6 }}>Search for a stock and tap "Watch" to track it here, or try one of these:</p>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 8, marginBottom: 16 }}>
                  {[
                    { t: "AAPL", n: "Apple", f: "🇺🇸" }, { t: "MSFT", n: "Microsoft", f: "🇺🇸" },
                    { t: "NVDA", n: "Nvidia", f: "🇺🇸" }, { t: "RELIANCE.NS", n: "Reliance", f: "🇮🇳" },
                    { t: "AZN.L", n: "AstraZeneca", f: "🇬🇧" }, { t: "AMZN", n: "Amazon", f: "🇺🇸" }
                  ].map(function(s) {
                    return (
                      <button key={s.t} onClick={function() { doSearch(s.t, s.n); setView("result"); }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10, border: "1px solid " + BD, background: WH, cursor: "pointer", textAlign: "left" }}>
                        <span style={{ fontSize: 16 }}><FlagImg emoji={s.f} size={15} /></span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: N }}>{s.t.replace(".NS","").replace(".L","")}</div>
                          <div style={{ fontSize: 10, color: TXM }}>{s.n}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button onClick={function() { setView("home"); }} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: N, color: WH, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Search stocks →</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <style>{`.wl-card{position:relative;overflow:hidden;border-radius:12px}.wl-del{position:absolute;right:0;top:0;bottom:0;width:72px;background:${ER};display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:700;cursor:pointer;border-radius:0 12px 12px 0}.wl-inner{position:relative;transition:transform .2s ease;background:#fff;border-radius:12px;border:1px solid ${BD}}`}</style>
                {watchlist.map(function(w) {
                  var isUp = (w.cp || 0) >= 0;
                  return (
                    <div key={w.t} className="wl-card"
                      onTouchStart={function(e) { _swipeMap[w.t] = e.touches[0].clientX; }}
                      onTouchMove={function(e) {
                        var startX = _swipeMap[w.t];
                        if (startX === undefined) return;
                        var dx = e.touches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".wl-inner");
                        if (dx < 0) inner.style.transform = "translateX(" + Math.max(dx, -72) + "px)";
                      }}
                      onTouchEnd={function(e) {
                        var startX = _swipeMap[w.t];
                        if (startX === undefined) return;
                        var dx = e.changedTouches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".wl-inner");
                        if (dx < -40) { inner.style.transform = "translateX(-72px)"; }
                        else { inner.style.transform = "translateX(0)"; }
                        delete _swipeMap[w.t];
                      }}>
                      <div className="wl-del" onClick={function() { setWatchlist(function(wl) { return wl.filter(function(x) { return x.t !== w.t; }); }); haptic(10); }}>Delete</div>
                      <div className="wl-inner" style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ flex: 1, cursor: "pointer", minWidth: 0 }} onClick={function() { doSearch(w.t, w.n); }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: N }}><FlagImg emoji={w.f} size={15} /> {w.t}</div>
                          <div style={{ fontSize: 12, color: TXM }}>{w.n}</div>
                          {notes[w.t] && <div style={{ fontSize: 11, color: TXM, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180, display: "flex", alignItems: "center", gap: 4 }}><MdStickyNote2 size={12} style={{ flexShrink: 0 }} /> {notes[w.t]}</div>}
                          {thesis[w.t] && thesis[w.t].activeAlert && (
                            <div onClick={function(e) { e.stopPropagation(); doSearch(w.t, w.n); setOpenSections(function(prev) { return Object.assign({}, prev, { thesis: true }); }); }}
                              style={{ marginTop: 6, background: WNS, borderRadius: 8, padding: "6px 9px", display: "flex", alignItems: "flex-start", gap: 6, maxWidth: 220, cursor: "pointer" }}>
                              <MdWarningAmber size={13} color={WN} style={{ flexShrink: 0, marginTop: 1 }} />
                              <span style={{ fontSize: 10.5, color: WN, lineHeight: 1.4 }}>{thesis[w.t].activeAlert.message}</span>
                            </div>
                          )}
                          {w.v && w.v !== "FAIRLY VALUED" && (
                            <div style={{ marginTop: 5 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
                                background: w.v === "UNDERVALUED" ? OKS : ERS,
                                color: w.v === "UNDERVALUED" ? OKT : ERT }}>
                                {w.v}
                              </span>
                              {w.sc && <span style={{ fontSize: 10, color: TX3, marginLeft: 5 }}>{w.sc}/100</span>}
                            </div>
                          )}
                          {w.v === "FAIRLY VALUED" && (
                            <div style={{ marginTop: 5 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: WNS, color: WN }}>FAIRLY VALUED</span>
                              {w.sc && <span style={{ fontSize: 10, color: TX3, marginLeft: 5 }}>{w.sc}/100</span>}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: TX }}>{(CS$[w.cur] || "$") + w.p.toFixed(2)}</div>
                          {w.cp !== undefined && (
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 11, fontWeight: 700, color: isUp ? OKT : ERT, background: isUp ? OKS : ERS, borderRadius: 20, padding: "2px 8px", marginTop: 3 }}>
                              {isUp ? <MdTrendingUp size={11} /> : <MdTrendingDown size={11} />}{Math.abs(w.cp).toFixed(2)}%
                            </div>
                          )}
                          {w.sc != null && <div style={{ fontSize: 11, fontWeight: 700, color: w.sc >= 65 ? OKT : w.sc >= 40 ? WN : ERT, marginTop: 3 }}>{w.sc}/100</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>
          </div>
        )}

        {/* Screener */}
        {view === "screener" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <TabHeader icon={<MdTune size={16} />} title="Stock Screener" subtitle={true} />
            <p style={{ fontSize: 13, color: TXM, marginBottom: 14, lineHeight: 1.6 }}>
              Find stocks worth a closer look — filter by overall valuation, business quality, or bullish momentum across US, UK, India, Japan, and Europe. Tap any result to see its full AI analysis, including DCF valuation, on the detail page.
            </p>

            {/* Region filter */}
            <div style={{ fontSize: 11, fontWeight: 600, color: TX3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>Region</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              {[{ k: "all", l: "All" }, { k: "US", l: "🇺🇸 US" }, { k: "UK", l: "🇬🇧 UK" }, { k: "India", l: "🇮🇳 India" }, { k: "Japan", l: "🇯🇵 Japan" }, { k: "Europe", l: "🇪🇺 Europe" }].map(function(r) {
                return (
                  <button key={r.k} onClick={function() { setScreenerRegion(r.k); setScreenerResults([]); setScreenerRaw([]); setScreenerHasRun(false); }}
                    style={{ padding: "5px 12px", borderRadius: 16, border: "1px solid " + (screenerRegion === r.k ? N : BD), background: screenerRegion === r.k ? N : WH, color: screenerRegion === r.k ? WH : TXM, fontSize: 12, cursor: "pointer" }}>
                    {r.l}
                  </button>
                );
              })}
            </div>

            {/* Filters */}
            <div style={{ fontSize: 11, fontWeight: 600, color: TX3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6 }}>Filters</div>
            <div style={Object.assign({}, card, { padding: 12, marginBottom: 14 })}>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TXM, marginBottom: 4 }}>Valuation</div>
                  <select value={screenerFilters.valuation || ""} onChange={function(e) { setScreenerFilters(function(prev) { return Object.assign({}, prev, { valuation: e.target.value || null }); }); }}
                    style={{ width: "100%", padding: "10px 8px", borderRadius: 8, border: "1.5px solid " + BD, fontSize: 12, fontWeight: 700, color: N, background: WH }}>
                    <option value="">Any</option>
                    <option value="UNDERVALUED">Undervalued</option>
                    <option value="FAIRLY VALUED">Fairly valued</option>
                    <option value="OVERVALUED">Overvalued</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TXM, marginBottom: 4 }}>Sector</div>
                  <select value={screenerFilters.sector || ""} onChange={function(e) { setScreenerFilters(function(prev) { return Object.assign({}, prev, { sector: e.target.value || null }); }); }}
                    style={{ width: "100%", padding: "10px 8px", borderRadius: 8, border: "1.5px solid " + BD, fontSize: 12, fontWeight: 700, color: N, background: WH }}>
                    <option value="">Any</option>
                    {["Technology", "Healthcare", "Financial Services", "Consumer Cyclical", "Consumer Defensive", "Industrials", "Energy", "Basic Materials", "Communication Services", "Utilities", "Real Estate"].map(function(sec) {
                      return <option key={sec} value={sec}>{sec}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: TXM, marginBottom: 4 }}>Sort by</div>
                <select value={screenerSort || ""} onChange={function(e) { setScreenerSort(e.target.value || null); }}
                  style={{ width: "100%", padding: "10px 8px", borderRadius: 8, border: "1.5px solid " + BD, fontSize: 12, fontWeight: 700, color: N, background: WH }}>
                  <option value="">Default order</option>
                  <option value="value">Value score (high to low)</option>
                  <option value="quality">Quality score (high to low)</option>
                  <option value="target">1-year target upside (high to low)</option>
                  <option value="safety">Altman Z-Score (high to low)</option>
                  <option value="change">Price change % (high to low)</option>
                </select>
              </div>
              <div style={{ fontSize: 10.5, color: TX3, marginBottom: 12 }}>Valuation reflects the same overall verdict shown on a stock's detail page — not just one metric.</div>

              {[
                { k: "quality", l: "High quality (75+)", desc: "Strong ROE, cash flow, low debt." },
                { k: "bullish", l: "Bullish momentum", desc: "Above its 50 & 200-day averages." },
                { k: "safe", l: "Financially safe (Z > 3.0)", desc: "Low distress risk (Altman Z-Score). Excludes banks and stocks with no score available." },
                { k: "positiveTarget", l: "Positive 1-year target", desc: "DCF or justified P/B model agrees the price should rise, not just the overall verdict. Excludes stocks with no target available." },
              ].map(function(f, i) {
                return (
                  <div key={f.k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                    <div style={{ paddingRight: 12 }}>
                      <div style={{ fontSize: 13, color: N, fontWeight: 500 }}>{f.l}</div>
                      <div style={{ fontSize: 10.5, color: TX3 }}>{f.desc}</div>
                    </div>
                    <div onClick={function() { setScreenerFilters(function(prev) { var n = Object.assign({}, prev); n[f.k] = !n[f.k]; return n; }); }}
                      style={{ width: 40, height: 24, borderRadius: 12, background: screenerFilters[f.k] ? N : BD, cursor: "pointer", position: "relative", flexShrink: 0, transition: "background .2s" }}>
                      <div style={{ position: "absolute", top: 3, left: screenerFilters[f.k] ? 18 : 3, width: 18, height: 18, borderRadius: "50%", background: WH, transition: "left .2s" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Score explanation */}
            <div style={Object.assign({}, card, { padding: 12, marginBottom: 14 })}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                onClick={function() { setScreenerResults(function(r) { return r; }); tog("screener_how"); }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: N }}>How the Valuation verdict is calculated</span>
                <span style={{ fontSize: 11, color: TX3 }}>{openSections.screener_how ? "▲" : "▼"}</span>
              </div>
              {openSections.screener_how && (
                <div style={{ marginTop: 10, borderTop: "1px solid " + BD, paddingTop: 10 }}>
                  <div style={{ fontSize: 11, color: TXM, lineHeight: 1.5, marginBottom: 10 }}>Not every factor applies to every stock, and the weighting itself adapts by sector — DCF matters more for stable, cash-generative businesses (like Energy or Industrials) and less for high-growth companies with small or volatile free cash flow (like Technology), where PEG and forward estimates carry more weight instead. Financials skip DCF entirely and lean on quality and analyst consensus. For emerging markets, weight shifts modestly from analyst consensus (thinner, less reliable coverage) toward DCF and fundamentals.</div>
                  {[["PEG ratio", "15–30%", "Price/earnings relative to growth rate. Below 1.0 suggests the price is reasonable for the growth you're getting."],
                    ["Forward P/E vs sector", "18–22%", "How the stock's forward P/E compares to its sector average — cheaper than peers scores higher."],
                    ["Analyst consensus", "12–24%", "The percentage of covering analysts rating the stock a Buy. Weighted down for emerging markets."],
                    ["DCF upside", "0–26%", "How far below our estimated fair value (discounted cash flow) the stock currently trades. 0% for financials, where DCF doesn't apply."],
                    ["Momentum", "10–18%", "Is the price above both its 50-day and 200-day moving averages?"],
                    ["Quality score", "8–20%", "Business fundamentals — ROE, cash flow, debt. Weighted higher for financials and mature businesses."],
                    ["Financial Safety", "0–15%", "Altman Z-Score — financial-distress risk from the balance sheet. Not applied to banks/financial companies, or when there isn't enough data to calculate it."],
                  ].map(function(r, i) {
                    return (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: i < 6 ? "1px solid " + BD : "none" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: TX }}>{r[0]}</div>
                          <div style={{ fontSize: 11, color: TXM, lineHeight: 1.4 }}>{r[2]}</div>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: B, flexShrink: 0 }}>{r[1]}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button onClick={function() {
              if (screenerLoading) return;
              setScreenerLoading(true); setScreenerResults([]); setScreenerRaw([]);
              setStickyToast("Scanning stocks…");
              // Same 165-stock, region-balanced pool used for Browse and the daily
              // AI Pick — replacing the old 73-stock, US-heavy list, and the old
              // "All regions" sampling (which took only 15 stocks total, 33% US)
              // with a genuinely balanced 8-per-region sample.
              var regionTickers = {
                US: ["AAPL","NVDA","MSFT","TSLA","GOOG","AMZN","META","JPM","V","JNJ","WMT","NFLX","ORCL","AMD","INTC",
                  "DIS","PG","KO","PEP","MA","BAC","XOM","CVX","UNH","PFE","HD","MCD","NKE","ADBE","CRM","QCOM","IBM",
                  "HON","VZ","GE","FCX","GRMN","BA","NOW","RL","EA",],
                UK: ["SHEL.L","AZN.L","BP.L","HSBA.L","ULVR.L","VOD.L","LSEG.L","GSK.L","RIO.L","BARC.L","DGE.L","REL.L",
                  "NG.L","BATS.L","PRU.L","LLOY.L","NWG.L","AAL.L","GLEN.L","CPG.L","TSCO.L","SBRY.L","IMB.L","SSE.L",
                  "CRH.L","III.L","ANTO.L","SGE.L","BA.L","RR.L","STAN.L","AML.L","ASOS.L","IQE.L","ITV.L","BNZL.L",
                  "BRBY.L","TM17.L","RTO.L","CRDA.L","TRST.L",],
                India: ["RELIANCE.NS","TCS.NS","HDFCBANK.NS","INFY.NS","ICICIBANK.NS","SBIN.NS","WIPRO.NS","BHARTIARTL.NS",
                  "BAJFINANCE.NS","MARUTI.NS","ADANIENT.NS","KOTAKBANK.NS","HINDUNILVR.NS","ITC.NS","LT.NS",
                  "AXISBANK.NS","ASIANPAINT.NS","SUNPHARMA.NS","TITAN.NS","ULTRACEMCO.NS","NESTLEIND.NS","ONGC.NS",
                  "NTPC.NS","POWERGRID.NS","TATASTEEL.NS","TATAMOTORS.NS","HCLTECH.NS","TECHM.NS","BAJAJFINSV.NS",
                  "HDFCLIFE.NS","SBILIFE.NS","DRREDDY.NS","CIPLA.NS","GRASIM.NS","JSWSTEEL.NS","COALINDIA.NS",
                  "INDUSINDBK.NS","NYKAA.NS","DIXON.NS","VEDL.NS","HAL.NS","HAVELLS.NS","PERSISTENT.NS","ZEEL.NS",
                  "NAZARA.NS","DMART.NS","TRENT.NS","EICHERMOT.NS","BAJAJ-AUTO.NS",],
                Japan: ["7203.T","6758.T","9984.T","6861.T","9432.T","7974.T","6501.T","8306.T","6098.T","8035.T","6902.T",
                  "7267.T","7201.T","4063.T","6367.T","8058.T","8031.T","8001.T","4502.T","4568.T","9433.T","9983.T",
                  "8316.T","8411.T","6273.T","6503.T","6178.T","4661.T","9020.T","5108.T","7011.T","1605.T","4755.T",
                  "5713.T","4452.T","4911.T","4684.T","6954.T","8604.T","7269.T",],
                Europe: ["ASML.AS","SAP.DE","MC.PA","SIE.DE","NESN.SW","NOVN.SW","OR.PA","AIR.PA","TTE.PA","SAN.PA","BNP.PA",
                  "KER.PA","DG.PA","ALV.DE","DTE.DE","BAS.DE","BMW.DE","MBG.DE","VOW3.DE","DBK.DE","IBE.MC","SAN.MC",
                  "ITX.MC","UCG.MI","ENI.MI","ISP.MI","RACE.MI","ABI.BR","ADYEN.AS","PHIA.AS","INGA.AS","ROG.SW",
                  "UBSG.SW","ZURN.SW","ZAL.DE","MT.AS","BOL.PA","UBI.PA","LOGN.SW","DHL.DE","VIV.PA",],
              };
              var tickers = screenerRegion === "all"
                ? [].concat(regionTickers.US.slice(0,40), regionTickers.UK.slice(0,40), regionTickers.India.slice(0,40), regionTickers.Japan.slice(0,40), regionTickers.Europe.slice(0,40))
                : (regionTickers[screenerRegion] || regionTickers.US);
              screenStocksFn({ tickers: tickers }).then(function(r) {
                var results = r.data.results || [];
                setScreenerRaw(results);
                setScreenerHasRun(true);
                setScreenerLoading(false);
                setStickyToast(null);
              }).catch(function() { setScreenerLoading(false); setStickyToast(null); setToast("Screener failed — Yahoo Finance timed out. Try a smaller region or try again."); });
            }} disabled={screenerLoading}
              style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: screenerLoading ? BG : N, color: screenerLoading ? TXM : WH, fontSize: 14, fontWeight: 700, cursor: screenerLoading ? "default" : "pointer", marginBottom: 16 }}>
              {screenerLoading ? "Scanning stocks…" : "Run Screener →"}
            </button>

            {screenerLoading && (
              <div style={{ background: N, borderRadius: 16, padding: "36px 20px", textAlign: "center" }}>
                <div style={{ position: "relative", width: 64, height: 64, margin: "0 auto 20px" }}>
                  <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid rgba(93,202,165,0.35)", animation: "pulseRing 1.8s ease-out infinite" }} />
                  <div style={{ position: "absolute", inset: 8, borderRadius: "50%", border: "2px solid rgba(93,202,165,0.5)", animation: "pulseRing 1.8s ease-out infinite .5s" }} />
                  <div style={{ position: "absolute", inset: 16, borderRadius: "50%", background: AC, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MdTune size={17} color={WH} />
                  </div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: WH, marginBottom: 6 }}>Scanning stocks…</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginBottom: 16 }}>Fetching live financial data for each stock in this region</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 20, padding: "7px 14px" }}>
                  <MdSchedule size={13} color="rgba(255,255,255,0.65)" />
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Can take up to 2 minutes for a full region — scanning more stocks per region now</span>
                </div>
              </div>
            )}

            {!screenerLoading && screenerResults.length === 0 && screenerHasRun && (screenerFilters.valuation || screenerFilters.quality || screenerFilters.bullish || screenerFilters.safe || screenerFilters.sector) && (
              <div style={Object.assign({}, card, { padding: 28, textAlign: "center" })}>
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><MdSearchOff size={28} color={TX3} /></div>
                <div style={{ fontSize: 15, fontWeight: 700, color: N, marginBottom: 6 }}>No stocks match your filters</div>
                <div style={{ fontSize: 13, color: TXM, lineHeight: 1.5 }}>Screener checks a sample of stocks per scan, not the entire market — combining several specific filters (e.g. one sector + high quality) can genuinely turn up nothing if none of the sampled stocks happen to fit. Try removing a filter, or run again for a fresh sample.</div>
              </div>
            )}

            {!screenerLoading && !screenerHasRun && screenerResults.length === 0 && (
              <div style={Object.assign({}, card, { padding: 24, textAlign: "center", marginBottom: 12 })}>
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><MdBarChart size={28} color={TX3} /></div>
                <div style={{ fontSize: 14, color: TXM, lineHeight: 1.6 }}>Select a region and tap Run Screener to find stocks.</div>
              </div>
            )}

            {screenerResults.length > 0 && (
              <div style={Object.assign({}, card, { padding: 14 })}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: N }}>{screenerResults.length} stocks found</div>
                  <button onClick={function() { setScreenerResults([]); setScreenerRaw([]); setScreenerHasRun(false); }}
                    style={{ fontSize: 11, fontWeight: 700, color: TXM, background: BG, border: "none", borderRadius: 20, padding: "5px 11px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                    <MdClose size={13} /> Clear
                  </button>
                </div>
                <div style={{ fontSize: 11, color: TXM, marginBottom: 12, lineHeight: 1.5 }}>These match your selected filter(s) only — a stock can pass one filter (e.g. bullish momentum) while scoring differently on others. Tap any stock for its full, multi-factor AI analysis.</div>
                {screenerResults.map(function(s, i) {
                  var sym = CS$[s.cur] || "$";
                  var flag = detectFlag(s.t, "");
                  var isWatchedS = watchlist.some(function(w) { return w.t === s.t; });

                  // Badge selection — prioritized, not exhaustive. Showing
                  // up to 7 competing badges per row (verdict, value,
                  // quality, Altman Z, momentum, PEG, distress) made every
                  // row read as equally important and hard to scan at a
                  // glance. Verdict always leads; distress risk always
                  // shows when present since it's safety-relevant and
                  // shouldn't get crowded out by everything else; beyond
                  // that, at most 2 standout signals — whichever of
                  // Value/Quality actually stands out, plus momentum if
                  // it's a real bullish/bearish signal. The full picture
                  // (all pillars, PEG, etc.) is still one tap away on the
                  // stock's own page — this row just needs to help decide
                  // whether that tap is worth it.
                  function pillarBadgeColor(v) { return v >= 65 ? { col: OKT, bg: OKS } : v >= 40 ? { col: WN, bg: WNS } : { col: ERT, bg: ERS }; }
                  var badges = [];
                  if (s.verdict === "UNDERVALUED") badges.push({ label: "Undervalued", col: OKT, bg: OKS });
                  else if (s.verdict === "OVERVALUED") badges.push({ label: "Overvalued", col: ERT, bg: ERS });
                  else if (s.verdict === "FAIRLY VALUED") badges.push({ label: "Fairly valued", col: WN, bg: WNS });
                  if (s.pillars && s.pillars.safetyRaw && s.pillars.safetyRaw.zone === "distress") badges.push({ label: "Distress risk", col: ERT, bg: ERS });

                  var standouts = [];
                  var valScore = s.pillars && s.pillars.value != null ? s.pillars.value : null;
                  var qualScore = s.qualityScore != null ? s.qualityScore : null;
                  if (valScore != null || qualScore != null) {
                    var useQuality = qualScore != null && (valScore == null || qualScore >= valScore);
                    var pillarLabel = useQuality ? "Quality " + qualScore : "Value " + valScore;
                    var pillarVal = useQuality ? qualScore : valScore;
                    standouts.push(Object.assign({ label: pillarLabel }, pillarBadgeColor(pillarVal)));
                  }
                  if (s.momentum === "bullish") standouts.push({ label: "Bullish", col: OKT, bg: OKS });
                  else if (s.momentum === "bearish") standouts.push({ label: "Bearish", col: ERT, bg: ERS });
                  badges = badges.concat(standouts.slice(0, 2));

                  return (
                    <div key={s.t} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderTop: i ? "1px solid " + BD : "none" }}>
                      <div onClick={function() { doSearch(s.t, s.n); }} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: N, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 1 }}><FlagImg emoji={flag} /> {s.n || s.t}</div>
                        <div style={{ fontSize: 11, color: TX3, marginBottom: 6 }}>{s.t}{s.sector ? " · " + s.sector : ""}</div>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                          {badges.map(function(b, bi) {
                            return <span key={bi} style={{ fontSize: 10, fontWeight: 700, color: b.col, background: b.bg, borderRadius: 20, padding: "3px 9px" }}>{b.label}</span>;
                          })}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                        <div onClick={function() { doSearch(s.t, s.n); }} style={{ cursor: "pointer", textAlign: "right" }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: N }}>{sym}{s.p.toLocaleString("en-US")}</div>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 11, fontWeight: 700, color: s.cp >= 0 ? OKT : ERT, background: s.cp >= 0 ? OKS : ERS, borderRadius: 20, padding: "2px 8px", marginTop: 2 }}>
                            {s.cp >= 0 ? <MdTrendingUp size={11} /> : <MdTrendingDown size={11} />}{Math.abs(s.cp)}%
                          </div>
                          {s.pe != null && <div style={{ fontSize: 10, color: TXM, marginTop: 2 }}>P/E {s.pe}</div>}
                          {s.oneYearUpside != null && <div style={{ fontSize: 10, color: s.oneYearUpside >= 0 ? OKT : ERT, marginTop: 2 }}>1yr target {s.oneYearUpside >= 0 ? "+" : ""}{s.oneYearUpside}%</div>}
                        </div>
                        <button onClick={function(e) { e.stopPropagation();
                          if (isWatchedS) { setWatchlist(function(w) { return w.filter(function(x) { return x.t !== s.t; }); }); setToast("Removed from watchlist"); }
                          else {
                            var wlLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? Infinity : 3;
                            if (watchlist.length >= wlLimit) { setToast("Standard watchlist holds 3 stocks at a time — remove one to add another, or upgrade to Pro for unlimited."); return; }
                            var wlVerdict = s.composite != null ? (s.composite >= 65 ? "UNDERVALUED" : s.composite >= 42 ? "FAIRLY VALUED" : "OVERVALUED") : null; setWatchlist(function(w) { return [{ t: s.t, n: s.n, f: flag, p: s.p, cur: s.cur, cp: s.cp, v: wlVerdict, sc: s.composite || null }].concat(w); }); setToast("Added to watchlist"); haptic([10,50,10]);
                          }
                        }} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, border: "1px solid " + (isWatchedS ? OK : BD), background: isWatchedS ? OKS : WH, color: isWatchedS ? OKT : TXM, cursor: "pointer", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {isWatchedS ? <MdStar size={12} /> : <MdStarBorder size={12} />} {isWatchedS ? "Saved" : "Watch"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {screenerResults.length > 0 && (function() {
              // Sector rotation heatmap — score, momentum breakdown, top pick per sector.
              // Moved below the actual results (was previously above them,
              // pushing the thing you actually asked for — the filtered
              // stock list — down the page behind a big secondary block).
              // Collapsed by default for the same reason: this is bonus
              // context on top of your results, not the primary deliverable.
              var sectorMap = {};
              screenerResults.forEach(function(s) {
                var sec = s.sector || "Other";
                if (!sectorMap[sec]) sectorMap[sec] = { scores: [], bullish: 0, bearish: 0, count: 0, topPick: null };
                if (s.composite != null) sectorMap[sec].scores.push(s.composite);
                if (s.momentum === "bullish") sectorMap[sec].bullish++;
                else if (s.momentum === "bearish") sectorMap[sec].bearish++;
                sectorMap[sec].count++;
                if (!sectorMap[sec].topPick || (s.composite != null && s.composite > (sectorMap[sec].topPick.composite || 0))) {
                  sectorMap[sec].topPick = s;
                }
              });
              var sectorList = Object.entries(sectorMap).map(function(e) {
                var avg = e[1].scores.length > 0 ? Math.round(e[1].scores.reduce(function(a,b){return a+b;},0) / e[1].scores.length) : null;
                var momPct = e[1].count > 0 ? Math.round((e[1].bullish / e[1].count) * 100) : 0;
                return { name: e[0], avg: avg, count: e[1].count, momPct: momPct, topPick: e[1].topPick };
              }).filter(function(s) { return s.avg !== null; }).sort(function(a,b) { return b.avg - a.avg; });
              if (sectorList.length < 2) return null;
              var maxAvg = sectorList[0].avg || 100;
              return (
                <div style={Object.assign({}, card, { padding: 14, marginTop: 12 })}>
                  <div onClick={function() { setShowSectorRotation(!showSectorRotation); }}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 2 }}>Sector Rotation</div>
                      <div style={{ fontSize: 11, color: TXM }}>Where is value concentrated right now?</div>
                    </div>
                    <span style={{ fontSize: 18, color: TX3, transform: showSectorRotation ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
                  </div>
                  {showSectorRotation && (
                    <div style={{ marginTop: 12 }}>
                      {sectorList.map(function(sec, i) {
                        var col = sec.avg >= 65 ? OK : sec.avg >= 42 ? WN : ER;
                        var colT = sec.avg >= 65 ? OKT : sec.avg >= 42 ? WN : ERT;
                        return (
                          <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < sectorList.length - 1 ? "1px solid " + BD : "none" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: N, display: "flex", alignItems: "center", gap: 6 }}>
                                {i < 3 && <span style={{ width: 16, height: 16, borderRadius: "50%", background: ACS, color: ACT, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>}
                                {sec.name}
                              </span>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 10, color: sec.momPct >= 60 ? OKT : sec.momPct <= 30 ? ERT : WN }}>
                                  {sec.momPct >= 60 ? "▲" : sec.momPct <= 30 ? "▼" : "→"} {sec.momPct}% bullish
                                </span>
                                <div style={{ textAlign: "right" }}>
                                  <span style={{ fontSize: 14, fontWeight: 800, color: colT }}>{sec.avg}</span>
                                  <div style={{ fontSize: 9, color: TX3 }}>avg score</div>
                                </div>
                              </div>
                            </div>
                            <div style={{ height: 8, background: BD, borderRadius: 4, marginBottom: 5 }}>
                              <div style={{ height: 8, background: col, borderRadius: 4, width: Math.round((sec.avg / maxAvg) * 100) + "%" }} />
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 10, color: TX3 }}>{sec.count} stock{sec.count > 1 ? "s" : ""} analysed</span>
                              {sec.topPick && (
                                <span onClick={function(e) { e.stopPropagation(); doSearch(sec.topPick.t, sec.topPick.n); }}
                                  style={{ fontSize: 11, color: B, fontWeight: 600, cursor: "pointer" }}>
                                  Top pick: {sec.topPick.t} →
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>
          </div>
        )}

        {/* Compare */}
        {view === "compare" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <TabHeader icon={<MdBalance size={16} />} title="Compare Stocks" badge={{ text: cmpList.length + "/" + ((effectivePlan === "pro" || isFounder) ? 6 : 3), atLimit: cmpList.length >= ((effectivePlan === "pro" || isFounder) ? 6 : 3) }} />

            {/* Compare search bar */}
            {cmpList.length < ((effectivePlan === "pro" || effectivePlan === "founder") ? 6 : 3) && (
              <div ref={cmpDropRef} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
                  <input
                    value={cmpQuery}
                    onChange={function(e) { setCmpQuery(e.target.value); }}
                    placeholder="Search stock to add..."
                    list="stock-suggestions"
                    style={{ flex: 1, minWidth: 0, background: WH, border: "2px solid " + BD, borderRadius: 10, padding: "10px 12px", fontSize: 16, color: TX }}
                  />
                </div>
                {cmpSearchResults.length > 0 && (
                  <div style={{ background: WH, border: "1px solid " + BD, borderRadius: 10, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
                    {cmpSearchResults.map(function(s, i) {
                      var already = cmpList.includes(s.t);
                      return (
                        <div key={s.t + i} onClick={function() { if (!already) { addToCompare(s.t); setCmpSearchResults([]); setCmpQuery(""); } }}
                          style={{ padding: "11px 14px", borderTop: i > 0 ? "1px solid " + BD : "none", cursor: already ? "default" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", opacity: already ? 0.4 : 1, transition: "background .1s" }}
                          onMouseEnter={function(e) { if (!already) e.currentTarget.style.background = BG; }}
                          onMouseLeave={function(e) { e.currentTarget.style.background = WH; }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontWeight: 700, color: N, marginRight: 6 }}><FlagImg emoji={s.f} /> {s.t}</span>
                            <span style={{ color: TXM, fontSize: 13 }}>{s.n}</span>
                          </div>
                          <span style={{ fontSize: 12, color: already ? TX3 : B, flexShrink: 0, marginLeft: 8 }}>{already ? "Added" : "+ Add"}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Recently viewed quick-add */}
            {recent.length > 0 && cmpList.length < ((effectivePlan === "pro" || effectivePlan === "founder") ? 6 : 3) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: TXM, marginBottom: 6 }}>Quick add from recent:</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {recent.filter(function(r) { return !cmpList.includes(r.t); }).map(function(r) {
                    return <button key={r.t} onClick={function() { addToCompare(r.t); }}
                      style={{ padding: "4px 10px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TX2, fontSize: 12, cursor: "pointer" }}><FlagImg emoji={r.f} /> {r.t}</button>;
                  })}
                </div>
              </div>
            )}

            {/* Selected stocks chips */}
            {cmpList.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {cmpList.map(function(t) {
                  var s = cmpStocks[t];
                  return (
                    <div key={t} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, background: N, color: WH, fontSize: 12, fontWeight: 600 }}>
                      {s ? (s.f + " " + t) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 9, height: 9, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: WH, borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} />
                          {t}
                        </span>
                      )}
                      <span onClick={function() { setCmp(function(c) { return c.filter(function(x) { return x !== t; }); }); }}
                        style={{ cursor: "pointer", opacity: 0.7, fontSize: 14 }}>×</span>
                    </div>
                  );
                })}
                <span style={{ fontSize: 12, color: TX3, alignSelf: "center" }}>{3 - cmpList.length} more</span>
              </div>
            )}

            {/* Comparison table */}
            {cmpList.length > 0 && (
              <div style={Object.assign({}, card, { overflowX: "auto" })}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid " + BD }}>
                      <th style={{ padding: 8, textAlign: "left", color: TX3, fontSize: 11 }}>Metric</th>
                      {cmpList.map(function(t) {
                        var s = cmpStocks[t];
                        return (
                          <th key={t} style={{ padding: 8, textAlign: "right", fontWeight: 800, color: N, fontSize: 12 }}>
                            {s ? (s.f + " " + t) : (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: TXM, fontWeight: 600 }}>
                                <span style={{ width: 10, height: 10, border: "2px solid " + BD, borderTopColor: TXM, borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} />
                                {t}
                              </span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(function() {
                      // Semantic pillar coloring reuses the exact same
                      // thresholds ScoreRing already uses elsewhere in the
                      // app (>=65 green, >=40 amber, below red) — same
                      // meaning, same colors, wherever a score shows up.
                      function pillarColor(v) {
                        if (v == null) return TX3;
                        return v >= 65 ? OKT : v >= 40 ? WN : ERT;
                      }
                      var rows = [
                        { row: ["Price", function(s) { return s ? (CS$[s.cur] || "$") + s.p.toFixed(2) : "..."; }] },
                        { row: ["Verdict", function(s) { return s ? s.v : "..."; }], color: function(v) { return v === "UNDERVALUED" ? OKT : v === "OVERVALUED" ? ERT : WN; } },
                        { row: ["vs Analyst Target", function(s) {
                          if (!s || !s.m || !s.m.at || !s.p) return "—";
                          var pct = ((s.m.at - s.p) / s.p) * 100;
                          return (pct >= 0 ? "+" : "") + pct.toFixed(1) + "%";
                        }], color: function(v) { return v === "—" ? TX3 : v.charAt(0) === "+" ? OKT : ERT; } },
                        { section: "Pillars" },
                        { row: ["Value", function(s) { return s && s.pillars && s.pillars.value != null ? s.pillars.value : "—"; }], color: function(v) { return v === "—" ? TX3 : pillarColor(v); } },
                        { row: ["Quality", function(s) { return s && s.pillars && s.pillars.quality != null ? s.pillars.quality : "—"; }], color: function(v) { return v === "—" ? TX3 : pillarColor(v); } },
                        { row: ["Altman Z", function(s) { return s && s.pillars && s.pillars.safety != null ? s.pillars.safety : "—"; }], color: function(v) { return v === "—" ? TX3 : pillarColor(v); } },
                        { section: "Key metrics" },
                        { row: ["P/E", function(s) { return s && s.m ? s.m.pe || "—" : "..."; }] },
                        { row: ["PEG", function(s) { return s && s.m ? s.m.peg || "—" : "..."; }] },
                        { row: ["Div Yield", function(s) { return s && s.m ? s.m.dy || "—" : "..."; }] },
                      ];
                      var moreRows = [
                        ["Fwd P/E", function(s) { return s && s.m ? s.m.fpe || "—" : "..."; }],
                        ["Mkt Cap", function(s) { return s && s.m ? s.m.mc || "—" : "..."; }],
                        ["EPS Growth", function(s) { return s && s.m ? s.m.eg || "—" : "..."; }],
                        ["Rev Growth", function(s) { return s && s.m ? s.m.rg || "—" : "..."; }],
                        ["ROE", function(s) { return s && s.m ? s.m.roe || "—" : "..."; }],
                        ["Beta", function(s) { return s && s.m ? s.m.bt || "—" : "..."; }],
                        ["52w High", function(s) { return s && s.m ? s.m.hi || "—" : "..."; }],
                        ["52w Low", function(s) { return s && s.m ? s.m.lo || "—" : "..."; }],
                      ];
                      if (cmpShowMore) { moreRows.forEach(function(r) { rows.push({ row: r }); }); }

                      return rows.map(function(item, i) {
                        if (item.section) {
                          return (
                            <tr key={"sec" + i}>
                              <td colSpan={cmpList.length + 1} style={{ padding: "12px 8px 5px", fontSize: 10.5, fontWeight: 800, color: B, textTransform: "uppercase", letterSpacing: 0.5 }}>{item.section}</td>
                            </tr>
                          );
                        }
                        var row = item.row;
                        return (
                          <tr key={i} style={{ borderBottom: "1px solid " + BD }}>
                            <td style={{ padding: 8, color: TXM, fontSize: 12 }}>{row[0]}</td>
                            {cmpList.map(function(t) {
                              var s = cmpStocks[t];
                              var v = row[1](s);
                              var color = item.color ? item.color(v) : TX;
                              return <td key={t} style={{ padding: 8, textAlign: "right", fontWeight: item.color ? 700 : 500, color: color, fontSize: 12 }}>{v}</td>;
                            })}
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
                <button onClick={function() { setCmpShowMore(function(v) { return !v; }); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: 10, background: "none", border: "none", borderTop: "1px solid " + BD, cursor: "pointer", color: B, fontSize: 12, fontWeight: 600 }}>
                  {cmpShowMore ? "Show fewer metrics" : "Show more metrics"}
                  <MdChevronRight size={14} style={{ transform: cmpShowMore ? "rotate(-90deg)" : "rotate(90deg)", transition: "transform .2s" }} />
                </button>
              </div>
            )}

            {cmpList.length === 0 && recent.length === 0 && (
              <div style={Object.assign({}, card, { padding: 24, textAlign: "center" })}>
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><MdBalance size={32} color={TX3} /></div>
                <div style={{ fontSize: 15, fontWeight: 700, color: N, marginBottom: 6 }}>Nothing to compare yet</div>
                <p style={{ fontSize: 13, color: TXM, marginBottom: 16, lineHeight: 1.6 }}>Search above to add stocks side by side, or start with:</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                  {[{ t: "AAPL", n: "Apple" }, { t: "MSFT", n: "Microsoft" }, { t: "GOOG", n: "Alphabet" }].map(function(s) {
                    return (
                      <button key={s.t} onClick={function() { addToCompare(s.t); }}
                        style={{ padding: "7px 13px", borderRadius: 20, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        {s.t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>
          </div>
        )}

        {/* Weekly Deep Dive — dedicated view */}
        {view === "weeklyInsight" && weeklyInsight && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <div onClick={function() { setView("home"); }} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, cursor: "pointer", color: TXM, fontSize: 13, fontWeight: 600 }}>
              <MdChevronRight size={16} style={{ transform: "rotate(180deg)" }} /> Back to Discover
            </div>

            <div style={{ background: "linear-gradient(135deg, #1E1240, #2D1B54)", borderRadius: 18, padding: 22, marginBottom: 16, color: WH, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(212,175,55,0.16)" }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, position: "relative" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <MdMenuBook size={15} color="#D4AF37" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#D4AF37", letterSpacing: 0.6 }}>THIS WEEK'S DEEP DIVE</span>
                </div>
                {deepDiveStreak >= 2 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(212,175,55,0.18)", borderRadius: 20, padding: "3px 10px" }}>
                    <MdLocalFireDepartment size={12} color="#D4AF37" />
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#D4AF37" }}>{deepDiveStreak}-week streak</span>
                  </div>
                )}
              </div>
              <div style={{ position: "relative" }}>
                <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 2 }}><FlagImg emoji={detectFlag(weeklyInsight.ticker, "")} size={18} /> {cleanCompanyName(weeklyInsight.name)}</div>
                <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 12 }}>{weeklyInsight.ticker} · {weeklyInsight.region}</div>
                {weeklyInsight.price != null && (
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 24, fontWeight: 800 }}>{(CS$[weeklyInsight.currency] || "$") + weeklyInsight.price.toFixed(2)}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: weeklyInsight.changePct >= 0 ? "#5DCAA5" : "#F09595" }}>{pct(weeklyInsight.changePct)}</span>
                    {weeklyInsight.verdict && (function() {
                      var vc = weeklyInsight.verdict === "UNDERVALUED" ? { bg: "rgba(93,202,165,0.2)", t: "#5DCAA5" } : weeklyInsight.verdict === "OVERVALUED" ? { bg: "rgba(240,149,149,0.2)", t: "#F09595" } : { bg: "rgba(212,175,55,0.2)", t: "#D4AF37" };
                      return <span style={{ background: vc.bg, color: vc.t, borderRadius: 16, padding: "4px 11px", fontSize: 12, fontWeight: 700 }}>{weeklyInsight.verdict}</span>;
                    })()}
                  </div>
                )}
                {weeklyInsight.priceAsOf != null && (
                  <div style={{ fontSize: 10, opacity: 0.5, marginBottom: 10 }}>
                    Price &amp; score update live — matches the full breakdown
                  </div>
                )}
                {weeklyInsight.content && weeklyInsight.content.thesis && (
                  <div style={{ fontSize: 13, lineHeight: 1.6, opacity: 0.9, marginBottom: 4, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#D4AF37", letterSpacing: 0.5, marginBottom: 4 }}>THE THESIS</div>
                    {weeklyInsight.content.thesis}
                  </div>
                )}
              </div>
              <button onClick={function(e) {
                e.stopPropagation();
                doSearch(weeklyInsight.ticker, weeklyInsight.name, {
                  price: weeklyInsight.price, changePct: weeklyInsight.changePct,
                  verdict: weeklyInsight.verdict, score: weeklyInsight.score,
                });
              }}
                style={{ marginTop: 14, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, padding: "10px 14px", color: WH, fontSize: 12, fontWeight: 700, cursor: "pointer", width: "100%", position: "relative" }}>
                See full breakdown — DCF, financials &amp; more →
              </button>
            </div>

            <div style={{ background: BW, borderRadius: 12, padding: 14, marginBottom: 14, fontSize: 12, color: TX2, lineHeight: 1.6 }}>
              Every week we pick a different company from a different region — US, UK, India, Japan, Europe — and go deeper than a valuation score: competitive landscape, growth drivers, risks, and the real thesis behind the pick.
            </div>

            {weeklyInsight.content && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={Object.assign({}, card, { padding: 16 })}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 8 }}>Overview</div>
                  <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6 }}>{weeklyInsight.content.overview}</div>
                </div>

                {weeklyInsight.competitors && weeklyInsight.competitors.length > 0 && (
                  <div style={Object.assign({}, card, { padding: 16 })}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 8 }}>Competitive Landscape</div>
                    <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6, marginBottom: 12 }}>{weeklyInsight.content.competitiveLandscape}</div>
                    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                      {weeklyInsight.competitors.map(function(c) {
                        var f = detectFlag(c.t, "");
                        return (
                          <div key={c.t} onClick={function() { doSearch(c.t, c.n); }}
                            style={{ background: BG, border: "1px solid " + BD, borderRadius: 10, padding: "10px 12px", minWidth: 110, flexShrink: 0, cursor: "pointer" }}>
                            <div style={{ fontSize: 10, color: TX3, marginBottom: 3 }}>{f} {c.t}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: N, whiteSpace: "nowrap" }}>{c.n}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {weeklyInsight.content.growthDrivers && weeklyInsight.content.growthDrivers.length > 0 && (
                  <div style={Object.assign({}, card, { padding: 16 })}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 10 }}>Growth Drivers</div>
                    {weeklyInsight.content.growthDrivers.map(function(g, i) {
                      return (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < weeklyInsight.content.growthDrivers.length - 1 ? 8 : 0 }}>
                          <MdTrendingUp size={16} color={OKT} style={{ flexShrink: 0, marginTop: 1 }} />
                          <div style={{ fontSize: 13, color: TX2, lineHeight: 1.5 }}>{g}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {weeklyInsight.content.risks && weeklyInsight.content.risks.length > 0 && (
                  <div style={Object.assign({}, card, { padding: 16 })}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 10 }}>Risks</div>
                    {weeklyInsight.content.risks.map(function(r, i) {
                      return (
                        <div key={i} style={{ display: "flex", gap: 8, marginBottom: i < weeklyInsight.content.risks.length - 1 ? 8 : 0 }}>
                          <MdWarningAmber size={16} color={ERT} style={{ flexShrink: 0, marginTop: 1 }} />
                          <div style={{ fontSize: 13, color: TX2, lineHeight: 1.5 }}>{r}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {weeklyInsight.content.recentDevelopments && (
                  <div style={Object.assign({}, card, { padding: 16 })}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 8 }}>Recent Developments</div>
                    <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6 }}>{weeklyInsight.content.recentDevelopments}</div>
                  </div>
                )}
              </div>
            )}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>
          </div>
        )}

        {/* Portfolio */}
        {view === "portfolio" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <TabHeader icon={<MdAccountBalanceWallet size={16} />} title="Mock Portfolio" badge={effectivePlan === "pro" || isFounder ? null : { text: portfolio.length + "/3", atLimit: portfolio.length >= 3 }} />
              <div style={{ display: "flex", gap: 6 }}>
                {portfolio.length > 0 && (
                  <button onClick={exportPortfolioCsv} title="Export as CSV" aria-label="Export portfolio as CSV"
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, padding: "6px 0", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, cursor: "pointer" }}>
                    <MdDownload size={14} />
                  </button>
                )}
                {portfolio.length > 0 && (
                  <button onClick={refreshPortfolio} disabled={refreshingPF}
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: refreshingPF ? TX3 : TXM, fontSize: 12, fontWeight: 600, cursor: refreshingPF ? "default" : "pointer" }}>
                    {refreshingPF ? (
                      <>{[0,0.2,0.4].map(function(d){return <div key={d} style={{width:5,height:5,borderRadius:"50%",background:N,animation:"qdot 1s "+d+"s ease-in-out infinite"}} />;})}<span>Refreshing</span></>
                    ) : "↻ Refresh prices"}
                  </button>
                )}
              </div>
            </div>
            {portfolio.length === 0 ? (
              <div style={Object.assign({}, card, { padding: 24, textAlign: "center" })}>
                <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}><MdAccountBalanceWallet size={32} color={TX3} /></div>
                <div style={{ fontSize: 15, fontWeight: 700, color: N, marginBottom: 6 }}>No investments yet</div>
                <p style={{ fontSize: 13, color: TXM, marginBottom: 16, lineHeight: 1.6 }}>Analyse a stock and tap "Mock Invest" to start paper-trading, or start with:</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                  {[{ t: "AAPL", n: "Apple" }, { t: "NVDA", n: "Nvidia" }, { t: "RELIANCE.NS", n: "Reliance" }].map(function(s) {
                    return (
                      <button key={s.t} onClick={function() { doSearch(s.t, s.n); setView("result"); }}
                        style={{ padding: "7px 13px", borderRadius: 20, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        {s.t}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <style>{`.pf-card{position:relative;overflow:hidden;border-radius:12px}.pf-del{position:absolute;right:0;top:0;bottom:0;width:72px;background:${ER};display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:700;cursor:pointer;border-radius:0 12px 12px 0}.pf-inner{position:relative;transition:transform .2s ease;background:#fff;border-radius:12px;border:1px solid ${BD}}`}</style>

                {/* Portfolio summary — converts every holding to a single
                    home currency using live FX rates (see getExchangeRates)
                    before summing. Previously, holdings in different
                    currencies were summed as raw numbers with a "mixed
                    currencies" disclaimer — that wasn't just unpolished,
                    it was a materially wrong total on screen. */}
                {(function() {
                  var currencies = [...new Set(portfolio.map(function(p) { return p.cur || "GBP"; }))];
                  var mixed = currencies.length > 1;
                  // Single currency: show natively, no conversion needed at all —
                  // conversion only matters once there's actually a mix to unify.
                  var displayCurrency = mixed ? homeCurrency : currencies[0];
                  var missingRate = mixed && currencies.some(function(c) { return fxRates[c] == null; });
                  var totalInv = 0, totalCurr = 0;
                  portfolio.forEach(function(p) {
                    var rate = mixed ? (fxRates[p.cur || "GBP"] != null ? fxRates[p.cur || "GBP"] : 1) : 1;
                    totalInv += (p.inv || 0) * rate;
                    totalCurr += (p.sh * p.cp) * rate;
                  });
                  var totalPnl = totalCurr - totalInv;
                  var totalPct = totalInv > 0 ? (totalPnl / totalInv) * 100 : 0;
                  var up = totalPnl >= 0;
                  var sym = CS$[displayCurrency] || displayCurrency + " ";
                  return (
                    <div style={{ background: N, borderRadius: 12, padding: 16, color: WH, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                        <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>Total portfolio</div>
                        {mixed && (
                          <select value={homeCurrency} onChange={function(e) {
                            var next = e.target.value;
                            setHomeCurrency(next);
                            try { localStorage.setItem("q_home_currency", next); } catch(err) {}
                          }} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: WH, fontSize: 10, fontWeight: 700, padding: "3px 6px" }}>
                            {["GBP","USD","EUR","INR","JPY"].map(function(c) { return <option key={c} value={c} style={{ color: "#000" }}>{c}</option>; })}
                          </select>
                        )}
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 300, marginBottom: 4 }}>
                        {mixed && fxLoading ? "…" : sym + totalCurr.toFixed(2)}
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{sym}{totalInv.toFixed(2)} invested</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: up ? "#5DCAA5" : "#F09595" }}>
                          {up ? "+" : ""}{sym}{totalPnl.toFixed(2)} ({up ? "+" : ""}{totalPct.toFixed(2)}%)
                        </div>
                      </div>
                      {mixed && (
                        <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6 }}>
                          {missingRate ? "Couldn't fetch a live rate for one currency — total may be approximate." : "Converted to " + homeCurrency + " at today's exchange rate."}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Portfolio performance chart */}
                {pfHistory.length >= 2 && (function() {
                  var vals = pfHistory.map(function(h) { return h.val; });
                  var first = pfHistory[0], last = pfHistory[pfHistory.length-1];
                  var chg = first.val > 0 ? ((last.val - first.val) / first.val) * 100 : 0;
                  var lineColor = chg >= 0 ? OK : ER;
                  // S&P 500 benchmark comparison
                  var spyChg = null;
                  if (spyBaseline && last.spy) {
                    spyChg = ((last.spy - spyBaseline.price) / spyBaseline.price) * 100;
                  } else if (pfHistory.length >= 2 && first.spy && last.spy) {
                    spyChg = ((last.spy - first.spy) / first.spy) * 100;
                  }
                  var beating = spyChg !== null && chg > spyChg;
                  // Chart
                  var mn = Math.min.apply(null, vals), mx = Math.max.apply(null, vals), rn = mx - mn || 1;
                  var W = 300, H = 60, padL = 2, padR = 2, padT = 4, padB = 4;
                  var cW = W - padL - padR, cH = H - padT - padB;
                  var pts = pfHistory.map(function(h, i) {
                    return {
                      x: padL + (i / (pfHistory.length - 1)) * cW,
                      y: padT + cH - ((h.val - mn) / rn) * cH
                    };
                  });
                  var pathD = pts.map(function(p, i) { return (i === 0 ? "M" : "L") + p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
                  var areaD = pathD + " L" + pts[pts.length-1].x.toFixed(1) + "," + (padT+cH) + " L" + padL + "," + (padT+cH) + " Z";
                  return (
                    <div style={Object.assign({}, card, { padding: 14, marginBottom: 8 })}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: N }}>Performance</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: chg >= 0 ? OK : ER }}>
                          {chg >= 0 ? "+" : ""}{chg.toFixed(2)}%
                        </div>
                      </div>
                      <svg viewBox={"0 0 " + W + " " + H} style={{ width: "100%", height: "auto", display: "block" }}>
                        <defs>
                          <linearGradient id="pfFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={lineColor} stopOpacity=".15" />
                            <stop offset="100%" stopColor={lineColor} stopOpacity=".01" />
                          </linearGradient>
                        </defs>
                        <path d={areaD} fill="url(#pfFill)" />
                        <path d={pathD} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                        <circle cx={pts[pts.length-1].x} cy={pts[pts.length-1].y} r="3" fill={lineColor} stroke={WH} strokeWidth="1.5" />
                      </svg>
                      {/* Benchmark row */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 8, borderTop: "1px solid " + BD }}>
                        <div style={{ display: "flex", gap: 14 }}>
                          <div>
                            <div style={{ fontSize: 10, color: TX3, marginBottom: 2 }}>Your portfolio</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: chg >= 0 ? OKT : ERT }}>{chg >= 0 ? "+" : ""}{chg.toFixed(2)}%</div>
                          </div>
                          {spyChg !== null && (
                            <div>
                              <div style={{ fontSize: 10, color: TX3, marginBottom: 2 }}>S&P 500</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: spyChg >= 0 ? OKT : ERT }}>{spyChg >= 0 ? "+" : ""}{spyChg.toFixed(2)}%</div>
                            </div>
                          )}
                        </div>
                        {spyChg !== null && (
                          <div style={{ background: beating ? OKS : ERS, borderRadius: 8, padding: "4px 10px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: beating ? OKT : ERT }}>
                              {beating ? "▲ Beating market" : "▼ Trailing market"}
                            </div>
                            <div style={{ fontSize: 10, color: beating ? OKT : ERT, textAlign: "center" }}>
                              by {Math.abs(chg - spyChg).toFixed(1)}%
                            </div>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: TX3, marginTop: 6 }}>Based on {pfHistory.length} refresh{pfHistory.length > 1 ? "es" : ""}. Tap ↻ Refresh to update.</div>
                    </div>
                  );
                })()}

                {/* Sector breakdown */}
                {(function() {
                  var sectors = {};
                  var totalInv = 0;
                  var sbCurrencies = [...new Set(portfolio.map(function(p) { return p.cur || "GBP"; }))];
                  var sbMixed = sbCurrencies.length > 1;
                  portfolio.forEach(function(p) {
                    var rate = sbMixed ? (fxRates[p.cur || "GBP"] != null ? fxRates[p.cur || "GBP"] : 1) : 1;
                    var convertedInv = (p.inv || 0) * rate;
                    var sec = p.s || "Other";
                    sectors[sec] = (sectors[sec] || 0) + convertedInv;
                    totalInv += convertedInv;
                  });
                  if (totalInv === 0 || Object.keys(sectors).length < 2) return null;
                  var sectorColors = [B, OK, ER, WN, TXM, N, TX2, TX3];
                  var entries = Object.entries(sectors).sort(function(a,b){ return b[1]-a[1]; });
                                    return (
                    <div style={Object.assign({}, card, { padding: 14, marginBottom: 4 })}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 10 }}>Portfolio breakdown</div>
                      {/* Stacked bar */}
                      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginBottom: 12 }}>
                        {entries.map(function(e, i) {
                          var pct2 = (e[1] / totalInv) * 100;
                          return <div key={i} style={{ width: pct2 + "%", background: sectorColors[i % sectorColors.length] }} />;
                        })}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }}>
                        {entries.map(function(e, i) {
                          var pct2 = Math.round((e[1] / totalInv) * 100);
                          return (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <div style={{ width: 8, height: 8, borderRadius: 2, background: sectorColors[i % sectorColors.length], flexShrink: 0 }} />
                              <span style={{ fontSize: 11, color: TXM }}>{e[0]}</span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: TX }}>{pct2}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {portfolio.map(function(p) {
                  var sym = CS$[p.cur] || "$";
                  var cv = p.sh * p.cp, pnl = cv - p.inv, up = pnl >= 0;
                  return (
                    <div key={p.id} className="pf-card"
                      onTouchStart={function(e) { _swipeMap[p.id] = e.touches[0].clientX; }}
                      onTouchMove={function(e) {
                        var startX = _swipeMap[p.id];
                        if (startX === undefined) return;
                        var dx = e.touches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".pf-inner");
                        if (dx < 0) inner.style.transform = "translateX(" + Math.max(dx, -72) + "px)";
                      }}
                      onTouchEnd={function(e) {
                        var startX = _swipeMap[p.id];
                        if (startX === undefined) return;
                        var dx = e.changedTouches[0].clientX - startX;
                        var inner = e.currentTarget.querySelector(".pf-inner");
                        if (dx < -40) { inner.style.transform = "translateX(-72px)"; }
                        else { inner.style.transform = "translateX(0)"; }
                        delete _swipeMap[p.id];
                      }}>
                      <div className="pf-del" onClick={function() { setPf(function(prev) { return prev.filter(function(x) { return x.id !== p.id; }); }); }}>Delete</div>
                      <div className="pf-inner" style={{ padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <div style={{ cursor: "pointer" }} onClick={function() { doSearch(p.t, p.co); }}>
                            <div style={{ fontWeight: 800, fontSize: 16, color: N }}>{p.t} <span style={{ fontSize: 11, color: B, fontWeight: 600 }}>→</span></div>
                            <div style={{ fontSize: 11, color: TX3 }}>{p.co} · {p.d}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: up ? OK : ER }}>{up ? "+" : "-"}{sym}{Math.abs(pnl).toFixed(2)}</div>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 11, fontWeight: 700, color: up ? OKT : ERT, background: up ? OKS : ERS, borderRadius: 20, padding: "2px 8px", marginTop: 3 }}>
                              {up ? <MdTrendingUp size={11} /> : <MdTrendingDown size={11} />}{pct((pnl / p.inv) * 100)}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid " + BD }}>
                          <div style={{ fontSize: 12, color: TX3 }}>
                            <span>{sym}{p.ep.toFixed(2)} entry</span>
                            <span style={{ margin: "0 6px", color: BD }}>·</span>
                            <span style={{ color: p.cp > p.ep ? OK : ER }}>{sym}{p.cp.toFixed(2)} now</span>
                          </div>
                          <span style={{ fontSize: 11, color: TX3 }}>← swipe to remove</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ padding: "18px 10px 4px", textAlign: "center" }}>
              <p style={{ fontSize: 10, color: TX3, lineHeight: 1.6 }}>Zoltra is not a regulated financial service. All data and AI-generated analysis is provided for informational purposes only and does not constitute investment advice. Always conduct your own research or consult a qualified financial adviser before making investment decisions. Your capital is at risk.</p>
            </div>
          </div>
        )}

        {/* Learn */}
        {view === "learn" && (function() {
          var filtered = GLOSSARY.filter(function(g) {
            if (!learnQuery.trim()) return true;
            var q = learnQuery.toLowerCase();
            return g.term.toLowerCase().includes(q) || g.def.toLowerCase().includes(q) || g.cat.toLowerCase().includes(q);
          });

          var cats = ["All"].concat(Array.from(new Set(GLOSSARY.map(function(g) { return g.cat; }))));
          var displayTerms = learnQuery.trim() ? filtered : GLOSSARY.filter(function(g) { return learnCat === "All" || g.cat === learnCat; });

          return (
            <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
              <TabHeader icon={<MdMenuBook size={16} />} title="Financial Glossary" />

              {/* Search */}
              <div style={{ position: "relative", marginBottom: 12 }}>
                <input
                  value={learnQuery}
                  onChange={function(e) { setLearnQuery(e.target.value); }}
                  placeholder="Search terms, e.g. P/E, beta, FCF..."
                  style={{ width: "100%", background: WH, border: "2px solid " + BD, borderRadius: 12, padding: "11px 14px 11px 38px", fontSize: 16, color: TX }}
                />
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: TX3, display: "flex", pointerEvents: "none" }}><MdSearch size={16} /></span>
                {learnQuery && (
                  <button onClick={function() { setLearnQuery(""); }} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: TX3, fontSize: 18, cursor: "pointer" }}>×</button>
                )}
              </div>

              {/* Category filters */}
              {!learnQuery && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                  {cats.map(function(c) {
                    var isActive = learnCat === c;
                    return (
                      <button key={c} onClick={function() { setLearnCat(c); setLearnQuery(""); }}
                        style={{ padding: "5px 12px", borderRadius: 16, border: "1px solid " + (isActive ? N : BD), background: isActive ? N : WH, color: isActive ? WH : TXM, fontSize: 12, cursor: "pointer" }}>
                        {c}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Results count */}
              {learnQuery.trim() && (
                <div style={{ fontSize: 12, color: TXM, marginBottom: 10 }}>{displayTerms.length} result{displayTerms.length !== 1 ? "s" : ""} for "{learnQuery.trim()}"</div>
              )}

              {/* Terms list */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {displayTerms.length === 0 ? (
                  <div style={Object.assign({}, card, { padding: 24, textAlign: "center" })}>
                    <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><MdSearchOff size={28} color={TX3} /></div>
                    <div style={{ fontSize: 14, color: TXM }}>No terms found for "{learnQuery}"</div>
                  </div>
                ) : displayTerms.map(function(g, i) {
                  return (
                    <div key={i} style={Object.assign({}, card, { padding: 14 })}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: N }}>{g.term}</div>
                        <span style={{ background: BW, color: B, borderRadius: 8, padding: "2px 8px", fontSize: 10, fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>{g.cat}</span>
                      </div>
                      <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6 }}>{g.def}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {view === "profile" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: N, marginBottom: 14 }}>Profile</div>

            <div style={{ display: "flex", background: BG, borderRadius: 8, padding: 2, marginBottom: 14 }}>
              {[{ k: "account", l: "Account" }, { k: "plans", l: "Plans" }].map(function(t) {
                return <button key={t.k} onClick={function() { setProfileTab(t.k); }} style={segBtn(profileTab === t.k)}>{t.l}</button>;
              })}
            </div>

            {profileTab === "account" && (<div>

            {/* Account — bold dark hero card, not a flat white box */}
            <div style={{ background: "linear-gradient(135deg, #081B33, #123059)", borderRadius: 18, padding: 20, marginBottom: 12, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(13,148,136,0.18)" }} />
              {(function() {
                return (
                <div style={{ position: "relative" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: avatarColor, color: WH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, flexShrink: 0, border: "2px solid rgba(255,255,255,0.2)" }}>
                      {(user.displayName || user.email || "U")[0].toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 18, color: WH, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.displayName || user.email}</div>
                      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                    </div>
                    <button onClick={function() { setEditName(user.displayName || ""); setEditAvatarColor(avatarColor); setShowEditProfile(true); }} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", color: WH, cursor: "pointer", flexShrink: 0 }}>
                      <MdEdit size={15} />
                    </button>
                  </div>
                  <button onClick={function() { handleSignOut(); }} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: WH, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <MdLogout size={15} /> Sign out
                  </button>
                  <button onClick={function() { exportMyData(); }} style={{ width: "100%", padding: 10, marginTop: 8, borderRadius: 8, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: WH, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                    <MdDownload size={15} /> Export my data
                  </button>
                  <button onClick={function() { setShowDeleteAccount(true); }} style={{ width: "100%", padding: 8, marginTop: 8, borderRadius: 8, border: "none", background: "none", color: "rgba(255,255,255,0.4)", fontSize: 11, cursor: "pointer" }}>
                    Delete account
                  </button>
                </div>
                );
              })()}
            </div>

            {/* Weekly analyses used */}
            {!isFounder && (function() {
              var limit = searchLimitInfo && searchLimitInfo.limit ? searchLimitInfo.limit : (effectivePlan === "pro" ? 25 : 5);
              var used = Math.min(searchLimitInfo && searchLimitInfo.count ? searchLimitInfo.count : 0, limit);
              var remaining = Math.max(limit - used, 0);
              var pctUsed = (used / limit) * 100;
              var resetLabel = null;
              if (searchLimitInfo && searchLimitInfo.weekStart) {
                // The underlying reset clock is one shared Monday 00:00 UK time —
                // simple to run server-side — but shown here converted to each
                // viewer's own local time, since "UK time" isn't meaningful at a
                // glance for users elsewhere in the world.
                var resetDate = new Date(searchLimitInfo.weekStart + 7 * 86400000);
                var resetDateLabel = resetDate.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
                var resetTimeLabel = resetDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                resetLabel = resetDateLabel + " at " + resetTimeLabel + " your time";
              }
              return (
                <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center" }}><MdAutoAwesome size={14} /></div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: N }}>Analyses used this week</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: remaining === 0 ? ER : TX }}>{used} / {limit}</span>
                  </div>
                  <div style={{ height: 6, background: BD, borderRadius: 3, marginBottom: 10 }}>
                    <div style={{ height: 6, width: pctUsed + "%", background: remaining === 0 ? ER : AC, borderRadius: 3 }} />
                  </div>
                  {resetLabel && <div style={{ fontSize: 12, color: TXM, marginBottom: effectivePlan !== "pro" ? 10 : 0 }}>Resets {resetLabel}</div>}
                  {effectivePlan !== "pro" && (
                    <div style={{ background: BW, borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <MdLock size={15} color={B} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ fontSize: 12, color: TX2, lineHeight: 1.5 }}>
                        {remaining > 0 ? (
                          <span><span onClick={function() { setProfileTab("plans"); }} style={{ color: B, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Upgrade to Pro</span> for 25 AI stock analyses per week.</span>
                        ) : (
                          <span>You've used all 5 AI stock analyses this week. <span onClick={function() { setProfileTab("plans"); }} style={{ color: B, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Upgrade to Pro</span> for 25 a week, or wait until it resets.</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Weekly thesis challenges used */}
            {!isFounder && (function() {
              var limit = thesisLimitInfo && thesisLimitInfo.limit ? thesisLimitInfo.limit : (effectivePlan === "pro" ? 10 : 3);
              var used = Math.min(thesisLimitInfo && thesisLimitInfo.count ? thesisLimitInfo.count : 0, limit);
              var remaining = Math.max(limit - used, 0);
              var pctUsed = (used / limit) * 100;
              var resetLabel = null;
              if (thesisLimitInfo && thesisLimitInfo.weekStart) {
                var resetDate2 = new Date(thesisLimitInfo.weekStart + 7 * 86400000);
                resetLabel = resetDate2.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" }) + " at " + resetDate2.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) + " your time";
              }
              return (
                <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 7, background: BW, color: B, display: "flex", alignItems: "center", justifyContent: "center" }}><MdPsychology size={14} /></div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: N }}>Thesis challenges used this week</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: remaining === 0 ? ER : TX }}>{used} / {limit}</span>
                  </div>
                  <div style={{ height: 6, background: BD, borderRadius: 3, marginBottom: 10 }}>
                    <div style={{ height: 6, width: pctUsed + "%", background: remaining === 0 ? ER : B, borderRadius: 3 }} />
                  </div>
                  {resetLabel && <div style={{ fontSize: 12, color: TXM, marginBottom: effectivePlan !== "pro" ? 10 : 0 }}>Resets {resetLabel}</div>}
                  {effectivePlan !== "pro" && (
                    <div style={{ background: BW, borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <MdLock size={15} color={B} style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ fontSize: 12, color: TX2, lineHeight: 1.5 }}>
                        {remaining > 0 ? (
                          <span><span onClick={function() { setProfileTab("plans"); }} style={{ color: B, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Upgrade to Pro</span> for 10 thesis challenges per week.</span>
                        ) : (
                          <span>You've used all 3 thesis challenges this week. <span onClick={function() { setProfileTab("plans"); }} style={{ color: B, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Upgrade to Pro</span> for 10 a week, or wait until it resets.</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Capacity usage — watchlist, portfolio, compare, alerts. These don't
                reset on a clock; they're a running total that only frees up when
                you remove something. */}
            {!isFounder && (function() {
              var isPro = effectivePlan === "pro";
              var activeAlertsCount = alerts.filter(function(a) { return !a.triggered; }).length;
              var rows = [
                { label: "Watchlist", used: watchlist.length, limit: isPro ? null : 3 },
                { label: "Portfolio positions", used: portfolio.length, limit: isPro ? null : 3 },
                { label: "Compare", used: cmpList.length, limit: isPro ? 6 : 3 },
                { label: "Active alerts", used: activeAlertsCount, limit: isPro ? null : 3 },
              ];
              return (
                <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 26, height: 26, borderRadius: 7, background: WNS, color: WN, display: "flex", alignItems: "center", justifyContent: "center" }}><MdBarChart size={14} /></div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: N }}>Your usage</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 10 }}>
                    {rows.map(function(row, i) {
                      var atLimit = row.limit !== null && row.used >= row.limit;
                      return (
                        <div key={i} style={{ background: BG, borderRadius: 8, padding: "10px 12px" }}>
                          <div style={{ fontSize: 11, color: TXM, marginBottom: 3 }}>{row.label}</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: atLimit ? ER : N }}>
                            {row.used}{row.limit !== null ? " / " + row.limit : ""}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!isPro && (
                    <div style={{ fontSize: 11, color: TX3, marginTop: 10, lineHeight: 1.5 }}>
                      These are capacity limits, not weekly — remove an item to free up a slot, or <span onClick={function() { setProfileTab("plans"); }} style={{ color: B, fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>upgrade to Pro</span> for higher (or unlimited) caps.
                    </div>
                  )}
                </div>
              );
            })()}

            {/* App settings — consolidated into one card instead of four
                separate, visually-repetitive white boxes */}
            <div style={{ fontSize: 11, fontWeight: 600, color: TX3, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.6, paddingLeft: 2 }}>App settings</div>
            <div style={Object.assign({}, card, { padding: 4, marginBottom: 10 })}>
              <button onClick={function() { setOnboardStep(0); setView("onboard"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", width: "100%", background: "none", border: "none", borderBottom: "1px solid " + BD, textAlign: "left", cursor: "pointer", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: BW, color: B, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdHelpOutline size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Take the app tour</span>
                <MdChevronRight size={17} color={TX3} />
              </button>

              <button onClick={function() {
                var msg = "I've been using Zoltra for AI-powered stock analysis. Check it out: https://zoltra.app";
                if (navigator.share) { navigator.share({ title: "Zoltra", text: msg, url: "https://zoltra.app" }).catch(function() {}); }
                else { try { navigator.clipboard.writeText(msg); setToast("Link copied!"); } catch(e) {} }
              }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", width: "100%", background: "none", border: "none", borderBottom: "1px solid " + BD, textAlign: "left", cursor: "pointer", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdShare size={15} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Share Zoltra with a friend</span>
                <MdChevronRight size={17} color={TX3} />
              </button>

              <button onClick={function() { setShowInstallGuide(true); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", width: "100%", background: "none", border: "none", borderBottom: "1px solid " + BD, textAlign: "left", cursor: "pointer", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdGetApp size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Install Zoltra on your phone</span>
                <MdChevronRight size={17} color={TX3} />
              </button>

              {/* Push notifications — persistent status + control, not just the
                  one-time prompt. Browsers don't let JS re-prompt once denied,
                  so "blocked" state points to browser settings instead. Uses a
                  green toggle specifically (not the app's usual navy) so
                  "enabled" is unambiguous at a glance. */}
              <div style={{ borderBottom: "1px solid " + BD }}>
                {(function() {
                  var perm = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
                  if (perm === "unsupported") {
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", color: TX3 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: BG, color: TX3, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdNotificationsOff size={16} /></div>
                        <span style={{ fontSize: 14, flex: 1 }}>Notifications not supported on this browser</span>
                      </div>
                    );
                  }
                  if (perm === "denied") {
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", color: TX }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: ERS, color: ERT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdNotificationsOff size={16} /></div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14 }}>Notifications blocked</div>
                          <div style={{ fontSize: 11, color: TXM, marginTop: 1 }}>Re-enable in your browser's site settings for Zoltra</div>
                        </div>
                      </div>
                    );
                  }
                  var isOn = perm === "granted" && notifPrefEnabled;
                  return (
                    <>
                      <div onClick={function() { toggleNotifPref(!isOn); }}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", cursor: "pointer" }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: isOn ? OKS : BG, color: isOn ? OKT : TXM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {isOn ? <MdNotificationsActive size={16} /> : <MdNotifications size={16} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: TX }}>{isOn ? "Push notifications enabled" : "Push notifications off"}</div>
                          <div style={{ fontSize: 11, color: TXM, marginTop: 1 }}>{isOn ? "Price alerts and the weekly deep dive will notify you" : "Get notified for price alerts and the weekly deep dive"}</div>
                        </div>
                        <div style={{ width: 40, height: 24, borderRadius: 12, background: isOn ? OK : BD, position: "relative", flexShrink: 0, transition: "background .2s" }}>
                          <div style={{ position: "absolute", top: 3, left: isOn ? 18 : 3, width: 18, height: 18, borderRadius: "50%", background: WH, transition: "left .2s" }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 10.5, color: TX3, padding: "0 12px 10px 54px", lineHeight: 1.4 }}>You can turn this on or off here anytime. It's also controlled by your browser or device's notification settings for Zoltra.</div>
                    </>
                  );
                })()}
              </div>

              {/* Force refresh — a low-friction alternative to deleting and
                  reinstalling the whole app. iOS treats an installed home-screen
                  app as an isolated storage container that regular "clear site
                  data" in Safari can't reach — this clears everything this page
                  itself has access to (Cache API storage, service worker
                  registration) and does a genuinely fresh reload, which covers
                  most staleness cases without needing a full reinstall. */}
              <button onClick={function() {
                setToast("Refreshing app — clearing cached data…");
                var cleanup = Promise.resolve();
                if ("caches" in window) {
                  cleanup = caches.keys().then(function(names) {
                    return Promise.all(names.map(function(n) { return caches.delete(n); }));
                  }).catch(function() {});
                }
                if ("serviceWorker" in navigator) {
                  cleanup = cleanup.then(function() {
                    return navigator.serviceWorker.getRegistrations().then(function(regs) {
                      return Promise.all(regs.map(function(r) { return r.unregister(); }));
                    });
                  }).catch(function() {});
                }
                cleanup.finally(function() {
                  window.location.href = window.location.origin + window.location.pathname + "?_refresh=" + Date.now();
                });
              }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", width: "100%", background: "none", border: "none", textAlign: "left", cursor: "pointer", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: WNS, color: WN, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdRefresh size={16} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>Force refresh app</div>
                  <div style={{ fontSize: 11, color: TXM, marginTop: 1 }}>If something looks out of date, try this before reinstalling</div>
                </div>
              </button>
            </div>

            {isFounder && (
              <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center" }}><MdMenuBook size={14} /></div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: N }}>Weekly Deep Dive — next up</div>
                </div>
                <p style={{ fontSize: 12, color: TXM, marginBottom: 12, lineHeight: 1.5 }}>Proposed automatically every Friday, cycling US → UK → India → Japan → Europe. Goes live Monday unless overridden here first.</p>
                {weeklyProposal ? (
                  <div style={{ background: BG, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: N }}>{cleanCompanyName(weeklyProposal.name)} ({weeklyProposal.ticker})</div>
                        <div style={{ fontSize: 12, color: TXM }}>{weeklyProposal.region} · week of {weeklyProposal.weekOf} · status: {weeklyProposal.status}</div>
                      </div>
                      <button onClick={function() {
                        getWeeklyProposalFn({}).then(function(r) {
                          setWeeklyProposal(r.data);
                          setToast(r.data ? "Reloaded — " + r.data.ticker + " (" + r.data.status + ")" : "No proposal found yet");
                        }).catch(function(e) { setToast(e.message || "Couldn't load proposal."); });
                      }} style={{ background: "none", border: "none", color: B, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                        Reload
                      </button>
                    </div>
                  </div>
                ) : (
                  <button onClick={function() {
                    getWeeklyProposalFn({}).then(function(r) { setWeeklyProposal(r.data); }).catch(function(e) { setToast(e.message || "Couldn't load proposal."); });
                  }} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 12 }}>
                    Load current proposal
                  </button>
                )}
                {weeklyProposal && (
                  <button disabled={shufflingWeekly} onClick={function() {
                    setShufflingWeekly(true);
                    shuffleWeeklyPickFn({}).then(function(r) {
                      setWeeklyProposal(r.data);
                      setToast("Now showing " + r.data.ticker + " — keep tapping to see more, or type a ticker below");
                      setShufflingWeekly(false);
                    }).catch(function(e) {
                      setToast(e.message || "Couldn't suggest another ticker.");
                      setShufflingWeekly(false);
                    });
                  }} style={{ width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TX, fontSize: 14, fontWeight: 700, cursor: shufflingWeekly ? "default" : "pointer", opacity: shufflingWeekly ? 0.6 : 1, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <MdRefresh size={16} />
                    {shufflingWeekly ? "Finding another…" : "Suggest a different ticker"}
                  </button>
                )}
                <div style={{ fontSize: 10.5, color: TX3, marginBottom: 6 }}>Not sure what to swap in? Tap "Suggest a different ticker" above to cycle through other {weeklyProposal ? weeklyProposal.region : ""} candidates — or type an exact ticker below if you already know which one you want.</div>
                <input value={weeklyOverrideInput} onChange={function(e) { setWeeklyOverrideInput(e.target.value.toUpperCase()); }} placeholder="Override ticker (e.g. AAPL)" type="text"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + BD, fontSize: 16, color: TX, marginBottom: 8 }} />
                <button onClick={function() {
                  if (!weeklyOverrideInput.trim()) return;
                  overrideWeeklyPickFn({ ticker: weeklyOverrideInput.trim() }).then(function(r) {
                    setWeeklyProposal(r.data);
                    setWeeklyOverrideInput("");
                    setToast("Weekly pick updated to " + r.data.ticker);
                  }).catch(function(e) { setToast(e.message || "Override failed."); });
                }} style={{ width: "100%", padding: 11, borderRadius: 8, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Override this week's pick
                </button>
              </div>
            )}

            {isFounder && (
              <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center" }}><MdInsights size={14} /></div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: N }}>Model validation</div>
                </div>
                <p style={{ fontSize: 12, color: TXM, marginBottom: 12, lineHeight: 1.5 }}>Every Monday, a sample of stocks is checked against a naive P/E-vs-sector-average baseline and compared to Zoltra's own verdict. This shows how often they <strong>agree</strong> — it does NOT measure real-world accuracy (that would need checking actual subsequent price moves, which isn't tracked yet).</p>
                {!validationData ? (
                  <button onClick={function() {
                    setValidationLoading(true);
                    getValidationSnapshotsFn({ weeks: 8 }).then(function(r) {
                      setValidationData(r.data);
                      setValidationLoading(false);
                    }).catch(function(e) { setToast(e.message || "Couldn't load validation data."); setValidationLoading(false); });
                  }} disabled={validationLoading} style={{ width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TX, fontSize: 14, fontWeight: 700, cursor: validationLoading ? "default" : "pointer", opacity: validationLoading ? 0.6 : 1 }}>
                    {validationLoading ? "Loading…" : "Load last 8 weeks"}
                  </button>
                ) : validationData.weeks.length === 0 ? (
                  <div style={{ fontSize: 13, color: TXM, textAlign: "center", padding: "12px 0" }}>No snapshots logged yet.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {validationData.weeks.map(function(w) {
                      var expanded = validationExpandedWeek === w.week;
                      var agreePct = w.comparable > 0 ? Math.round((w.agree / w.comparable) * 100) : null;
                      return (
                        <div key={w.week} style={{ border: "1px solid " + BD, borderRadius: 8, overflow: "hidden" }}>
                          <div onClick={function() { setValidationExpandedWeek(expanded ? null : w.week); }} style={{ padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: expanded ? BG : WH }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: N }}>{w.week}</div>
                              <div style={{ fontSize: 11, color: TXM }}>{w.analyzed}/{w.sampled} had a cached analysis{agreePct != null ? " · " + agreePct + "% agreed with baseline" : ""}</div>
                            </div>
                            <MdChevronRight size={16} color={TX3} style={{ transform: expanded ? "rotate(90deg)" : "none" }} />
                          </div>
                          {expanded && (
                            <div style={{ padding: "6px 12px 10px", borderTop: "1px solid " + BD }}>
                              {w.items.map(function(item, i) {
                                var match = item.zoltraVerdict && item.naiveVerdict && item.zoltraVerdict === item.naiveVerdict;
                                return (
                                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: i > 0 ? "1px solid " + BG : "none", fontSize: 11.5 }}>
                                    <span style={{ fontWeight: 700, color: N }}>{item.ticker}</span>
                                    <span style={{ color: TXM }}>naive: {item.naiveVerdict || "—"}</span>
                                    <span style={{ color: item.zoltraVerdict ? (match ? OKT : ERT) : TX3, fontWeight: 700 }}>{item.zoltraVerdict || "not analyzed"}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {isFounder && (
              <div style={Object.assign({}, card, { padding: 16, marginBottom: 10 })}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center" }}><MdRefresh size={14} /></div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: N }}>Push refresh to all users</div>
                </div>
                <p style={{ fontSize: 12, color: TXM, marginBottom: 12, lineHeight: 1.5 }}>Silently clears every user's cached app data and reloads their session — no visible notification. Works reliably on Android and desktop; on iOS installed home-screen apps this can wake the app briefly but isn't guaranteed to get enough runtime to complete the refresh, given how iOS suspends backgrounded PWAs.</p>
                {!confirmPushRefresh ? (
                  <button onClick={function() { setConfirmPushRefresh(true); }}
                    style={{ width: "100%", padding: 11, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TX, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    Push refresh to all users
                  </button>
                ) : (
                  <div>
                    <div style={{ fontSize: 12, color: ERT, fontWeight: 600, marginBottom: 8 }}>This affects every user with notifications enabled. Confirm?</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={function() { setConfirmPushRefresh(false); }}
                        style={{ flex: 1, padding: 11, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        Cancel
                      </button>
                      <button disabled={pushRefreshSending} onClick={function() {
                        setPushRefreshSending(true);
                        pushForceRefreshFn({}).then(function(r) {
                          setToast("Sent to " + r.data.sent + " of " + r.data.totalUsers + " users (" + r.data.failed + " failed)");
                          setConfirmPushRefresh(false);
                          setPushRefreshSending(false);
                        }).catch(function(e) {
                          setToast(e.message || "Push refresh failed.");
                          setPushRefreshSending(false);
                        });
                      }} style={{ flex: 1, padding: 11, borderRadius: 8, border: "none", background: ER, color: WH, fontSize: 13, fontWeight: 700, cursor: pushRefreshSending ? "default" : "pointer", opacity: pushRefreshSending ? 0.6 : 1 }}>
                        {pushRefreshSending ? "Sending…" : "Confirm & send"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* About */}
            <div style={Object.assign({}, card, { padding: 4, marginBottom: 10 })}>
              <div onClick={function() { setView("about"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", cursor: "pointer", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: BG, color: TXM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdPerson size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>About Zoltra</span>
                <MdChevronRight size={17} color={TX3} />
              </div>
            </div>

            {/* Email preferences */}
            <div style={Object.assign({}, card, { padding: 14, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" })}>
              <div style={{ flex: 1, marginRight: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 2 }}>Monthly watchlist digest</div>
                <div style={{ fontSize: 11.5, color: TXM, lineHeight: 1.4 }}>A summary email on the 1st of each month, only sent if you have watchlist items.</div>
              </div>
              <button onClick={function() {
                var next = !monthlyDigestOn;
                setMonthlyDigestOn(next);
                if (user) {
                  setDoc(doc(firestore, "users", user.uid, "data", "emailPrefs"), { monthlyDigest: next }, { merge: true }).catch(console.error);
                }
              }} style={{ flexShrink: 0, width: 44, height: 26, borderRadius: 13, border: "none", background: monthlyDigestOn ? AC : BD, cursor: "pointer", position: "relative", padding: 0 }}>
                <div style={{ position: "absolute", top: 3, left: monthlyDigestOn ? 21 : 3, width: 20, height: 20, borderRadius: "50%", background: WH, transition: "left .15s" }} />
              </button>
            </div>

            {/* Legal */}
            <div style={Object.assign({}, card, { padding: 4, marginBottom: 10 })}>
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", textDecoration: "none", color: TX, borderBottom: "1px solid " + BD }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: BG, color: TXM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdPrivacyTip size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Privacy Policy</span>
                <MdChevronRight size={17} color={TX3} />
              </a>
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", textDecoration: "none", color: TX, borderBottom: "1px solid " + BD }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: BG, color: TXM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdGavel size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Terms &amp; Conditions</span>
                <MdChevronRight size={17} color={TX3} />
              </a>
              {/* Required attribution for the stock logos shown throughout
                  the app — a real term of using the (free, no-API-key)
                  Ticker Logos service, not optional. */}
              <a href="https://www.allinvestview.com/tools/ticker-logos/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px", textDecoration: "none", color: TX }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: BG, color: TXM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><MdImage size={16} /></div>
                <span style={{ fontSize: 14, flex: 1 }}>Logos by AllInvestView</span>
                <MdChevronRight size={17} color={TX3} />
              </a>
            </div>

            </div>)}

            {profileTab === "plans" && (<div>

              <div style={{ background: BW, borderRadius: 10, padding: "10px 14px", marginBottom: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <MdCheckCircle size={15} color={B} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, color: TX2, lineHeight: 1.5 }}>Zoltra <strong>Standard is free, forever</strong> — no trial, no credit card.</div>
              </div>

              {effectivePlan === "founder" ? (
                <div style={Object.assign({}, card, { padding: 18, marginBottom: 10, background: N, border: "none" })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <MdWorkspacePremium size={22} color={WH} />
                    <div style={{ fontSize: 16, fontWeight: 800, color: WH }}>Founder access</div>
                  </div>
                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", lineHeight: 1.5, margin: 0 }}>You have permanent, unlimited access to every Zoltra Pro feature — no billing, ever.</p>
                </div>
              ) : effectivePlan === "pro" ? (
                <div style={Object.assign({}, card, { padding: 18, marginBottom: 10 })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <MdWorkspacePremium size={22} color={AC} />
                    <div style={{ fontSize: 16, fontWeight: 800, color: N }}>{userPlan.source === "founder_gift" ? "You're on Zoltra Founder Gifted Pro" : "You're on Zoltra Pro"}</div>
                  </div>
                  <p style={{ fontSize: 13, color: TXM, lineHeight: 1.5, marginBottom: userPlan.expiresAt ? 10 : 0 }}>
                    {userPlan.source === "founder_gift" ? "Gifted by the Zoltra founder — enjoy!" : "Thanks for supporting Zoltra."}
                  </p>
                  {userPlan.expiresAt && (
                    <div style={{ fontSize: 12, color: TX3 }}>Access until {new Date(userPlan.expiresAt).toLocaleDateString("en-GB")}</div>
                  )}
                  <button onClick={function() { setToast("Subscription management is coming soon with Stripe."); }} style={{ marginTop: 12, width: "100%", padding: 10, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Manage subscription</button>
                </div>
              ) : (
                <div>
                  {!hasUsedTrial && (
                    <div style={Object.assign({}, card, { padding: 18, marginBottom: 12, border: "1px solid " + AC })}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <MdWorkspacePremium size={22} color={AC} />
                        <div style={{ fontSize: 16, fontWeight: 800, color: N }}>Try 7-Day Pro Trial — free</div>
                      </div>
                      <p style={{ fontSize: 13, color: TXM, lineHeight: 1.5, marginBottom: 12 }}>Full Pro access for 7 days. No card required, and nothing is charged automatically when it ends — you'll just revert to Standard.</p>
                      <button onClick={function() {
                        claimTrialFn({}).then(function(r) {
                          if (r.data && r.data.granted) {
                            setUserPlan({ tier: "pro", source: "trial", expiresAt: r.data.expiresAt });
                            setHasUsedTrial(true);
                            setToast("Your 7-Day Pro Trial has started!");
                          } else if (r.data && r.data.reason === "already_used") {
                            setHasUsedTrial(true);
                            setToast("You've already used your trial on this account.");
                          } else {
                            setToast("Couldn't start your trial — please try again.");
                          }
                        }).catch(function(e) { setToast(e.message || "Couldn't start your trial."); });
                      }} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: AC, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                        Start my 7-Day Pro Trial
                      </button>
                    </div>
                  )}
                  <div style={Object.assign({}, card, { padding: 18, marginBottom: 10 })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <MdWorkspacePremium size={22} color={AC} />
                    <div style={{ fontSize: 16, fontWeight: 800, color: N }}>Upgrade to Zoltra Pro</div>
                  </div>
                  <p style={{ fontSize: 13, color: TXM, lineHeight: 1.5, marginBottom: 14 }}>Premium AI on every stock, unlimited everything.</p>

                  <div style={{ display: "flex", background: BG, borderRadius: 8, padding: 2, marginBottom: 14 }}>
                    <button onClick={function() { setBillingCycle("monthly"); }} style={segBtn(billingCycle === "monthly")}>Monthly</button>
                    <button onClick={function() { setBillingCycle("annual"); }} style={segBtn(billingCycle === "annual")}>Annual — save 33%</button>
                  </div>

                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 16 }}>
                    <span style={{ fontSize: 32, fontWeight: 800, color: N }}>{billingCycle === "monthly" ? "£4.99" : "£39.99"}</span>
                    <span style={{ fontSize: 13, color: TXM }}>{billingCycle === "monthly" ? "/month" : "/year"}</span>
                    {billingCycle === "annual" && <span style={{ fontSize: 11, color: TX3 }}>(≈ £3.33/mo)</span>}
                  </div>

                  <button onClick={function() { setToast("Stripe checkout is coming soon — thanks for your interest in Pro!"); }} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    Upgrade to Pro — {billingCycle === "monthly" ? "£4.99/mo" : "£39.99/yr"}
                  </button>
                  <div style={{ fontSize: 11, color: TX3, textAlign: "center", marginTop: 8 }}>Cancel anytime. Payments powered by Stripe (coming soon).</div>
                  </div>
                </div>
              )}

              {/* Full Standard vs Pro comparison — shown to everyone, including Founder,
                  so anyone can see exactly what each tier does and doesn't include. */}
              <div style={Object.assign({}, card, { padding: 0, marginBottom: 10, overflow: "hidden" })}>
                <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", background: BG, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TXM, textTransform: "uppercase", letterSpacing: 0.3 }}>Feature</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: TXM, textAlign: "center" }}>Standard</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: AC, textAlign: "center" }}>Pro</div>
                </div>
                {[
                  { f: "Stock analyses per week", free: "5", pro: "25" },
                  { f: "Watchlist", free: "Up to 3", pro: "Unlimited" },
                  { f: "Portfolio positions", free: "Up to 3", pro: "Unlimited" },
                  { f: "AI thesis challenges", free: "3 per week", pro: "10 per week" },
                  { f: "Compare stocks", free: "Up to 3", pro: "Up to 6" },
                  { f: "Save stock filters (Screener)", free: "—", pro: "Yes" },
                  { f: "Active price alerts", free: "Up to 3 at once", pro: "Unlimited" },
                ].map(function(row, i) {
                  return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr", padding: "10px 12px", borderTop: "1px solid " + BD, alignItems: "center" }}>
                      <div style={{ fontSize: 12, color: TX2 }}><FlagImg emoji={row.f} /></div>
                      <div style={{ fontSize: 11, color: TXM, textAlign: "center" }}>{row.free}</div>
                      <div style={{ fontSize: 11, color: N, fontWeight: 700, textAlign: "center" }}>{row.pro}</div>
                    </div>
                  );
                })}
                <div style={{ padding: "10px 12px", borderTop: "1px solid " + BD, background: BG, display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <MdHelpOutline size={13} color={TX3} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 10.5, color: TX3, lineHeight: 1.5 }}>
                    Watchlist, portfolio, and alerts are capacity limits — on Standard you can hold up to 3 at a time, and need to remove one to add another. Stock analyses and thesis challenges are weekly allowances that reset every Monday.
                  </div>
                </div>
              </div>

              {/* Founder-only: gift Pro to any user, existing or not yet signed up */}
              {isFounder && (
                <div style={Object.assign({}, card, { padding: 16, marginTop: 10 })}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <MdCardGiftcard size={17} color={AC} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: N }}>Gift Pro access</div>
                  </div>
                  <p style={{ fontSize: 12, color: TXM, marginBottom: 12, lineHeight: 1.5 }}>Grant any email free Pro access — existing user or not signed up yet. They'll get an email with the details, and if they haven't joined yet, access is applied automatically the first time they sign in.</p>
                  <input value={giftName} onChange={function(e) { setGiftName(e.target.value); }} placeholder="Recipient's name (optional, personalises the email)" type="text"
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + BD, fontSize: 16, color: TX, marginBottom: 8 }} />
                  <input value={giftEmail} onChange={function(e) { setGiftEmail(e.target.value); setGiftMsg(""); }} placeholder="Email address" type="email"
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + BD, fontSize: 16, color: TX, marginBottom: 8 }} />
                  <div style={{ display: "flex", background: BG, borderRadius: 8, padding: 2, marginBottom: 12 }}>
                    {[{ k: "1month", l: "1 month" }, { k: "1year", l: "1 year" }, { k: "lifetime", l: "Lifetime" }].map(function(d) {
                      return <button key={d.k} onClick={function() { setGiftDuration(d.k); }} style={segBtn(giftDuration === d.k)}>{d.l}</button>;
                    })}
                  </div>
                  <button onClick={function() {
                    if (!giftEmail.trim() || !giftEmail.includes("@")) { setGiftMsg("Enter a valid email address."); return; }
                    setGiftBusy(true); setGiftMsg("");
                    grantFounderGiftFn({ email: giftEmail.trim().toLowerCase(), name: giftName.trim(), duration: giftDuration }).then(function(r) {
                      setGiftBusy(false);
                      setGiftMsg(r.data && r.data.message ? r.data.message : "Done.");
                      setGiftEmail(""); setGiftName("");
                    }).catch(function(e) { setGiftBusy(false); setGiftMsg(e.message || "Something went wrong."); });
                  }} disabled={giftBusy} style={{ width: "100%", padding: 11, borderRadius: 8, border: "none", background: giftBusy ? BG : N, color: giftBusy ? TXM : WH, fontSize: 14, fontWeight: 700, cursor: giftBusy ? "default" : "pointer" }}>
                    {giftBusy ? "Sending…" : "Gift Pro access"}
                  </button>
                  {giftMsg && <div style={{ fontSize: 12, color: TX2, marginTop: 10, lineHeight: 1.5 }}>{giftMsg}</div>}
                </div>
              )}

            </div>)}

            <div style={{ fontSize: 11, color: TX3, textAlign: "center" }}>Zoltra — part of the Tivara family of apps</div>
          </div>
        )}

        {view === "about" && (
          <div className="fi" style={{ paddingTop: 14, paddingBottom: 32 }}>
            <div onClick={function() { setView("profile"); }} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, cursor: "pointer", color: TXM, fontSize: 13, fontWeight: 600 }}>
              <MdChevronRight size={16} style={{ transform: "rotate(180deg)" }} /> Back to Profile
            </div>

            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg, #1E1240, #2D1B54)", color: "#D4AF37", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800 }}>KK</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: N, textAlign: "center", marginBottom: 2 }}>Karan Kothari</div>
            <div style={{ fontSize: 13, color: TXM, textAlign: "center", marginBottom: 24 }}>Founder, Zoltra</div>

            <div style={Object.assign({}, card, { padding: 18, marginBottom: 12 })}>
              <div style={{ fontSize: 14, fontWeight: 700, color: N, marginBottom: 8 }}>Why I built this</div>
              <p style={{ fontSize: 13, color: TX2, lineHeight: 1.7, margin: 0 }}>I built Zoltra because I wanted a way to research stocks properly — real financial data, not just a headline number — without needing a finance degree to make sense of it. It's a solo project, built and maintained by me, with AI doing the heavy lifting on analysis so you get a genuinely useful starting point for your own research.</p>
            </div>

            <div style={Object.assign({}, card, { padding: 18, marginBottom: 12 })}>
              <div style={{ fontSize: 14, fontWeight: 700, color: N, marginBottom: 8 }}>How the AI works</div>
              <p style={{ fontSize: 13, color: TX2, lineHeight: 1.7, margin: 0 }}>Zoltra's analysis is based on Claude models (by Anthropic), using live financial data from Yahoo Finance. AI can make mistakes, and it isn't a substitute for your own research or professional financial advice — you'll see that disclaimer throughout the app because it's genuinely important, not just boilerplate.</p>
            </div>

            <div style={Object.assign({}, card, { padding: 18, marginBottom: 12 })}>
              <div style={{ fontSize: 14, fontWeight: 700, color: N, marginBottom: 8 }}>Get in touch</div>
              <p style={{ fontSize: 13, color: TX2, lineHeight: 1.7, margin: "0 0 10px" }}>Questions, feedback, or something looks broken — I read every message myself.</p>
              <a href="mailto:support@zoltra.app" style={{ fontSize: 13, color: B, fontWeight: 700, textDecoration: "none" }}>support@zoltra.app</a>
            </div>

            <div style={{ fontSize: 11, color: TX3, textAlign: "center", marginTop: 8 }}>Zoltra is part of the Tivara family of apps</div>
          </div>
        )}

        <div style={{ padding: "12px 0 16px", borderTop: "1px solid " + BD }}>
          {/* Share Zoltra */}
          <button onClick={function() {
            var shareData = { title: "Zoltra", text: "Check out Zoltra — free AI-powered stock analysis across US, UK, India, Japan and European markets.", url: "https://zoltra.app" };
            if (navigator.share) {
              navigator.share(shareData).catch(function() {});
            } else {
              navigator.clipboard.writeText("https://zoltra.app").then(function() {
                setToast("Link copied to clipboard!");
              }).catch(function() { setToast("zoltra.app"); });
            }
          }} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "10px 0", marginBottom: 12, borderRadius: 10, border: "1px solid " + BD, background: WH, color: N, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            Share Zoltra with a friend
          </button>
          <div style={{ background: BG, border: "1px solid " + BD, borderRadius: 10, padding: "12px 14px", display: "flex", gap: 8, alignItems: "flex-start" }}>
            <MdGavel size={13} color={TX3} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 10.5, color: TXM, lineHeight: 1.6 }}>
              <strong style={{ color: TX2 }}>Disclaimer:</strong> this app uses AI, which can make mistakes and is not always right. Nothing here is financial advice — always do your own research and consider speaking with a certified, regulated financial adviser before making investment decisions. Zoltra is not a regulated financial product. Your capital is at risk if you act on any analysis, verdict, or output generated by Zoltra's AI models.
            </div>
          </div>
        </div>
      </div>

      {/* Invest Modal */}
      {showInvest && stock && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { setShowInvest(false); }}>
          <div style={Object.assign({}, card, { padding: 20, borderRadius: 14, width: "100%" })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: N, marginBottom: 12 }}>Invest in {stock.t}</div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: TXM, marginBottom: 4 }}>Amount ({CS$[stock.cur] || "$"})</div>
              <input type="number" value={investAmt} onChange={function(e) { setInvestAmt(e.target.value); }}
                style={{ width: "100%", background: BG, border: "1px solid " + BD, borderRadius: 8, padding: "10px 12px", fontSize: 18, color: N }} />
            </div>
            <div style={{ background: BW, borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13, color: B }}>
              ≈ {(parseFloat(investAmt || 0) / stock.p).toFixed(4)} shares
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={function() { setShowInvest(false); }} style={{ flex: 1, padding: 10, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 14, cursor: "pointer" }}>Cancel</button>
              <button onClick={function() {
                var a = parseFloat(investAmt);
                if (!a || a <= 0) return;
                var pfLimit = (effectivePlan === "pro" || effectivePlan === "founder") ? Infinity : 3;
                if (portfolio.length >= pfLimit) { setToast("Standard portfolio holds 3 positions at a time — remove one to add another, or upgrade to Pro for unlimited."); setShowInvest(false); return; }
                setPf(function(p) { return [{ id: Date.now().toString(), t: stock.t, co: stock.n, ep: stock.p, cp: stock.p, sh: a / stock.p, inv: a, cur: stock.cur, d: new Date().toLocaleDateString("en-GB"), s: stock.s || "Other" }].concat(p); });
                setShowInvest(false);
                setToast("Invested " + (CS$[stock.cur] || "$") + a.toFixed(0) + " in " + stock.t);
              }} style={{ flex: 1, padding: 10, borderRadius: 8, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Invest</button>
            </div>
          </div>
        </div>
      )}

      {/* Install Guide Modal */}
      {showDcfMethodology && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { setShowDcfMethodology(false); }}>
          <div style={Object.assign({}, card, { padding: 20, borderRadius: 14, width: "100%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto" })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: N }}>How our DCF works</div>
              <button onClick={function() { setShowDcfMethodology(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: TX3, display: "flex" }}><MdClose size={20} /></button>
            </div>
            <p style={{ fontSize: 12, color: TXM, marginBottom: 16, lineHeight: 1.5 }}>The full formula and assumptions, so you can judge them for yourself rather than take a number on faith.</p>

            {[
              ["1. Starting cash flow", "We use the company's actual reported free cash flow (operating cash flow minus capital expenditure) when it's available and positive. For high-growth or pre-profit companies where FCF is negative or unreliable, we estimate a proxy instead — operating margin × revenue × 0.65 for companies with some profitability, or gross margin × revenue × 0.20 for pre-profit growth companies. These stocks are clearly flagged as \"Projected DCF\" with a higher-uncertainty warning."],
              ["2. Growth rate", "Blended from three inputs, weighted by how much we trust each: forward EPS growth estimates (weight 3 — most forward-looking), trailing earnings growth (weight 2), and revenue growth (weight 1 — the hardest number to manipulate). The result is floored at the revenue growth rate and capped at both 2.5× the discount rate and an absolute 25% ceiling, so a single optimistic estimate can't produce an unrealistic result."],
              ["3. The 10-year fade", "Rather than assuming that growth rate holds steady for a full decade and then suddenly drops, we fade it linearly each year — starting near the calculated rate in year 1 and declining smoothly toward the terminal growth rate by year 10. This avoids overvaluing high-growth stocks by assuming unrealistically sustained growth."],
              ["4. Discount rate", "Reflects the return an investor should reasonably require, built from three parts: a country-specific base rate (local risk-free rate plus an equity risk premium — e.g. ~13% for India, ~7% for Japan, ~9-10% for the US/UK, reflecting real differences in local interest rates and growth expectations), a smooth adjustment for the stock's beta (higher volatility → higher required return), and a company-size premium (smaller, less-established companies carry genuinely more uncertainty than a mega-cap with the same beta). The combined rate is floored at 7% and capped at 18%."],
              ["5. Terminal value", "After year 10, we assume the company settles into a stable, mature growth rate forever — anchored to that country's long-run GDP growth expectation — and value that using the standard Gordon Growth Model."],
              ["6. Net debt adjustment", "Total debt minus cash is subtracted before dividing by shares outstanding, converting enterprise value into equity value per share — what a shareholder's stake is actually worth, not the value of the whole business including its debt."],
              ["7. Sanity check", "If the resulting estimate would be wildly implausible — below 5% or above 20× the current price — we suppress the DCF entirely rather than show a number we don't trust. That's the most common reason you'll see \"not shown for this stock.\""],
            ].map(function(section, i) {
              return (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: N, marginBottom: 4 }}>{section[0]}</div>
                  <div style={{ fontSize: 12, color: TX2, lineHeight: 1.6 }}>{section[1]}</div>
                </div>
              );
            })}

            <div style={{ background: WNS, border: "1px solid " + WN, borderRadius: 8, padding: 12, marginTop: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: WN, marginBottom: 4 }}>The honest limitation</div>
              <div style={{ fontSize: 11, color: TX2, lineHeight: 1.6 }}>DCF is a model built on assumptions, not a fact — small changes to the growth or discount rate can meaningfully swing the result. It works best for stable, cash-generative businesses, and less well for pre-revenue or highly cyclical companies. That's exactly why it's one input among several in the overall score, not a standalone verdict on its own.</div>
            </div>
          </div>
        </div>
      )}

      {showInstallGuide && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { setShowInstallGuide(false); }}>
          <div style={Object.assign({}, card, { padding: 20, borderRadius: 14, width: "100%", maxWidth: 380 })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: N }}>Install Zoltra</div>
              <button onClick={function() { setShowInstallGuide(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: TX3, display: "flex" }}><MdClose size={20} /></button>
            </div>

            <div style={{ display: "flex", background: BG, borderRadius: 8, padding: 2, marginBottom: 16 }}>
              <button onClick={function() { setInstallPlatform("ios"); }} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer", background: installPlatform === "ios" ? WH : "transparent", color: installPlatform === "ios" ? N : TX3, fontSize: 13, fontWeight: installPlatform === "ios" ? 700 : 400, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <MdPhoneIphone size={15} /> iPhone / iPad
              </button>
              <button onClick={function() { setInstallPlatform("android"); }} style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "none", cursor: "pointer", background: installPlatform === "android" ? WH : "transparent", color: installPlatform === "android" ? N : TX3, fontSize: 13, fontWeight: installPlatform === "android" ? 700 : 400, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                <MdAndroid size={15} /> Android
              </button>
            </div>

            {installPlatform === "ios" ? (
              <div>
                <div style={{ fontSize: 12, color: TXM, marginBottom: 14 }}>Open Zoltra in <strong>Safari</strong> first — this doesn't work from Chrome or other browsers on iPhone.</div>
                {[
                  { icon: <MdIosShare size={18} color={B} />, text: <>Tap the <strong>Share</strong> icon in Safari's toolbar</> },
                  { icon: <MdAddBox size={18} color={B} />, text: <>Scroll down and tap <strong>Add to Home Screen</strong></> },
                  { icon: <MdCheckCircle size={18} color={B} />, text: <>Tap <strong>Add</strong> in the top right</> },
                ].map(function(step, i) {
                  return (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: BW, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, fontWeight: 700, color: B }}>{i + 1}</div>
                      <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6, paddingTop: 4 }}>{step.text}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: TXM, marginBottom: 14 }}>Open Zoltra in <strong>Chrome</strong> for the smoothest install on Android.</div>
                {[
                  { text: <>Tap the <strong>⋮</strong> menu (top right)</> },
                  { text: <>Tap <strong>Install app</strong> (or <strong>Add to Home screen</strong>)</> },
                  { text: <>Tap <strong>Install</strong> to confirm</> },
                ].map(function(step, i) {
                  return (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: BW, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, fontWeight: 700, color: B }}>{i + 1}</div>
                      <div style={{ fontSize: 13, color: TX2, lineHeight: 1.6, paddingTop: 4 }}>{step.text}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ background: BG, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: TXM, lineHeight: 1.5, marginBottom: 14 }}>
              Once installed, Zoltra opens like any other app from your home screen — full screen, no browser bar, and it updates itself automatically.
            </div>

            <button onClick={function() { setShowInstallGuide(false); }} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid " + BD, background: WH, color: TXM, fontSize: 14, cursor: "pointer" }}>Got it</button>
          </div>
        </div>
      )}

      {/* Edit Profile */}
      {showEditProfile && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { if (!editBusy) setShowEditProfile(false); }}>
          <div style={Object.assign({}, card, { padding: 22, borderRadius: 14, width: "100%", maxWidth: 340 })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: N, marginBottom: 16 }}>Edit profile</div>

            <div style={{ width: 72, height: 72, borderRadius: "50%", background: editAvatarColor, color: WH, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 700, margin: "0 auto 20px" }}>
              {(editName.trim()[0] || "?").toUpperCase()}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: TXM, marginBottom: 6 }}>Name</div>
            <input value={editName} onChange={function(e) { setEditName(e.target.value); }} placeholder="Full name"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid " + BD, fontSize: 16, color: TX, marginBottom: 16 }} />

            <div style={{ fontSize: 12, fontWeight: 700, color: TXM, marginBottom: 10 }}>Avatar colour</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              {[N, B, AC, OK, WN, "#7C3AED"].map(function(c) {
                return <button key={c} onClick={function() { setEditAvatarColor(c); }} style={{ width: 36, height: 36, borderRadius: "50%", background: c, border: "3px solid " + (editAvatarColor === c ? c : "transparent"), boxShadow: editAvatarColor === c ? "0 0 0 2px " + BD : "none", cursor: "pointer" }} />;
              })}
            </div>

            <button onClick={saveProfileEdit} disabled={editBusy} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: editBusy ? BG : N, color: editBusy ? TXM : WH, fontSize: 14, fontWeight: 700, cursor: editBusy ? "default" : "pointer", marginBottom: 8 }}>
              {editBusy ? "Saving…" : "Save changes"}
            </button>
            <button onClick={function() { setShowEditProfile(false); }} disabled={editBusy} style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", background: "transparent", color: TXM, fontSize: 13, cursor: editBusy ? "default" : "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Account confirmation */}
      {showDeleteAccount && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { if (!deleteBusy) { setShowDeleteAccount(false); setDeletePassword(""); setDeleteError(""); } }}>
          <div style={Object.assign({}, card, { padding: 22, borderRadius: 14, width: "100%", maxWidth: 340 })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: ERS, color: ER, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <MdWarningAmber size={24} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: N, marginBottom: 8 }}>Delete your account?</div>
            <p style={{ fontSize: 13, color: TXM, lineHeight: 1.6, marginBottom: 16 }}>This permanently deletes your account and all your data — watchlist, portfolio, notes, and alerts. This can't be undone.</p>
            <input type="password" value={deletePassword} onChange={function(e) { setDeletePassword(e.target.value); setDeleteError(""); }} placeholder="Confirm your password" autoFocus
              style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid " + (deleteError ? ER : BD), fontSize: 16, color: TX, marginBottom: deleteError ? 6 : 16 }}
              onKeyDown={function(e) { if (e.key === "Enter") handleDeleteAccount(); }} />
            {deleteError && <div style={{ fontSize: 12, color: ER, marginBottom: 10 }}>{deleteError}</div>}
            <button onClick={handleDeleteAccount} disabled={deleteBusy} style={{ width: "100%", padding: 12, borderRadius: 8, border: "none", background: deleteBusy ? BG : ER, color: deleteBusy ? TXM : WH, fontSize: 14, fontWeight: 700, cursor: deleteBusy ? "default" : "pointer", marginBottom: 8 }}>
              {deleteBusy ? "Deleting…" : "Permanently delete my account"}
            </button>
            <button onClick={function() { setShowDeleteAccount(false); setDeletePassword(""); setDeleteError(""); }} disabled={deleteBusy} style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", background: "transparent", color: TXM, fontSize: 13, cursor: deleteBusy ? "default" : "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Weekly limit reached — a real modal, not a fleeting toast, since this
          is a meaningful blocker the user needs to actually register and act on. */}
      {showLimitModal && (function() {
        var isProUser = effectivePlan === "pro";
        var searchCopy = isProUser
          ? { title: "You've used all 25 stock analyses this week", body: "Nice work — that's the Pro weekly allowance. It resets Monday." }
          : { title: "You've used all 5 AI stock analyses this week", body: <span>Upgrade to Pro and get <strong style={{ color: N }}>25 AI stock analyses every week</strong> — 5× more research, plus higher caps on watchlist, portfolio, compare, and thesis challenges.</span> };
        var thesisCopy = isProUser
          ? { title: "You've used all 10 thesis challenges this week", body: "That's the Pro weekly allowance. It resets Monday." }
          : { title: "You've used all 3 thesis challenges this week", body: <span>Upgrade to Pro for <strong style={{ color: N }}>10 AI thesis challenges a week</strong> — plus 25 stock analyses a week, and higher caps on watchlist, portfolio, compare, and alerts.</span> };
        var copy = showLimitModal === "thesis" ? thesisCopy : searchCopy;
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 110, padding: 16 }} onClick={function() { setShowLimitModal(null); }}>
            <div style={Object.assign({}, card, { padding: 24, borderRadius: 16, width: "100%", maxWidth: 360, textAlign: "center" })} onClick={function(e) { e.stopPropagation(); }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: BW, color: B, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <MdLock size={26} />
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: N, marginBottom: 8 }}>{copy.title}</div>
              <p style={{ fontSize: 13, color: TXM, lineHeight: 1.6, marginBottom: 18 }}>{copy.body}</p>
              {isProUser ? (
                <button onClick={function() { setShowLimitModal(null); }} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: N, color: WH, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  Got it
                </button>
              ) : (
                <div>
                  <button onClick={function() { setShowLimitModal(null); setProfileTab("plans"); setView("profile"); }} style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", background: N, color: WH, fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
                    Upgrade to Pro →
                  </button>
                  <button onClick={function() { setShowLimitModal(null); }} style={{ width: "100%", padding: 10, borderRadius: 10, border: "none", background: "transparent", color: TXM, fontSize: 13, cursor: "pointer" }}>
                    Maybe later
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Push notification opt-in — shown once, after the user's first watchlist add */}
      {showPushPrompt && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={function() { setShowPushPrompt(false); }}>
          <div style={Object.assign({}, card, { padding: 22, borderRadius: 14, width: "100%", maxWidth: 340, textAlign: "center" })} onClick={function(e) { e.stopPropagation(); }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: ACS, color: AC, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <MdNotificationsActive size={26} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: N, marginBottom: 8 }}>Never miss a move</div>
            <p style={{ fontSize: 13, color: TXM, lineHeight: 1.6, marginBottom: 20 }}>Get notified when a stock you're watching reports earnings, or when your investment thesis breaks. You can turn this off anytime.</p>
            <button onClick={function() { requestNotificationPermission(); setShowPushPrompt(false); }} style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: N, color: WH, fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 8 }}>
              Enable notifications
            </button>
            <button onClick={function() { setShowPushPrompt(false); }} style={{ width: "100%", padding: 10, borderRadius: 10, border: "none", background: "transparent", color: TXM, fontSize: 13, cursor: "pointer" }}>
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {stickyToast ? (
        <div style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", background: N, color: WH, padding: "8px 18px", borderRadius: 10, fontSize: 13, zIndex: 200, display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: WH, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
          {stickyToast}
        </div>
      ) : (toast && <div role="status" aria-live="polite" style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", background: N, color: WH, padding: "8px 18px", borderRadius: 10, fontSize: 13, zIndex: 200 }}>{toast}</div>)}
    </div>
    </div>
  );
}
